<template lang="pug">
.banner-editor
  template(v-if="edit")
    .be-toolbar
      .be-tour-slide-mgmt(ref="tourSlideMgmt" style="display:inline-flex;align-items:center;gap:4px")
        el-tooltip(:content="$t('Add slide')" placement="top")
          el-button(size="mini" plain icon="el-icon-fa-plus fas" @click="addSlide") {{ $t('Slide') }}
        el-tooltip(:content="$t('Duplicate active slide')" placement="top")
          el-button(size="mini" plain icon="el-icon-fa-copy far" @click="duplicateSlide(activeSlideIndex)")
        el-tooltip(:content="$t('Delete active slide')" placement="top")
          el-button(size="mini" plain icon="el-icon-fa-trash-alt far" :disabled="slides.length <= 1" @click="removeSlide(activeSlideIndex)")
      .be-toolbar-sep
      .be-tour-undo-wrap(ref="tourUndoRedo" style="display:inline-flex;align-items:center;gap:4px")
        el-tooltip(:content="$t('Undo (Ctrl+Z)')" placement="top")
          el-button(size="mini" icon="el-icon-fa-undo fas" :disabled="historyIndex <= 0" @click="undo")
        el-tooltip(:content="$t('Redo (Ctrl+Y)')" placement="top")
          el-button(size="mini" icon="el-icon-fa-redo fas" :disabled="historyIndex >= history.length - 1" @click="redo")
      .be-toolbar-sep
      el-tooltip(:content="$t('Show/hide guides')" placement="top")
        el-button(size="mini" :plain="!showGuides" :type="showGuides ? 'primary' : 'default'" icon="el-icon-fa-border-all fas" @click="showGuides = !showGuides")
      .be-toolbar-sep
      .be-lp-wrap
        el-button(size="mini" :plain="!showLayoutPanel" :type="showLayoutPanel ? 'primary' : 'default'" icon="el-icon-fa-sliders-h fas" @click.stop="showLayoutPanel = !showLayoutPanel") {{ $t('Layout') }}
        template(v-if="showLayoutPanel")
          .be-lp-backdrop(@click.stop="showLayoutPanel = false")
          .be-lp(@mousedown.stop @click.stop)
            .be-lp-row
              span.be-lp-label {{ $t('Layout') }}
              select.be-layout-select.be-lp-select(@change="setCanvasLayout($event.target.value)")
                option(value="container" :selected="canvasLayout === 'container'") {{ $t('Centered (in container)') }}
                option(value="full" :selected="canvasLayout === 'full'") {{ $t('Full width') }}
                option(value="full-contained" :selected="canvasLayout === 'full-contained'") {{ $t('Full width (container)') }}
            template(v-if="canvasLayout === 'container'")
              .be-lp-row
                span.be-lp-label {{ $t('Outer area') }}
                select.be-layout-select.be-lp-select(@change="setContainerBgMode($event.target.value)")
                  option(value="none" :selected="containerBgMode === 'none'") {{ $t('Empty') }}
                  option(value="color" :selected="containerBgMode === 'color'") {{ $t('Color / Gradient') }}
                  option(value="image-blur" :selected="containerBgMode === 'image-blur'") {{ $t('Blurred') }}
              template(v-if="containerBgMode === 'color'")
                .be-lp-row
                  span.be-lp-label {{ $t('Fill') }}
                  .be-cbg-fill-btns
                    button(:class="{ 'is-on': containerBgFillMode !== 'gradient' }" @click="containerBgFillMode = 'solid'; emitChange()") Einfarbig
                    button(:class="{ 'is-on': containerBgFillMode === 'gradient' }" @click="containerBgFillMode = 'gradient'; emitChange()") Verlauf
                .be-lp-row
                  span.be-lp-label {{ $t('Color') }}
                  label.be-cbg-color-label(:title="$t('Outer area color')")
                    span.be-cbg-color-swatch(:style="{ background: containerBgColor }")
                    input.be-cbg-color-input(type="color" :value="containerBgColor" @input="setContainerBgColor($event.target.value)")
                template(v-if="containerBgFillMode === 'gradient'")
                  .be-lp-row
                    span.be-lp-label Farbe 2
                    label.be-cbg-color-label
                      span.be-cbg-color-swatch(:style="{ background: containerBgColor2 }")
                      input.be-cbg-color-input(type="color" :value="containerBgColor2" @input="containerBgColor2 = $event.target.value; emitChange()")
                  .be-lp-row
                    span.be-lp-label Winkel
                    .be-cbg-angle-wrap
                      input.be-cbg-angle-slider(type="range" min="0" max="360" step="5" :value="containerBgGradientAngle" @input="containerBgGradientAngle = parseInt($event.target.value); emitChange()")
                      span.be-cbg-angle-val {{ containerBgGradientAngle }}°
                  .be-cbg-presets
                    button.be-cbg-preset(
                      v-for="p in containerBgGradientPresets"
                      :key="p.label"
                      :style="{ background: `linear-gradient(135deg, ${p.c1}, ${p.c2})` }"
                      :title="p.label"
                      @click="containerBgColor = p.c1; containerBgColor2 = p.c2; containerBgGradientAngle = p.angle || 135; emitChange()"
                    )
      .be-toolbar-spacer
      el-button(size="mini" plain icon="el-icon-fa-question-circle fas" @click="showHelp = true") {{ $t('Help & Guide') }}

    // ── Tour ─────────────────────────────────────────────────────
    .be-tour-ask(v-if="tourAsk")
      .be-tour-ask-card
        .be-tour-ask-icon
          i.el-icon-fa-magic.fas
        h3 {{ $t('Welcome to the Slider Editor!') }}
        p {{ $t('Would you like a brief introduction to the most important features?') }}
        .be-tour-ask-actions
          button.be-tour-btn-skip(@click="tourDecline") {{ $t('No thanks') }}
          button.be-tour-btn-start(@click="tourStart") {{ $t('Start tour') }}

    .be-tour(v-if="tourStep >= 0")
      .be-tour-spotlight(v-if="tourSpotlight" :style="tourSpotlightStyle")
      .be-tour-card(:style="tourCardPos")
        .be-tour-progress
          .be-tour-dot(v-for="(s, i) in tourSteps" :key="i" :class="{ 'is-active': i === tourStep }")
        .be-tour-card-header
          span.be-tour-step-num {{ tourStep + 1 }} / {{ tourSteps.length }}
          h3 {{ tourSteps[tourStep].title }}
        p.be-tour-card-text {{ tourSteps[tourStep].text }}
        .be-tour-card-actions
          button.be-tour-btn-skip(@click="tourEnd") {{ $t('Skip') }}
          button.be-tour-btn-start(@click="tourNext") {{ tourStep === tourSteps.length - 1 ? $t('Done ✓') : $t('Next →') }}

    // ── Hilfe-Dialog ─────────────────────────────────────────────
    el-dialog(
      :title="$t('Help & Guide')"
      :visible.sync="showHelp"
      :append-to-body="true"
      custom-class="be-help-dialog"
      width="780px"
      top="5vh"
    )
      .be-help-body
        .be-help-section
          .be-help-section-title
            i.el-icon-fa-list-ol.fas
            span {{ $t('Step-by-step guide') }}
          .be-help-steps
            .be-help-step
              .be-help-step-num 1
              .be-help-step-body
                strong {{ $t('Create slide') }}
                span {{ $t('Click "Slide" in the top left to add a new empty slide. An existing slide can be copied via the duplicate icon or reordered by drag & drop in the left sidebar.') }}
            .be-help-step
              .be-help-step-num 2
              .be-help-step-body
                strong {{ $t('Set up background') }}
                span {{ $t('Click "Background" at the bottom → choose image, video or color/gradient. Via "Background Options" you can set overlay color, blur, position, grayscale and sepia filters.') }}
            .be-help-step
              .be-help-step-num 3
              .be-help-step-body
                strong {{ $t('Add elements') }}
                span {{ $t('Click "+ Element" at the bottom → choose block type: Text, Shape, Button, Image or Video. The new block appears centered on the slide and is immediately selected. Elements can also be added via right-click on the canvas.') }}
            .be-help-step
              .be-help-step-num 4
              .be-help-step-body
                strong {{ $t('Position & scale element') }}
                span {{ $t('Click a block and drag it to the desired position. Resize using the white corner handles. Arrow keys move by 0.5% (with Shift: 2%). Enable guides (toolbar) – elements snap automatically to grid and center lines.') }}
            .be-help-step
              .be-help-step-num 5
              .be-help-step-body
                strong {{ $t('Customize appearance') }}
                span {{ $t('Select a block → the block toolbar appears automatically at the top. Depending on type: font, color, gradient, border, shadow, corners, opacity, rotation etc. Double-click on text or button blocks to edit the text directly.') }}
            .be-help-step
              .be-help-step-num 6
              .be-help-step-body
                strong {{ $t('Add animation (optional)') }}
                span {{ $t('Right-click on an element → Animation → select effect. Set delay and duration in the same menu or adjust by dragging in the "Animation Timeline" (button at the bottom).') }}
            .be-help-step
              .be-help-step-num 7
              .be-help-step-body
                strong {{ $t('Link zuweisen (optional)') }}
                span {{ $t('Rechtsklick auf ein Element → Link. Verfügbare Typen: Intern, Extern, E-Mail, Telefon und Mediathek-Datei. Button-Blöcke haben das Link-Icon auch direkt in der Toolbar.') }}
            .be-help-step
              .be-help-step-num 8
              .be-help-step-body
                strong {{ $t('Mobil-Ansicht anpassen (optional)') }}
                span {{ $t('Wechsle oben in der Toolbar mit dem Toggle zu "Mobil". Verschiebe oder skaliere Elemente für die mobile Ansicht – diese Änderungen gelten nur auf Smartphones. Elemente können separat für Mobil oder Desktop ausgeblendet werden.') }}
            .be-help-step
              .be-help-step-num 9
              .be-help-step-body
                strong {{ $t('Speichern') }}
                span {{ $t('Klicke außerhalb des Editors auf "Speichern" um alle Änderungen zu übernehmen. Rückgängig / Wiederholen jederzeit via Strg+Z / Strg+Y oder die Toolbar-Buttons.') }}

        .be-help-divider

        .be-help-section
          .be-help-section-title
            i.el-icon-fa-film.fas
            span {{ $t('Manage slides') }}
          .be-help-grid
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-plus.fas
              .be-help-item-body
                strong {{ $t('Add slide') }}
                span {{ $t('Insert a new empty slide at the end. Each slide has its own background, elements and settings.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-copy.far
              .be-help-item-body
                strong {{ $t('Duplicate slide') }}
                span {{ $t('Copy the active slide with all blocks, animations and settings – ideal for similar slides.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-trash-alt.far
              .be-help-item-body
                strong {{ $t('Delete slide') }}
                span {{ $t('Remove the active slide. At least one slide must always remain.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-grip-vertical.fas
              .be-help-item-body
                strong {{ $t('Change order') }}
                span {{ $t('Reorder slides in the left sidebar via drag & drop.') }}

        .be-help-divider

        .be-help-section
          .be-help-section-title
            i.el-icon-fa-image.fas
            span {{ $t('Slide background') }}
          .be-help-grid
            .be-help-item
              .be-help-item-icon
                i.el-icon-picture-outline
              .be-help-item-body
                strong {{ $t('Background image') }}
                span {{ $t('Choose an image from the media library. The position (Top / Center / Bottom) can be set under "Background Options".') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-film.fas
              .be-help-item-body
                strong {{ $t('Background video') }}
                span {{ $t('Choose a video from the media library. The video plays automatically muted in a loop.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-fill-drip.fas
              .be-help-item-body
                strong {{ $t('Background color / Gradient') }}
                span {{ $t('Define a solid background or a linear/radial gradient with two colors and adjustable angle.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-adjust.fas
              .be-help-item-body
                strong {{ $t('Background options') }}
                span {{ $t('Color overlay (tint), blur, image position, grayscale filter and sepia filter. Active options are shown counted in parentheses.') }}

        .be-help-divider

        .be-help-section
          .be-help-section-title
            i.el-icon-fa-shapes.fas
            span {{ $t('Block types') }}
          .be-help-grid
            .be-help-item
              .be-help-item-icon.be-help-item-icon--text T
              .be-help-item-body
                strong {{ $t('Text block') }}
                span {{ $t('Multi-line body text. Double-click to edit. Supports Bold, Italic, Underline, Strikethrough, font family, font size, color, alignment, line-height and letter-spacing, background color/gradient, border (width, style, color, corners) and shadow.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-shapes.fas
              .be-help-item-body
                strong {{ $t('Shape block') }}
                span {{ $t('Geometric shapes: Rectangle, Circle, Triangle, Diamond, Star, Hexagon, Arrow. Solid fill or linear/radial gradient. Border (width, style, color) only for Rectangle and Circle. Rounded corners only for Rectangle. Opacity and shadow for all shapes.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-hand-pointer.fas
              .be-help-item-body
                strong {{ $t('Button block') }}
                span {{ $t('Clickable button with an assigned link. Double-click to edit text. Background solid or as gradient (linear/radial). Font: size, color, family, Bold, Italic, Underline, Strikethrough. Border (width, style, color), rounded corners and shadow also configurable.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-image.fas
              .be-help-item-body
                strong {{ $t('Image block') }}
                span {{ $t('Freely position and scale an image from the media library. Crop tool available directly in the toolbar. Image fit: Contain / Cover / Stretch (right-click → Image size). Filters: B&W, Sepia, Duotone (preset or custom 2 colors). Background color, border (width, style, individual sides, color, corner radius) and shadow are also configurable.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-video.fas
              .be-help-item-body
                strong {{ $t('Video block') }}
                span {{ $t('Embed YouTube or Vimeo via URL, or use a video from the media library (MP4/WebM). Controls: Autoplay, Mute, Loop, Show controls, Round display, Portrait/Landscape orientation (for YouTube/Vimeo). Filters: B&W, Sepia, Duotone. Border, rounded corners, shadow and opacity adjustable.') }}

        .be-help-divider

        .be-help-section
          .be-help-section-title
            i.el-icon-fa-mouse-pointer.fas
            span {{ $t('Position & Scale') }}
          .be-help-grid
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-arrows-alt.fas
              .be-help-item-body
                strong {{ $t('Move') }}
                span {{ $t('Click and drag a block. Alternatively: arrow keys (0.5% per step), hold Shift (2% per step).') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-expand-arrows-alt.fas
              .be-help-item-body
                strong {{ $t('Scale') }}
                span {{ $t('Drag the white corner handles. Width and height are stored as a percentage of the slide size and scale proportionally on every device.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-sync-alt.fas
              .be-help-item-body
                strong {{ $t('Rotation') }}
                span {{ $t('Click the rotation button (circular arrow) in the block toolbar and enter the angle in degrees.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-border-all.fas
              .be-help-item-body
                strong {{ $t('Guides') }}
                span {{ $t('Safety frame (8% margin), center lines (50%) and third lines (33 / 66%). Elements snap automatically to these lines when dragging.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-layer-group.fas
              .be-help-item-body
                strong {{ $t('Layer order') }}
                span {{ $t('Right-click on an element → "Bring forward" / "Send backward" to change the stacking order.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-ruler.fas
              .be-help-item-body
                strong {{ $t('Unit vw') }}
                span {{ $t('All size and position values are relative to the slider width (vw = 1% of the width). This means all elements scale proportionally on every device – no fixed pixel values needed.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-object-group.fas
              .be-help-item-body
                strong {{ $t('Multi-select') }}
                span {{ $t('Hold Shift and click multiple blocks to select them simultaneously. A multi-selection bar appears at the top showing the count. All selected blocks can be deleted at once.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-arrows-alt.fas
              .be-help-item-body
                strong {{ $t('Align via right-click') }}
                span {{ $t('Right-click on an element → Position → Align left / Center horizontally / Align right / Top / Center vertically / Bottom. Alternatively enter exact X and Y values (%) directly in the position sub-menu.') }}

        .be-help-divider

        .be-help-section
          .be-help-section-title
            i.el-icon-fa-font.fas
            span {{ $t('Text & Button toolbar') }}
          .be-help-grid
            .be-help-item
              .be-help-item-icon
                b B
              .be-help-item-body
                strong {{ $t('Bold / Italic / Underline / Strikethrough') }}
                span {{ $t('Formatting buttons directly in the block toolbar. For text blocks, the formatting applies to the entire block.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-font.fas
              .be-help-item-body
                strong {{ $t('Font family & size') }}
                span {{ $t('Choose a font from the dropdown. Enter font size in vw – adapts automatically to every screen size (e.g. 2 vw ≈ 38 px on Full HD).') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-text-height.fas
              .be-help-item-body
                strong {{ $t('Line height & letter spacing') }}
                span {{ $t('Set line-height and letter-spacing in the typography dropdown (Aa icon).') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-align-left.fas
              .be-help-item-body
                strong {{ $t('Text alignment') }}
                span {{ $t('Left, center or right – for text blocks only.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-fill-drip.fas
              .be-help-item-body
                strong {{ $t('Text background') }}
                span {{ $t('Background color with adjustable opacity and optional padding. Border: width, style (Solid, Dashed, Dotted), color and corner radius.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-palette.fas
              .be-help-item-body
                strong {{ $t('Button background') }}
                span {{ $t('Solid or gradient (linear with angle or radial). Two colors freely selectable, CI colors are offered as quick selection.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-border-style.fas
              .be-help-item-body
                strong {{ $t('Button border') }}
                span {{ $t('Border width in px, style (Solid, Dashed, Dotted) and border color. CI colors are available as quick selection.') }}

        .be-help-divider

        .be-help-section
          .be-help-section-title
            i.el-icon-fa-image.fas
            span {{ $t('Image block toolbar') }}
          .be-help-grid
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-crop-alt.fas
              .be-help-item-body
                strong {{ $t('Crop image') }}
                span {{ $t('Click "Crop image" in the toolbar to open the cropping dialog. After cropping, the cut section is saved as a separate image URL – the original is preserved.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-expand-arrows-alt.fas
              .be-help-item-body
                strong {{ $t('Image fit (Contain / Cover / Stretch)') }}
                span {{ $t('Right-click on the image block → Image size → choose fit mode. Contain: image fully visible with letterboxing. Cover: fills the block, cropped. Stretch: distorted to fill.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-eye.fas
              .be-help-item-body
                strong {{ $t('Opacity & Filter') }}
                span {{ $t('Toolbar button "Opacity / Filter": set transparency (0–100 %). Filters: B&W (grayscale), Sepia or Duotone – choose from presets or define two custom colors for shadow and highlight tones.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-fill-drip.fas
              .be-help-item-body
                strong {{ $t('Background / Border') }}
                span {{ $t('Add a color frame behind the image. Border: width, style (Solid, Dashed, Dotted), color and individually toggleable sides (Top / Right / Bottom / Left). Rounded corners in px.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-adjust.fas
              .be-help-item-body
                strong {{ $t('Shadow (image)') }}
                span {{ $t('Enable drop shadow; set blur radius and color (CI colors, standard palette or custom hex color).') }}

        .be-help-divider

        .be-help-section
          .be-help-section-title
            i.el-icon-fa-video.fas
            span {{ $t('Video block toolbar') }}
          .be-help-grid
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-link.fas
              .be-help-item-body
                strong {{ $t('Video URL') }}
                span {{ $t('Paste a YouTube or Vimeo URL into the URL field – the video ID is detected automatically. Alternatively select a video from the media library (MP4, WebM).') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-play.fas
              .be-help-item-body
                strong {{ $t('Playback controls') }}
                span {{ $t('Autoplay (requires Mute), Mute, Loop and Show player controls are each individually switchable via toolbar buttons.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-circle.far
              .be-help-item-body
                strong {{ $t('Round display / Portrait') }}
                span {{ $t('Round: shows the video clipped as a circle. Portrait / Landscape toggle (YouTube/Vimeo): switches between 9:16 and 16:9 aspect ratio in the block.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-eye.fas
              .be-help-item-body
                strong {{ $t('Opacity & Filter (video)') }}
                span {{ $t('Opacity slider plus color filters: B&W, Sepia or Duotone (preset or custom 2-color combination). Duotone maps the dark tones to color 1 and the light tones to color 2.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-border-style.fas
              .be-help-item-body
                strong {{ $t('Border & Corners (video)') }}
                span {{ $t('Border width in px, style (Solid, Dashed, Dotted), selective sides, border color. Rounded corners in px (0 = sharp, 200 = fully rounded/circle).') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-adjust.fas
              .be-help-item-body
                strong {{ $t('Shadow (video)') }}
                span {{ $t('Drop shadow with adjustable blur radius and color.') }}

        .be-help-divider

        .be-help-section
          .be-help-section-title
            i.el-icon-fa-magic.fas
            span {{ $t('Animations') }}
          .be-help-grid
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-play.fas
              .be-help-item-body
                strong {{ $t('Assign animation') }}
                span {{ $t('Right-click on an element → Animation → select effect. 18 entrance effects available (Slide, Fade, Zoom, Bounce, Fly in, etc.).') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-fighter-jet.fas
              .be-help-item-body
                strong {{ $t('Fly-in animations') }}
                span {{ $t('4 new effects: "Fly from top / bottom / left / right". The element starts completely outside the visible area and flies into its position – no fade, pure movement.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-sliders-h.fas
              .be-help-item-body
                strong {{ $t('Delay & duration') }}
                span {{ $t('In the right-click menu under "Animation" or directly in the timeline (drag bar = delay, drag right edge = duration).') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-bezier-curve.fas
              .be-help-item-body
                strong {{ $t('Easing (motion curve)') }}
                span {{ $t('Right-click → Animation → Type. 8 curves from "Ease" to "Elastic" selectable.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-clock.fas
              .be-help-item-body
                strong {{ $t('Animation timeline') }}
                span {{ $t('Show button at the bottom. Displays all blocks of the active slide in a timeline – change delay and duration by dragging.') }}

        .be-help-divider

        .be-help-section
          .be-help-section-title
            i.el-icon-fa-mobile-alt.fas
            span {{ $t('Responsive / Mobil-Ansicht') }}
          .be-help-grid
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-desktop.fas
              .be-help-item-body
                strong {{ $t('Desktop / Mobil-Toggle') }}
                span {{ $t('In der Toolbar-Zeile kannst du zwischen Desktop- und Mobil-Modus wechseln. Im Mobil-Modus siehst du eine 550px breite Vorschau und kannst für jeden Block separate mobile Einstellungen festlegen.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-arrows-alt.fas
              .be-help-item-body
                strong {{ $t('Mobile Position & Größe') }}
                span {{ $t('Im Mobil-Modus werden Drag & Resize-Aktionen als mobile Overrides gespeichert (block.mobile.x/y/w/h). Die Desktop-Werte bleiben unverändert. Fehlende mobile Werte verwenden automatisch den Desktop-Wert als Fallback.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-font.fas
              .be-help-item-body
                strong {{ $t('Mobile Schriftgröße') }}
                span {{ $t('Im Mobil-Modus kann die Schriftgröße separat für Mobil angepasst werden. Sie wird als block.mobile.fontSize gespeichert und auf Mobilgeräten (< 768 px) automatisch verwendet.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-ruler-combined.fas
              .be-help-item-body
                strong {{ $t('Mobile Banner-Höhe') }}
                span {{ $t('Im Mobil-Modus erscheint rechts in der Toolbar ein Schalter "Benutzerdefinierte Mobile Höhe". Wenn aktiv, kann eine eigene Banner-Höhe (vw) für Mobilgeräte gesetzt werden – z. B. 50 vw statt 30 vw auf Desktop.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-desktop.fas
              .be-help-item-body
                strong {{ $t('Element ausblenden') }}
                span {{ $t('Neben dem Desktop/Mobil-Toggle befindet sich (wenn ein Block ausgewählt ist) das Panel "Element Ausblenden" mit zwei Buttons: Desktop-Icon = auf Desktop ausblenden, Handy-Icon = auf Mobil ausblenden. Auch per Rechtsklick → "Auf Mobil/Desktop ausblenden" erreichbar.') }}

        .be-help-divider

        .be-help-section
          .be-help-section-title
            i.el-icon-fa-sliders-h.fas
            span {{ $t('Layout & Einstellungen') }}
          .be-help-grid
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-ruler-combined.fas
              .be-help-item-body
                strong {{ $t('Banner-Höhe') }}
                span {{ $t('Direkt in der Toolbar-Zeile (rechts) einstellbar: 10–100 vw. Der Wert bedeutet: der Banner ist x% so hoch wie der Bildschirm breit ist. Im Mobil-Modus kann eine separate Höhe aktiviert werden.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-columns.fas
              .be-help-item-body
                strong {{ $t('Canvas-Layout') }}
                span {{ $t('"Zentriert (im Container)" = Slider zentriert in der Seitenbreite. "Volle Breite" = Slider über die gesamte Seitenbreite. "Volle Breite (Container)" = volle Breite, Inhalt auf Container-Breite begrenzt.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-paint-roller.fas
              .be-help-item-body
                strong {{ $t('Außenbereich (Container-Layout)') }}
                span {{ $t('Bei "Zentriert"-Layout kann der seitliche Außenbereich leer, eingefärbt oder mit Bildunschärfe ausgefüllt werden.') }}

        .be-help-divider

        .be-help-section
          .be-help-section-title
            i.el-icon-fa-history.fas
            span {{ $t('History & shortcuts') }}
          .be-help-grid
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-undo.fas
              .be-help-item-body
                strong {{ $t('Undo / Redo') }}
                span {{ $t('Ctrl+Z / Ctrl+Y or the arrow buttons in the toolbar. The history covers all changes of the current session.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-mouse.fas
              .be-help-item-body
                strong {{ $t('Rechtsklick-Menü (Element)') }}
                span {{ $t('Rechtsklick auf ein Element öffnet: Bearbeiten, Kopieren, Einfügen, Löschen, Position (ausrichten + X/Y-Eingabe), Ebene (nach vorne / hinten), Drehen (−90°/−10°/+10°/+90°, exakte Grad-Eingabe, Zurücksetzen), Animation, Link, Bildgröße (bei Bild-Blöcken), Auf Mobil/Desktop ausblenden.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-mouse.fas
              .be-help-item-body
                strong {{ $t('Right-click menu (canvas)') }}
                span {{ $t('Right-click on the canvas background opens: Add element (all types including Video), Paste, Change/remove background image or video.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-copy.far
              .be-help-item-body
                strong {{ $t('Copy & Paste') }}
                span {{ $t('Right-click on an element → Copy. Then right-click anywhere → Paste to insert a duplicate. Works across slides within the same editor session.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-link.fas
              .be-help-item-body
                strong {{ $t('Add link') }}
                span {{ $t('Right-click → Link or link icon in the button toolbar. Link types: Internal (page), External (URL), Email, Phone, Media Library file.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-keyboard.fas
              .be-help-item-body
                strong {{ $t('Keyboard shortcuts') }}
                span {{ $t('Del = delete selected element. Arrow keys = move element 0.5% per step (+ Shift = 2% per step). Ctrl+Z = Undo. Ctrl+Y = Redo.') }}
            .be-help-item
              .be-help-item-icon
                i.el-icon-fa-magic.fas
              .be-help-item-body
                strong {{ $t('Guided tour') }}
                span {{ $t('Click "Start guided tour" at the bottom of this dialog to get a step-by-step interactive introduction to all key areas of the editor.') }}

      template(v-slot:footer)
        .be-help-footer
          el-button(size="small" icon="el-icon-fa-magic fas" @click="showHelp = false; tourAsk = false; tourStep = 0; _tourUpdateSpotlight()") {{ $t('Start guided tour') }}
          el-button(size="small" type="primary" @click="showHelp = false") {{ $t('Close') }}

    .be-body
      svg.be-thumb-duotone-defs(v-if="slidesDuotoneFilters.length" aria-hidden="true")
        defs
          filter(v-for="f in slidesDuotoneFilters" :key="f.id" :id="f.id" color-interpolation-filters="sRGB")
            feColorMatrix(type="saturate" values="0")
            feComponentTransfer
              feFuncR(type="table" :tableValues="f.values.r")
              feFuncG(type="table" :tableValues="f.values.g")
              feFuncB(type="table" :tableValues="f.values.b")
      .be-slide-list(ref="tourSlideList")
        draggable(
          v-model="slides"
          handle=".be-slide-handle"
          :animation="150"
          ghost-class="be-slide-ghost"
          @end="emitChange"
        )
          .be-slide-item(
            v-for="(slide, i) in slides"
            :key="slide.id"
            :class="{ 'is-active': activeSlideIndex === i }"
            @click="selectSlide(i)"
          )
            .be-slide-handle
              svg(xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="#9ca3af")
                circle(cx="2" cy="2" r="1.2")
                circle(cx="6" cy="2" r="1.2")
                circle(cx="2" cy="7" r="1.2")
                circle(cx="6" cy="7" r="1.2")
                circle(cx="2" cy="12" r="1.2")
                circle(cx="6" cy="12" r="1.2")
            .be-slide-thumb
              .be-slide-thumb-bg(v-if="slide.image && slide.image.url" :style="slideThumbBgStyle(slide)")
              video.be-slide-thumb-video(v-else-if="slide.video && slide.video.url" :key="slide.video.url" :style="slideThumbVideoStyle(slide)" muted playsinline preload="metadata")
                source(:src="slide.video.url")
              .be-slide-thumb-color(v-else-if="slideHasBgColor(slide)" :style="slideBgColorStyle(slide)")
              i.el-icon-picture-outline(v-else)
            span.be-slide-nr {{ i + 1 }}
            button.be-slide-dup(type="button" :title="$t('Duplicate slide')" @click.stop="duplicateSlide(i)")
              i.el-icon-fa-copy.far
            button.be-slide-del(v-if="slides.length > 1" type="button" :title="$t('Delete slide')" @click.stop="removeSlide(i)")
              i.el-icon-fa-times.fas

        button.be-slide-add(type="button" @click="addSlide")
          i.el-icon-fa-plus.fas
          | &nbsp;{{ $t('Slide') }}

      .be-main(v-if="activeSlide")

        // ── Block-Toolbar ─────────────────────────────────────────
        .be-block-bar.be-block-bar--multi(v-if="selectedBlockIds.length > 1" @mousedown.stop @click.stop)
          i.el-icon-fa-object-group.fas(style="font-size:13px;color:#6366f1")
          span.be-multi-label {{ selectedBlockIds.length }} Elemente ausgewählt
          .be-tb-sep
          el-tooltip(content="Alle löschen" placement="top")
            button.be-tb-btn.be-tb-del(@click.stop="removeBlock(activeBlockId)")
              i.el-icon-fa-trash-alt.far
        .be-block-bar(v-else ref="tourToolbar" :class="{ 'is-disabled': !activeBlock }" @mousedown.stop @click.stop)

          // Text
          template(v-if="!blockProps.type || blockProps.type === 'text'")
            el-tooltip(:content="$t('Bold')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.bold }" @mousedown.prevent="toggleFormat('bold', 'bold')")
                b B
            el-tooltip(:content="$t('Italic')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.italic }" @mousedown.prevent="toggleFormat('italic', 'italic')")
                i I
            el-tooltip(:content="$t('Underline')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.underline }" @mousedown.prevent="toggleFormat('underline', 'underline')")
                u U
            el-tooltip(:content="$t('Strikethrough')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.strikethrough }" @mousedown.prevent="toggleFormat('strikethrough', 'strikeThrough')")
                s S
            .be-tb-sep
            el-select.be-tb-font-select(
              :value="blockProps.fontFamily || ''"
              size="mini"
              :placeholder="$t('Font family')"
              clearable
              @change="setBlockProp('fontFamily', $event || '')"
            )
              el-option(v-for="f in fontFamilies" :key="f.key || '__tpl__'" :label="f.label" :value="f.key" :style="{ fontFamily: f.previewFont || f.key }")
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Typography')" placement="top")
                button.be-tb-btn.be-tb-color-btn(@click.stop="toggleColorPicker('text-typo')")
                  i.el-icon-fa-text-height.fas(style="font-size:11px")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'text-typo'" style="min-width:200px")
                .be-cd-section
                  .be-cd-label {{ $t('Line height') }}
                  .be-cd-blur-row(@click.stop @mousedown.stop)
                    button.be-cd-blur-btn(@click.stop="setBlockProp('lineHeight', Math.max(0.8, Math.round(((blockProps.lineHeight !== undefined ? blockProps.lineHeight : 1.2) - 0.1) * 10) / 10))") −
                    input.be-cd-blur-val(type="number" :value="(blockProps.lineHeight !== undefined ? blockProps.lineHeight : 1.2).toFixed(1)" min="0.8" max="4" step="0.1" @change="setBlockProp('lineHeight', Math.min(4, Math.max(0.8, parseFloat($event.target.value) || 1.2)))" @keydown.enter.prevent="$event.target.blur()" @focus="$event.target.select()" @mousedown.stop @click.stop @keydown.stop)
                    button.be-cd-blur-btn(@click.stop="setBlockProp('lineHeight', Math.min(4, Math.round(((blockProps.lineHeight !== undefined ? blockProps.lineHeight : 1.2) + 0.1) * 10) / 10))") +
                .be-cd-sep
                .be-cd-section
                  .be-cd-label {{ $t('Letter spacing') }}
                  .be-cd-blur-row(@click.stop @mousedown.stop)
                    button.be-cd-blur-btn(@click.stop="setBlockProp('letterSpacing', Math.max(-0.2, Math.round(((blockProps.letterSpacing || 0) - 0.02) * 100) / 100))") −
                    input.be-cd-blur-val(type="number" :value="(blockProps.letterSpacing || 0).toFixed(2)" min="-0.2" max="1" step="0.02" @change="setBlockProp('letterSpacing', Math.min(1, Math.max(-0.2, parseFloat($event.target.value) || 0)))" @keydown.enter.prevent="$event.target.blur()" @focus="$event.target.select()" @mousedown.stop @click.stop @keydown.stop)
                    button.be-cd-blur-btn(@click.stop="setBlockProp('letterSpacing', Math.min(1, Math.round(((blockProps.letterSpacing || 0) + 0.02) * 100) / 100))") +
            .be-tb-sep
            el-tooltip(:content="$t('Align left')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': !blockProps.align || blockProps.align === 'left' }" @click="setBlockProp('align', 'left')")
                i.el-icon-fa-align-left.fas
            el-tooltip(:content="$t('Align center')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.align === 'center' }" @click="setBlockProp('align', 'center')")
                i.el-icon-fa-align-center.fas
            el-tooltip(:content="$t('Align right')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.align === 'right' }" @click="setBlockProp('align', 'right')")
                i.el-icon-fa-align-right.fas
            .be-tb-sep
            el-tooltip(:content="$t('Decrease font size')" placement="top")
              button.be-tb-btn(@click="setBlockProp('fontSize', Math.max(0.2, Math.round(((blockProps.fontSize || 2) - 0.1) * 10) / 10))") −
            .be-tb-size-wrap
              input.be-tb-size-input(type="number" :value="(blockProps.fontSize || 2).toFixed(1)" min="0.2" max="15" step="0.1" @focus="$event.target.select()" @input="setFontSizeFromInput($event, 2)" @blur="$event.target.value = (blockProps.fontSize || 2).toFixed(1)" @keydown.enter.prevent="$event.target.blur()" @mousedown.stop @click.stop)
              span.be-tb-size-unit vw
            el-tooltip(:content="$t('Increase font size')" placement="top")
              button.be-tb-btn(@click="setBlockProp('fontSize', Math.min(15, Math.round(((blockProps.fontSize || 2) + 0.1) * 10) / 10))") +
            .be-tb-sep
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Text color')" placement="top")
                button.be-tb-btn.be-tb-color-btn(@click.stop="toggleColorPicker('text-color')")
                  .be-tb-color-swatch(:style="{ background: blockProps.color || '#ffffff' }")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'text-color'")
                template(v-if="ciColors && ciColors.length")
                  .be-cd-section
                    .be-cd-label {{ $t('CI colors') }}
                    .be-cd-swatches
                      button.be-cd-swatch(v-for="c in ciColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.color === c }" @click.stop="pickColor('color', c)")
                  .be-cd-sep
                .be-cd-section
                  .be-cd-label {{ $t('Standard colors') }}
                  .be-cd-swatches
                    button.be-cd-swatch(v-for="c in textColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.color === c }" @click.stop="pickColor('color', c)")
                .be-cd-sep
                .be-cd-custom
                  span.be-cd-label {{ $t('Custom color') }}
                  .be-cd-custom-row
                    input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('color')" @keydown.enter.prevent="onHexInput('color')")
                    input.be-cd-color-input(type="color" :value="toHex(blockProps.color)" @input="pickColor('color', $event.target.value, false)")
                .be-cd-sep
                .be-cd-section
                  .be-cd-label {{ $t('Opacity') }}
                  .be-cd-opacity-row
                    input.be-cd-opacity-slider(type="range" min="0" max="100" step="1" :value="Math.round((blockProps.opacity !== undefined ? blockProps.opacity : 1) * 100)" @input="setBlockProp('opacity', parseFloat($event.target.value) / 100)" @mousedown.stop @click.stop)
                    span.be-cd-opacity-val {{ Math.round((blockProps.opacity !== undefined ? blockProps.opacity : 1) * 100) }}%
            .be-tb-sep
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Shadow')" placement="top")
                button.be-tb-btn.be-tb-color-btn(:class="{ 'is-on': blockProps.shadow }" @click.stop="toggleColorPicker('shadow-settings')")
                  i.el-icon-fa-adjust.fas(style="font-size:11px")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'shadow-settings'" style="min-width:210px")
                .be-cd-section
                  .be-cd-switch-row
                    span.be-cd-label {{ $t('Shadow') }}
                    el-switch(:value="!!blockProps.shadow" @change="setBlockProp('shadow', $event)" @click.stop @mousedown.stop)
                template(v-if="blockProps.shadow")
                  .be-cd-sep
                  .be-cd-section
                    .be-cd-label {{ $t('Blur') }}
                    .be-cd-blur-row(@click.stop @mousedown.stop)
                      button.be-cd-blur-btn(@click.stop="setBlockProp('shadowBlur', Math.max(0, Math.round(((blockProps.shadowBlur !== undefined ? blockProps.shadowBlur : 0.3) - 0.1) * 10) / 10))") −
                      input.be-cd-blur-val(type="number" :value="(blockProps.shadowBlur !== undefined ? blockProps.shadowBlur : 0.3).toFixed(1)" min="0" max="5" step="0.1" @change="setBlockProp('shadowBlur', Math.min(5, Math.max(0, parseFloat($event.target.value) || 0)))" @keydown.enter.prevent="$event.target.blur()" @focus="$event.target.select()" @mousedown.stop @click.stop @keydown.stop)
                      button.be-cd-blur-btn(@click.stop="setBlockProp('shadowBlur', Math.min(5, Math.round(((blockProps.shadowBlur !== undefined ? blockProps.shadowBlur : 0.3) + 0.1) * 10) / 10))") +
                  .be-cd-sep
                  template(v-if="ciColors && ciColors.length")
                    .be-cd-section
                      .be-cd-label {{ $t('CI colors') }}
                      .be-cd-swatches
                        button.be-cd-swatch(v-for="c in ciColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.shadowColor === c }" @click.stop="pickColor('shadowColor', c, false)")
                    .be-cd-sep
                  .be-cd-section
                    .be-cd-label {{ $t('Standard colors') }}
                    .be-cd-swatches
                      button.be-cd-swatch(v-for="c in shadowColors" :key="c" :style="{ background: c }" :class="{ 'is-on': (blockProps.shadowColor || 'rgba(0,0,0,0.7)') === c }" @click.stop="pickColor('shadowColor', c, false)")
                  .be-cd-sep
                  .be-cd-custom
                    span.be-cd-label {{ $t('Custom color') }}
                    .be-cd-custom-row
                      input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('shadowColor')" @keydown.enter.prevent="onHexInput('shadowColor')")
                      input.be-cd-color-input(type="color" :value="toHex(blockProps.shadowColor)" @input="pickColor('shadowColor', $event.target.value, false)")

            .be-tb-sep
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Background / Border')" placement="top")
                button.be-tb-btn.be-tb-color-btn(:class="{ 'is-on': !!blockProps.bgColor || !!blockProps.bgBorderWidth || blockProps.bgFillMode === 'gradient' }" @click.stop="toggleColorPicker('text-bg')")
                  i.el-icon-fa-fill-drip.fas(style="font-size:11px")
                  .be-tb-color-swatch(v-if="blockProps.bgFillMode === 'gradient'" :style="{ background: `linear-gradient(90deg, ${blockProps.bgGradientColor1 || '#3b82f6'}, ${blockProps.bgGradientColor2 || 'transparent'})` }")
                  .be-tb-color-swatch(v-else-if="blockProps.bgColor" :style="{ background: blockProps.bgColor }")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'text-bg'" style="min-width:440px;padding:0")
                .be-cd-two-col
                  .be-cd-col
                    .be-cd-section
                      .be-cd-fill-mode-switch
                        button(:class="{ 'is-on': !blockProps.bgFillMode || blockProps.bgFillMode === 'solid' }" @click.stop="setBlockProp('bgFillMode', 'solid')") {{ $t('Solid') }}
                        button(:class="{ 'is-on': blockProps.bgFillMode === 'gradient' }" @click.stop="setBlockProp('bgFillMode', 'gradient')") {{ $t('Gradient') }}
                    .be-cd-sep
                    template(v-if="!blockProps.bgFillMode || blockProps.bgFillMode === 'solid'")
                      template(v-if="ciColors && ciColors.length")
                        .be-cd-section
                          .be-cd-label {{ $t('CI colors') }}
                          .be-cd-swatches
                            button.be-cd-swatch(v-for="c in ciColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgColor === c }" @click.stop="pickColor('bgColor', c, false)")
                        .be-cd-sep
                      .be-cd-section
                        .be-cd-label {{ $t('Standard colors') }}
                        .be-cd-swatches
                          button.be-cd-swatch.be-cd-swatch--none(:class="{ 'is-on': !blockProps.bgColor && !blockProps.bgBorderWidth }" @click.stop="setBlockProp('bgColor', ''); setBlockProp('bgBorderWidth', 0); activeColorPicker = null" :title="$t('No background / border')") ✕
                          button.be-cd-swatch(v-for="c in textBgColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgColor === c }" @click.stop="pickColor('bgColor', c, false)")
                      .be-cd-sep
                      .be-cd-custom
                        span.be-cd-label {{ $t('Custom color') }}
                        .be-cd-custom-row
                          input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('bgColor')" @keydown.enter.prevent="onHexInput('bgColor')")
                          input.be-cd-color-input(type="color" :value="toHex(blockProps.bgColor)" @input="pickColor('bgColor', $event.target.value, false)")
                      template(v-if="blockProps.bgColor")
                        .be-cd-sep
                        .be-cd-section
                          .be-cd-label {{ $t('Opacity') }}
                          .be-cd-opacity-row
                            input.be-cd-opacity-slider(type="range" min="0" max="100" step="1" :value="Math.round((blockProps.bgOpacity !== undefined ? blockProps.bgOpacity : 1) * 100)" @input="setBlockProp('bgOpacity', parseFloat($event.target.value) / 100)" @mousedown.stop @click.stop)
                            span.be-cd-opacity-val {{ Math.round((blockProps.bgOpacity !== undefined ? blockProps.bgOpacity : 1) * 100) }}%
                    template(v-else)
                      .be-cd-section
                        .be-cd-label {{ $t('Type') }}
                        .be-cd-fill-mode-switch
                          button(:class="{ 'is-on': !blockProps.bgGradientType || blockProps.bgGradientType === 'linear' }" @click.stop="setBlockProp('bgGradientType', 'linear')") {{ $t('Linear') }}
                          button(:class="{ 'is-on': blockProps.bgGradientType === 'radial' }" @click.stop="setBlockProp('bgGradientType', 'radial')") {{ $t('Radial') }}
                      template(v-if="!blockProps.bgGradientType || blockProps.bgGradientType === 'linear'")
                        .be-cd-sep
                        .be-cd-section
                          .be-cd-label {{ $t('Angle') }}
                          .be-cd-blur-row(@click.stop @mousedown.stop)
                            button.be-cd-blur-btn(@click.stop="setBlockProp('bgGradientAngle', ((blockProps.bgGradientAngle !== undefined ? blockProps.bgGradientAngle : 90) - 15 + 360) % 360)") −
                            input.be-cd-blur-val(type="number" :value="blockProps.bgGradientAngle !== undefined ? blockProps.bgGradientAngle : 90" min="0" max="359" step="1" @change="setBlockProp('bgGradientAngle', ((parseInt($event.target.value) || 0) % 360 + 360) % 360)" @keydown.enter.prevent="$event.target.blur()" @focus="$event.target.select()" @mousedown.stop @click.stop @keydown.stop)
                            button.be-cd-blur-btn(@click.stop="setBlockProp('bgGradientAngle', ((blockProps.bgGradientAngle !== undefined ? blockProps.bgGradientAngle : 90) + 15) % 360)") +
                      .be-cd-sep
                      .be-cd-section
                        .be-cd-label {{ $t('Color 1') }}
                        .be-cd-swatches
                          template(v-if="ciColors && ciColors.length")
                            button.be-cd-swatch(v-for="c in ciColors" :key="'ci1'+c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgGradientColor1 === c }" @click.stop="setBlockProp('bgGradientColor1', c)")
                          button.be-cd-swatch(v-for="c in textBgColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgGradientColor1 === c }" @click.stop="setBlockProp('bgGradientColor1', c)")
                      .be-cd-sep
                      .be-cd-custom
                        span.be-cd-label {{ $t('Custom color') }}
                        .be-cd-custom-row
                          input.be-cd-hex-input(type="text" :value="toHex(blockProps.bgGradientColor1)" placeholder="#000000" spellcheck="false" @blur="setBlockProp('bgGradientColor1', $event.target.value)" @keydown.enter.prevent="setBlockProp('bgGradientColor1', $event.target.value); $event.target.blur()" @mousedown.stop @click.stop @keydown.stop)
                          input.be-cd-color-input(type="color" :value="toHex(blockProps.bgGradientColor1 || '#3b82f6')" @input="setBlockProp('bgGradientColor1', $event.target.value)")
                      .be-cd-sep
                      .be-cd-section
                        .be-cd-label {{ $t('Color 2') }}
                        .be-cd-swatches
                          button.be-cd-swatch.be-cd-swatch--none(:class="{ 'is-on': !blockProps.bgGradientColor2 }" @click.stop="setBlockProp('bgGradientColor2', 'transparent')" :title="$t('Transparent')") ✕
                          template(v-if="ciColors && ciColors.length")
                            button.be-cd-swatch(v-for="c in ciColors" :key="'ci2'+c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgGradientColor2 === c }" @click.stop="setBlockProp('bgGradientColor2', c)")
                          button.be-cd-swatch(v-for="c in textBgColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgGradientColor2 === c }" @click.stop="setBlockProp('bgGradientColor2', c)")
                      .be-cd-sep
                      .be-cd-custom
                        span.be-cd-label {{ $t('Custom color') }}
                        .be-cd-custom-row
                          input.be-cd-hex-input(type="text" :value="toHex(blockProps.bgGradientColor2)" placeholder="transparent" spellcheck="false" @blur="setBlockProp('bgGradientColor2', $event.target.value)" @keydown.enter.prevent="setBlockProp('bgGradientColor2', $event.target.value); $event.target.blur()" @mousedown.stop @click.stop @keydown.stop)
                          input.be-cd-color-input(type="color" :value="toHex(blockProps.bgGradientColor2 || '#ffffff')" @input="setBlockProp('bgGradientColor2', $event.target.value)")
                  .be-cd-col-sep
                  .be-cd-col
                    .be-cd-section
                      .be-cd-label {{ $t('Border') }}
                      .be-cd-blur-row(@click.stop @mousedown.stop)
                        button.be-cd-blur-btn(@click.stop="setBlockProp('bgBorderWidth', Math.max(0, (blockProps.bgBorderWidth || 0) - 1))") −
                        .be-tb-size-wrap(style="margin:0")
                          input.be-tb-size-input(type="number" :value="blockProps.bgBorderWidth || 0" min="0" max="20" step="1" @focus="$event.target.select()" @input="setPxPropFromInput($event, 'bgBorderWidth', 0, 0, 20)" @blur="$event.target.value = blockProps.bgBorderWidth || 0" @keydown.enter.prevent="$event.target.blur()" @mousedown.stop @click.stop)
                          span.be-tb-size-unit px
                        button.be-cd-blur-btn(@click.stop="setBlockProp('bgBorderWidth', Math.min(20, (blockProps.bgBorderWidth || 0) + 1))") +
                      .be-cd-border-style-row(v-if="blockProps.bgBorderWidth" style="margin-top:6px")
                        button(:class="{ 'is-on': !blockProps.bgBorderStyle || blockProps.bgBorderStyle === 'solid' }" @click.stop="setBlockProp('bgBorderStyle', 'solid')" :title="$t('Solid')")
                          .be-cd-border-preview(style="border-bottom-style:solid")
                        button(:class="{ 'is-on': blockProps.bgBorderStyle === 'dashed' }" @click.stop="setBlockProp('bgBorderStyle', 'dashed')" :title="$t('Dashed')")
                          .be-cd-border-preview(style="border-bottom-style:dashed")
                        button(:class="{ 'is-on': blockProps.bgBorderStyle === 'dotted' }" @click.stop="setBlockProp('bgBorderStyle', 'dotted')" :title="$t('Dotted')")
                          .be-cd-border-preview(style="border-bottom-style:dotted")
                      .be-cd-border-sides-row(v-if="blockProps.bgBorderWidth")
                        button.be-bside-btn(:class="{ 'is-on': borderSidesHas('bgBorderSides', 'top') }" @click.stop="toggleBorderSide('bgBorderSides', 'top')" :title="$t('Top')")
                          .be-bside-icon.be-bside--top
                        button.be-bside-btn(:class="{ 'is-on': borderSidesHas('bgBorderSides', 'right') }" @click.stop="toggleBorderSide('bgBorderSides', 'right')" :title="$t('Right')")
                          .be-bside-icon.be-bside--right
                        button.be-bside-btn(:class="{ 'is-on': borderSidesHas('bgBorderSides', 'bottom') }" @click.stop="toggleBorderSide('bgBorderSides', 'bottom')" :title="$t('Bottom')")
                          .be-bside-icon.be-bside--bottom
                        button.be-bside-btn(:class="{ 'is-on': borderSidesHas('bgBorderSides', 'left') }" @click.stop="toggleBorderSide('bgBorderSides', 'left')" :title="$t('Left')")
                          .be-bside-icon.be-bside--left
                      template(v-if="blockProps.bgBorderWidth")
                        .be-cd-sep
                        template(v-if="ciColors && ciColors.length")
                          .be-cd-section
                            .be-cd-label {{ $t('CI colors') }}
                            .be-cd-swatches
                              button.be-cd-swatch(v-for="c in ciColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgBorderColor === c }" @click.stop="pickColor('bgBorderColor', c, false)")
                          .be-cd-sep
                        .be-cd-section
                          .be-cd-label {{ $t('Standard colors') }}
                          .be-cd-swatches
                            button.be-cd-swatch(v-for="c in textBgColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgBorderColor === c }" @click.stop="pickColor('bgBorderColor', c, false)")
                        .be-cd-sep
                        .be-cd-custom
                          span.be-cd-label {{ $t('Custom color') }}
                          .be-cd-custom-row
                            input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('bgBorderColor')" @keydown.enter.prevent="onHexInput('bgBorderColor')")
                            input.be-cd-color-input(type="color" :value="toHex(blockProps.bgBorderColor)" @input="pickColor('bgBorderColor', $event.target.value, false)")
                    .be-cd-sep(v-if="blockProps.bgColor || blockProps.bgBorderWidth || blockProps.bgFillMode === 'gradient'")
                    .be-cd-section(v-if="blockProps.bgColor || blockProps.bgBorderWidth || blockProps.bgFillMode === 'gradient'")
                      .be-cd-label {{ $t('Padding') }}
                      .be-cd-xy-row(@click.stop @mousedown.stop)
                        span.be-cd-xy-label X
                        button.be-cd-blur-btn(@click.stop="setBlockProp('bgPaddingX', Math.max(0, (blockProps.bgPaddingX !== undefined ? blockProps.bgPaddingX : (blockProps.bgPadding || 0)) - 1))") −
                        .be-tb-size-wrap(style="margin:0")
                          input.be-tb-size-input(type="number" :value="blockProps.bgPaddingX !== undefined ? blockProps.bgPaddingX : (blockProps.bgPadding || 0)" min="0" max="120" step="1" @focus="$event.target.select()" @input="setPxPropFromInput($event, 'bgPaddingX', 0, 0, 120)" @blur="$event.target.value = blockProps.bgPaddingX !== undefined ? blockProps.bgPaddingX : (blockProps.bgPadding || 0)" @keydown.enter.prevent="$event.target.blur()" @mousedown.stop @click.stop)
                          span.be-tb-size-unit px
                        button.be-cd-blur-btn(@click.stop="setBlockProp('bgPaddingX', Math.min(120, (blockProps.bgPaddingX !== undefined ? blockProps.bgPaddingX : (blockProps.bgPadding || 0)) + 1))") +
                      .be-cd-xy-row(@click.stop @mousedown.stop)
                        span.be-cd-xy-label Y
                        button.be-cd-blur-btn(@click.stop="setBlockProp('bgPaddingY', Math.max(0, (blockProps.bgPaddingY !== undefined ? blockProps.bgPaddingY : (blockProps.bgPadding || 0)) - 1))") −
                        .be-tb-size-wrap(style="margin:0")
                          input.be-tb-size-input(type="number" :value="blockProps.bgPaddingY !== undefined ? blockProps.bgPaddingY : (blockProps.bgPadding || 0)" min="0" max="120" step="1" @focus="$event.target.select()" @input="setPxPropFromInput($event, 'bgPaddingY', 0, 0, 120)" @blur="$event.target.value = blockProps.bgPaddingY !== undefined ? blockProps.bgPaddingY : (blockProps.bgPadding || 0)" @keydown.enter.prevent="$event.target.blur()" @mousedown.stop @click.stop)
                          span.be-tb-size-unit px
                        button.be-cd-blur-btn(@click.stop="setBlockProp('bgPaddingY', Math.min(120, (blockProps.bgPaddingY !== undefined ? blockProps.bgPaddingY : (blockProps.bgPadding || 0)) + 1))") +
                    .be-cd-sep(v-if="blockProps.bgColor || blockProps.bgBorderWidth || blockProps.bgFillMode === 'gradient'")
                    .be-cd-section(v-if="blockProps.bgColor || blockProps.bgBorderWidth || blockProps.bgFillMode === 'gradient'")
                      .be-cd-label {{ $t('Round corners') }}
                      .be-cd-blur-row(@click.stop @mousedown.stop)
                        button.be-cd-blur-btn(@click.stop="setBlockProp('bgBorderRadius', Math.max(0, (blockProps.bgBorderRadius || 0) - 2))") −
                        .be-tb-size-wrap(style="margin:0")
                          input.be-tb-size-input(type="number" :value="blockProps.bgBorderRadius || 0" min="0" max="200" step="1" @focus="$event.target.select()" @input="setPxPropFromInput($event, 'bgBorderRadius', 0, 0, 200)" @blur="$event.target.value = blockProps.bgBorderRadius || 0" @keydown.enter.prevent="$event.target.blur()" @mousedown.stop @click.stop)
                          span.be-tb-size-unit px
                        button.be-cd-blur-btn(@click.stop="setBlockProp('bgBorderRadius', Math.min(200, (blockProps.bgBorderRadius || 0) + 2))") +

          // Form
          template(v-else-if="blockProps.type === 'shape'")
            el-tooltip(:content="$t('Rectangle')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.shapeType === 'rect' || !blockProps.shapeType }" @click="setBlockProp('shapeType', 'rect')")
                .be-tb-shape-icon
                  svg(viewBox="0 0 24 24" fill="currentColor")
                    rect(x="2" y="2" width="20" height="20")
            el-tooltip(:content="$t('Circle')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.shapeType === 'circle' }" @click="setBlockProp('shapeType', 'circle')")
                .be-tb-shape-icon
                  svg(viewBox="0 0 24 24" fill="currentColor")
                    circle(cx="12" cy="12" r="10")
            el-tooltip(:content="$t('Triangle')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.shapeType === 'triangle' }" @click="setBlockProp('shapeType', 'triangle')")
                .be-tb-shape-icon
                  svg(viewBox="0 0 24 24" fill="currentColor")
                    polygon(points="12,2 22,22 2,22")
            el-tooltip(:content="$t('Diamond')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.shapeType === 'diamond' }" @click="setBlockProp('shapeType', 'diamond')")
                .be-tb-shape-icon
                  svg(viewBox="0 0 24 24" fill="currentColor")
                    polygon(points="12,2 22,12 12,22 2,12")
            el-tooltip(:content="$t('Star')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.shapeType === 'star' }" @click="setBlockProp('shapeType', 'star')")
                .be-tb-shape-icon
                  svg(viewBox="0 0 24 24" fill="currentColor")
                    polygon(points="12,2 15,9 22,9 16,14 18,21 12,17 6,21 8,14 2,9 9,9")
            el-tooltip(:content="$t('Hexagon')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.shapeType === 'hexagon' }" @click="setBlockProp('shapeType', 'hexagon')")
                .be-tb-shape-icon
                  svg(viewBox="0 0 24 24" fill="currentColor")
                    polygon(points="6,2 18,2 24,12 18,22 6,22 0,12")
            el-tooltip(:content="$t('Arrow right')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.shapeType === 'arrow' }" @click="setBlockProp('shapeType', 'arrow')")
                .be-tb-shape-icon
                  svg(viewBox="0 0 24 24" fill="currentColor")
                    polygon(points="0,8 14,8 14,4 24,12 14,20 14,16 0,16")
            .be-tb-sep
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Fill / Border')" placement="top")
                button.be-tb-btn.be-tb-color-btn(:class="{ 'is-on': true }" @click.stop="toggleColorPicker('shape-fill')")
                  .be-tb-color-swatch(:style="{ background: blockProps.fillMode === 'gradient' ? `linear-gradient(90deg, ${blockProps.gradientColor1 || '#3b82f6'}, ${blockProps.gradientColor2 || 'transparent'})` : (blockProps.fillColor || '#3b82f6') }")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'shape-fill'" style="min-width:440px;padding:0")
                .be-cd-two-col
                  .be-cd-col
                    .be-cd-section
                      .be-cd-fill-mode-switch
                        button(:class="{ 'is-on': !blockProps.fillMode || blockProps.fillMode === 'solid' }" @click.stop="setBlockProp('fillMode', 'solid')") {{ $t('Solid') }}
                        button(:class="{ 'is-on': blockProps.fillMode === 'gradient' }" @click.stop="setBlockProp('fillMode', 'gradient')") {{ $t('Gradient') }}
                    .be-cd-sep
                    template(v-if="!blockProps.fillMode || blockProps.fillMode === 'solid'")
                      .be-cd-section
                        .be-cd-label {{ $t('Fill color') }}
                        .be-cd-swatches
                          button.be-cd-swatch.be-cd-swatch--none(:class="{ 'is-on': !blockProps.fillColor }" @click.stop="setBlockProp('fillColor', '')" :title="$t('No fill')") ✕
                          button.be-cd-swatch(v-for="c in shapeFillColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.fillColor === c }" @click.stop="pickColor('fillColor', c, false)")
                        template(v-if="ciColors && ciColors.length")
                          .be-cd-sep
                          .be-cd-section
                            .be-cd-label {{ $t('CI colors') }}
                            .be-cd-swatches
                              button.be-cd-swatch(v-for="c in ciColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.fillColor === c }" @click.stop="pickColor('fillColor', c, false)")
                      .be-cd-sep
                      .be-cd-custom
                        span.be-cd-label {{ $t('Custom color') }}
                        .be-cd-custom-row
                          input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('fillColor')" @keydown.enter.prevent="onHexInput('fillColor')")
                          input.be-cd-color-input(type="color" :value="toHex(blockProps.fillColor)" @input="pickColor('fillColor', $event.target.value, false)")
                    template(v-else)
                      .be-cd-section
                        .be-cd-label {{ $t('Type') }}
                        .be-cd-fill-mode-switch
                          button(:class="{ 'is-on': !blockProps.gradientType || blockProps.gradientType === 'linear' }" @click.stop="setBlockProp('gradientType', 'linear')") {{ $t('Linear') }}
                          button(:class="{ 'is-on': blockProps.gradientType === 'radial' }" @click.stop="setBlockProp('gradientType', 'radial')") {{ $t('Radial') }}
                      template(v-if="!blockProps.gradientType || blockProps.gradientType === 'linear'")
                        .be-cd-sep
                        .be-cd-section
                          .be-cd-label {{ $t('Angle') }}
                          .be-cd-blur-row(@click.stop @mousedown.stop)
                            button.be-cd-blur-btn(@click.stop="setBlockProp('gradientAngle', ((blockProps.gradientAngle !== undefined ? blockProps.gradientAngle : 90) - 15 + 360) % 360)") −
                            input.be-cd-blur-val(type="number" :value="blockProps.gradientAngle !== undefined ? blockProps.gradientAngle : 90" min="0" max="359" step="1" @change="setBlockProp('gradientAngle', ((parseInt($event.target.value) || 0) % 360 + 360) % 360)" @keydown.enter.prevent="$event.target.blur()" @focus="$event.target.select()" @mousedown.stop @click.stop @keydown.stop)
                            button.be-cd-blur-btn(@click.stop="setBlockProp('gradientAngle', ((blockProps.gradientAngle !== undefined ? blockProps.gradientAngle : 90) + 15) % 360)") +
                      .be-cd-sep
                      .be-cd-section
                        .be-cd-label {{ $t('Color 1') }}
                        .be-cd-swatches
                          button.be-cd-swatch(v-for="c in shapeFillColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.gradientColor1 === c }" @click.stop="setBlockProp('gradientColor1', c)")
                        .be-cd-sep
                        .be-cd-custom-row
                          input.be-cd-hex-input(type="text" :value="toHex(blockProps.gradientColor1)" placeholder="#000000" spellcheck="false" @blur="setBlockProp('gradientColor1', $event.target.value)" @keydown.enter.prevent="setBlockProp('gradientColor1', $event.target.value); $event.target.blur()" @mousedown.stop @click.stop @keydown.stop)
                          input.be-cd-color-input(type="color" :value="toHex(blockProps.gradientColor1 || '#3b82f6')" @input="setBlockProp('gradientColor1', $event.target.value)")
                      .be-cd-sep
                      .be-cd-section
                        .be-cd-label {{ $t('Color 2') }}
                        .be-cd-swatches
                          button.be-cd-swatch.be-cd-swatch--none(:class="{ 'is-on': !blockProps.gradientColor2 }" @click.stop="setBlockProp('gradientColor2', 'transparent')" :title="$t('Transparent')") ✕
                          button.be-cd-swatch(v-for="c in shapeFillColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.gradientColor2 === c }" @click.stop="setBlockProp('gradientColor2', c)")
                        .be-cd-sep
                        .be-cd-custom-row
                          input.be-cd-hex-input(type="text" :value="toHex(blockProps.gradientColor2)" placeholder="transparent" spellcheck="false" @blur="setBlockProp('gradientColor2', $event.target.value)" @keydown.enter.prevent="setBlockProp('gradientColor2', $event.target.value); $event.target.blur()" @mousedown.stop @click.stop @keydown.stop)
                          input.be-cd-color-input(type="color" :value="toHex(blockProps.gradientColor2 || '#ffffff')" @input="setBlockProp('gradientColor2', $event.target.value)")
                    .be-cd-sep
                    .be-cd-section
                      .be-cd-label {{ $t('Opacity') }}
                      .be-cd-opacity-row
                        input.be-cd-opacity-slider(type="range" min="0" max="100" step="1" :value="Math.round((blockProps.opacity !== undefined ? blockProps.opacity : 1) * 100)" @input="setBlockProp('opacity', parseFloat($event.target.value) / 100)" @mousedown.stop @click.stop)
                        span.be-cd-opacity-val {{ Math.round((blockProps.opacity !== undefined ? blockProps.opacity : 1) * 100) }}%
                  .be-cd-col-sep
                  .be-cd-col
                    .be-cd-section(v-if="!['triangle','diamond','star','hexagon','arrow'].includes(blockProps.shapeType)")
                      .be-cd-label {{ $t('Border') }}
                      .be-cd-blur-row(@click.stop @mousedown.stop)
                        button.be-cd-blur-btn(@click.stop="setBlockProp('borderWidth', Math.max(0, (blockProps.borderWidth || 0) - 1))") −
                        .be-tb-size-wrap(style="margin:0")
                          input.be-tb-size-input(type="number" :value="blockProps.borderWidth || 0" min="0" max="20" step="1" @focus="$event.target.select()" @input="setPxPropFromInput($event, 'borderWidth', 0, 0, 20)" @blur="$event.target.value = blockProps.borderWidth || 0" @keydown.enter.prevent="$event.target.blur()" @mousedown.stop @click.stop)
                          span.be-tb-size-unit px
                        button.be-cd-blur-btn(@click.stop="setBlockProp('borderWidth', Math.min(20, (blockProps.borderWidth || 0) + 1))") +
                      template(v-if="blockProps.borderWidth")
                        .be-cd-sep
                        .be-cd-section
                          .be-cd-label {{ $t('Style') }}
                          .be-cd-border-style-row
                            button(:class="{ 'is-on': !blockProps.borderStyle || blockProps.borderStyle === 'solid' }" @click.stop="setBlockProp('borderStyle', 'solid')" :title="$t('Solid')")
                              .be-cd-border-preview(style="border-bottom-style:solid")
                            button(:class="{ 'is-on': blockProps.borderStyle === 'dashed' }" @click.stop="setBlockProp('borderStyle', 'dashed')" :title="$t('Dashed')")
                              .be-cd-border-preview(style="border-bottom-style:dashed")
                            button(:class="{ 'is-on': blockProps.borderStyle === 'dotted' }" @click.stop="setBlockProp('borderStyle', 'dotted')" :title="$t('Dotted')")
                              .be-cd-border-preview(style="border-bottom-style:dotted")
                        .be-cd-sep
                        .be-cd-section
                          .be-cd-label {{ $t('Border color') }}
                          .be-cd-swatches
                            button.be-cd-swatch(v-for="c in shapeFillColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.borderColor === c }" @click.stop="pickColor('borderColor', c, false)")
                          .be-cd-sep
                          .be-cd-custom
                            span.be-cd-label {{ $t('Custom color') }}
                            .be-cd-custom-row
                              input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('borderColor')" @keydown.enter.prevent="onHexInput('borderColor')")
                              input.be-cd-color-input(type="color" :value="toHex(blockProps.borderColor)" @input="pickColor('borderColor', $event.target.value, false)")
                        .be-cd-sep
                        .be-cd-border-sides-row
                          button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'top') }" @click.stop="toggleBorderSide('borderSides', 'top')" :title="$t('Top')")
                            .be-bside-icon.be-bside--top
                          button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'right') }" @click.stop="toggleBorderSide('borderSides', 'right')" :title="$t('Right')")
                            .be-bside-icon.be-bside--right
                          button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'bottom') }" @click.stop="toggleBorderSide('borderSides', 'bottom')" :title="$t('Bottom')")
                            .be-bside-icon.be-bside--bottom
                          button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'left') }" @click.stop="toggleBorderSide('borderSides', 'left')" :title="$t('Left')")
                            .be-bside-icon.be-bside--left
                    template(v-if="!['circle','triangle','diamond','star','hexagon','arrow'].includes(blockProps.shapeType)")
                      .be-cd-sep
                      .be-cd-section
                        .be-cd-label {{ $t('Round corners') }}
                        .be-cd-blur-row(@click.stop @mousedown.stop)
                          button.be-cd-blur-btn(@click.stop="setBlockProp('borderRadius', Math.max(0, (blockProps.borderRadius || 0) - 2))") −
                          .be-tb-size-wrap(style="margin:0")
                            input.be-tb-size-input(type="number" :value="blockProps.borderRadius || 0" min="0" max="100" step="1" @focus="$event.target.select()" @input="setPxPropFromInput($event, 'borderRadius', 0, 0, 100)" @blur="$event.target.value = blockProps.borderRadius || 0" @keydown.enter.prevent="$event.target.blur()" @mousedown.stop @click.stop)
                            span.be-tb-size-unit px
                          button.be-cd-blur-btn(@click.stop="setBlockProp('borderRadius', Math.min(100, (blockProps.borderRadius || 0) + 2))") +
            .be-tb-sep
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Shadow')" placement="top")
                button.be-tb-btn.be-tb-color-btn(:class="{ 'is-on': blockProps.shadow }" @click.stop="toggleColorPicker('shape-shadow')")
                  i.el-icon-fa-adjust.fas(style="font-size:11px")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'shape-shadow'" style="min-width:210px")
                .be-cd-section
                  .be-cd-switch-row
                    span.be-cd-label {{ $t('Shadow') }}
                    el-switch(:value="!!blockProps.shadow" @change="setBlockProp('shadow', $event)" @click.stop @mousedown.stop)
                template(v-if="blockProps.shadow")
                  .be-cd-sep
                  .be-cd-section
                    .be-cd-label {{ $t('Blur') }}
                    .be-cd-blur-row(@click.stop @mousedown.stop)
                      button.be-cd-blur-btn(@click.stop="setBlockProp('shadowBlur', Math.max(0, Math.round(((blockProps.shadowBlur !== undefined ? blockProps.shadowBlur : 0.3) - 0.1) * 10) / 10))") −
                      input.be-cd-blur-val(type="number" :value="(blockProps.shadowBlur !== undefined ? blockProps.shadowBlur : 0.3).toFixed(1)" min="0" max="5" step="0.1" @change="setBlockProp('shadowBlur', Math.min(5, Math.max(0, parseFloat($event.target.value) || 0)))" @keydown.enter.prevent="$event.target.blur()" @focus="$event.target.select()" @mousedown.stop @click.stop @keydown.stop)
                      button.be-cd-blur-btn(@click.stop="setBlockProp('shadowBlur', Math.min(5, Math.round(((blockProps.shadowBlur !== undefined ? blockProps.shadowBlur : 0.3) + 0.1) * 10) / 10))") +
                  .be-cd-sep
                  .be-cd-section
                    .be-cd-label {{ $t('Color') }}
                    .be-cd-swatches
                      button.be-cd-swatch(v-for="c in shadowColors" :key="c" :style="{ background: c }" :class="{ 'is-on': (blockProps.shadowColor || 'rgba(0,0,0,0.7)') === c }" @click.stop="pickColor('shadowColor', c, false)")
                  .be-cd-sep
                  .be-cd-custom
                    span.be-cd-label {{ $t('Custom color') }}
                    .be-cd-custom-row
                      input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('shadowColor')" @keydown.enter.prevent="onHexInput('shadowColor')")
                      input.be-cd-color-input(type="color" :value="toHex(blockProps.shadowColor)" @input="pickColor('shadowColor', $event.target.value, false)")

          // Button
          template(v-else-if="blockProps.type === 'button'")
            el-tooltip(:content="$t('Bold')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.bold }" @click="setBlockProp('bold', !blockProps.bold)")
                b B
            el-tooltip(:content="$t('Italic')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.italic }" @click="setBlockProp('italic', !blockProps.italic)")
                i I
            el-tooltip(:content="$t('Underline')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.underline }" @click="setBlockProp('underline', !blockProps.underline)")
                span(style="text-decoration:underline") U
            el-tooltip(:content="$t('Strikethrough')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.strikethrough }" @click="setBlockProp('strikethrough', !blockProps.strikethrough)")
                span(style="text-decoration:line-through") S
            .be-tb-sep
            el-select.be-tb-font-select(
              :value="blockProps.fontFamily || ''"
              size="mini"
              :placeholder="$t('Font family')"
              clearable
              @change="setBlockProp('fontFamily', $event || '')"
            )
              el-option(v-for="f in fontFamilies" :key="f.key || '__tpl__'" :label="f.label" :value="f.key" :style="{ fontFamily: f.previewFont || f.key }")
            .be-tb-sep
            el-tooltip(:content="$t('Decrease font size')" placement="top")
              button.be-tb-btn(@click="setBlockProp('fontSize', Math.max(0.2, Math.round(((blockProps.fontSize || 1.2) - 0.1) * 10) / 10))") −
            .be-tb-size-wrap
              input.be-tb-size-input(type="number" :value="(blockProps.fontSize || 1.2).toFixed(1)" min="0.2" max="15" step="0.1" @focus="$event.target.select()" @input="setFontSizeFromInput($event, 1.2)" @blur="$event.target.value = (blockProps.fontSize || 1.2).toFixed(1)" @keydown.enter.prevent="$event.target.blur()" @mousedown.stop @click.stop)
              span.be-tb-size-unit vw
            el-tooltip(:content="$t('Increase font size')" placement="top")
              button.be-tb-btn(@click="setBlockProp('fontSize', Math.min(15, Math.round(((blockProps.fontSize || 1.2) + 0.1) * 10) / 10))") +
            .be-tb-sep
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Text color')" placement="top")
                button.be-tb-btn.be-tb-color-btn(@click.stop="toggleColorPicker('btn-text-color')")
                  .be-tb-color-swatch(:style="{ background: blockProps.color || '#ffffff' }")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'btn-text-color'")
                template(v-if="ciColors && ciColors.length")
                  .be-cd-section
                    .be-cd-label {{ $t('CI colors') }}
                    .be-cd-swatches
                      button.be-cd-swatch(v-for="c in ciColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.color === c }" @click.stop="pickColor('color', c)")
                  .be-cd-sep
                .be-cd-section
                  .be-cd-label {{ $t('Standard colors') }}
                  .be-cd-swatches
                    button.be-cd-swatch(v-for="c in textColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.color === c }" @click.stop="pickColor('color', c)")
                .be-cd-sep
                .be-cd-custom
                  span.be-cd-label {{ $t('Custom color') }}
                  .be-cd-custom-row
                    input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('color')" @keydown.enter.prevent="onHexInput('color')")
                    input.be-cd-color-input(type="color" :value="toHex(blockProps.color)" @input="pickColor('color', $event.target.value, false)")
            .be-tb-sep
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Background color')" placement="top")
                button.be-tb-btn.be-tb-color-btn(@click.stop="toggleColorPicker('btn-bg-color')")
                  .be-tb-color-swatch(:style="{ background: blockProps.bgFillMode === 'gradient' ? `linear-gradient(90deg, ${blockProps.bgGradientColor1 || '#3b82f6'}, ${blockProps.bgGradientColor2 || 'transparent'})` : (blockProps.bgColor || '#3b82f6') }")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'btn-bg-color'" style="min-width:210px")
                .be-cd-section
                  .be-cd-fill-mode-switch
                    button(:class="{ 'is-on': !blockProps.bgFillMode || blockProps.bgFillMode === 'solid' }" @click.stop="setBlockProp('bgFillMode', 'solid')") {{ $t('Solid') }}
                    button(:class="{ 'is-on': blockProps.bgFillMode === 'gradient' }" @click.stop="setBlockProp('bgFillMode', 'gradient')") {{ $t('Gradient') }}
                .be-cd-sep
                template(v-if="!blockProps.bgFillMode || blockProps.bgFillMode === 'solid'")
                  template(v-if="ciColors && ciColors.length")
                    .be-cd-section
                      .be-cd-label {{ $t('CI colors') }}
                      .be-cd-swatches
                        button.be-cd-swatch(v-for="c in ciColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgColor === c }" @click.stop="pickColor('bgColor', c)")
                    .be-cd-sep
                  .be-cd-section
                    .be-cd-label {{ $t('Standard colors') }}
                    .be-cd-swatches
                      button.be-cd-swatch(v-for="c in btnBgColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgColor === c }" @click.stop="pickColor('bgColor', c)")
                  .be-cd-sep
                  .be-cd-custom
                    span.be-cd-label {{ $t('Custom color') }}
                    .be-cd-custom-row
                      input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('bgColor')" @keydown.enter.prevent="onHexInput('bgColor')")
                      input.be-cd-color-input(type="color" :value="toHex(blockProps.bgColor)" @input="pickColor('bgColor', $event.target.value, false)")
                template(v-else)
                  .be-cd-section
                    .be-cd-label {{ $t('Type') }}
                    .be-cd-fill-mode-switch
                      button(:class="{ 'is-on': !blockProps.bgGradientType || blockProps.bgGradientType === 'linear' }" @click.stop="setBlockProp('bgGradientType', 'linear')") {{ $t('Linear') }}
                      button(:class="{ 'is-on': blockProps.bgGradientType === 'radial' }" @click.stop="setBlockProp('bgGradientType', 'radial')") {{ $t('Radial') }}
                  template(v-if="!blockProps.bgGradientType || blockProps.bgGradientType === 'linear'")
                    .be-cd-sep
                    .be-cd-section
                      .be-cd-label {{ $t('Angle') }}
                      .be-cd-blur-row(@click.stop @mousedown.stop)
                        button.be-cd-blur-btn(@click.stop="setBlockProp('bgGradientAngle', ((blockProps.bgGradientAngle !== undefined ? blockProps.bgGradientAngle : 90) - 15 + 360) % 360)") −
                        input.be-cd-blur-val(type="number" :value="blockProps.bgGradientAngle !== undefined ? blockProps.bgGradientAngle : 90" min="0" max="359" step="1" @change="setBlockProp('bgGradientAngle', ((parseInt($event.target.value) || 0) % 360 + 360) % 360)" @keydown.enter.prevent="$event.target.blur()" @focus="$event.target.select()" @mousedown.stop @click.stop @keydown.stop)
                        button.be-cd-blur-btn(@click.stop="setBlockProp('bgGradientAngle', ((blockProps.bgGradientAngle !== undefined ? blockProps.bgGradientAngle : 90) + 15) % 360)") +
                  .be-cd-sep
                  .be-cd-section
                    .be-cd-label {{ $t('Color 1') }}
                    template(v-if="ciColors && ciColors.length")
                      .be-cd-swatches
                        button.be-cd-swatch(v-for="c in ciColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgGradientColor1 === c }" @click.stop="setBlockProp('bgGradientColor1', c)")
                      .be-cd-sep
                    .be-cd-swatches
                      button.be-cd-swatch(v-for="c in btnBgColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgGradientColor1 === c }" @click.stop="setBlockProp('bgGradientColor1', c)")
                    .be-cd-sep
                    .be-cd-custom-row
                      input.be-cd-hex-input(type="text" :value="toHex(blockProps.bgGradientColor1)" placeholder="#000000" spellcheck="false" @blur="setBlockProp('bgGradientColor1', $event.target.value)" @keydown.enter.prevent="setBlockProp('bgGradientColor1', $event.target.value); $event.target.blur()" @mousedown.stop @click.stop @keydown.stop)
                      input.be-cd-color-input(type="color" :value="toHex(blockProps.bgGradientColor1 || '#3b82f6')" @input="setBlockProp('bgGradientColor1', $event.target.value)")
                  .be-cd-sep
                  .be-cd-section
                    .be-cd-label {{ $t('Color 2') }}
                    template(v-if="ciColors && ciColors.length")
                      .be-cd-swatches
                        button.be-cd-swatch(v-for="c in ciColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgGradientColor2 === c }" @click.stop="setBlockProp('bgGradientColor2', c)")
                      .be-cd-sep
                    .be-cd-swatches
                      button.be-cd-swatch.be-cd-swatch--none(:class="{ 'is-on': !blockProps.bgGradientColor2 }" @click.stop="setBlockProp('bgGradientColor2', 'transparent')" :title="$t('Transparent')") ✕
                      button.be-cd-swatch(v-for="c in btnBgColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgGradientColor2 === c }" @click.stop="setBlockProp('bgGradientColor2', c)")
                    .be-cd-sep
                    .be-cd-custom-row
                      input.be-cd-hex-input(type="text" :value="toHex(blockProps.bgGradientColor2)" placeholder="transparent" spellcheck="false" @blur="setBlockProp('bgGradientColor2', $event.target.value)" @keydown.enter.prevent="setBlockProp('bgGradientColor2', $event.target.value); $event.target.blur()" @mousedown.stop @click.stop @keydown.stop)
                      input.be-cd-color-input(type="color" :value="toHex(blockProps.bgGradientColor2 || '#ffffff')" @input="setBlockProp('bgGradientColor2', $event.target.value)")
            .be-tb-sep
            el-tooltip(:content="$t('Less rounded corners')" placement="top")
              button.be-tb-btn(@click="setBlockProp('borderRadius', Math.max(0, (blockProps.borderRadius !== undefined ? blockProps.borderRadius : 6) - 2))") −
            .be-tb-size-wrap
              input.be-tb-size-input(type="number" :value="blockProps.borderRadius !== undefined ? blockProps.borderRadius : 6" min="0" max="100" step="1" @focus="$event.target.select()" @input="setPxPropFromInput($event, 'borderRadius', 6, 0, 100)" @blur="$event.target.value = blockProps.borderRadius !== undefined ? blockProps.borderRadius : 6" @keydown.enter.prevent="$event.target.blur()" @mousedown.stop @click.stop)
              span.be-tb-size-unit px
            el-tooltip(:content="$t('More rounded corners')" placement="top")
              button.be-tb-btn(@click="setBlockProp('borderRadius', Math.min(100, (blockProps.borderRadius !== undefined ? blockProps.borderRadius : 6) + 2))") +
            .be-tb-sep
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Shadow')" placement="top")
                button.be-tb-btn.be-tb-color-btn(:class="{ 'is-on': blockProps.shadow }" @click.stop="toggleColorPicker('btn-shadow')")
                  i.el-icon-fa-adjust.fas(style="font-size:11px")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'btn-shadow'" style="min-width:210px")
                .be-cd-section
                  .be-cd-switch-row
                    span.be-cd-label {{ $t('Shadow') }}
                    el-switch(:value="!!blockProps.shadow" @change="setBlockProp('shadow', $event)" @click.stop @mousedown.stop)
                template(v-if="blockProps.shadow")
                  .be-cd-sep
                  .be-cd-section
                    .be-cd-label {{ $t('Blur') }}
                    .be-cd-blur-row(@click.stop @mousedown.stop)
                      button.be-cd-blur-btn(@click.stop="setBlockProp('shadowBlur', Math.max(0, Math.round(((blockProps.shadowBlur !== undefined ? blockProps.shadowBlur : 0.3) - 0.1) * 10) / 10))") −
                      input.be-cd-blur-val(type="number" :value="(blockProps.shadowBlur !== undefined ? blockProps.shadowBlur : 0.3).toFixed(1)" min="0" max="5" step="0.1" @change="setBlockProp('shadowBlur', Math.min(5, Math.max(0, parseFloat($event.target.value) || 0)))" @keydown.enter.prevent="$event.target.blur()" @focus="$event.target.select()" @mousedown.stop @click.stop @keydown.stop)
                      button.be-cd-blur-btn(@click.stop="setBlockProp('shadowBlur', Math.min(5, Math.round(((blockProps.shadowBlur !== undefined ? blockProps.shadowBlur : 0.3) + 0.1) * 10) / 10))") +
                  .be-cd-sep
                  .be-cd-section
                    .be-cd-label {{ $t('Color') }}
                    .be-cd-swatches
                      button.be-cd-swatch(v-for="c in shadowColors" :key="c" :style="{ background: c }" :class="{ 'is-on': (blockProps.shadowColor || 'rgba(0,0,0,0.7)') === c }" @click.stop="pickColor('shadowColor', c, false)")
                  .be-cd-sep
                  .be-cd-custom
                    span.be-cd-label {{ $t('Custom color') }}
                    .be-cd-custom-row
                      input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('shadowColor')" @keydown.enter.prevent="onHexInput('shadowColor')")
                      input.be-cd-color-input(type="color" :value="toHex(blockProps.shadowColor)" @input="pickColor('shadowColor', $event.target.value, false)")
            .be-tb-sep
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Border')" placement="top")
                button.be-tb-btn.be-tb-color-btn(:class="{ 'is-on': !!blockProps.borderWidth }" @click.stop="toggleColorPicker('btn-border')")
                  i.el-icon-fa-border-style.fas(style="font-size:11px")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'btn-border'" style="min-width:210px")
                .be-cd-section
                  .be-cd-label {{ $t('Width') }}
                  .be-cd-blur-row(@click.stop @mousedown.stop)
                    button.be-cd-blur-btn(@click.stop="setBlockProp('borderWidth', Math.max(0, (blockProps.borderWidth || 0) - 1))") −
                    .be-tb-size-wrap(style="margin:0")
                      input.be-tb-size-input(type="number" :value="blockProps.borderWidth || 0" min="0" max="20" step="1" @focus="$event.target.select()" @input="setPxPropFromInput($event, 'borderWidth', 0, 0, 20)" @blur="$event.target.value = blockProps.borderWidth || 0" @keydown.enter.prevent="$event.target.blur()" @mousedown.stop @click.stop)
                      span.be-tb-size-unit px
                    button.be-cd-blur-btn(@click.stop="setBlockProp('borderWidth', Math.min(20, (blockProps.borderWidth || 0) + 1))") +
                template(v-if="blockProps.borderWidth")
                  .be-cd-sep
                  .be-cd-section
                    .be-cd-label {{ $t('Style') }}
                    .be-cd-border-style-row
                      button(:class="{ 'is-on': !blockProps.borderStyle || blockProps.borderStyle === 'solid' }" @click.stop="setBlockProp('borderStyle', 'solid')" :title="$t('Solid')")
                        .be-cd-border-preview(style="border-bottom-style:solid")
                      button(:class="{ 'is-on': blockProps.borderStyle === 'dashed' }" @click.stop="setBlockProp('borderStyle', 'dashed')" :title="$t('Dashed')")
                        .be-cd-border-preview(style="border-bottom-style:dashed")
                      button(:class="{ 'is-on': blockProps.borderStyle === 'dotted' }" @click.stop="setBlockProp('borderStyle', 'dotted')" :title="$t('Dotted')")
                        .be-cd-border-preview(style="border-bottom-style:dotted")
                  .be-cd-sep
                  template(v-if="ciColors && ciColors.length")
                    .be-cd-section
                      .be-cd-label {{ $t('CI colors') }}
                      .be-cd-swatches
                        button.be-cd-swatch(v-for="c in ciColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.borderColor === c }" @click.stop="pickColor('borderColor', c, false)")
                    .be-cd-sep
                  .be-cd-section
                    .be-cd-label {{ $t('Standard colors') }}
                    .be-cd-swatches
                      button.be-cd-swatch(v-for="c in btnBgColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.borderColor === c }" @click.stop="pickColor('borderColor', c, false)")
                  .be-cd-sep
                  .be-cd-custom
                    span.be-cd-label {{ $t('Custom color') }}
                    .be-cd-custom-row
                      input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('borderColor')" @keydown.enter.prevent="onHexInput('borderColor')")
                      input.be-cd-color-input(type="color" :value="toHex(blockProps.borderColor)" @input="pickColor('borderColor', $event.target.value, false)")
                  .be-cd-sep
                  .be-cd-border-sides-row
                    button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'top') }" @click.stop="toggleBorderSide('borderSides', 'top')" title="Oben")
                      .be-bside-icon.be-bside--top
                    button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'right') }" @click.stop="toggleBorderSide('borderSides', 'right')" title="Rechts")
                      .be-bside-icon.be-bside--right
                    button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'bottom') }" @click.stop="toggleBorderSide('borderSides', 'bottom')" title="Unten")
                      .be-bside-icon.be-bside--bottom
                    button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'left') }" @click.stop="toggleBorderSide('borderSides', 'left')" title="Links")
                      .be-bside-icon.be-bside--left

          // Bild
          template(v-else-if="blockProps.type === 'image'")
            el-button(size="mini" plain icon="el-icon-picture-outline" @click="openImageForBlock(activeBlockId)") {{ $t('Change image') }}
            el-button(v-if="blockProps.imageUrl" size="mini" plain icon="el-icon-fa-crop-alt fas" @click="reopenCropForBlock(activeBlockId)") {{ $t('Crop image') }}
            el-tooltip(content="Rund darstellen" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.imageRound }" @click="setBlockProp('imageRound', !blockProps.imageRound)")
                i.el-icon-fa-circle.far(style="font-size:13px")
            .be-tb-sep
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Innenabstand')" placement="top")
                button.be-tb-btn.be-tb-color-btn(:class="{ 'is-on': blockProps.bgPaddingX || blockProps.bgPaddingY }" @click.stop="toggleColorPicker('btn-padding')")
                  i.el-icon-fa-expand-alt.fas(style="font-size:11px")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'btn-padding'" style="min-width:180px")
                .be-cd-section
                  .be-cd-label {{ $t('Horizontal (px)') }}
                  .be-cd-blur-row(@click.stop @mousedown.stop)
                    button.be-cd-blur-btn(@click.stop="setBlockProp('bgPaddingX', Math.max(0, (blockProps.bgPaddingX || 0) - 1))") −
                    input.be-cd-blur-val(type="number" :value="blockProps.bgPaddingX || 0" min="0" max="200" step="1" @change="setBlockProp('bgPaddingX', Math.max(0, parseInt($event.target.value) || 0))" @keydown.enter.prevent="$event.target.blur()" @focus="$event.target.select()" @mousedown.stop @click.stop @keydown.stop)
                    button.be-cd-blur-btn(@click.stop="setBlockProp('bgPaddingX', Math.min(200, (blockProps.bgPaddingX || 0) + 1))") +
                .be-cd-section
                  .be-cd-label {{ $t('Vertikal (px)') }}
                  .be-cd-blur-row(@click.stop @mousedown.stop)
                    button.be-cd-blur-btn(@click.stop="setBlockProp('bgPaddingY', Math.max(0, (blockProps.bgPaddingY || 0) - 1))") −
                    input.be-cd-blur-val(type="number" :value="blockProps.bgPaddingY || 0" min="0" max="200" step="1" @change="setBlockProp('bgPaddingY', Math.max(0, parseInt($event.target.value) || 0))" @keydown.enter.prevent="$event.target.blur()" @focus="$event.target.select()" @mousedown.stop @click.stop @keydown.stop)
                    button.be-cd-blur-btn(@click.stop="setBlockProp('bgPaddingY', Math.min(200, (blockProps.bgPaddingY || 0) + 1))") +
            .be-tb-sep
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Opacity / Filter')" placement="top")
                button.be-tb-btn.be-tb-color-btn(:class="{ 'is-on': blockProps.opacity !== undefined && blockProps.opacity < 1 }" @click.stop="toggleColorPicker('img-opacity')")
                  i.el-icon-fa-eye.fas(style="font-size:11px")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'img-opacity'" style="width:300px")
                .be-cd-section
                  .be-cd-label {{ $t('Opacity') }}
                  .be-cd-opacity-row
                    input.be-cd-opacity-slider(type="range" min="0" max="100" step="1" :value="Math.round((blockProps.opacity !== undefined ? blockProps.opacity : 1) * 100)" @input="setBlockProp('opacity', parseFloat($event.target.value) / 100)" @mousedown.stop @click.stop)
                    span.be-cd-opacity-val {{ Math.round((blockProps.opacity !== undefined ? blockProps.opacity : 1) * 100) }}%
                .be-cd-sep
                .be-cd-section
                  .be-cd-label {{ $t('Filter') }}
                  .be-cd-filter-row
                    button.be-cd-filter-btn(:class="{ 'is-on': blockProps.imageGrayscale }" @click.stop="setBlockProp('imageGrayscale', !blockProps.imageGrayscale); setBlockProp('imageSepia', false)")
                      .be-cd-filter-swatch.be-cd-filter--bw
                      span {{ $t('B&W') }}
                    button.be-cd-filter-btn(:class="{ 'is-on': blockProps.imageSepia }" @click.stop="setBlockProp('imageSepia', !blockProps.imageSepia); setBlockProp('imageGrayscale', false)")
                      .be-cd-filter-swatch.be-cd-filter--sepia
                      span {{ $t('Sepia') }}
                    button.be-cd-filter-btn(:class="{ 'is-on': showImgDuotoneGrid || !!blockProps.imageDuotone }" @click.stop="showImgDuotoneGrid = !showImgDuotoneGrid")
                      .be-cd-dt-swatch-wrap
                        .be-cd-filter-swatch(:style="activeBlockDuotoneGradientStyle")
                        .be-cd-dt-badge(v-if="blockProps.imageDuotone") Aktiv
                      span Duotone
                .be-img-dt-panel(v-if="showImgDuotoneGrid && blockProps.imageUrl")
                  svg(aria-hidden="true" width="0" height="0" style="position:absolute;overflow:hidden;pointer-events:none")
                    defs
                      filter#be-img-dt-custom(color-interpolation-filters="sRGB")
                        feColorMatrix(type="saturate" values="0")
                        feComponentTransfer
                          feFuncR(type="table" :tableValues="allImgDuotoneFilterValues.custom.r")
                          feFuncG(type="table" :tableValues="allImgDuotoneFilterValues.custom.g")
                          feFuncB(type="table" :tableValues="allImgDuotoneFilterValues.custom.b")
                      filter(
                        v-for="preset in duotonePresets"
                        :key="preset.key"
                        :id="`be-img-dt-${preset.key}`"
                        color-interpolation-filters="sRGB"
                      )
                        feColorMatrix(type="saturate" values="0")
                        feComponentTransfer
                          feFuncR(type="table" :tableValues="allImgDuotoneFilterValues[preset.key].r")
                          feFuncG(type="table" :tableValues="allImgDuotoneFilterValues[preset.key].g")
                          feFuncB(type="table" :tableValues="allImgDuotoneFilterValues[preset.key].b")
                  .be-img-dt-header
                    span.be-img-dt-title Duotone
                    button.be-ep-duotone-clear(v-if="blockProps.imageDuotone" @click.stop="setImgDuotone(null)")
                      i.el-icon-fa-times.fas
                      | &nbsp;{{ $t('Remove duotone') }}
                  .be-ep-duotone-custom(v-if="blockProps.imageDuotone === 'custom'")
                    .be-ep-dc-row
                      label {{ $t('Shadow') }}
                      .be-ep-dc-inputs
                        input(type="color" :value="blockProps.imageDuotoneColor1 || '#000000'" @input="setBlockProp('imageDuotoneColor1', $event.target.value)" @mousedown.stop @click.stop)
                        input.be-ep-dc-hex(
                          type="text"
                          :value="blockProps.imageDuotoneColor1 || '#000000'"
                          maxlength="7"
                          spellcheck="false"
                          @blur="setImgDuotoneColor('imageDuotoneColor1', $event.target.value)"
                          @keydown.enter.prevent="setImgDuotoneColor('imageDuotoneColor1', $event.target.value); $event.target.blur()"
                          @mousedown.stop @click.stop @keydown.stop
                        )
                    .be-ep-dc-row
                      label {{ $t('Highlight') }}
                      .be-ep-dc-inputs
                        input(type="color" :value="blockProps.imageDuotoneColor2 || '#ffffff'" @input="setBlockProp('imageDuotoneColor2', $event.target.value)" @mousedown.stop @click.stop)
                        input.be-ep-dc-hex(
                          type="text"
                          :value="blockProps.imageDuotoneColor2 || '#ffffff'"
                          maxlength="7"
                          spellcheck="false"
                          @blur="setImgDuotoneColor('imageDuotoneColor2', $event.target.value)"
                          @keydown.enter.prevent="setImgDuotoneColor('imageDuotoneColor2', $event.target.value); $event.target.blur()"
                          @mousedown.stop @click.stop @keydown.stop
                        )
                  .be-img-dt-grid
                    button.be-img-dt-btn(
                      :class="{ 'is-active': blockProps.imageDuotone === 'custom' }"
                      @click.stop="setImgDuotone(blockProps.imageDuotone === 'custom' ? null : 'custom')"
                    )
                      .be-img-dt-thumb(:style="{ backgroundImage: `url(${blockProps.imageUrl})`, filter: 'url(#be-img-dt-custom)' }")
                      span {{ $t('Custom') }}
                    button.be-img-dt-btn(
                      v-for="preset in duotonePresets"
                      :key="preset.key"
                      :class="{ 'is-active': blockProps.imageDuotone === preset.key }"
                      @click.stop="setImgDuotone(blockProps.imageDuotone === preset.key ? null : preset.key)"
                    )
                      .be-img-dt-thumb(:style="{ backgroundImage: `url(${blockProps.imageUrl})`, filter: `url(#be-img-dt-${preset.key})` }")
                      span {{ $t(preset.label) }}
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Background / Border')" placement="top")
                button.be-tb-btn.be-tb-color-btn(:class="{ 'is-on': !!blockProps.bgColor || !!blockProps.borderWidth }" @click.stop="toggleColorPicker('img-bg')")
                  i.el-icon-fa-fill-drip.fas(style="font-size:11px")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'img-bg'" style="min-width:420px;padding:0")
                .be-cd-two-col
                  .be-cd-col
                    template(v-if="ciColors && ciColors.length")
                      .be-cd-section
                        .be-cd-label {{ $t('CI colors') }}
                        .be-cd-swatches
                          button.be-cd-swatch(v-for="c in ciColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgColor === c }" @click.stop="pickColor('bgColor', c, false)")
                      .be-cd-sep
                    .be-cd-section
                      .be-cd-label {{ $t('Standard colors') }}
                      .be-cd-swatches
                        button.be-cd-swatch.be-cd-swatch--none(:class="{ 'is-on': !blockProps.bgColor && !blockProps.borderWidth }" @click.stop="setBlockProp('bgColor', ''); setBlockProp('borderWidth', 0); activeColorPicker = null" :title="$t('No background / border')") ✕
                        button.be-cd-swatch(v-for="c in textBgColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.bgColor === c }" @click.stop="pickColor('bgColor', c, false)")
                    .be-cd-sep
                    .be-cd-custom
                      span.be-cd-label {{ $t('Custom color') }}
                      .be-cd-custom-row
                        input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('bgColor')" @keydown.enter.prevent="onHexInput('bgColor')")
                        input.be-cd-color-input(type="color" :value="toHex(blockProps.bgColor)" @input="pickColor('bgColor', $event.target.value, false)")
                  .be-cd-col-sep
                  .be-cd-col
                    .be-cd-section
                      .be-cd-label {{ $t('Border') }}
                      .be-cd-blur-row(@click.stop @mousedown.stop)
                        button.be-cd-blur-btn(@click.stop="setBlockProp('borderWidth', Math.max(0, (blockProps.borderWidth || 0) - 1))") −
                        .be-tb-size-wrap(style="margin:0")
                          input.be-tb-size-input(type="number" :value="blockProps.borderWidth || 0" min="0" max="20" step="1" @focus="$event.target.select()" @input="setPxPropFromInput($event, 'borderWidth', 0, 0, 20)" @blur="$event.target.value = blockProps.borderWidth || 0" @keydown.enter.prevent="$event.target.blur()" @mousedown.stop @click.stop)
                          span.be-tb-size-unit px
                        button.be-cd-blur-btn(@click.stop="setBlockProp('borderWidth', Math.min(20, (blockProps.borderWidth || 0) + 1))") +
                      template(v-if="blockProps.borderWidth")
                        .be-cd-sep
                        template(v-if="ciColors && ciColors.length")
                          .be-cd-section
                            .be-cd-label {{ $t('CI colors') }}
                            .be-cd-swatches
                              button.be-cd-swatch(v-for="c in ciColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.borderColor === c }" @click.stop="pickColor('borderColor', c, false)")
                          .be-cd-sep
                        .be-cd-section
                          .be-cd-label {{ $t('Standard colors') }}
                          .be-cd-swatches
                            button.be-cd-swatch(v-for="c in textBgColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.borderColor === c }" @click.stop="pickColor('borderColor', c, false)")
                        .be-cd-sep
                        .be-cd-custom
                          span.be-cd-label {{ $t('Custom color') }}
                          .be-cd-custom-row
                            input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('borderColor')" @keydown.enter.prevent="onHexInput('borderColor')")
                            input.be-cd-color-input(type="color" :value="toHex(blockProps.borderColor)" @input="pickColor('borderColor', $event.target.value, false)")
                        .be-cd-sep
                        .be-cd-border-sides-row
                          button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'top') }" @click.stop="toggleBorderSide('borderSides', 'top')" :title="$t('Top')")
                            .be-bside-icon.be-bside--top
                          button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'right') }" @click.stop="toggleBorderSide('borderSides', 'right')" :title="$t('Right')")
                            .be-bside-icon.be-bside--right
                          button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'bottom') }" @click.stop="toggleBorderSide('borderSides', 'bottom')" :title="$t('Bottom')")
                            .be-bside-icon.be-bside--bottom
                          button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'left') }" @click.stop="toggleBorderSide('borderSides', 'left')" :title="$t('Left')")
                            .be-bside-icon.be-bside--left
                    .be-cd-sep
                    .be-cd-section
                      .be-cd-label {{ $t('Round corners') }}
                      .be-cd-blur-row(@click.stop @mousedown.stop)
                        button.be-cd-blur-btn(@click.stop="setBlockProp('borderRadius', Math.max(0, (blockProps.borderRadius || 0) - 2))") −
                        .be-tb-size-wrap(style="margin:0")
                          input.be-tb-size-input(type="number" :value="blockProps.borderRadius || 0" min="0" max="200" step="1" @focus="$event.target.select()" @input="setPxPropFromInput($event, 'borderRadius', 0, 0, 200)" @blur="$event.target.value = blockProps.borderRadius || 0" @keydown.enter.prevent="$event.target.blur()" @mousedown.stop @click.stop)
                          span.be-tb-size-unit px
                        button.be-cd-blur-btn(@click.stop="setBlockProp('borderRadius', Math.min(200, (blockProps.borderRadius || 0) + 2))") +
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Shadow')" placement="top")
                button.be-tb-btn.be-tb-color-btn(:class="{ 'is-on': blockProps.shadow }" @click.stop="toggleColorPicker('img-shadow')")
                  i.el-icon-fa-adjust.fas(style="font-size:11px")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'img-shadow'" style="min-width:210px")
                .be-cd-section
                  .be-cd-switch-row
                    span.be-cd-label {{ $t('Shadow') }}
                    el-switch(:value="!!blockProps.shadow" @change="setBlockProp('shadow', $event)" @click.stop @mousedown.stop)
                template(v-if="blockProps.shadow")
                  .be-cd-sep
                  .be-cd-section
                    .be-cd-label {{ $t('Blur') }}
                    .be-cd-blur-row(@click.stop @mousedown.stop)
                      button.be-cd-blur-btn(@click.stop="setBlockProp('shadowBlur', Math.max(0, Math.round(((blockProps.shadowBlur !== undefined ? blockProps.shadowBlur : 0.3) - 0.1) * 10) / 10))") −
                      input.be-cd-blur-val(type="number" :value="(blockProps.shadowBlur !== undefined ? blockProps.shadowBlur : 0.3).toFixed(1)" min="0" max="5" step="0.1" @change="setBlockProp('shadowBlur', Math.min(5, Math.max(0, parseFloat($event.target.value) || 0)))" @keydown.enter.prevent="$event.target.blur()" @focus="$event.target.select()" @mousedown.stop @click.stop @keydown.stop)
                      button.be-cd-blur-btn(@click.stop="setBlockProp('shadowBlur', Math.min(5, Math.round(((blockProps.shadowBlur !== undefined ? blockProps.shadowBlur : 0.3) + 0.1) * 10) / 10))") +
                  .be-cd-sep
                  template(v-if="ciColors && ciColors.length")
                    .be-cd-section
                      .be-cd-label {{ $t('CI colors') }}
                      .be-cd-swatches
                        button.be-cd-swatch(v-for="c in ciColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.shadowColor === c }" @click.stop="pickColor('shadowColor', c, false)")
                    .be-cd-sep
                  .be-cd-section
                    .be-cd-label {{ $t('Standard colors') }}
                    .be-cd-swatches
                      button.be-cd-swatch(v-for="c in shadowColors" :key="c" :style="{ background: c }" :class="{ 'is-on': (blockProps.shadowColor || 'rgba(0,0,0,0.7)') === c }" @click.stop="pickColor('shadowColor', c, false)")
                  .be-cd-sep
                  .be-cd-custom
                    span.be-cd-label {{ $t('Custom color') }}
                    .be-cd-custom-row
                      input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('shadowColor')" @keydown.enter.prevent="onHexInput('shadowColor')")
                      input.be-cd-color-input(type="color" :value="toHex(blockProps.shadowColor)" @input="pickColor('shadowColor', $event.target.value, false)")

          // Video
          template(v-else-if="blockProps.type === 'video'")
            .be-tb-video-url-wrap(@mousedown.stop @click.stop)
              input.be-tb-video-url-input(
                type="text"
                :placeholder="$t('YouTube, Vimeo or video URL...')"
                :value="blockProps.videoUrl || ''"
                @change="setVideoUrl($event.target.value)"
                @keydown.enter.prevent="setVideoUrl($event.target.value); $event.target.blur()"
                @keydown.stop
                @focus="$event.target.select()"
              )
            .be-tb-sep
            el-tooltip(:content="$t('Autoplay (requires mute)')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.autoplay }" @click="toggleAutoplay")
                i.el-icon-fa-play.fas(style="font-size:9px")
            el-tooltip(:content="$t('Mute')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.muted !== false }" @click="toggleMuted")
                i.el-icon-fa-volume-mute.fas(style="font-size:10px")
            el-tooltip(:content="$t('Loop')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.loop }" @click="setBlockProp('loop', !blockProps.loop)")
                i.el-icon-fa-redo.fas(style="font-size:9px")
            el-tooltip(:content="$t('Controls')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.controls !== false }" @click="setBlockProp('controls', !(blockProps.controls !== false))")
                i.el-icon-fa-sliders-h.fas(style="font-size:9px")
            el-tooltip(content="Rund darstellen" placement="top")
              button.be-tb-btn(:class="{ 'is-on': blockProps.videoRound }" @click="setBlockProp('videoRound', !blockProps.videoRound)")
                i.el-icon-fa-circle.far(style="font-size:13px")
            template(v-if="blockProps.videoType !== 'custom'")
              .be-tb-sep
              el-tooltip(:content="$t('Portrait / Landscape')" placement="top")
                button.be-tb-btn(:class="{ 'is-on': blockProps.videoOrientation === 'portrait' }" @click="toggleVideoOrientation")
                  i.el-icon-fa-mobile-alt.fas(:style="{ fontSize: '13px', transform: blockProps.videoOrientation !== 'portrait' ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }")
            .be-tb-sep
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Border / Corners')" placement="top")
                button.be-tb-btn.be-tb-color-btn(:class="{ 'is-on': !!blockProps.borderWidth || !!blockProps.borderRadius }" @click.stop="toggleColorPicker('vid-border')")
                  i.el-icon-fa-border-style.fas(style="font-size:11px")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'vid-border'" style="min-width:200px")
                .be-cd-section
                  .be-cd-label {{ $t('Border') }}
                  .be-cd-blur-row(@click.stop @mousedown.stop)
                    button.be-cd-blur-btn(@click.stop="setBlockProp('borderWidth', Math.max(0, (blockProps.borderWidth || 0) - 1))") −
                    .be-tb-size-wrap(style="margin:0")
                      input.be-tb-size-input(type="number" :value="blockProps.borderWidth || 0" min="0" max="20" step="1" @focus="$event.target.select()" @input="setPxPropFromInput($event, 'borderWidth', 0, 0, 20)" @blur="$event.target.value = blockProps.borderWidth || 0" @keydown.enter.prevent="$event.target.blur()" @mousedown.stop @click.stop)
                      span.be-tb-size-unit px
                    button.be-cd-blur-btn(@click.stop="setBlockProp('borderWidth', Math.min(20, (blockProps.borderWidth || 0) + 1))") +
                template(v-if="blockProps.borderWidth")
                  .be-cd-sep
                  .be-cd-section
                    .be-cd-label {{ $t('Style') }}
                    .be-cd-border-style-row
                      button(:class="{ 'is-on': !blockProps.borderStyle || blockProps.borderStyle === 'solid' }" @click.stop="setBlockProp('borderStyle', 'solid')" :title="$t('Solid')")
                        .be-cd-border-preview(style="border-bottom-style:solid")
                      button(:class="{ 'is-on': blockProps.borderStyle === 'dashed' }" @click.stop="setBlockProp('borderStyle', 'dashed')" :title="$t('Dashed')")
                        .be-cd-border-preview(style="border-bottom-style:dashed")
                      button(:class="{ 'is-on': blockProps.borderStyle === 'dotted' }" @click.stop="setBlockProp('borderStyle', 'dotted')" :title="$t('Dotted')")
                        .be-cd-border-preview(style="border-bottom-style:dotted")
                  .be-cd-sep
                  .be-cd-border-sides-row
                    button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'top') }" @click.stop="toggleBorderSide('borderSides', 'top')" :title="$t('Top')")
                      .be-bside-icon.be-bside--top
                    button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'right') }" @click.stop="toggleBorderSide('borderSides', 'right')" :title="$t('Right')")
                      .be-bside-icon.be-bside--right
                    button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'bottom') }" @click.stop="toggleBorderSide('borderSides', 'bottom')" :title="$t('Bottom')")
                      .be-bside-icon.be-bside--bottom
                    button.be-bside-btn(:class="{ 'is-on': borderSidesHas('borderSides', 'left') }" @click.stop="toggleBorderSide('borderSides', 'left')" :title="$t('Left')")
                      .be-bside-icon.be-bside--left
                  .be-cd-sep
                  .be-cd-section
                    .be-cd-label {{ $t('Standard colors') }}
                    .be-cd-swatches
                      button.be-cd-swatch(v-for="c in textBgColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.borderColor === c }" @click.stop="pickColor('borderColor', c, false)")
                  .be-cd-sep
                  .be-cd-custom
                    span.be-cd-label {{ $t('Custom color') }}
                    .be-cd-custom-row
                      input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('borderColor')" @keydown.enter.prevent="onHexInput('borderColor')")
                      input.be-cd-color-input(type="color" :value="toHex(blockProps.borderColor)" @input="pickColor('borderColor', $event.target.value, false)")
                .be-cd-sep
                .be-cd-section
                  .be-cd-label {{ $t('Round corners') }}
                  .be-cd-blur-row(@click.stop @mousedown.stop)
                    button.be-cd-blur-btn(@click.stop="setBlockProp('borderRadius', Math.max(0, (blockProps.borderRadius || 0) - 4))") −
                    .be-tb-size-wrap(style="margin:0")
                      input.be-tb-size-input(type="number" :value="blockProps.borderRadius || 0" min="0" max="200" step="1" @focus="$event.target.select()" @input="setPxPropFromInput($event, 'borderRadius', 0, 0, 200)" @blur="$event.target.value = blockProps.borderRadius || 0" @keydown.enter.prevent="$event.target.blur()" @mousedown.stop @click.stop)
                      span.be-tb-size-unit px
                    button.be-cd-blur-btn(@click.stop="setBlockProp('borderRadius', Math.min(200, (blockProps.borderRadius || 0) + 4))") +

            .be-tb-sep
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Shadow')" placement="top")
                button.be-tb-btn.be-tb-color-btn(:class="{ 'is-on': blockProps.shadow }" @click.stop="toggleColorPicker('vid-shadow')")
                  i.el-icon-fa-adjust.fas(style="font-size:11px")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'vid-shadow'" style="min-width:210px")
                .be-cd-section
                  .be-cd-switch-row
                    span.be-cd-label {{ $t('Shadow') }}
                    el-switch(:value="!!blockProps.shadow" @change="setBlockProp('shadow', $event)" @click.stop @mousedown.stop)
                .be-cd-sep
                .be-cd-section
                  .be-cd-label {{ $t('Blur') }}
                  .be-cd-blur-row(@click.stop @mousedown.stop)
                    button.be-cd-blur-btn(@click.stop="setBlockProp('shadowBlur', Math.max(0, Math.round(((blockProps.shadowBlur !== undefined ? blockProps.shadowBlur : 0.3) - 0.1) * 10) / 10))") −
                    input.be-cd-blur-val(type="number" :value="(blockProps.shadowBlur !== undefined ? blockProps.shadowBlur : 0.3).toFixed(1)" min="0" max="5" step="0.1" @change="setBlockProp('shadowBlur', Math.min(5, Math.max(0, parseFloat($event.target.value) || 0)))" @keydown.enter.prevent="$event.target.blur()" @focus="$event.target.select()" @mousedown.stop @click.stop @keydown.stop)
                    button.be-cd-blur-btn(@click.stop="setBlockProp('shadowBlur', Math.min(5, Math.round(((blockProps.shadowBlur !== undefined ? blockProps.shadowBlur : 0.3) + 0.1) * 10) / 10))") +
                .be-cd-sep
                template(v-if="ciColors && ciColors.length")
                  .be-cd-section
                    .be-cd-label {{ $t('CI colors') }}
                    .be-cd-swatches
                      button.be-cd-swatch(v-for="c in ciColors" :key="c" :style="{ background: c }" :class="{ 'is-on': blockProps.shadowColor === c }" @click.stop="pickColor('shadowColor', c, false)")
                  .be-cd-sep
                .be-cd-section
                  .be-cd-label {{ $t('Standard colors') }}
                  .be-cd-swatches
                    button.be-cd-swatch(v-for="c in shadowColors" :key="c" :style="{ background: c }" :class="{ 'is-on': (blockProps.shadowColor || 'rgba(0,0,0,0.7)') === c }" @click.stop="pickColor('shadowColor', c, false)")
                .be-cd-sep
                .be-cd-custom
                  span.be-cd-label {{ $t('Custom color') }}
                  .be-cd-custom-row
                    input.be-cd-hex-input(type="text" v-model="hexDraft" placeholder="#000000" spellcheck="false" @blur="onHexInput('shadowColor')" @keydown.enter.prevent="onHexInput('shadowColor')")
                    input.be-cd-color-input(type="color" :value="toHex(blockProps.shadowColor)" @input="pickColor('shadowColor', $event.target.value, false)")
            .be-tb-sep
            .be-tb-color-wrap(@mousedown.stop @click.stop)
              el-tooltip(:content="$t('Opacity / Filter')" placement="top")
                button.be-tb-btn.be-tb-color-btn(:class="{ 'is-on': blockProps.videoGrayscale || blockProps.videoSepia || !!blockProps.videoDuotone || (blockProps.opacity !== undefined && blockProps.opacity < 1) }" @click.stop="toggleColorPicker('vid-filter')")
                  i.el-icon-fa-eye.fas(style="font-size:11px")
                  i.el-icon-fa-caret-down.fas.be-tb-caret
              .be-color-dropdown(v-if="activeColorPicker === 'vid-filter'" style="width:300px")
                .be-cd-section
                  .be-cd-label {{ $t('Opacity') }}
                  .be-cd-opacity-row
                    input.be-cd-opacity-slider(type="range" min="0" max="100" step="1" :value="Math.round((blockProps.opacity !== undefined ? blockProps.opacity : 1) * 100)" @input="setBlockProp('opacity', parseFloat($event.target.value) / 100)" @mousedown.stop @click.stop)
                    span.be-cd-opacity-val {{ Math.round((blockProps.opacity !== undefined ? blockProps.opacity : 1) * 100) }}%
                .be-cd-sep
                .be-cd-section
                  .be-cd-label {{ $t('Filter') }}
                  .be-cd-filter-row
                    button.be-cd-filter-btn(:class="{ 'is-on': blockProps.videoGrayscale }" @click.stop="setBlockProp('videoGrayscale', !blockProps.videoGrayscale); setBlockProp('videoSepia', false); if(blockProps.videoDuotone) setVidDuotone(null)")
                      .be-cd-filter-swatch.be-cd-filter--bw
                      span {{ $t('B&W') }}
                    button.be-cd-filter-btn(:class="{ 'is-on': blockProps.videoSepia }" @click.stop="setBlockProp('videoSepia', !blockProps.videoSepia); setBlockProp('videoGrayscale', false); if(blockProps.videoDuotone) setVidDuotone(null)")
                      .be-cd-filter-swatch.be-cd-filter--sepia
                      span {{ $t('Sepia') }}
                    button.be-cd-filter-btn(:class="{ 'is-on': showVidDuotoneGrid || !!blockProps.videoDuotone }" @click.stop="showVidDuotoneGrid = !showVidDuotoneGrid")
                      .be-cd-dt-swatch-wrap
                        .be-cd-filter-swatch(:style="activeBlockVideoDuotoneGradientStyle")
                        .be-cd-dt-badge(v-if="blockProps.videoDuotone") Aktiv
                      span Duotone
                .be-vid-dt-panel(v-if="showVidDuotoneGrid")
                  .be-img-dt-header
                    span.be-img-dt-title Duotone
                    button.be-ep-duotone-clear(v-if="blockProps.videoDuotone" @click.stop="setVidDuotone(null)")
                      i.el-icon-fa-times.fas
                      | &nbsp;{{ $t('Remove duotone') }}
                  .be-ep-duotone-custom(v-if="blockProps.videoDuotone === 'custom'")
                    .be-ep-dc-row
                      label {{ $t('Shadow') }}
                      .be-ep-dc-inputs
                        input(type="color" :value="blockProps.videoDuotoneColor1 || '#000000'" @input="setBlockProp('videoDuotoneColor1', $event.target.value)" @mousedown.stop @click.stop)
                        input.be-ep-dc-hex(
                          type="text"
                          :value="blockProps.videoDuotoneColor1 || '#000000'"
                          maxlength="7"
                          spellcheck="false"
                          @blur="setVidDuotoneColor('videoDuotoneColor1', $event.target.value)"
                          @keydown.enter.prevent="setVidDuotoneColor('videoDuotoneColor1', $event.target.value); $event.target.blur()"
                          @mousedown.stop @click.stop @keydown.stop
                        )
                    .be-ep-dc-row
                      label {{ $t('Highlight') }}
                      .be-ep-dc-inputs
                        input(type="color" :value="blockProps.videoDuotoneColor2 || '#ffffff'" @input="setBlockProp('videoDuotoneColor2', $event.target.value)" @mousedown.stop @click.stop)
                        input.be-ep-dc-hex(
                          type="text"
                          :value="blockProps.videoDuotoneColor2 || '#ffffff'"
                          maxlength="7"
                          spellcheck="false"
                          @blur="setVidDuotoneColor('videoDuotoneColor2', $event.target.value)"
                          @keydown.enter.prevent="setVidDuotoneColor('videoDuotoneColor2', $event.target.value); $event.target.blur()"
                          @mousedown.stop @click.stop @keydown.stop
                        )
                  .be-img-dt-grid
                    button.be-img-dt-btn(
                      :class="{ 'is-active': blockProps.videoDuotone === 'custom' }"
                      @click.stop="setVidDuotone(blockProps.videoDuotone === 'custom' ? null : 'custom')"
                    )
                      .be-img-dt-thumb(:style="{ background: `linear-gradient(135deg, ${blockProps.videoDuotoneColor1 || '#000000'}, ${blockProps.videoDuotoneColor2 || '#ffffff'})` }")
                      span {{ $t('Custom') }}
                    button.be-img-dt-btn(
                      v-for="preset in duotonePresets"
                      :key="preset.key"
                      :class="{ 'is-active': blockProps.videoDuotone === preset.key }"
                      @click.stop="setVidDuotone(blockProps.videoDuotone === preset.key ? null : preset.key)"
                    )
                      .be-img-dt-thumb(:style="{ background: `linear-gradient(135deg, ${preset.color1}, ${preset.color2})` }")
                      span {{ $t(preset.label) }}
          .be-tb-sep
          .be-tb-color-wrap(@mousedown.stop @click.stop)
            el-tooltip(:content="$t('Link')" placement="top")
              button.be-tb-btn(:class="{ 'is-on': !!(blockProps.hyperlink && (blockProps.hyperlink.href || (blockProps.hyperlink.menuItem && blockProps.hyperlink.menuItem.id))) }" @click.stop="toggleColorPicker('link')")
                i.el-icon-fa-link.fas
            .be-color-dropdown.be-link-dropdown(v-if="activeColorPicker === 'link'" style="width:320px")
              banner-link-picker(
                :value="blockProps.hyperlink || {}"
                :options="options"
                @input="setBlockProp('hyperlink', $event)"
              )
          .be-tb-sep
          el-tooltip(:content="$t('Delete block')" placement="top")
            button.be-tb-btn.be-tb-del(@click="removeBlock(activeBlockId)")
              i.el-icon-fa-trash-alt.far

        // ── Viewport-Toggle ──────────────────────────────────────
        .be-viewport-row(@mousedown.stop @click.stop)
          .be-viewport-toggle
            button.be-vt-btn(:class="{ 'is-active': !mobileEditMode }" @click="mobileEditMode = false")
              i.el-icon-fa-desktop.fas
              span {{ $t('Desktop') }}
            button.be-vt-btn(:class="{ 'is-active': mobileEditMode }" @click="mobileEditMode = true")
              i.el-icon-fa-mobile-alt.fas
              span {{ $t('Mobile') }}
          .be-vp-visibility(v-if="activeBlockId")
            span.be-zoom-label {{ $t('Element Ausblenden') }}
            .be-vp-vis-btns
              el-tooltip(:content="blockProps.desktopHidden ? $t('Auf Desktop anzeigen') : $t('Auf Desktop ausblenden')" placement="top")
                button.be-vis-btn(:class="{ 'is-on': blockProps.desktopHidden }" @click="setBlockProp('desktopHidden', !blockProps.desktopHidden)")
                  i.el-icon-fa-desktop.fas
              el-tooltip(:content="blockProps.mobileHidden ? $t('Auf Mobil anzeigen') : $t('Auf Mobil ausblenden')" placement="top")
                button.be-vis-btn(:class="{ 'is-on': blockProps.mobileHidden }" @click="setBlockProp('mobileHidden', !blockProps.mobileHidden)")
                  i.el-icon-fa-mobile-alt.fas
          .be-vp-spacer
          .be-mobile-height(@mousedown.stop @click.stop)
            template(v-if="mobileEditMode")
              .be-mh-switch-wrap
                span.be-mh-switch-label {{ $t('Benutzerdefinierte Mobile Höhe') }}
                el-switch(
                  :value="mobileBannerHeight !== null"
                  @change="mobileBannerHeight = $event ? bannerHeight : null"
                )
            template(v-if="!mobileEditMode || mobileBannerHeight !== null")
              .be-mh-label-wrap
                span.be-zoom-label(v-if="!mobileEditMode") {{ $t('Banner-Höhe') }}
                el-tooltip(placement="top")
                  template(v-slot:content)
                    div(v-if="mobileEditMode")
                      div {{ $t('Setzt die Banner-Höhe für mobile Geräte.') }}
                      div {{ $t('Leer lassen um die Desktop-Höhe zu verwenden.') }}
                    div(v-else)
                      div {{ $t('Setzt die Banner-Höhe als Prozentsatz') }}
                      div {{ $t('der Viewport-Breite (vw). Beispiel: 30 = der') }}
                      div {{ $t('Banner ist 30% so hoch wie der Bildschirm breit ist.') }}
                  i.el-icon-fa-question-circle.far.be-mh-help
              .be-mh-wrap
                button.be-mh-btn(@click="mobileEditMode ? mobileBannerHeight = Math.max(10, (mobileBannerHeight !== null ? mobileBannerHeight : bannerHeight) - 1) : setBannerHeight(bannerHeight - 1)") −
                input.be-mh-input(
                  type="number"
                  :value="mobileEditMode ? (mobileBannerHeight !== null ? mobileBannerHeight : bannerHeight) : bannerHeight"
                  min="10" max="100" step="1"
                  @focus="$event.target.select()"
                  @change="mobileEditMode ? mobileBannerHeight = Math.min(100, Math.max(10, parseInt($event.target.value) || bannerHeight)) : setBannerHeight($event.target.value)"
                  @keydown.enter.prevent="$event.target.blur()"
                  @mousedown.stop @click.stop
                )
                span.be-mh-unit vw
                button.be-mh-btn(@click="mobileEditMode ? mobileBannerHeight = Math.min(100, (mobileBannerHeight !== null ? mobileBannerHeight : bannerHeight) + 1) : setBannerHeight(bannerHeight + 1)") +

        // ── Canvas ──────────────────────────────────────────────
        .be-canvas-outer(:class="{ 'has-outer-bg': canvasLayout === 'container' && containerBgMode !== 'none', 'is-mobile-preview': mobileEditMode }")
          .be-canvas-outer-bg(v-if="canvasLayout === 'container' && containerBgMode !== 'none'" :style="canvasOuterBgStyle")
          .be-canvas-container(:style="canvasContainerStyle")
          .be-canvas(ref="canvas" data-tour-canvas @mousedown="onCanvasMouseDown" @selectstart="onCanvasSelectStart" @contextmenu.prevent.stop="onCanvasContextMenu")
            svg.be-duotone-defs(v-if="activeSlide && activeSlide.bgDuotone" aria-hidden="true" width="0" height="0")
              defs
                filter#be-duotone-canvas(color-interpolation-filters="sRGB")
                  feColorMatrix(type="saturate" values="0")
                  feComponentTransfer
                    feFuncR(type="table" :tableValues="activeSlideDuotoneValues.r")
                    feFuncG(type="table" :tableValues="activeSlideDuotoneValues.g")
                    feFuncB(type="table" :tableValues="activeSlideDuotoneValues.b")
            svg.be-duotone-defs(v-if="allBlocksDuotoneFilters.length" aria-hidden="true" width="0" height="0")
              defs
                filter(v-for="f in allBlocksDuotoneFilters" :key="f.id" :id="f.id" color-interpolation-filters="sRGB")
                  feColorMatrix(type="saturate" values="0")
                  feComponentTransfer
                    feFuncR(type="table" :tableValues="f.values.r")
                    feFuncG(type="table" :tableValues="f.values.g")
                    feFuncB(type="table" :tableValues="f.values.b")
            .be-canvas-bg-color(v-if="slideHasBgColor(activeSlide)" :style="slideBgColorStyle(activeSlide)" @mousedown="onCanvasMouseDown")
            video.be-canvas-video(
              v-if="activeSlide.video && activeSlide.video.url"
              :key="activeSlide.video.url"
              :style="{ filter: canvasBgFilter, transform: canvasBgTransform, objectPosition: activeSlide.bgPosition || 'center' }"
              autoplay muted loop playsinline
              @mousedown="onCanvasMouseDown"
            )
              source(:src="activeSlide.video.url")
            .be-canvas-bg(v-else-if="activeSlide.image && activeSlide.image.url" :style="{ filter: canvasBgFilter, transform: canvasBgTransform }" @mousedown="onCanvasMouseDown")
              img.be-canvas-bg-img(:src="activeSlide.image.url" :style="canvasBgImgStyle" alt="")
            .be-canvas-overlay(v-if="activeSlide.overlayColor" :style="{ background: activeSlide.overlayColor }" style="pointer-events:none")
            svg.be-guides(v-if="showGuides && ((activeSlide.image && activeSlide.image.url) || (activeSlide.video && activeSlide.video.url) || slideHasBgColor(activeSlide))" viewBox="0 0 100 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg")
              rect(x="8" y="8" width="84" height="84" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="0.4" stroke-dasharray="2.5 1.5")
              line(x1="50" y1="0" x2="50" y2="100" stroke="rgba(255,255,255,0.18)" stroke-width="0.25" stroke-dasharray="2 2")
              line(x1="0" y1="50" x2="100" y2="50" stroke="rgba(255,255,255,0.18)" stroke-width="0.25" stroke-dasharray="2 2")
              line(x1="33.3" y1="8" x2="33.3" y2="92" stroke="rgba(255,255,255,0.1)" stroke-width="0.2" stroke-dasharray="1.5 2")
              line(x1="66.7" y1="8" x2="66.7" y2="92" stroke="rgba(255,255,255,0.1)" stroke-width="0.2" stroke-dasharray="1.5 2")
              line(x1="8" y1="33.3" x2="92" y2="33.3" stroke="rgba(255,255,255,0.1)" stroke-width="0.2" stroke-dasharray="1.5 2")
              line(x1="8" y1="66.7" x2="92" y2="66.7" stroke="rgba(255,255,255,0.1)" stroke-width="0.2" stroke-dasharray="1.5 2")
              //- Container boundary guides for full-contained mode
              template(v-if="canvasLayout === 'full-contained'")
                line(:x1="containerGuidePercent" y1="0" :x2="containerGuidePercent" y2="100" stroke="rgba(99,202,99,0.7)" stroke-width="0.5" stroke-dasharray="3 2")
                line(:x1="100 - containerGuidePercent" y1="0" :x2="100 - containerGuidePercent" y2="100" stroke="rgba(99,202,99,0.7)" stroke-width="0.5" stroke-dasharray="3 2")
            .be-canvas-empty(
              v-if="(!activeSlide.image || !activeSlide.image.url) && (!activeSlide.video || !activeSlide.video.url) && !slideHasBgColor(activeSlide)"
            )
              i.el-icon-picture-outline
              span {{ $t('Select background') }}
              .be-canvas-empty-hint
                el-button(size="mini" plain icon="el-icon-picture-outline" @click.stop="showImageLibrary = true") {{ $t('Select image') }}
                el-button(size="mini" plain icon="el-icon-fa-film fas" @click.stop="showVideoLibrary = true") {{ $t('Select video') }}
                el-button(size="mini" plain icon="el-icon-fa-fill-drip fas" @click.stop="showBgColorPanel = true") {{ $t('Set color') }}

            .be-block(
              v-for="block in activeSlide.blocks"
              :key="block.id"
              :data-block-id="block.id"
              :class="{ 'is-active': activeBlockId === block.id, 'is-selected': selectedBlockIds.includes(block.id) && activeBlockId !== block.id, ['be-block--' + (block.type || 'text')]: true, 'be-block--mobile-hidden': mobileEditMode && block.mobileHidden, 'be-block--desktop-hidden': !mobileEditMode && block.desktopHidden }"
              :style="blockStyle(block)"
              @mousedown.stop="onBlockMouseDown(block.id, $event)"
              @dblclick.stop="onBlockDblClick(block.id, block.type, $event)"
              @contextmenu.prevent.stop="openContextMenu(block.id, $event)"
            )
              .be-block-anim-wrap(:key="animPreviewKeys[block.id] || 0" :style="blockAnimStyle(block)")
                // ── Text ──
                template(v-if="!block.type || block.type === 'text'")
                  div.be-block-text(
                    v-if="activeBlockId === block.id && textEditMode"
                    :key="block.id + '-edit'"
                    ref="activeEditable"
                    contenteditable="true"
                    :style="blockTextStyle(block)"
                    @input="onContentEditableInput(block.id, $event)"
                    @mousedown="onContentEditableMouseDown(block.id, $event)"
                    @keydown.stop
                    @paste.prevent="onPlainTextPaste($event)"
                  )
                  .be-block-text(v-else :key="block.id + '-view'" :style="blockTextStyle(block)" v-html="block.html || block.text || 'Text ...'")


                // ── Form ──
                template(v-else-if="block.type === 'shape'")
                  .be-block-shape(:style="shapeInnerStyle(block)")

                // ── Button ──
                .be-block-btn(v-else-if="block.type === 'button'" :style="buttonContainerStyle(block)")
                  div.be-block-btn-text(
                    v-if="activeBlockId === block.id && textEditMode"
                    :key="block.id + '-edit'"
                    ref="activeEditable"
                    contenteditable="true"
                    :style="btnTextStyle(block)"
                    @input="onContentEditableInput(block.id, $event)"
                    @mousedown="onContentEditableMouseDown(block.id, $event)"
                    @keydown.stop
                    @keydown.enter.prevent
                  )
                  span.be-block-btn-text(v-else :key="block.id + '-view'" :style="btnTextStyle(block)") {{ block.text || 'Button' }}

                // ── Bild ──
                .be-block-img(v-else-if="block.type === 'image'" :style="blockImgStyle(block)")
                  img(v-if="block.imageUrl" :src="block.imageUrl" :style="imgElementStyle(block)")
                  .be-block-img-empty(v-else @mousedown.stop @click.stop="openImageForBlock(block.id)")
                    i.el-icon-picture-outline
                    span {{ $t('Choose image') }}

                // ── Video ──
                .be-block-video(v-else-if="block.type === 'video'" :style="blockVideoWrapStyle(block)")
                  iframe.be-block-video-frame(
                    v-if="block.videoType !== 'custom' && blockVideoEmbedUrl(block, true)"
                    :src="blockVideoEmbedUrl(block, true)"
                    frameborder="0"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowfullscreen
                  )
                  video.be-block-video-native(
                    v-else-if="block.videoType === 'custom' && block.videoUrl"
                    :src="block.videoUrl"
                    :muted="true"
                    autoplay loop playsinline
                    :style="block.videoRound ? { width: '100%', height: '100%', objectFit: 'cover' } : undefined"
                    @loadedmetadata="autoFitVideoBlock(block, $event)"
                  )
                  .be-block-video-empty(v-else)
                    i.el-icon-fa-video.fas
                    span {{ $t('Enter video URL') }}
                  .be-block-video-overlay

              .be-block-link-badge(v-if="block.hyperlink && (block.hyperlink.href || (block.hyperlink.menuItem && block.hyperlink.menuItem.id))" :title="block.hyperlink.href || (block.hyperlink.menuItem && block.hyperlink.menuItem.title) || ''")
                i.el-icon-fa-link.fas

              .be-block-delete(
                v-if="activeBlockId === block.id && selectedBlockIds.length <= 1"
                :title="$t('Delete block')"
                @mousedown.prevent.stop
                @click.stop="removeBlock(block.id)"
              )
                i.el-icon-fa-times.fas
              .be-block-rotate(
                v-if="activeBlockId === block.id && selectedBlockIds.length <= 1"
                :title="$t('Rotate')"
                @mousedown.prevent.stop="onRotateMouseDown(block.id, $event)"
              )
                i.el-icon-fa-redo.fas
              .be-block-resize(
                v-if="activeBlockId === block.id && selectedBlockIds.length <= 1"
                :title="block.type === 'shape' || block.type === 'image' || block.type === 'video' || (block.type === 'button' && block.w && block.h) ? $t('Drag to resize') : $t('Drag font size')"
                @mousedown.prevent.stop="onResizeMouseDown(block.id, $event)"
              )
                i.el-icon-fa-expand-alt.fas

            // ── Multi-Select Bbox ────────────────────────────────
            .be-multi-bbox(v-if="selectedBlockIds.length > 1 && multiSelectBbox" :style="multiBboxStyle")
              .be-block-delete(
                :title="$t('Delete block')"
                style="pointer-events:auto"
                @mousedown.prevent.stop
                @click.stop="removeBlock(activeBlockId)"
              )
                i.el-icon-fa-times.fas
              .be-block-rotate(
                :title="$t('Rotate')"
                style="pointer-events:auto"
                @mousedown.prevent.stop="onRotateMouseDown(activeBlockId, $event)"
              )
                i.el-icon-fa-redo.fas
              .be-block-resize(
                :title="$t('Drag to resize')"
                style="pointer-events:auto"
                @mousedown.prevent.stop="onResizeMouseDown(activeBlockId, $event)"
              )
                i.el-icon-fa-expand-alt.fas

        // ── Canvas Footer ────────────────────────────────────────
        .be-canvas-footer
          .be-footer-group
            el-dropdown(ref="tourBgBtn" trigger="click" size="mini" @command="onBgDropdown")
              el-button(size="mini" plain :type="(activeSlide.image && activeSlide.image.url) || (activeSlide.video && activeSlide.video.url) || slideHasBgColor(activeSlide) ? 'primary' : 'default'")
                i.el-icon-fa-film.fas(v-if="activeSlide.video && activeSlide.video.url" style="margin-right:4px")
                i.el-icon-fa-fill-drip.fas(v-else-if="slideHasBgColor(activeSlide)" style="margin-right:4px")
                i.el-icon-picture-outline(v-else style="margin-right:4px")
                | {{ activeSlide.video && activeSlide.video.url ? $t('Video') : activeSlide.image && activeSlide.image.url ? $t('Image') : slideHasBgColor(activeSlide) ? (activeSlide.bgFillMode === 'gradient' ? $t('Gradient') : $t('Color')) : $t('Background') }}
                i.el-icon-arrow-down.el-icon--right
              el-dropdown-menu(slot="dropdown")
                el-dropdown-item(command="image" icon="el-icon-picture-outline") {{ activeSlide.image && activeSlide.image.url ? $t('Change image') : $t('Select image') }}
                el-dropdown-item(command="video" icon="el-icon-fa-film fas") {{ activeSlide.video && activeSlide.video.url ? $t('Change video') : $t('Select video') }}
                el-dropdown-item(command="bgColor" icon="el-icon-fa-fill-drip fas") {{ $t('Background color / Gradient') }}
                el-dropdown-item(v-if="activeSlide.video && activeSlide.video.url" command="removeVideo" divided) {{ $t('Remove video') }}
                el-dropdown-item(v-if="slideHasBgColor(activeSlide)" command="removeBgColor" :divided="!(activeSlide.video && activeSlide.video.url)") {{ $t('Remove background color') }}
          .be-footer-sep
          .be-footer-group
            el-button(
              ref="tourBgOptions"
              size="mini"
              plain
              @click.stop="showEffectsPanel = !showEffectsPanel; showBgColorPanel = false"
            ) {{ $t('Background options') }}{{ (activeSlide.overlayColor ? 1 : 0) + (activeSlide.bgBlur ? 1 : 0) + (activeSlide.bgPosition && activeSlide.bgPosition !== 'center' ? 1 : 0) + (activeSlide.bgGrayscale ? 1 : 0) + (activeSlide.bgSepia ? 1 : 0) + (activeSlide.bgDuotone ? 1 : 0) > 0 ? ` (${(activeSlide.overlayColor ? 1 : 0) + (activeSlide.bgBlur ? 1 : 0) + (activeSlide.bgPosition && activeSlide.bgPosition !== 'center' ? 1 : 0) + (activeSlide.bgGrayscale ? 1 : 0) + (activeSlide.bgSepia ? 1 : 0) + (activeSlide.bgDuotone ? 1 : 0)})` : '' }}
          .be-footer-spacer
          .be-footer-sep
          .be-footer-group
            el-button(ref="tourTimelineBtn" size="mini" plain icon="el-icon-fa-sliders-h fas" @click="showTimeline = !showTimeline") {{ $t('Animation timeline') }}{{ (activeSlide.blocks || []).filter(b => b.animationType && b.animationType !== 'none').length > 0 ? ` (${(activeSlide.blocks || []).filter(b => b.animationType && b.animationType !== 'none').length})` : '' }}
          .be-footer-sep
          .be-footer-group
            .be-add-wrap(v-if="(activeSlide.image && activeSlide.image.url) || (activeSlide.video && activeSlide.video.url) || slideHasBgColor(activeSlide)" @mousedown.stop @click.stop)
              .be-add-dropdown(v-if="showAddMenu")
                button.be-add-opt(@click="addBlockAndClose('text')")
                  i.el-icon-fa-font.fas
                  span {{ $t('Text') }}
                .be-add-sub-wrap
                  button.be-add-opt.has-sub(@click.stop="showFormenMenu = !showFormenMenu")
                    i.el-icon-fa-shapes.fas
                    span {{ $t('Shapes') }}
                    i.el-icon-fa-chevron-right.fas.be-add-arrow(:class="{ 'is-open': showFormenMenu }")
                  .be-add-sub(v-show="showFormenMenu")
                    button.be-add-sub-opt(@click="addBlockAndClose('shape', { shapeType: 'rect' })")
                      .be-add-shape-icon
                        svg(viewBox="0 0 24 24" fill="currentColor")
                          rect(x="2" y="2" width="20" height="20")
                      span {{ $t('Rectangle') }}
                    button.be-add-sub-opt(@click="addBlockAndClose('shape', { shapeType: 'circle' })")
                      .be-add-shape-icon
                        svg(viewBox="0 0 24 24" fill="currentColor")
                          circle(cx="12" cy="12" r="10")
                      span {{ $t('Circle') }}
                    button.be-add-sub-opt(@click="addBlockAndClose('shape', { shapeType: 'triangle' })")
                      .be-add-shape-icon
                        svg(viewBox="0 0 24 24" fill="currentColor")
                          polygon(points="12,2 22,22 2,22")
                      span {{ $t('Triangle') }}
                    button.be-add-sub-opt(@click="addBlockAndClose('shape', { shapeType: 'diamond' })")
                      .be-add-shape-icon
                        svg(viewBox="0 0 24 24" fill="currentColor")
                          polygon(points="12,2 22,12 12,22 2,12")
                      span {{ $t('Diamond') }}
                    button.be-add-sub-opt(@click="addBlockAndClose('shape', { shapeType: 'star' })")
                      .be-add-shape-icon
                        svg(viewBox="0 0 24 24" fill="currentColor")
                          polygon(points="12,2 15,9 22,9 16,14 18,21 12,17 6,21 8,14 2,9 9,9")
                      span {{ $t('Star') }}
                    button.be-add-sub-opt(@click="addBlockAndClose('shape', { shapeType: 'hexagon' })")
                      .be-add-shape-icon
                        svg(viewBox="0 0 24 24" fill="currentColor")
                          polygon(points="6,2 18,2 24,12 18,22 6,22 0,12")
                      span {{ $t('Hexagon') }}
                    button.be-add-sub-opt(@click="addBlockAndClose('shape', { shapeType: 'arrow' })")
                      .be-add-shape-icon
                        svg(viewBox="0 0 24 24" fill="currentColor")
                          polygon(points="0,8 14,8 14,4 24,12 14,20 14,16 0,16")
                      span {{ $t('Arrow right') }}
                button.be-add-opt(@click="addBlockAndClose('button')")
                  i.el-icon-fa-hand-pointer.far
                  span {{ $t('Button') }}
                button.be-add-opt(@click="addBlockAndClose('image')")
                  i.el-icon-fa-image.far
                  span {{ $t('Image') }}
                button.be-add-opt(@click="openVideoSourceDialog")
                  i.el-icon-fa-video.fas
                  span {{ $t('Video') }}
              button.be-add-trigger(ref="tourAddBtn" @click="showAddMenu = !showAddMenu" :class="{ 'is-open': showAddMenu }" :title="$t('Add element')")
                i.el-icon-fa-plus.fas
                span {{ $t('Element') }}

        // ── Hintergrundfarbe Panel ──────────────────────────────
        .be-bcp(v-if="showBgColorPanel && activeSlide" @mousedown.stop @click.stop)
          .be-bcp-header
            .be-bcp-fill-switch
              button.be-bcp-fill-btn(:class="{ 'is-active': !activeSlide.bgFillMode || activeSlide.bgFillMode === 'solid' }" @click="setSlideColor('bgFillMode', 'solid')") {{ $t('Solid') }}
              button.be-bcp-fill-btn(:class="{ 'is-active': activeSlide.bgFillMode === 'gradient' }" @click="setSlideColor('bgFillMode', 'gradient')") {{ $t('Gradient') }}
            button.be-bcp-close(@click="showBgColorPanel = false") ×
          template(v-if="!activeSlide.bgFillMode || activeSlide.bgFillMode === 'solid'")
            .be-bcp-swatches
              button.be-bcp-sw(
                v-for="c in slideBgColors"
                :key="c"
                :style="{ background: c }"
                :class="{ 'is-on': activeSlide.bgColor === c }"
                @click="setSlideColor('bgColor', c)"
              )
            .be-bcp-body
              input.be-cd-color-input(type="color" :value="activeSlide.bgColor || '#000000'" @input="setSlideColor('bgColor', $event.target.value, false)" @mousedown.stop @click.stop)
              input.be-cd-hex-input.be-bcp-hex(type="text" :value="toHex(activeSlide.bgColor || '#000000')" placeholder="#000000" spellcheck="false" @blur="setSlideColor('bgColor', $event.target.value)" @keydown.enter.prevent="setSlideColor('bgColor', $event.target.value); $event.target.blur()" @mousedown.stop @click.stop @keydown.stop)
          template(v-else)
            .be-bcp-swatches
              button.be-bcp-gsw(
                v-for="(g, gi) in slideBgGradients"
                :key="gi"
                :style="{ background: `linear-gradient(135deg, ${g.c1}, ${g.c2})` }"
                :class="{ 'is-on': activeSlide.bgGradientColor1 === g.c1 && activeSlide.bgGradientColor2 === g.c2 }"
                @click="setSlideColor('bgGradientColor1', g.c1); setSlideColor('bgGradientColor2', g.c2)"
              )
            .be-bcp-body
              .be-bcp-grad-preview(:style="slideBgColorStyle(activeSlide)")
              .be-bcp-vsp
              .be-bcp-type-switch
                button.be-bcp-type-btn(:class="{ 'is-active': !activeSlide.bgGradientType || activeSlide.bgGradientType === 'linear' }" @click="setSlideColor('bgGradientType', 'linear')") {{ $t('Linear') }}
                button.be-bcp-type-btn(:class="{ 'is-active': activeSlide.bgGradientType === 'radial' }" @click="setSlideColor('bgGradientType', 'radial')") {{ $t('Radial') }}
              template(v-if="!activeSlide.bgGradientType || activeSlide.bgGradientType === 'linear'")
                .be-bcp-angle
                  input.be-bcp-angle-input(type="number" :value="activeSlide.bgGradientAngle !== undefined ? activeSlide.bgGradientAngle : 135" min="0" max="360" step="1" @change="setSlideColor('bgGradientAngle', parseInt($event.target.value) || 0)" @mousedown.stop @click.stop)
                  span °
              .be-bcp-vsp
              input.be-cd-color-input(type="color" :value="activeSlide.bgGradientColor1 || '#000000'" @input="setSlideColor('bgGradientColor1', $event.target.value, false)" @mousedown.stop @click.stop)
              input.be-cd-hex-input.be-bcp-hex(type="text" :value="toHex(activeSlide.bgGradientColor1 || '#000000')" placeholder="#000000" spellcheck="false" @blur="setSlideColor('bgGradientColor1', $event.target.value)" @keydown.enter.prevent="setSlideColor('bgGradientColor1', $event.target.value); $event.target.blur()" @mousedown.stop @click.stop @keydown.stop)
              i.el-icon-fa-arrow-right.fas.be-bcp-arrow
              input.be-cd-color-input(type="color" :value="activeSlide.bgGradientColor2 || '#ffffff'" @input="setSlideColor('bgGradientColor2', $event.target.value, false)" @mousedown.stop @click.stop)
              input.be-cd-hex-input.be-bcp-hex(type="text" :value="toHex(activeSlide.bgGradientColor2 || '#ffffff')" placeholder="#ffffff" spellcheck="false" @blur="setSlideColor('bgGradientColor2', $event.target.value)" @keydown.enter.prevent="setSlideColor('bgGradientColor2', $event.target.value); $event.target.blur()" @mousedown.stop @click.stop @keydown.stop)

        // ── Effekte Panel ──────────────────────────────────────
        .be-ep(v-if="showEffectsPanel && activeSlide" @mousedown.stop @click.stop)
          button.be-ep-close(@click.stop="showEffectsPanel = false") ×
          .be-ep-groups
            //- Gruppe 1: Bildausschnitt (nur für Bild/Video)
            .be-ep-group(v-if="(activeSlide.image && activeSlide.image.url) || (activeSlide.video && activeSlide.video.url)")
              .be-ep-group-title {{ $t('Bildausschnitt') }}
              .be-ep-group-body
                .be-ep-sub-col
                  template(v-if="activeSlide.image && activeSlide.image.url && activeSlide.image.crop")
                    .be-ep-label {{ $t('Zuschneiden') }}
                    .be-ep-crop-btns
                      button.be-ep-crop-btn(@click.stop="reopenCropForBackground")
                        i.el-icon-fa-crop-alt.fas
                        | &nbsp;{{ $t('Change crop') }}
                      button.be-ep-crop-btn.is-danger(@click.stop="removeCropForBackground")
                        i.el-icon-fa-times.fas
                        | &nbsp;{{ $t('Remove crop') }}
                  template(v-else)
                    .be-ep-label {{ $t('Position') }}
                    .be-ep-pos-btns
                      button.be-ep-pos-btn(
                        :class="{ 'is-active': !activeSlide.bgPosition || activeSlide.bgPosition === 'top' }"
                        @click.stop="setSlideColor('bgPosition', 'top')"
                        :title="$t('Top edge')"
                      )
                        svg(viewBox="0 0 20 14" width="20" height="14")
                          rect(x="0" y="0" width="20" height="14" rx="2" fill="#e5e7eb")
                          rect(x="0" y="0" width="20" height="7" rx="2" fill="currentColor")
                      button.be-ep-pos-btn(
                        :class="{ 'is-active': activeSlide.bgPosition === 'center' }"
                        @click.stop="setSlideColor('bgPosition', 'center')"
                        :title="$t('Center')"
                      )
                        svg(viewBox="0 0 20 14" width="20" height="14")
                          rect(x="0" y="0" width="20" height="14" rx="2" fill="#e5e7eb")
                          rect(x="0" y="3.5" width="20" height="7" fill="currentColor")
                      button.be-ep-pos-btn(
                        :class="{ 'is-active': activeSlide.bgPosition === 'bottom' }"
                        @click.stop="setSlideColor('bgPosition', 'bottom')"
                        :title="$t('Bottom edge')"
                      )
                        svg(viewBox="0 0 20 14" width="20" height="14")
                          rect(x="0" y="0" width="20" height="14" rx="2" fill="#e5e7eb")
                          rect(x="0" y="7" width="20" height="7" rx="2" fill="currentColor")
                    template(v-if="activeSlide.image && activeSlide.image.url")
                      .be-ep-crop-divider
                      button.be-ep-crop-btn(@click.stop="reopenCropForBackground")
                        i.el-icon-fa-crop-alt.fas
                        | &nbsp;{{ $t('Set crop') }}
                .be-ep-sub-divider
                .be-ep-sub-col
                  .be-ep-label {{ $t('Spiegeln') }}
                  .be-ep-pos-btns
                    button.be-ep-pos-btn(
                      :class="{ 'is-active': activeSlide.bgFlipH }"
                      @click.stop="setSlideColor('bgFlipH', !activeSlide.bgFlipH)"
                      :title="$t('Horizontal')"
                    )
                      svg(viewBox="0 0 20 14" width="20" height="14")
                        rect(x="1" y="2" width="8" height="10" rx="1" fill="currentColor")
                        rect(x="11" y="2" width="8" height="10" rx="1" fill="currentColor" opacity="0.3")
                        line(x1="10" y1="0" x2="10" y2="14" stroke="currentColor" stroke-width="1.5")
                    button.be-ep-pos-btn(
                      :class="{ 'is-active': activeSlide.bgFlipV }"
                      @click.stop="setSlideColor('bgFlipV', !activeSlide.bgFlipV)"
                      :title="$t('Vertikal')"
                    )
                      svg(viewBox="0 0 20 14" width="20" height="14")
                        rect(x="2" y="1" width="16" height="5" rx="1" fill="currentColor")
                        rect(x="2" y="8" width="16" height="5" rx="1" fill="currentColor" opacity="0.3")
                        line(x1="0" y1="7" x2="20" y2="7" stroke="currentColor" stroke-width="1.5")

            //- Gruppe 2: Effekte (Blur + Filter)
            .be-ep-group(v-if="(activeSlide.image && activeSlide.image.url) || (activeSlide.video && activeSlide.video.url)")
              .be-ep-group-title {{ $t('Effekte') }}
              .be-ep-group-body
                .be-ep-sub-col
                  .be-ep-label {{ $t('Blur') }}
                  .be-ep-row
                    button.be-blur-btn(@click.stop="setSlideColor('bgBlur', Math.max(0, (activeSlide.bgBlur || 0) - 1))") −
                    input.be-blur-input(
                      type="number"
                      :value="activeSlide.bgBlur || 0"
                      min="0" max="20" step="1"
                      @change="setSlideColor('bgBlur', Math.min(20, Math.max(0, parseInt($event.target.value) || 0)))"
                      @keydown.enter.prevent="$event.target.blur()"
                      @focus="$event.target.select()"
                      @mousedown.stop @click.stop @keydown.stop
                    )
                    button.be-blur-btn(@click.stop="setSlideColor('bgBlur', Math.min(20, (activeSlide.bgBlur || 0) + 1))") +
                .be-ep-sub-divider
                .be-ep-sub-col
                  .be-ep-label {{ $t('Filter') }}
                  .be-ep-filter-btns
                    button.be-ep-filter-btn(
                      :class="{ 'is-active': activeSlide.bgGrayscale }"
                      @click.stop="setSlideColor('bgGrayscale', !activeSlide.bgGrayscale); setSlideColor('bgSepia', false)"
                    )
                      svg(viewBox="0 0 36 22" width="36" height="22")
                        defs
                          linearGradient#sw-grad(x1="0" y1="0" x2="1" y2="0")
                            stop(offset="0%" stop-color="#111")
                            stop(offset="100%" stop-color="#eee")
                        rect(x="0" y="0" width="36" height="22" rx="3" fill="url(#sw-grad)")
                      span S/W
                    button.be-ep-filter-btn(
                      :class="{ 'is-active': activeSlide.bgSepia }"
                      @click.stop="setSlideColor('bgSepia', !activeSlide.bgSepia); setSlideColor('bgGrayscale', false)"
                    )
                      svg(viewBox="0 0 36 22" width="36" height="22")
                        defs
                          linearGradient#sepia-grad(x1="0" y1="0" x2="1" y2="0")
                            stop(offset="0%" stop-color="#3d2b1f")
                            stop(offset="100%" stop-color="#c8a87a")
                        rect(x="0" y="0" width="36" height="22" rx="3" fill="url(#sepia-grad)")
                      span Sepia
                    button.be-ep-filter-btn(
                      :class="{ 'is-active': showDuotoneGrid || !!activeSlide.bgDuotone }"
                      @click.stop="showDuotoneGrid = !showDuotoneGrid"
                    )
                      .be-ep-filter-dt-swatch-wrap
                        .be-ep-filter-dt-swatch(:style="activeDuotoneGradientStyle")
                        .be-ep-dt-badge(v-if="activeSlide.bgDuotone") Aktiv
                      span Duotone

            //- Gruppe 3: Overlay
            .be-ep-group.be-ep-group--fill
              .be-ep-group-title {{ $t('Overlay') }}
              .be-ep-group-body
                .be-ep-sub-col
                  .be-ep-label {{ $t('Color') }}
                  .be-ep-swatches
                    button.be-ep-sw(
                      v-for="(c, i) in overlayColors"
                      :key="i"
                      :style="c ? { background: c } : {}"
                      :class="{ 'is-on': (activeSlide.overlayColor || null) === c, 'be-ep-sw--none': !c }"
                      :title="c || $t('No overlay')"
                      @click="setSlideOverlay(c)"
                    )
                  .be-ep-custom
                    input(
                      type="color"
                      :value="activeSlide.overlayColor && activeSlide.overlayColor.startsWith('#') ? activeSlide.overlayColor : '#000000'"
                      @input="setSlideOverlay($event.target.value)"
                      @mousedown.stop @click.stop
                    )
                    input(
                      type="text"
                      :value="activeSlide.overlayColor || ''"
                      placeholder="rgba(0,0,0,0.5)"
                      spellcheck="false"
                      @blur="setSlideOverlay($event.target.value || null)"
                      @keydown.enter.prevent="setSlideOverlay($event.target.value || null); $event.target.blur()"
                      @mousedown.stop @click.stop @keydown.stop
                    )
          //- Duotone sub-panel (innerhalb .be-ep, unter .be-ep-cols)
          .be-ep-dt-panel(v-if="showDuotoneGrid && ((activeSlide.image && activeSlide.image.url) || (activeSlide.video && activeSlide.video.url))")
            svg.be-ep-dt-defs(aria-hidden="true")
              defs
                filter#be-dt-custom(color-interpolation-filters="sRGB")
                  feColorMatrix(type="saturate" values="0")
                  feComponentTransfer
                    feFuncR(type="table" :tableValues="allDuotoneFilterValues.custom.r")
                    feFuncG(type="table" :tableValues="allDuotoneFilterValues.custom.g")
                    feFuncB(type="table" :tableValues="allDuotoneFilterValues.custom.b")
                filter(
                  v-for="preset in duotonePresets"
                  :key="preset.key"
                  :id="`be-dt-${preset.key}`"
                  color-interpolation-filters="sRGB"
                )
                  feColorMatrix(type="saturate" values="0")
                  feComponentTransfer
                    feFuncR(type="table" :tableValues="allDuotoneFilterValues[preset.key].r")
                    feFuncG(type="table" :tableValues="allDuotoneFilterValues[preset.key].g")
                    feFuncB(type="table" :tableValues="allDuotoneFilterValues[preset.key].b")
            .be-ep-dt-panel-header
              .be-ep-label {{ $t('Duotone') }}
              button.be-ep-duotone-clear(v-if="activeSlide.bgDuotone" @click.stop="setDuotone(null)")
                i.el-icon-fa-times.fas
                | &nbsp;{{ $t('Remove duotone') }}
            .be-ep-duotone-custom(v-if="activeSlide.bgDuotone === 'custom'")
              .be-ep-dc-row
                label {{ $t('Shadow') }}
                .be-ep-dc-inputs
                  input(type="color" :value="activeSlide.bgDuotoneColor1 || '#000000'" @input="setSlideColor('bgDuotoneColor1', $event.target.value)" @mousedown.stop @click.stop)
                  input.be-ep-dc-hex(
                    type="text"
                    :value="activeSlide.bgDuotoneColor1 || '#000000'"
                    maxlength="7"
                    spellcheck="false"
                    @blur="setDuotoneColor('bgDuotoneColor1', $event.target.value)"
                    @keydown.enter.prevent="setDuotoneColor('bgDuotoneColor1', $event.target.value); $event.target.blur()"
                    @mousedown.stop @click.stop @keydown.stop
                  )
              .be-ep-dc-row
                label {{ $t('Highlight') }}
                .be-ep-dc-inputs
                  input(type="color" :value="activeSlide.bgDuotoneColor2 || '#ffffff'" @input="setSlideColor('bgDuotoneColor2', $event.target.value)" @mousedown.stop @click.stop)
                  input.be-ep-dc-hex(
                    type="text"
                    :value="activeSlide.bgDuotoneColor2 || '#ffffff'"
                    maxlength="7"
                    spellcheck="false"
                    @blur="setDuotoneColor('bgDuotoneColor2', $event.target.value)"
                    @keydown.enter.prevent="setDuotoneColor('bgDuotoneColor2', $event.target.value); $event.target.blur()"
                    @mousedown.stop @click.stop @keydown.stop
                  )
            .be-ep-dt-grid
              button.be-ep-dt-btn(
                :class="{ 'is-active': activeSlide.bgDuotone === 'custom' }"
                @click.stop="setDuotone(activeSlide.bgDuotone === 'custom' ? null : 'custom')"
              )
                .be-ep-dt-swatch(:style="duotoneSwatchAspect")
                  img.be-ep-dt-img(
                    v-if="activeSlide.image && activeSlide.image.url"
                    :src="activeSlide.image.url"
                    :style="[duotoneImgStyle, { filter: 'url(#be-dt-custom)' }]"
                    alt=""
                  )
                  .be-ep-dt-img-fallback(
                    v-else
                    :style="{ background: `linear-gradient(135deg, ${activeSlide.bgDuotoneColor1 || '#000000'}, ${activeSlide.bgDuotoneColor2 || '#ffffff'})` }"
                  )
                span {{ $t('Custom') }}
              button.be-ep-dt-btn(
                v-for="preset in duotonePresets"
                :key="preset.key"
                :class="{ 'is-active': activeSlide.bgDuotone === preset.key }"
                @click.stop="setDuotone(activeSlide.bgDuotone === preset.key ? null : preset.key)"
              )
                .be-ep-dt-swatch(:style="duotoneSwatchAspect")
                  img.be-ep-dt-img(
                    v-if="activeSlide.image && activeSlide.image.url"
                    :src="activeSlide.image.url"
                    :style="[duotoneImgStyle, { filter: `url(#be-dt-${preset.key})` }]"
                    alt=""
                  )
                  .be-ep-dt-img-fallback(
                    v-else
                    :style="{ background: `linear-gradient(135deg, ${preset.color1}, ${preset.color2})` }"
                  )
                span {{ $t(preset.label) }}

        // ── Animations-Zeitleiste ──────────────────────────────
        .be-timeline(v-if="showTimeline && activeSlide")
          .be-tl-header
            .be-tl-label-col
              button.be-tl-play-all(@click.stop="tlPlayAll" title="Alle abspielen")
                i.el-icon-fa-play.fas
            .be-tl-ruler(ref="tlRuler")
              .be-tl-ruler-tick(v-for="t in timelineTicks" :key="t.ms" :style="{ left: (t.ms / timelineMax * 100) + '%' }")
                span {{ t.label }}
          .be-tl-rows
            .be-tl-row(
              v-for="block in activeSlide.blocks"
              :key="block.id"
              :class="{ 'is-active': activeBlockId === block.id }"
              @click.stop="activeBlockId = block.id"
            )
              .be-tl-label
                i(:class="tlBlockIcon(block)")
                span {{ tlBlockLabel(block) }}
              .be-tl-type-pick
                select.be-tl-type-select(@change.stop="setBlockPropById(block.id, 'animationType', $event.target.value)" @click.stop @mousedown.stop :value="block.animationType || 'none'")
                  option(v-for="a in animationTypes" :key="a.key" :value="a.key") {{ a.label }}
                button.be-tl-play-btn(@click.stop="triggerAnimationPreview(block.id)" :disabled="!block.animationType || block.animationType === 'none'" title="Vorschau")
                  i.el-icon-fa-play.fas
              .be-tl-track(
                ref="tlTrack"
                @mousedown.stop="onTlTrackMouseDown(block.id, $event)"
              )
                template(v-if="block.animationType && block.animationType !== 'none'")
                  .be-tl-bar(
                    :style="tlBarStyle(block)"
                    @mousedown.stop="onTlBarMouseDown(block.id, $event)"
                  )
                    .be-tl-bar-resize(@mousedown.stop="onTlBarResizeMouseDown(block.id, $event)")
                template(v-else)
                  .be-tl-no-anim(@mousedown.stop @click.stop="setAnimForBlock(block.id)")
                    i.el-icon-fa-plus.fas
                    span {{ $t('Add animation') }}

    // ── Kontextmenü ──────────────────────────────────────────────
    .be-context-menu(
      v-if="contextMenu"
      :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
      :class="{ 'sub-left': contextMenu.subOnLeft }"
      @mousedown.stop
      @click.stop
      @contextmenu.prevent.stop
    )
      // Canvas-Kontextmenü
      template(v-if="contextMenu.type === 'canvas'")
        .be-cm-sub-wrap(v-if="activeSlide && (activeSlide.image && activeSlide.image.url || activeSlide.video && activeSlide.video.url || slideHasBgColor(activeSlide))")
          .be-cm-item.has-sub
            i.el-icon-fa-plus.fas
            span {{ $t('Add element') }}
            i.el-icon-fa-chevron-right.fas.be-cm-arrow
          .be-cm-sub
            button.be-cm-item(@click.stop="cmAddBlock('text')")
              i.el-icon-fa-font.fas
              span {{ $t('Text') }}
            .be-cm-sub-wrap
              .be-cm-item.has-sub
                i.el-icon-fa-shapes.fas
                span {{ $t('Shapes') }}
                i.el-icon-fa-chevron-right.fas.be-cm-arrow
              .be-cm-sub
                button.be-cm-item(@click.stop="cmAddBlock('shape', { shapeType: 'rect' })")
                  .be-cm-shape-icon
                    svg(viewBox="0 0 24 24" fill="currentColor")
                      rect(x="2" y="2" width="20" height="20")
                  span {{ $t('Rectangle') }}
                button.be-cm-item(@click.stop="cmAddBlock('shape', { shapeType: 'circle' })")
                  .be-cm-shape-icon
                    svg(viewBox="0 0 24 24" fill="currentColor")
                      circle(cx="12" cy="12" r="10")
                  span {{ $t('Circle') }}
                button.be-cm-item(@click.stop="cmAddBlock('shape', { shapeType: 'triangle' })")
                  .be-cm-shape-icon
                    svg(viewBox="0 0 24 24" fill="currentColor")
                      polygon(points="12,2 22,22 2,22")
                  span {{ $t('Triangle') }}
                button.be-cm-item(@click.stop="cmAddBlock('shape', { shapeType: 'diamond' })")
                  .be-cm-shape-icon
                    svg(viewBox="0 0 24 24" fill="currentColor")
                      polygon(points="12,2 22,12 12,22 2,12")
                  span {{ $t('Diamond') }}
                button.be-cm-item(@click.stop="cmAddBlock('shape', { shapeType: 'star' })")
                  .be-cm-shape-icon
                    svg(viewBox="0 0 24 24" fill="currentColor")
                      polygon(points="12,2 15,9 22,9 16,14 18,21 12,17 6,21 8,14 2,9 9,9")
                  span {{ $t('Star') }}
                button.be-cm-item(@click.stop="cmAddBlock('shape', { shapeType: 'hexagon' })")
                  .be-cm-shape-icon
                    svg(viewBox="0 0 24 24" fill="currentColor")
                      polygon(points="6,2 18,2 24,12 18,22 6,22 0,12")
                  span {{ $t('Hexagon') }}
                button.be-cm-item(@click.stop="cmAddBlock('shape', { shapeType: 'arrow' })")
                  .be-cm-shape-icon
                    svg(viewBox="0 0 24 24" fill="currentColor")
                      polygon(points="0,8 14,8 14,4 24,12 14,20 14,16 0,16")
                  span {{ $t('Arrow right') }}
            button.be-cm-item(@click.stop="cmAddBlock('button')")
              i.el-icon-fa-hand-pointer.far
              span {{ $t('Button') }}
            button.be-cm-item(@click.stop="cmAddBlock('image')")
              i.el-icon-fa-image.far
              span {{ $t('Image') }}
            button.be-cm-item(@click.stop="cmAddVideo")
              i.el-icon-fa-video.fas
              span {{ $t('Video') }}
        .be-cm-sep(v-if="copiedBlock")
        button.be-cm-item(v-if="copiedBlock" @click="cmPaste()")
          i.el-icon-fa-clipboard.fas
          span {{ $t('Paste') }}
        .be-cm-sep
        button.be-cm-item(@click="showImageLibrary = true; contextMenu = null")
          i.el-icon-picture-outline.el-icon
          span {{ activeSlide && activeSlide.image && activeSlide.image.url ? $t('Change background image') : $t('Choose background image') }}
        button.be-cm-item(@click="showVideoLibrary = true; contextMenu = null")
          i.el-icon-fa-film.fas
          span {{ activeSlide && activeSlide.video && activeSlide.video.url ? $t('Change background video') : $t('Choose background video') }}
        button.be-cm-item.is-danger(v-if="activeSlide && activeSlide.video && activeSlide.video.url" @click="removeSlideVideo(); contextMenu = null")
          i.el-icon-fa-times.fas
          span {{ $t('Remove background video') }}

      // Block-Kontextmenü
      template(v-else)
        button.be-cm-item(v-if="activeBlock && activeBlock.type === 'image'" @click="openImageForBlock(activeBlock.id); contextMenu = null")
          i.el-icon-fa-image.far
          span {{ $t('Change image') }}
        .be-cm-sep(v-if="activeBlock && activeBlock.type === 'image'")
        .be-cm-sub-wrap
          .be-cm-item.has-sub
            i.el-icon-fa-arrows-alt.fas
            span {{ $t('Position') }}
            i.el-icon-fa-chevron-right.fas.be-cm-arrow
          .be-cm-sub
            button.be-cm-item(@click.stop="cmAlign('left')")
              i.el-icon-fa-align-left.fas
              span {{ $t('Align left') }}
            button.be-cm-item(@click.stop="cmAlign('center-h')")
              i.el-icon-fa-align-center.fas
              span {{ $t('Center horizontally') }}
            button.be-cm-item(@click.stop="cmAlign('right')")
              i.el-icon-fa-align-right.fas
              span {{ $t('Align right') }}
            .be-cm-sep
            button.be-cm-item(@click.stop="cmAlign('top')")
              i.el-icon-fa-arrow-up.fas
              span {{ $t('Top') }}
            button.be-cm-item(@click.stop="cmAlign('center-v')")
              i.el-icon-fa-grip-lines.fas
              span {{ $t('Center vertically') }}
            button.be-cm-item(@click.stop="cmAlign('bottom')")
              i.el-icon-fa-arrow-down.fas
              span {{ $t('Bottom') }}
            .be-cm-sep
            .be-cm-pos-row(@mousedown.stop @click.stop)
              .be-cm-pos-field
                label X
                input.be-cm-pos-input(
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  :value="activeBlock ? Math.round(activeBlock.x * 10) / 10 : 0"
                  @change="cmSetPos('x', $event.target.value)"
                  @keydown.enter.prevent="cmSetPos('x', $event.target.value)"
                  @keydown.stop
                )
                span %
              .be-cm-pos-field
                label Y
                input.be-cm-pos-input(
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  :value="activeBlock ? Math.round(activeBlock.y * 10) / 10 : 0"
                  @change="cmSetPos('y', $event.target.value)"
                  @keydown.enter.prevent="cmSetPos('y', $event.target.value)"
                  @keydown.stop
                )
                span %
        .be-cm-sub-wrap
          .be-cm-item.has-sub
            i.el-icon-fa-layer-group.fas
            span {{ $t('Layer') }}
            i.el-icon-fa-chevron-right.fas.be-cm-arrow
          .be-cm-sub
            button.be-cm-item(@click.stop="cmLayer('front')")
              i.el-icon-fa-arrow-up.fas
              span {{ $t('Bring to front') }}
            button.be-cm-item(@click.stop="cmLayer('back')")
              i.el-icon-fa-arrow-down.fas
              span {{ $t('Send to back') }}
        .be-cm-sub-wrap
          .be-cm-item.has-sub
            i.el-icon-fa-redo.fas
            span {{ $t('Rotate') }}
            i.el-icon-fa-chevron-right.fas.be-cm-arrow
          .be-cm-sub
            button.be-cm-item(@click.stop="cmRotate(-90)")
              i.el-icon-fa-undo.fas
              span −90°
            button.be-cm-item(@click.stop="cmRotate(-10)")
              i.el-icon-fa-undo.fas
              span −10°
            .be-cm-sep
            .be-cm-pos-row(@mousedown.stop @click.stop)
              .be-cm-pos-field
                label °
                input.be-cm-pos-input(
                  type="number"
                  min="0"
                  max="359"
                  step="1"
                  :value="activeBlock ? Math.round((activeBlock.rotation || 0) * 10) / 10 : 0"
                  @change="cmSetRotation($event.target.value)"
                  @keydown.enter.prevent="cmSetRotation($event.target.value)"
                  @keydown.stop
                )
            .be-cm-sep
            button.be-cm-item(@click.stop="cmRotate(10)")
              i.el-icon-fa-redo.fas
              span +10°
            button.be-cm-item(@click.stop="cmRotate(90)")
              i.el-icon-fa-redo.fas
              span +90°
            .be-cm-sep
            button.be-cm-item(@click.stop="cmSetRotation(0)")
              i.el-icon-fa-times.fas
              span {{ $t('Reset') }}
        .be-cm-sub-wrap
          .be-cm-item.has-sub(:class="{ 'is-on': activeBlock && activeBlock.animationType && activeBlock.animationType !== 'none' }")
            i.el-icon-fa-magic.fas
            span {{ $t('Animation') }}
            i.el-icon-fa-chevron-right.fas.be-cm-arrow
          .be-cm-sub.be-cm-anim-sub
            .be-cm-sub-wrap
              .be-cm-item.has-sub
                i.el-icon-fa-magic.fas
                span {{ $t('Effect') }}
                i.el-icon-fa-chevron-right.fas.be-cm-arrow
              .be-cm-sub
                .be-cm-anim-list
                  button.be-cm-item(
                    v-for="a in animationTypes"
                    :key="a.key"
                    :class="{ 'is-on': (activeBlock && activeBlock.animationType || 'none') === a.key }"
                    @click.stop="setBlockProp('animationType', a.key); contextMenu = null"
                  )
                    i(:class="a.icon")
                    span {{ a.label }}
            .be-cm-sub-wrap
              .be-cm-item.has-sub
                i.el-icon-fa-sliders-h.fas
                span {{ $t('Easing') }}
                i.el-icon-fa-chevron-right.fas.be-cm-arrow
              .be-cm-sub
                button.be-cm-item(
                  v-for="e in animationEasings"
                  :key="e.value"
                  :class="{ 'is-on': (blockProps.animationEasing || 'ease') === e.value }"
                  @click.stop="setBlockProp('animationEasing', e.value); contextMenu = null"
                )
                  span {{ e.label }}
            .be-cm-sep
            .be-cm-pos-row(@mousedown.stop @click.stop)
              .be-cm-pos-field
                label {{ $t('Delay') }}
                input.be-cm-pos-input(
                  type="number" :value="blockProps.animationDelay || 0"
                  min="0" max="3000" step="100"
                  @change="setBlockProp('animationDelay', Math.min(3000, Math.max(0, parseInt($event.target.value) || 0)))"
                  @keydown.enter.prevent="$event.target.blur()" @focus="$event.target.select()" @keydown.stop
                )
                span ms
            .be-cm-pos-row(@mousedown.stop @click.stop)
              .be-cm-pos-field
                label {{ $t('Duration') }}
                input.be-cm-pos-input(
                  type="number" :value="(blockProps.animationDuration !== undefined ? blockProps.animationDuration : 0.6).toFixed(1)"
                  min="0.1" max="5" step="0.1"
                  @change="setBlockProp('animationDuration', Math.min(5, Math.max(0.1, parseFloat($event.target.value) || 0.6)))"
                  @keydown.enter.prevent="$event.target.blur()" @focus="$event.target.select()" @keydown.stop
                )
                span s
            .be-cm-sep
            button.be-cm-item(@click.stop="triggerAnimationPreview(activeBlockId); contextMenu = null")
              i.el-icon-fa-play.fas
              span {{ $t('Play preview') }}
        .be-cm-sub-wrap(v-if="activeBlock && activeBlock.type === 'image'")
          .be-cm-item.has-sub
            i.el-icon-fa-expand.fas
            span {{ $t('Image size') }}
            i.el-icon-fa-chevron-right.fas.be-cm-arrow
          .be-cm-sub
            .be-cm-pos-row(@mousedown.stop @click.stop)
              .be-cm-pos-field
                label W
                input.be-cm-pos-input(
                  type="number"
                  min="1"
                  max="100"
                  step="0.1"
                  :value="activeBlock ? Math.round((activeBlock.w || 20) * 10) / 10 : 20"
                  @change="cmSetSize('w', $event.target.value)"
                  @keydown.enter.prevent="cmSetSize('w', $event.target.value)"
                  @keydown.stop
                )
                span %
            .be-cm-pos-row(@mousedown.stop @click.stop)
              .be-cm-pos-field
                label H
                input.be-cm-pos-input(
                  type="number"
                  min="1"
                  max="100"
                  step="0.1"
                  :value="activeBlock ? Math.round((activeBlock.h || 15) * 10) / 10 : 15"
                  @change="cmSetSize('h', $event.target.value)"
                  @keydown.enter.prevent="cmSetSize('h', $event.target.value)"
                  @keydown.stop
                )
                span %
            .be-cm-sep
            button.be-cm-item(:class="{ 'is-on': !activeBlock.imageFit || activeBlock.imageFit === 'contain' }" @click.stop="setBlockProp('imageFit', 'contain'); contextMenu = null")
              i.el-icon-fa-compress-arrows-alt.fas
              span {{ $t('Contain') }}
            button.be-cm-item(:class="{ 'is-on': activeBlock.imageFit === 'cover' }" @click.stop="setBlockProp('imageFit', 'cover'); contextMenu = null")
              i.el-icon-fa-expand-arrows-alt.fas
              span {{ $t('Cover') }}
            button.be-cm-item(:class="{ 'is-on': activeBlock.imageFit === 'fill' }" @click.stop="setBlockProp('imageFit', 'fill'); contextMenu = null")
              i.el-icon-fa-arrows-alt.fas
              span {{ $t('Stretch') }}
        .be-cm-sep
        button.be-cm-item(:class="{ 'is-on': activeBlock && activeBlock.mobileHidden }" @click="setBlockProp('mobileHidden', !(activeBlock && activeBlock.mobileHidden)); contextMenu = null")
          i.el-icon-fa-mobile-alt.fas
          span {{ activeBlock && activeBlock.mobileHidden ? $t('Auf Mobil anzeigen') : $t('Auf Mobil ausblenden') }}
        button.be-cm-item(:class="{ 'is-on': activeBlock && activeBlock.desktopHidden }" @click="setBlockProp('desktopHidden', !(activeBlock && activeBlock.desktopHidden)); contextMenu = null")
          i.el-icon-fa-desktop.fas
          span {{ activeBlock && activeBlock.desktopHidden ? $t('Auf Desktop anzeigen') : $t('Auf Desktop ausblenden') }}
        .be-cm-sep
        button.be-cm-item(@click="cmEdit()")
          i.el-icon-fa-pen.fas
          span {{ $t('Edit') }}
        button.be-cm-item(@click="cmCopy()")
          i.el-icon-fa-copy.far
          span {{ $t('Copy') }}
        button.be-cm-item(@click="cmPaste()")
          i.el-icon-fa-clipboard.fas
          span {{ $t('Paste') }}
        .be-cm-sep
        button.be-cm-item.is-danger(@click="cmDelete()")
          i.el-icon-fa-trash-alt.far
          span {{ $t('Delete') }}

    image-library-dialog(
      :visible.sync="showImageLibrary"
      selection-type="image"
      :show-crop-button="!pendingImageBlockId"
      @select="onImageSelect"
      @select-with-crop="onImageSelectWithCrop"
    )
    image-library-dialog(
      :visible.sync="showVideoLibrary"
      selection-type="file"
      :accepted-mime-types="['video/mp4','video/webm','video/ogg','video/quicktime','video/x-msvideo','video/x-matroska']"
      @select="onVideoSelect"
    )

    el-dialog(
      :visible.sync="videoSourceDialog.visible"
      :title="$t('Add video')"
      :append-to-body="true"
      width="340px"
      custom-class="be-video-source-dialog"
      top="30vh"
    )
      .be-vsd-options
        button.be-vsd-opt(@click="addVideoFromLibrary")
          i.el-icon-fa-film.fas
          span {{ $t('Media library') }}
          small {{ $t('MP4, WebM from own library') }}
        button.be-vsd-opt(@click="addVideoExternal")
          i.el-icon-fa-globe.fas
          span {{ $t('External service') }}
          small {{ $t('YouTube or Vimeo URL') }}

    el-dialog(
      :title="$t('Crop image')"
      :visible.sync="cropModal.visible"
      :before-close="confirmDiscard(() => cropModal.moved)"
      :append-to-body="true"
      :close-on-click-modal="false"
      custom-class="be-crop-dialog"
      width="700px"
      top="5vh"
      @opened="cropModal.ready = true"
      @closed="cropModal.ready = false"
    )
      .be-crop-body
        .be-crop-inner
          vue-cropper(
            v-if="cropModal.ready"
            :key="cropModal.src"
            ref="beCropper"
            drag-mode="crop"
            :src="cropModal.src"
            :cropend="markCropMoved"
            :aspect-ratio="cropModal.aspectRatio"
            :rotatable="false"
            :background="false"
            :restore="false"
            :check-orientation="false"
            :view-mode="2"
            :zoomable="false"
            :auto-crop-area="1"
            :img-style="{ maxHeight: '380px', display: 'block' }"
          )
      template(v-slot:footer)
        el-button(size="mini" icon="el-icon-fa-times fas" @click="onCropSkip") {{ $t('Skip') }}
        el-button(size="mini" type="primary" icon="el-icon-fa-crop-alt fas" @click="onCropConfirm") {{ $t('Crop image') }}

  .be-preview(v-else)
    svg(aria-hidden="true" width="0" height="0" style="display:block;position:absolute;overflow:hidden;pointer-events:none")
      defs
        filter#be-duotone-preview(color-interpolation-filters="sRGB")
          feColorMatrix(type="saturate" values="0")
          feComponentTransfer
            feFuncR(type="table" :tableValues="previewSlideDuotoneValues.r")
            feFuncG(type="table" :tableValues="previewSlideDuotoneValues.g")
            feFuncB(type="table" :tableValues="previewSlideDuotoneValues.b")
        filter(v-for="f in allBlocksDuotoneFilters" :key="f.id" :id="f.id" color-interpolation-filters="sRGB")
          feColorMatrix(type="saturate" values="0")
          feComponentTransfer
            feFuncR(type="table" :tableValues="f.values.r")
            feFuncG(type="table" :tableValues="f.values.g")
            feFuncB(type="table" :tableValues="f.values.b")
    template(v-if="previewSlide")
      .be-preview-canvas(ref="previewCanvasEl" :style="{ paddingTop: bannerHeight + '%' }" @click="onPreviewCanvasClick")
        .be-preview-bg-color(v-if="slideHasBgColor(previewSlide)" :style="slideBgColorStyle(previewSlide)")
        .be-preview-bg-wrap(v-if="previewSlide.image && previewSlide.image.url" :style="previewBgWrapStyle(previewSlide)")
          img.be-preview-bg-img(:src="previewSlide.image.url" :style="previewBgImgStyle(previewSlide)" alt="")
        video.be-preview-video(
          v-if="previewSlide.video && previewSlide.video.url"
          :key="previewSlide.video.url"
          :style="previewVideoStyle"
          autoplay muted loop playsinline
        )
          source(:src="previewSlide.video.url")
        .be-preview-overlay(v-if="previewSlide.overlayColor" :style="{ background: previewSlide.overlayColor }")
        .be-preview-block.be-preview-text(v-for="block in previewSlide.blocks || []" v-if="!block.type || block.type === 'text'" :key="block.id" :style="blockPreviewStyle(block)" v-html="block.html || block.text || ''")
        template(v-for="block in previewSlide.blocks || []")
          .be-preview-block(v-if="block.type === 'shape'" :key="block.id + '-s'" :style="blockPreviewStyle(block)")
            div(:style="shapeInnerStyle(block)" style="width:100%;height:100%")
          .be-preview-block(v-else-if="block.type === 'button'" :key="block.id + '-b'" :style="blockPreviewStyle(block)")
            span(:style="btnPreviewStyle(block)") {{ block.text || 'Button' }}
          .be-preview-block(v-else-if="block.type === 'image' && block.imageUrl" :key="block.id + '-i'" :style="blockPreviewStyle(block)")
            .be-preview-img-inner(:style="blockImgStyle(block)")
              img(:src="block.imageUrl" :style="imgElementStyle(block)")
          .be-preview-block.be-preview-video-block(v-else-if="block.type === 'video'" :key="block.id + '-v'" :style="blockPreviewStyle(block)")
        button.be-preview-nav-btn.be-preview-nav-prev(
          v-if="slides.length > 1"
          type="button"
          :style="{ '--be-nav-size': navPreviewBtnSize + 'px' }"
          @click.stop="previewSlideIndex = (previewSlideIndex - 1 + slides.length) % slides.length"
          aria-label="Zurück"
        )
        button.be-preview-nav-btn.be-preview-nav-next(
          v-if="slides.length > 1"
          type="button"
          :style="{ '--be-nav-size': navPreviewBtnSize + 'px' }"
          @click.stop="previewSlideIndex = (previewSlideIndex + 1) % slides.length"
          aria-label="Weiter"
        )
      .be-preview-badge
        i.el-icon-fa-images.fas
        span {{ slides.length }} {{ slides.length === 1 ? $t('Slide') : $t('Slides') }}
        template(v-if="slides.length > 1")
          .be-preview-dots
            span.be-preview-dot(
              v-for="(s, i) in slides"
              :key="i"
              :class="{ 'is-active': i === previewSlideIndex }"
              @click.stop="previewSlideIndex = i"
            )
    .be-preview-placeholder(v-else)
      i.el-icon-fa-images.fas
      span {{ $t('Banner Slider') }}
</template>

<script>
import draggable from 'vuedraggable'
import ImageLibraryDialog from './ImageLibraryDialog'
import BannerLinkPicker from './BannerLinkPicker'
import VueCropper from 'vue-cropperjs'
import discardGuard from '@/mixins/discardGuard'

let _idCounter = Date.now()
function newId() { return `be${++_idCounter}` }

const TEXT_COLORS = [
    '#ffffff', '#f3f4f6', '#e5e7eb', '#9ca3af',
    '#374151', '#000000', '#ef4444', '#f97316',
    '#eab308', '#22c55e', '#06b6d4', '#3b82f6',
    '#8b5cf6', '#ec4899',
]

const SHAPE_FILL_COLORS = [
    '#3b82f6', '#1d4ed8', '#8b5cf6', '#ec4899',
    '#ef4444', '#f97316', '#eab308', '#22c55e',
    '#ffffff', '#000000',
    'rgba(255,255,255,0.4)', 'rgba(0,0,0,0.4)',
]

const BTN_BG_COLORS = [
    '#3b82f6', '#1d4ed8', '#8b5cf6', '#ec4899',
    '#ef4444', '#22c55e', '#f97316', '#000000',
    '#374151', '#ffffff',
]

const TEXT_BG_COLORS = [
    '#000000', '#1e293b', '#374151', '#6b7280',
    '#ffffff', '#f9fafb', '#f3f4f6', '#e5e7eb',
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
]

const SLIDE_DUOTONE_PRESETS = [
    { key: 'kirsche',     label: 'Kirsche',     color1: '#1a0040', color2: '#ff1a44' },
    { key: 'fuchsia',     label: 'Fuchsia',     color1: '#0d0020', color2: '#ee00cc' },
    { key: 'pop',         label: 'Pop',         color1: '#1a0000', color2: '#ff6600' },
    { key: 'violett',     label: 'Violett',     color1: '#0d001a', color2: '#8833ff' },
    { key: 'ozeanblau',   label: 'Ozeanblau',   color1: '#001433', color2: '#00aaff' },
    { key: 'ozeangruen',  label: 'Ozeangrün',   color1: '#001a14', color2: '#00cc66' },
    { key: 'senfgelb',    label: 'Senfgelb',    color1: '#1a1400', color2: '#ddaa00' },
    { key: 'bernstein',   label: 'Bernstein',   color1: '#1a0800', color2: '#ff8800' },
    { key: 'pomelo',      label: 'Pomelo',      color1: '#200010', color2: '#ff5500' },
    { key: 'blush',       label: 'Blush',       color1: '#1a0020', color2: '#ff99cc' },
    { key: 'pfefferminz', label: 'Pfefferminz', color1: '#001433', color2: '#00ffcc' },
    { key: 'mystisch',    label: 'Mystisch',    color1: '#200040', color2: '#ff33aa' },
    { key: 'pastell',     label: 'Pastell',     color1: '#cc5577', color2: '#ffddee' },
]
const SLIDE_DUOTONE_MAP = Object.fromEntries(SLIDE_DUOTONE_PRESETS.map(p => [p.key, p]))

function duotoneSvgValues(c1, c2) {
    const h = hex => { const c = (hex || '#000000').replace('#','').padEnd(6,'0'); return [parseInt(c.slice(0,2),16)/255,parseInt(c.slice(2,4),16)/255,parseInt(c.slice(4,6),16)/255] }
    const [r1,g1,b1] = h(c1); const [r2,g2,b2] = h(c2)
    return { r:`${r1.toFixed(3)} ${r2.toFixed(3)}`, g:`${g1.toFixed(3)} ${g2.toFixed(3)}`, b:`${b1.toFixed(3)} ${b2.toFixed(3)}` }
}

const SLIDE_BG_COLORS = [
    '#000000', '#1e293b', '#374151', '#6b7280',
    '#94a3b8', '#cbd5e1', '#e2e8f0', '#ffffff',
    '#1d4ed8', '#7c3aed', '#db2777', '#dc2626',
    '#ea580c', '#ca8a04', '#16a34a', '#0891b2',
]

const SLIDE_BG_GRADIENTS = [
    { c1: '#0f172a', c2: '#1e40af' },
    { c1: '#1e3a8a', c2: '#60a5fa' },
    { c1: '#4c1d95', c2: '#a78bfa' },
    { c1: '#7c3aed', c2: '#f472b6' },
    { c1: '#831843', c2: '#f43f5e' },
    { c1: '#7f1d1d', c2: '#f97316' },
    { c1: '#713f12', c2: '#fbbf24' },
    { c1: '#064e3b', c2: '#34d399' },
    { c1: '#0e7490', c2: '#7dd3fc' },
    { c1: '#000000', c2: '#374151' },
    { c1: '#374151', c2: '#d1d5db' },
    { c1: '#1e293b', c2: '#64748b' },
]

const FONT_FAMILIES = [
    // ── Template-Fonts (self-hosted) ──────────────────────────────
    { key: '"Roboto Flex", sans-serif',     label: 'Roboto Flex',     group: 'template' },
    { key: '"Libre Franklin", sans-serif',  label: 'Libre Franklin',  group: 'template' },
    { key: '"Rubik", sans-serif',           label: 'Rubik',           group: 'template' },
    { key: '"Work Sans", sans-serif',       label: 'Work Sans',       group: 'template' },
    { key: '"Shantell Sans", sans-serif',   label: 'Shantell Sans',   group: 'template' },
    { key: '"Oswald", sans-serif',          label: 'Oswald',          group: 'template' },
    { key: '"Dancing Script", cursive',     label: 'Dancing Script',  group: 'template' },
    { key: '"Open Sans", sans-serif',       label: 'Open Sans',       group: 'template' },
    // ── Systemschriften ───────────────────────────────────────────
    { key: 'Arial, Helvetica, sans-serif',          label: 'Arial',           group: 'system' },
    { key: 'Verdana, Geneva, sans-serif',            label: 'Verdana',         group: 'system' },
    { key: 'Trebuchet MS, sans-serif',               label: 'Trebuchet MS',    group: 'system' },
    { key: 'Georgia, serif',                         label: 'Georgia',         group: 'system' },
    { key: '"Times New Roman", Times, serif',        label: 'Times New Roman', group: 'system' },
    { key: 'Impact, Charcoal, sans-serif',           label: 'Impact',          group: 'system' },
    { key: '"Courier New", Courier, monospace',      label: 'Courier New',     group: 'system' },
]

const SHADOW_COLORS = [
    'rgba(0,0,0,0.9)', 'rgba(0,0,0,0.7)', 'rgba(0,0,0,0.5)', 'rgba(0,0,0,0.3)',
    'rgba(255,255,255,0.9)', 'rgba(255,255,255,0.6)',
    '#000000', '#1e293b', '#374151', '#ffffff',
]

const OVERLAY_COLORS = [
    null,
    'rgba(0,0,0,0.2)',
    'rgba(0,0,0,0.4)',
    'rgba(0,0,0,0.6)',
    'rgba(0,0,0,0.75)',
    'rgba(255,255,255,0.2)',
    'rgba(255,255,255,0.5)',
    'rgba(15,23,42,0.5)',
    'rgba(30,64,175,0.4)',
    'rgba(127,29,29,0.4)',
]

const ANIMATION_EASINGS = [
    { value: 'ease',                            label: 'Sanft' },
    { value: 'ease-in',                         label: 'Einsteigen' },
    { value: 'ease-out',                        label: 'Aussteigen' },
    { value: 'ease-in-out',                     label: 'Sanft ein & aus' },
    { value: 'linear',                          label: 'Linear' },
    { value: 'cubic-bezier(0.34,1.56,0.64,1)', label: 'Überschwingen' },
    { value: 'cubic-bezier(0.68,-0.55,0.27,1.55)', label: 'Elastic' },
    { value: 'steps(6,end)',                    label: 'Stufenweise' },
]

const ANIMATION_TYPES = [
    { key: 'none',        label: 'Keine',              icon: 'el-icon-fa-ban fas' },
    { key: 'fadeIn',      label: 'Einblenden',         icon: 'el-icon-fa-eye fas' },
    { key: 'slideUp',     label: 'Von unten',          icon: 'el-icon-fa-arrow-up fas' },
    { key: 'slideDown',   label: 'Von oben',           icon: 'el-icon-fa-arrow-down fas' },
    { key: 'slideLeft',   label: 'Von rechts',         icon: 'el-icon-fa-arrow-left fas' },
    { key: 'slideRight',  label: 'Von links',          icon: 'el-icon-fa-arrow-right fas' },
    { key: 'zoomIn',      label: 'Hereinzoomen',       icon: 'el-icon-fa-search-plus fas' },
    { key: 'zoomOut',     label: 'Herauszoomen',       icon: 'el-icon-fa-search-minus fas' },
    { key: 'flipX',       label: 'Klappen horizontal', icon: 'el-icon-fa-arrows-alt-h fas' },
    { key: 'flipY',       label: 'Klappen vertikal',   icon: 'el-icon-fa-arrows-alt-v fas' },
    { key: 'bounceIn',    label: 'Bouncing',           icon: 'el-icon-fa-circle fas' },
    { key: 'rotateIn',    label: 'Eindrehen',          icon: 'el-icon-fa-sync fas' },
    { key: 'blurIn',      label: 'Unschärfe',          icon: 'el-icon-fa-adjust fas' },
    { key: 'slideUpFade', label: 'Aufsteigen',         icon: 'el-icon-fa-level-up-alt fas' },
    { key: 'flyTop',     label: 'Fliegen von oben',   icon: 'el-icon-fa-arrow-down fas' },
    { key: 'flyBottom',  label: 'Fliegen von unten',  icon: 'el-icon-fa-arrow-up fas' },
    { key: 'flyLeft',    label: 'Fliegen von links',  icon: 'el-icon-fa-arrow-right fas' },
    { key: 'flyRight',   label: 'Fliegen von rechts', icon: 'el-icon-fa-arrow-left fas' },
]

const SNAP_X = [8, 33.3, 50, 66.7, 92]
const SNAP_Y = [8, 33.3, 50, 66.7, 92]
const SNAP_THRESHOLD = 2.5

// Photoshop-artiges Snapping mit gemessener Block-Größe
// measuredW/H: tatsächliche Blockgröße in Canvas-Prozent (aus DOM gemessen)
function snapBlock(rawX, rawY, block, measuredW, measuredH) {
    const type = block.type || 'text'
    const hw = measuredW / 2
    const hh = measuredH / 2

    // X-Anker: alle relevanten horizontalen Punkte
    let xAnchors
    if (type === 'text') {
        const align = block.align || 'left'
        if (align === 'left')        xAnchors = [rawX, rawX + measuredW]
        else if (align === 'right')  xAnchors = [rawX - measuredW, rawX]
        else                         xAnchors = [rawX - hw, rawX, rawX + hw]
    } else {
        // shape/image/button: Anker = Mitte
        xAnchors = [rawX - hw, rawX, rawX + hw]
    }

    // Y-Anker: Oberkante, Mitte, Unterkante (für alle Typen)
    const yAnchors = [rawY - hh, rawY, rawY + hh]

    let snappedX = rawX, minDx = SNAP_THRESHOLD
    for (const anchor of xAnchors) {
        for (const guide of SNAP_X) {
            const d = Math.abs(anchor - guide)
            if (d < minDx) { minDx = d; snappedX = rawX + (guide - anchor) }
        }
    }

    let snappedY = rawY, minDy = SNAP_THRESHOLD
    for (const anchor of yAnchors) {
        for (const guide of SNAP_Y) {
            const d = Math.abs(anchor - guide)
            if (d < minDy) { minDy = d; snappedY = rawY + (guide - anchor) }
        }
    }

    return {
        x: Math.round(Math.max(0, Math.min(100, snappedX)) * 10) / 10,
        y: Math.round(Math.max(0, Math.min(100, snappedY)) * 10) / 10,
    }
}

export default {
    name: 'BannerEditor',
    model: { prop: 'value', event: 'input' },
    mixins: [discardGuard],
    components: { draggable, ImageLibraryDialog, BannerLinkPicker, VueCropper },
    props: {
        value: { type: String, default: '' },
        edit: { type: Boolean, default: false },
        ciColors: { type: Array, default: () => [] },
        bodyFont: { type: String, default: '' },
        options: { type: Object, default: () => ({}) },
        openAtSlide: { type: Number, default: -1 },
    },
    data() {
        return {
            slides: [this._emptySlide()],
            activeSlideIndex: 0,
            activeBlockId: null,
            selectedBlockIds: [],
            multiSelectBbox: null,
            showImageLibrary: false,
            showVideoLibrary: false,
            videoSourceDialog: { visible: false },
            cropModal: {
                visible: false,
                ready: false,
                moved: false,
                src: '',
                origW: 0,
                origH: 0,
                aspectRatio: NaN,
                onConfirm: null,
            },
            textColors: TEXT_COLORS,
            textBgColors: TEXT_BG_COLORS,
            shapeFillColors: SHAPE_FILL_COLORS,
            btnBgColors: BTN_BG_COLORS,
            overlayColors: OVERLAY_COLORS,
            shadowColors: SHADOW_COLORS,
            slideBgColors: SLIDE_BG_COLORS,
            slideBgGradients: SLIDE_BG_GRADIENTS,
            duotonePresets: SLIDE_DUOTONE_PRESETS,
            showDuotoneGrid: false,
            showImgDuotoneGrid: false,
            showVidDuotoneGrid: false,
            history: [],
            historyIndex: -1,
            undoRedoActive: false,
            typingHistoryPushed: false,
            emitTimer: null,
            pendingImageBlockId: null,
            ctxVideoPos: null,
            textEditMode: false,
            dblClickPos: null,
            showAddMenu: false,
            showFormenMenu: false,
            showGuides: true,
            canvasLayout: 'container',
            containerBgMode: 'image-blur',
            containerBgColor: '#9ca3af',
            containerBgColor2: '#374151',
            containerBgFillMode: 'solid',
            containerBgGradientAngle: 135,
            bannerHeight: 30,
            mobileBannerHeight: null,
            showHelp: false,
            showLayoutPanel: false,
            showBgColorPanel: false,
            showEffectsPanel: false,
            contextMenu: null,
            copiedBlock: null,
            activeColorPicker: null,
            hexDraft: '',
            canvasWidth: 900,
            previewCanvasWidth: 0,
            previewSlideIndex: 0,
            generatedJson: '',
            animPreviewKeys: {},
            showTimeline: false,
            timelineMax: 5000,
            mobileEditMode: false,
            tourAsk: false,
            tourStep: -1,
            tourSpotlight: null,
            tourSteps: [
                {
                    title: this.$t('Willkommen im Slider-Editor!'),
                    text: this.$t('Diese kurze Tour zeigt dir alle wichtigen Funktionen. Du kannst jederzeit auf "Überspringen" klicken oder die Tour über "Hilfe & Anleitung" neu starten.'),
                    ref: null,
                    position: 'center',
                },
                {
                    title: this.$t('Folien verwalten'),
                    text: this.$t('Links siehst du alle Folien des Sliders. Mit den Buttons oben links kannst du neue Folien hinzufügen, duplizieren oder löschen. Die Reihenfolge änderst du per Drag & Drop in der Seitenleiste.'),
                    ref: 'tourSlideList',
                    position: 'right',
                },
                {
                    title: this.$t('Folien-Toolbar'),
                    text: this.$t('Hier fügst du Folien hinzu, duplizierst oder löschst sie. Rückgängig (Strg+Z) und Wiederholen (Strg+Y) sind ebenfalls hier zu finden.'),
                    ref: 'tourSlideMgmt',
                    position: 'bottom',
                },
                {
                    title: this.$t('Hintergrund einrichten'),
                    text: this.$t('Jede Folie hat einen eigenen Hintergrund. Klicke diesen Button um ein Bild, ein Video oder eine Hintergrundfarbe bzw. einen Verlauf auszuwählen.'),
                    ref: 'tourBgBtn',
                    position: 'top',
                },
                {
                    title: this.$t('Hintergrund-Optionen'),
                    text: this.$t('Hier stellst du Farbüberlagerung (Tönung), Weichzeichner, Bildposition (oben / mitte / unten), Graustufen- und Sepia-Filter ein. Die Anzahl aktiver Optionen wird in Klammern angezeigt.'),
                    ref: 'tourBgOptions',
                    position: 'top',
                },
                {
                    title: this.$t('Elemente hinzufügen'),
                    text: this.$t('Nutze den "+ Element"-Button unten rechts um neue Blöcke zur Folie hinzuzufügen. Zur Auswahl stehen: Text, Formen (Rechteck, Kreis, Dreieck, Raute, Stern, Hexagon, Pfeil), Button, Bild und Video.'),
                    ref: null,
                    position: 'center',
                },
                {
                    title: this.$t('Position & Skalierung'),
                    text: this.$t('Blöcke per Drag & Drop verschieben. Größe an den weißen Eckpunkten ändern. Pfeiltasten: 0,5% pro Schritt (Shift = 2%). Mit dem Führungsraster rasten Elemente automatisch an Mittel- und Drittellinien ein.'),
                    ref: null,
                    position: 'center',
                },
                {
                    title: this.$t('Block-Toolbar'),
                    text: this.$t('Sobald ein Element ausgewählt ist, erscheint diese Toolbar. Je nach Block-Typ verfügbar: Schrift, Größe, Farbe, Fett, Kursiv, Unterstrichen, Ausrichtung, Hintergrund, Rahmen, Eckenradius, Schatten, Transparenz, Rotation und Link.'),
                    ref: 'tourToolbar',
                    position: 'bottom',
                },
                {
                    title: this.$t('Rechtsklick-Menü'),
                    text: this.$t('Rechtsklick auf ein Element öffnet das Kontextmenü: Duplizieren, Löschen, Ebene vor/zurück, Link, Animation konfigurieren sowie "Auf Mobil ausblenden" und "Auf Desktop ausblenden" – alles an einem Ort.'),
                    ref: null,
                    position: 'center',
                },
                {
                    title: this.$t('Responsive: Desktop / Mobil'),
                    text: this.$t('Oben in der Toolbar kannst du zwischen Desktop- und Mobil-Ansicht wechseln. Im Mobil-Modus kannst du Elemente separat positionieren, skalieren oder ausblenden. Alle Änderungen gelten nur auf Mobilgeräten – der Desktop bleibt unverändert.'),
                    ref: null,
                    position: 'center',
                },
                {
                    title: this.$t('Banner-Höhe & Mobile Höhe'),
                    text: this.$t('Rechts in der Toolbar-Zeile findest du die Banner-Höhe (in vw). Im Mobil-Modus kannst du über den Schalter "Benutzerdefinierte Mobile Höhe" eine abweichende Höhe für Smartphones aktivieren.'),
                    ref: null,
                    position: 'center',
                },
                {
                    title: this.$t('Animationen'),
                    text: this.$t('Hier weist du jedem Element einen Einblend-Effekt zu und steuerst Verzögerung und Dauer. In der Timeline kannst du die Balken ziehen um sie zu verschieben und in der Länge anzupassen.'),
                    ref: 'tourTimelineBtn',
                    position: 'top',
                },
                {
                    title: this.$t('Rückgängig & Speichern'),
                    text: this.$t('Alle Änderungen können mit Strg+Z rückgängig gemacht werden (Strg+Y zum Wiederholen). Klicke abschließend außerhalb des Editors auf "Speichern" um alle Folien zu übernehmen. Fertig!'),
                    ref: 'tourUndoRedo',
                    position: 'bottom',
                },
            ],
        }
    },
    computed: {
        animationTypes() {
            const labelMap = {
                none:        this.$t('None'),
                fadeIn:      this.$t('Fade in'),
                slideUp:     this.$t('From below'),
                slideDown:   this.$t('From above'),
                slideLeft:   this.$t('From right'),
                slideRight:  this.$t('From left'),
                zoomIn:      this.$t('Zoom in'),
                zoomOut:     this.$t('Zoom out'),
                flipX:       this.$t('Flip horizontal'),
                flipY:       this.$t('Flip vertical'),
                bounceIn:    this.$t('Bouncing'),
                rotateIn:    this.$t('Rotate in'),
                blurIn:      this.$t('Blur in'),
                slideUpFade: this.$t('Rise up'),
                flyTop:      this.$t('Fly from top'),
                flyBottom:   this.$t('Fly from bottom'),
                flyLeft:     this.$t('Fly from left'),
                flyRight:    this.$t('Fly from right'),
            }
            return ANIMATION_TYPES.map(a => ({ ...a, label: labelMap[a.key] || a.label }))
        },
        animationEasings() {
            const labelMap = {
                'ease':                                this.$t('Smooth'),
                'ease-in':                             this.$t('Ease in'),
                'ease-out':                            this.$t('Ease out'),
                'ease-in-out':                         this.$t('Ease in & out'),
                'linear':                              this.$t('Linear'),
                'cubic-bezier(0.34,1.56,0.64,1)':     this.$t('Overshoot'),
                'cubic-bezier(0.68,-0.55,0.27,1.55)': this.$t('Elastic'),
                'steps(6,end)':                        this.$t('Stepwise'),
            }
            return ANIMATION_EASINGS.map(e => ({ ...e, label: labelMap[e.value] || e.label }))
        },
        fontFamilies() {
            return [
                { key: '', label: this.$t('Template Font'), group: 'template', previewFont: this.bodyFont || 'inherit' },
                ...FONT_FAMILIES,
            ]
        },
        activeSlide() {
            return this.slides[this.activeSlideIndex] || null
        },
        activeBlock() {
            if (!this.activeSlide || !this.activeBlockId) return null
            return this.activeSlide.blocks.find(b => b.id === this.activeBlockId) || null
        },
        blockProps() {
            const block = this.activeBlock
            if (!block) return { type: null, bold: false, italic: false, align: 'left', fontSize: 32, color: '#ffffff', shapeType: 'rect', fillColor: '#3b82f6', borderRadius: 0, bgColor: '', w: 20, h: 15 }
            if (!this.mobileEditMode || !block.mobile) return block
            return {
                ...block,
                fontSize: block.mobile.fontSize ?? block.fontSize,
                opacity: block.mobile.opacity !== undefined ? block.mobile.opacity : block.opacity,
            }
        },
        canvasContainerStyle() {
            if (this.mobileEditMode) {
                const mobileW = 550
                return {
                    width: `${mobileW}px`,
                    height: `${Math.round(mobileW * this.effectiveBannerHeight / 100)}px`,
                    margin: '0 auto',
                }
            }
            return { paddingTop: this.bannerHeight + '%' }
        },
        effectiveBannerHeight() {
            if (this.mobileEditMode && this.mobileBannerHeight !== null) return this.mobileBannerHeight
            return this.bannerHeight
        },
        containerBgGradientPresets() {
            return [
                { label: 'Ozean',     c1: '#0f2027', c2: '#2c5364', angle: 135 },
                { label: 'Abendhimmel', c1: '#1a1a2e', c2: '#e94560', angle: 135 },
                { label: 'Wald',      c1: '#134e5e', c2: '#71b280', angle: 135 },
                { label: 'Sonnenuntergang', c1: '#f83600', c2: '#f9d423', angle: 135 },
                { label: 'Violett',   c1: '#4a00e0', c2: '#8e2de2', angle: 135 },
                { label: 'Grau',      c1: '#2c3e50', c2: '#bdc3c7', angle: 135 },
                { label: 'Rose',      c1: '#f953c6', c2: '#b91d73', angle: 135 },
                { label: 'Mint',      c1: '#00b4db', c2: '#0083b0', angle: 135 },
            ]
        },
        containerBgCss() {
            if (this.containerBgFillMode === 'gradient') {
                return `linear-gradient(${this.containerBgGradientAngle}deg, ${this.containerBgColor}, ${this.containerBgColor2})`
            }
            return this.containerBgColor
        },
        canvasOuterBgStyle() {
            if (this.mobileEditMode) {
                return { background: this.containerBgColor || '#e5e7eb' }
            }
            if (this.containerBgMode === 'color') {
                return { background: this.containerBgCss, filter: 'brightness(0.75)' }
            }
            if (this.containerBgMode === 'image-blur') {
                const s = this.activeSlide
                if (s && s.image && s.image.url) {
                    return { backgroundImage: `url(${s.image.url})`, backgroundSize: 'cover', backgroundPosition: s.bgPosition || 'center', filter: 'blur(10px) brightness(0.75)' }
                }
                if (s && s.bgFillMode === 'gradient') {
                    const angle = s.bgGradientAngle !== undefined ? s.bgGradientAngle : 135
                    const c1 = s.bgGradientColor1 || '#000000'
                    const c2 = s.bgGradientColor2 || '#ffffff'
                    const bg = s.bgGradientType === 'radial' ? `radial-gradient(circle, ${c1}, ${c2})` : `linear-gradient(${angle}deg, ${c1}, ${c2})`
                    return { background: bg, filter: 'blur(4px) brightness(0.75)' }
                }
                if (s && s.bgColor) return { background: s.bgColor, filter: 'brightness(0.75)' }
                return { background: this.containerBgColor, filter: 'brightness(0.75)' }
            }
            return {}
        },
        containerGuidePercent() {
            const vw = this.canvasWidth || 900
            let maxW = vw
            if (vw >= 1536) maxW = 1536
            else if (vw >= 1280) maxW = 1280
            else if (vw >= 1024) maxW = 1024
            else if (vw >= 768) maxW = 768
            else if (vw >= 640) maxW = 640
            return (vw - maxW) / 2 / vw * 100
        },
        canvasBgImgStyle() {
            const img = this.activeSlide && this.activeSlide.image
            if (!img) return {}
            const crop = img.crop
            if (crop && crop.w && crop.h && crop.origW && crop.origH) {
                return {
                    position: 'absolute',
                    width: `${(crop.origW / crop.w) * 100}%`,
                    height: `${(crop.origH / crop.h) * 100}%`,
                    left: `${-(crop.x / crop.w) * 100}%`,
                    top: `${-(crop.y / crop.h) * 100}%`,
                    right: 'auto',
                    bottom: 'auto',
                    maxWidth: 'none',
                    maxHeight: 'none',
                    objectFit: 'fill',
                }
            }
            return {
                position: 'absolute',
                inset: '0',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: (this.activeSlide && this.activeSlide.bgPosition) || 'center',
            }
        },
        canvasBgTransform() {
            const s = this.activeSlide
            if (!s) return undefined
            const parts = []
            if (s.bgBlur) parts.push('scale(1.05)')
            if (s.bgFlipH) parts.push('scaleX(-1)')
            if (s.bgFlipV) parts.push('scaleY(-1)')
            return parts.length ? parts.join(' ') : undefined
        },
        canvasBgFilter() {
            const s = this.activeSlide
            if (!s) return undefined
            const parts = []
            if (s.bgDuotone) parts.push('url(#be-duotone-canvas)')
            if (s.bgBlur) parts.push(`blur(${s.bgBlur}px)`)
            if (s.bgGrayscale) parts.push('grayscale(100%)')
            if (s.bgSepia) parts.push('sepia(100%)')
            return parts.length ? parts.join(' ') : undefined
        },
        activeSlideDuotoneValues() {
            return this._duotoneValues(this.activeSlide)
        },
        previewSlideDuotoneValues() {
            return this._duotoneValues(this.previewSlide)
        },
        slidesDuotoneFilters() {
            return this.slides
                .filter(s => s.bgDuotone)
                .map(s => ({ id: `be-duotone-thumb-${s.id}`, values: this._duotoneValues(s) }))
        },
        previewVideoStyle() {
            const slide = this.previewSlide
            if (!slide) return {}
            const parts = []
            if (slide.bgDuotone) parts.push('url(#be-duotone-preview)')
            if (slide.bgBlur) parts.push(`blur(${slide.bgBlur}px)`)
            if (slide.bgGrayscale) parts.push('grayscale(100%)')
            if (slide.bgSepia) parts.push('sepia(100%)')
            const transforms = []
            if (slide.bgBlur) transforms.push('scale(1.05)')
            if (slide.bgFlipH) transforms.push('scaleX(-1)')
            if (slide.bgFlipV) transforms.push('scaleY(-1)')
            return {
                objectPosition: slide.bgPosition || 'center',
                filter: parts.length ? parts.join(' ') : undefined,
                transform: transforms.length ? transforms.join(' ') : undefined,
            }
        },
        activeDuotoneGradientStyle() {
            const s = this.activeSlide
            if (!s || !s.bgDuotone) return { background: 'linear-gradient(135deg, #1a0040, #00aaff)' }
            const preset = SLIDE_DUOTONE_MAP[s.bgDuotone]
            if (preset) return { background: `linear-gradient(135deg, ${preset.color1}, ${preset.color2})` }
            return { background: `linear-gradient(135deg, ${s.bgDuotoneColor1 || '#000000'}, ${s.bgDuotoneColor2 || '#ffffff'})` }
        },
        allDuotoneFilterValues() {
            const result = {}
            SLIDE_DUOTONE_PRESETS.forEach(p => {
                result[p.key] = duotoneSvgValues(p.color1, p.color2)
            })
            const s = this.activeSlide
            result.custom = duotoneSvgValues(s?.bgDuotoneColor1 || '#000000', s?.bgDuotoneColor2 || '#ffffff')
            return result
        },
        activeBlockDuotoneValues() {
            const b = this.activeBlock
            if (!b || !b.imageDuotone) return { r: '0 1', g: '0 1', b: '0 1' }
            const preset = SLIDE_DUOTONE_MAP[b.imageDuotone]
            const c1 = preset ? preset.color1 : (b.imageDuotoneColor1 || '#000000')
            const c2 = preset ? preset.color2 : (b.imageDuotoneColor2 || '#ffffff')
            return duotoneSvgValues(c1, c2)
        },
        activeBlockDuotoneGradientStyle() {
            const b = this.activeBlock
            if (!b || !b.imageDuotone) return { background: 'linear-gradient(135deg, #1a0040, #00aaff)' }
            const preset = SLIDE_DUOTONE_MAP[b.imageDuotone]
            if (preset) return { background: `linear-gradient(135deg, ${preset.color1}, ${preset.color2})` }
            return { background: `linear-gradient(135deg, ${b.imageDuotoneColor1 || '#000000'}, ${b.imageDuotoneColor2 || '#ffffff'})` }
        },
        activeBlockVideoDuotoneGradientStyle() {
            const b = this.activeBlock
            if (!b || !b.videoDuotone) return { background: 'linear-gradient(135deg, #1a0040, #00aaff)' }
            const preset = SLIDE_DUOTONE_MAP[b.videoDuotone]
            if (preset) return { background: `linear-gradient(135deg, ${preset.color1}, ${preset.color2})` }
            return { background: `linear-gradient(135deg, ${b.videoDuotoneColor1 || '#000000'}, ${b.videoDuotoneColor2 || '#ffffff'})` }
        },
        allImgDuotoneFilterValues() {
            const result = {}
            SLIDE_DUOTONE_PRESETS.forEach(p => {
                result[p.key] = duotoneSvgValues(p.color1, p.color2)
            })
            const b = this.activeBlock
            result.custom = duotoneSvgValues(b?.imageDuotoneColor1 || '#000000', b?.imageDuotoneColor2 || '#ffffff')
            return result
        },
        allBlocksDuotoneFilters() {
            const filters = []
            const seen = new Set()
            for (const slide of this.slides || []) {
                for (const block of slide.blocks || []) {
                    if (!block.id || seen.has(block.id)) continue
                    seen.add(block.id)
                    const isVideo = block.type === 'video'
                    const duotoneKey = isVideo ? block.videoDuotone : block.imageDuotone
                    if (!duotoneKey) continue
                    const c1raw = isVideo ? (block.videoDuotoneColor1 || '#000000') : (block.imageDuotoneColor1 || '#000000')
                    const c2raw = isVideo ? (block.videoDuotoneColor2 || '#ffffff') : (block.imageDuotoneColor2 || '#ffffff')
                    const preset = SLIDE_DUOTONE_MAP[duotoneKey]
                    const c1 = preset ? preset.color1 : c1raw
                    const c2 = preset ? preset.color2 : c2raw
                    const prefix = isVideo ? 'be-duotone-vid-' : 'be-duotone-img-'
                    filters.push({ id: `${prefix}${block.id}`, values: duotoneSvgValues(c1, c2) })
                }
            }
            return filters
        },
        multiBboxStyle() {
            const b = this.multiSelectBbox
            if (!b) return {}
            return {
                position: 'absolute',
                left: `${b.left}px`,
                top: `${b.top}px`,
                width: `${b.width}px`,
                height: `${b.height}px`,
                pointerEvents: 'none',
                zIndex: 10,
            }
        },
        duotoneSwatchAspect() {
            return { aspectRatio: `100 / ${this.bannerHeight || 30}` }
        },
        duotoneImgStyle() {
            const img = this.activeSlide?.image
            if (!img || !img.url) return {}
            const crop = img.crop
            if (!crop || !crop.w || !crop.h || !crop.origW || !crop.origH) {
                return { objectFit: 'cover', objectPosition: img.bgPosition || 'center' }
            }
            // Funktioniert korrekt wenn Container-Aspect = crop.w/crop.h
            return {
                position: 'absolute',
                width: `${(crop.origW / crop.w) * 100}%`,
                height: `${(crop.origH / crop.h) * 100}%`,
                left: `${-(crop.x / crop.w) * 100}%`,
                top: `${-(crop.y / crop.h) * 100}%`,
                right: 'auto',
                bottom: 'auto',
                maxWidth: 'none',
                maxHeight: 'none',
                objectFit: 'fill',
            }
        },
        previewSlide() {
            if (!this.slides || !this.slides.length) return null
            const idx = Math.min(this.previewSlideIndex, this.slides.length - 1)
            const s = this.slides[idx]
            if (!s) return null
            if (this.slides.length > 1) return s
            return (s.image && s.image.url) || (s.blocks && s.blocks.length > 0) ? s : null
        },
        navPreviewBtnSize() {
            const w = this.previewCanvasWidth
            if (!w) return 26
            return Math.max(20, Math.round(w * 44 / 1536))
        },
        tourCardPos() {
            if (!this.tourSpotlight) return { bottom: '20px', left: '50%', transform: 'translateX(-50%)' }
            const r = this.tourSpotlight
            const step = this.tourSteps[this.tourStep] || {}
            if (step.position === 'right') return { top: r.top + 'px', left: (r.right + 16) + 'px' }
            if (step.position === 'bottom') return { top: (r.bottom + 16) + 'px', left: r.left + 'px' }
            if (step.position === 'top') return { bottom: (window.innerHeight - r.top + 16) + 'px', left: r.left + 'px' }
            return { bottom: '20px', left: '50%', transform: 'translateX(-50%)' }
        },
        tourSpotlightStyle() {
            if (!this.tourSpotlight) return {}
            const r = this.tourSpotlight
            return { top: (r.top - 6) + 'px', left: (r.left - 6) + 'px', width: (r.width + 12) + 'px', height: (r.height + 12) + 'px' }
        },
        timelineTicks() {
            const ticks = []
            const step = this.timelineMax <= 3000 ? 500 : 1000
            for (let ms = 0; ms <= this.timelineMax; ms += step) {
                ticks.push({ ms, label: ms === 0 ? '0s' : (ms / 1000) + 's' })
            }
            return ticks
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(json) {
                if (json === this.generatedJson) return
                this.parseJson(json)
            },
        },
        activeBlockId() {
            this.textEditMode = false
        },
        mobileBannerHeight() {
            this.emitChange()
        },
        selectedBlockIds() {
            this.$nextTick(() => this._updateMultiSelectBbox())
        },
        edit(newVal) {
            this._updateCanvasWidth()
            if (!newVal && this._resizeObserver) {
                this.$nextTick(() => {
                    if (this.$refs.previewCanvasEl) this._resizeObserver.observe(this.$refs.previewCanvasEl)
                })
            }
        },
        textEditMode(val) {
            if (!val) {
                // Leerer Text-Block beim Verlassen automatisch löschen
                const blockId = this._textEditingBlockId
                this._textEditingBlockId = null
                if (blockId) {
                    const slide = this.slides[this.activeSlideIndex]
                    const block = slide && slide.blocks.find(b => b.id === blockId)
                    if (block && (!block.type || block.type === 'text')) {
                        const text = (block.text || '').trim()
                        const html = (block.html || '').replace(/<br\s*\/?>/gi, '').replace(/<[^>]*>/g, '').trim()
                        if (!text && !html) {
                            this.$nextTick(() => this.removeBlock(blockId))
                        }
                    }
                }
                return
            }
            this._textEditingBlockId = this.activeBlockId
            this.$nextTick(() => {
                const ref = this.$refs.activeEditable
                const el = Array.isArray(ref) ? ref[0] : ref
                if (!el) return
                const block = this.activeBlock
                if (block && block.html) {
                    el.innerHTML = block.html
                } else {
                    el.innerText = block ? (block.text || '') : ''
                }
                el.focus()
                const clickPos = this.dblClickPos
                this.dblClickPos = null
                const sel = window.getSelection()
                if (!sel) return
                sel.removeAllRanges()
                if (clickPos) {
                    let range = null
                    if (document.caretRangeFromPoint) {
                        range = document.caretRangeFromPoint(clickPos.x, clickPos.y)
                    } else if (document.caretPositionFromPoint) {
                        const caret = document.caretPositionFromPoint(clickPos.x, clickPos.y)
                        if (caret) {
                            range = document.createRange()
                            range.setStart(caret.offsetNode, caret.offset)
                            range.collapse(true)
                        }
                    }
                    if (range) { sel.addRange(range); return }
                }
                const range = document.createRange()
                range.selectNodeContents(el)
                range.collapse(false)
                sel.addRange(range)
            })
        },
    },
    mounted() {
        this._onKeyDown = (e) => {
            if (!this.edit) return
            const el = document.activeElement
            const inInput = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')
            if (inInput) return

            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
                e.preventDefault(); this.undo(); return
            }
            if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
                e.preventDefault(); this.redo(); return
            }

            if ((e.ctrlKey || e.metaKey) && e.key === 'v' && this.copiedBlock && !this.textEditMode) {
                e.preventDefault(); this._pasteBlock(); return
            }

            if (!this.activeBlockId) return

            // Escape: erst Edit-Modus verlassen, dann deselektieren
            if (e.key === 'Escape') {
                e.preventDefault()
                if (this.contextMenu) { this.contextMenu = null; return }
                if (this.showAddMenu) { this.showAddMenu = false; return }
                if (this.activeColorPicker) { this.activeColorPicker = null; return }
                if (this.textEditMode) this.textEditMode = false
                else this.deselectBlock()
                return
            }

            // Copy/Paste nur außerhalb Edit-Modus
            if (!this.textEditMode) {
                if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                    this.copiedBlock = JSON.parse(JSON.stringify(this.activeBlock)); return
                }
                if ((e.ctrlKey || e.metaKey) && e.key === 'v' && this.copiedBlock) {
                    e.preventDefault(); this._pasteBlock(); return
                }
                const step = e.shiftKey ? 2 : 0.5
                if (e.key === 'ArrowLeft')       { e.preventDefault(); this._moveActiveBlock(-step, 0) }
                else if (e.key === 'ArrowRight') { e.preventDefault(); this._moveActiveBlock(step, 0) }
                else if (e.key === 'ArrowUp')    { e.preventDefault(); this._moveActiveBlock(0, -step) }
                else if (e.key === 'ArrowDown')  { e.preventDefault(); this._moveActiveBlock(0, step) }
                else if (e.key === 'Delete' || e.key === 'Backspace') {
                    e.preventDefault(); this.removeBlock(this.activeBlockId)
                    this.selectedBlockIds = []
                }
            }
        }
        this._updateCanvasWidth()
        if (typeof ResizeObserver !== 'undefined') {
            this._resizeObserver = new ResizeObserver(() => this._updateCanvasWidth())
            this.$nextTick(() => {
                if (this.$refs.canvas) this._resizeObserver.observe(this.$refs.canvas)
                if (this.$refs.previewCanvasEl) this._resizeObserver.observe(this.$refs.previewCanvasEl)
            })
        } else {
            this._onWindowResize = () => this._updateCanvasWidth()
            window.addEventListener('resize', this._onWindowResize)
        }
        document.addEventListener('keydown', this._onKeyDown, true)
        this._onDocClick = (e) => {
            if (e.target && e.target.closest && e.target.closest('.el-dialog__wrapper')) return
            if (e.target && e.target.closest && e.target.closest('.be-color-dropdown')) return
            this.contextMenu = null
            this.activeColorPicker = null
        }
        document.addEventListener('click', this._onDocClick)
        document.addEventListener('contextmenu', this._onDocClick)

        this._dragObserver = new MutationObserver(() => {
            this.$el.querySelectorAll('.be-color-dropdown:not([data-drag-init])').forEach(el => {
                el.setAttribute('data-drag-init', '1')
                this._makeDraggable(el)
            })
        })
        this._dragObserver.observe(this.$el, { childList: true, subtree: true })

        if (this.edit) {
            const stored = sessionStorage.getItem('be_open_at')
            if (stored !== null) {
                sessionStorage.removeItem('be_open_at')
                const idx = parseInt(stored, 10)
                if (!isNaN(idx) && idx >= 0 && idx < this.slides.length) {
                    this.activeSlideIndex = idx
                }
            } else if (this.openAtSlide >= 0 && this.openAtSlide < this.slides.length) {
                this.activeSlideIndex = this.openAtSlide
            }
        }
        if (this.edit) this.tourCheckFirst()
    },
    beforeDestroy() {
        if (this._resizeObserver) this._resizeObserver.disconnect()
        if (this._dragObserver) this._dragObserver.disconnect()
        if (this._onWindowResize) window.removeEventListener('resize', this._onWindowResize)
        document.removeEventListener('keydown', this._onKeyDown, true)
        document.removeEventListener('click', this._onDocClick)
        document.removeEventListener('contextmenu', this._onDocClick)
    },
    methods: {
        // ── factories ─────────────────────────────────────────────
        _emptySlide() {
            return { id: newId(), image: null, overlayColor: null, blocks: [] }
        },
        _fitBlockToCrop(block, crop) {
            if (!crop || !crop.w || !crop.h || !block.w) return
            // block.h muss zur Crop-Ratio passen damit die CSS-Formel unverzerrte Darstellung liefert:
            // container-AR = block.w / (block.h × bannerHeight/100) muss = crop.w / crop.h sein
            const newH = Math.round(block.w * 100 / ((crop.w / crop.h) * (this.bannerHeight || 30)) * 10) / 10
            this.$set(block, 'h', Math.max(2, Math.min(95, newH)))
        },
        _emptyBlock(type = 'text', opts = {}) {
            const base = { id: newId(), type, x: 50, y: 40 }
            switch (type) {
                case 'shape': {
                    const st = opts.shapeType || 'rect'
                    const squareShapes = ['circle', 'triangle', 'diamond', 'star', 'hexagon']
                    const isSquare = squareShapes.includes(st)
                    // squareShapes: aspectRatio:1 → w controls size
                    // rect/arrow: h% is % of canvas height → w=h gives visual ratio = canvas_w/canvas_h (≈3:1 at 30vw)
                    const w = isSquare ? 8 : 15
                    const h = isSquare ? 8 : 15
                    return { ...base, shapeType: st, w, h, fillColor: '#3b82f6', borderRadius: 0 }
                }
                case 'button': {
                    const bh = Math.round(1.2 * 2.45 / (this.bannerHeight || 30) * 100 * 10) / 10
                    return { ...base, text: 'Button', fontSize: 1.2, color: '#ffffff', bgColor: '#3b82f6', borderRadius: 6, bold: false, fontFamily: this.bodyFont || '', w: 15, h: bh }
                }
                case 'image':
                    return { ...base, imageUrl: '', imageName: '', w: 30, h: 20 }
                case 'video': {
                    const vw = 25
                    const vh = Math.round(vw * 9 / 16 / (this.bannerHeight || 30) * 100 * 10) / 10
                    return { ...base, videoType: 'youtube', videoUrl: '', videoId: '', w: vw, h: vh, autoplay: true, muted: true, loop: false, controls: false, borderRadius: 0 }
                }
                default:
                    return { ...base, text: 'Text', fontSize: 2, color: '#ffffff', align: 'left', bold: false, italic: false, shadow: true, fontFamily: this.bodyFont || '' }
            }
        },

        // ── history ───────────────────────────────────────────────
        _snapshot() { return JSON.parse(JSON.stringify(this.slides)) },
        _resetHistory() {
            this.history = [this._snapshot()]
            this.historyIndex = 0
            this.typingHistoryPushed = false
        },
        pushHistory() {
            this.history = this.history.slice(0, this.historyIndex + 1)
            this.history.push(this._snapshot())
            if (this.history.length > 50) this.history.shift()
            else this.historyIndex++
        },
        undo() {
            if (this.historyIndex <= 0) return
            this.historyIndex--
            this.undoRedoActive = true
            this.slides = JSON.parse(JSON.stringify(this.history[this.historyIndex]))
            if (this.activeSlideIndex >= this.slides.length) this.activeSlideIndex = this.slides.length - 1
            this.activeBlockId = null
            this.emitChange()
        },
        redo() {
            if (this.historyIndex >= this.history.length - 1) return
            this.historyIndex++
            this.undoRedoActive = true
            this.slides = JSON.parse(JSON.stringify(this.history[this.historyIndex]))
            if (this.activeSlideIndex >= this.slides.length) this.activeSlideIndex = this.slides.length - 1
            this.activeBlockId = null
            this.emitChange()
        },

        // ── parse / generate ──────────────────────────────────────
        _normalizeSlide(s) {
            // Ensure image and video are always reactive properties (even if absent in stored JSON).
            if (!('image' in s)) s.image = null
            if (!('video' in s)) s.video = null
            return s
        },
        parseJson(jsonStr) {
            if (!jsonStr) {
                this.slides = [this._emptySlide()]
                this.activeSlideIndex = 0
                this.activeBlockId = null
                if (!this.undoRedoActive) this._resetHistory()
                else this.undoRedoActive = false
                return
            }
            try {
                const data = JSON.parse(jsonStr)
                const slides = Array.isArray(data.slides) ? data.slides : []
                this.slides = slides.length ? slides.map(s => this._normalizeSlide(s)) : [this._emptySlide()]
                this.canvasLayout = ['full', 'full-contained', 'container'].includes(data.canvasLayout) ? data.canvasLayout : 'container'
                this.containerBgMode = ['none', 'color', 'image-blur'].includes(data.containerBgMode) ? data.containerBgMode : 'image-blur'
                this.containerBgColor = /^#[0-9a-fA-F]{6}$/.test(data.containerBgColor) ? data.containerBgColor : '#9ca3af'
                this.containerBgColor2 = /^#[0-9a-fA-F]{6}$/.test(data.containerBgColor2) ? data.containerBgColor2 : '#374151'
                this.containerBgFillMode = data.containerBgFillMode === 'gradient' ? 'gradient' : 'solid'
                this.containerBgGradientAngle = (Number(data.containerBgGradientAngle) >= 0 && Number(data.containerBgGradientAngle) <= 360) ? Number(data.containerBgGradientAngle) : 135
                this.bannerHeight = (Number(data.bannerHeight) >= 10 && Number(data.bannerHeight) <= 100) ? Number(data.bannerHeight) : 30
                this.mobileBannerHeight = (Number(data.mobileBannerHeight) >= 10 && Number(data.mobileBannerHeight) <= 100) ? Number(data.mobileBannerHeight) : null
                if (this.activeSlideIndex >= this.slides.length) this.activeSlideIndex = 0
                this.activeBlockId = null
            } catch (_) {
                this.slides = [this._emptySlide()]
                this.activeSlideIndex = 0
                this.activeBlockId = null
            }
            if (!this.undoRedoActive) this._resetHistory()
            else this.undoRedoActive = false
        },
        generateJson() {
            return JSON.stringify({ slides: this.slides, canvasLayout: this.canvasLayout, containerBgMode: this.containerBgMode, containerBgColor: this.containerBgColor, containerBgColor2: this.containerBgColor2, containerBgFillMode: this.containerBgFillMode, containerBgGradientAngle: this.containerBgGradientAngle, bannerHeight: this.bannerHeight, ...(this.mobileBannerHeight !== null ? { mobileBannerHeight: this.mobileBannerHeight } : {}) })
        },
        emitChange() {
            this.generatedJson = this.generateJson()
            this.$emit('input', this.generatedJson)
        },
        scheduleEmit() {
            clearTimeout(this.emitTimer)
            this.emitTimer = setTimeout(() => {
                this.emitChange()
                this.typingHistoryPushed = false
            }, 300)
        },

        // ── slides ────────────────────────────────────────────────
        addSlide() {
            this.pushHistory()
            this.slides.push(this._emptySlide())
            this.activeSlideIndex = this.slides.length - 1
            this.activeBlockId = null
            this.emitChange()
        },
        removeSlide(index) {
            if (this.slides.length <= 1) return
            this.pushHistory()
            this.slides.splice(index, 1)
            if (this.activeSlideIndex >= this.slides.length) this.activeSlideIndex = this.slides.length - 1
            this.activeBlockId = null
            this.emitChange()
        },
        duplicateSlide(index) {
            this.pushHistory()
            const copy = JSON.parse(JSON.stringify(this.slides[index]))
            copy.id = newId()
            copy.blocks = copy.blocks.map(b => ({ ...b, id: newId() }))
            this.slides.splice(index + 1, 0, copy)
            this.activeSlideIndex = index + 1
            this.activeBlockId = null
            this.emitChange()
        },
        selectSlide(index) {
            this.activeSlideIndex = index
            this.activeBlockId = null
        },

        // ── blocks ────────────────────────────────────────────────
        addBlock(type = 'text', opts = {}) {
            if (!this.activeSlide) return
            this.pushHistory()
            const block = this._emptyBlock(type, opts)
            block.x = 50
            block.y = 30 + this.activeSlide.blocks.length * 15
            this.activeSlide.blocks.push(block)
            this.activeBlockId = block.id
            if (type === 'image') {
                this.pendingImageBlockId = block.id
                this.showImageLibrary = true
            } else {
                this.emitChange()
            }
        },
        removeBlock(id) {
            if (!this.activeSlide) return
            this.pushHistory()
            const ids = this.selectedBlockIds.length > 1 && this.selectedBlockIds.includes(id)
                ? [...this.selectedBlockIds]
                : [id]
            ids.forEach(bid => {
                const idx = this.activeSlide.blocks.findIndex(b => b.id === bid)
                if (idx >= 0) this.activeSlide.blocks.splice(idx, 1)
            })
            if (ids.includes(this.activeBlockId)) this.activeBlockId = null
            this.selectedBlockIds = []
            this.emitChange()
        },
        selectBlock(id, shiftKey = false) {
            this.showAddMenu = false
            if (shiftKey) {
                const idx = this.selectedBlockIds.indexOf(id)
                if (idx >= 0) {
                    this.selectedBlockIds.splice(idx, 1)
                    this.activeBlockId = this.selectedBlockIds[this.selectedBlockIds.length - 1] || null
                } else {
                    this.selectedBlockIds.push(id)
                    this.activeBlockId = id
                }
            } else {
                // Wenn Block bereits in Mehrfachauswahl: Selektion behalten (für Multi-Drag)
                if (!this.selectedBlockIds.includes(id)) {
                    this.selectedBlockIds = [id]
                }
                this.activeBlockId = id
            }
        },
        onBlockDblClick(blockId, blockType, event) {
            const type = blockType || 'text'
            if (type === 'text' || type === 'button') {
                this.activeBlockId = blockId
                this.dblClickPos = event ? { x: event.clientX, y: event.clientY } : null
                this.textEditMode = true
            }
        },
        _moveActiveBlock(dx, dy) {
            if (!this.activeSlide || !this.activeBlockId) return
            if (!this._movingHistoryPushed) {
                this.pushHistory()
                this._movingHistoryPushed = true
            }
            clearTimeout(this._moveTimer)
            this._moveTimer = setTimeout(() => {
                this._movingHistoryPushed = false
                this.emitChange()
                this._updateMultiSelectBbox()
            }, 400)
            const ids = this.selectedBlockIds.length > 1 ? this.selectedBlockIds : [this.activeBlockId]
            ids.forEach(id => {
                const block = this.activeSlide.blocks.find(b => b.id === id)
                if (!block) return
                this.$set(block, 'x', Math.round(Math.max(0, Math.min(100, (block.x || 0) + dx)) * 10) / 10)
                this.$set(block, 'y', Math.round(Math.max(0, Math.min(100, (block.y || 0) + dy)) * 10) / 10)
            })
        },
        _pasteBlock() {
            if (!this.activeSlide || !this.copiedBlock) return
            this.pushHistory()
            const copy = JSON.parse(JSON.stringify(this.copiedBlock))
            copy.id = newId()
            copy.x = Math.min(95, (copy.x || 50) + 5)
            copy.y = Math.min(95, (copy.y || 50) + 5)
            this.activeSlide.blocks.push(copy)
            this.activeBlockId = copy.id
            this.emitChange()
        },
        onCanvasMouseDown(e) {
            if (!e.target.closest('.be-block')) {
                e.preventDefault()
                this.deselectBlock()
            }
        },
        _updateMultiSelectBbox() {
            if (this.selectedBlockIds.length <= 1) { this.multiSelectBbox = null; return }
            const canvas = this.$refs.canvas
            if (!canvas) { this.multiSelectBbox = null; return }
            const cr = canvas.getBoundingClientRect()
            let minL = Infinity, minT = Infinity, maxR = -Infinity, maxB = -Infinity
            for (const id of this.selectedBlockIds) {
                const el = canvas.querySelector(`[data-block-id="${id}"]`)
                if (!el) continue
                const r = el.getBoundingClientRect()
                minL = Math.min(minL, r.left - cr.left)
                minT = Math.min(minT, r.top - cr.top)
                maxR = Math.max(maxR, r.right - cr.left)
                maxB = Math.max(maxB, r.bottom - cr.top)
            }
            if (minL === Infinity) { this.multiSelectBbox = null; return }
            this.multiSelectBbox = { left: minL, top: minT, width: maxR - minL, height: maxB - minT }
        },
        deselectBlock() {
            this.activeBlockId = null
            this.selectedBlockIds = []
            this.showAddMenu = false
            this.showFormenMenu = false
            this.activeColorPicker = null
            const sel = window.getSelection()
            if (sel) sel.removeAllRanges()
        },
        toggleColorPicker(key) {
            this.activeColorPicker = this.activeColorPicker === key ? null : key
            if (this.activeColorPicker) {
                const propMap = {
                    'text-color': 'color', 'fill-color': 'fillColor', 'shape-fill': 'fillColor', 'shape-text-color': 'color',
                    'btn-text-color': 'color', 'btn-bg-color': 'bgColor', 'btn-shadow': 'shadowColor', 'btn-border': 'borderColor',
                    'shadow-color': 'shadowColor',
                    'shadow-settings': 'shadowColor',
                    'border-color': 'borderColor',
                    'text-bg': 'bgColor',
                    'bg-border-color': 'bgBorderColor',
                    'img-bg': 'bgColor',
                    'img-border-color': 'borderColor',
                }
                const prop = propMap[this.activeColorPicker]
                this.hexDraft = this.toHex(prop ? this.blockProps[prop] : null)
                this.$nextTick(() => {
                    const dropdown = this.$el.querySelector('.be-color-dropdown')
                    if (!dropdown) return
                    const wrap = dropdown.closest('.be-tb-color-wrap')
                    if (!wrap) return
                    const triggerBtn = wrap.querySelector('.be-tb-color-btn') || wrap
                    const btnRect = triggerBtn.getBoundingClientRect()
                    const dropW = dropdown.getBoundingClientRect().width
                    const btnCenterX = btnRect.left + btnRect.width / 2
                    let left = btnCenterX - dropW / 2
                    const top = btnRect.bottom + 8
                    left = Math.max(8, Math.min(left, window.innerWidth - dropW - 8))
                    const arrowLeft = btnCenterX - left
                    dropdown.style.position = 'fixed'
                    dropdown.style.top = top + 'px'
                    dropdown.style.left = left + 'px'
                    dropdown.style.transform = 'none'
                    dropdown.style.setProperty('--arrow-left', arrowLeft + 'px')
                })
            }
        },
        pickColor(prop, value, close = true) {
            this.setBlockProp(prop, value)
            this.hexDraft = this.toHex(value)
            if (close) this.activeColorPicker = null
        },
        toHex(color) {
            if (!color) return '#ffffff'
            if (color.startsWith('#')) return color
            return '#ffffff'
        },
        onHexInput(prop) {
            let val = this.hexDraft.trim()
            if (!val.startsWith('#')) val = '#' + val
            if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(val)) {
                this.pickColor(prop, val, false)
            }
        },
        openContextMenu(blockId, event) {
            this.activeBlockId = blockId
            this.textEditMode = false
            const x = Math.min(event.clientX, window.innerWidth - 220)
            const y = Math.min(event.clientY, window.innerHeight - 200)
            const subOnLeft = (x + 220 + 200) > window.innerWidth
            this.contextMenu = { type: 'block', x, y, blockId, subOnLeft }
        },
        onCanvasContextMenu(e) {
            const canvas = this.$refs.canvas
            const rect = canvas ? canvas.getBoundingClientRect() : null
            const canvasX = rect ? Math.round(((e.clientX - rect.left) / rect.width) * 100) : 50
            const canvasY = rect ? Math.round(((e.clientY - rect.top) / rect.height) * 100) : 50
            const x = Math.min(e.clientX, window.innerWidth - 220)
            const y = Math.min(e.clientY, window.innerHeight - 200)
            const subOnLeft = (x + 220 + 200) > window.innerWidth
            this.contextMenu = { type: 'canvas', x, y, canvasX, canvasY, subOnLeft }
        },
        cmAddBlock(type, opts) {
            if (!this.activeSlide) return
            this.pushHistory()
            const block = this._emptyBlock(type, opts || {})
            if (this.contextMenu) {
                block.x = this.contextMenu.canvasX || 50
                block.y = this.contextMenu.canvasY || 50
            }
            this.activeSlide.blocks.push(block)
            this.activeBlockId = block.id
            this.contextMenu = null
            if (type === 'image') {
                this.pendingImageBlockId = block.id
                this.showImageLibrary = true
            } else {
                this.emitChange()
            }
        },
        cmAlign(direction) {
            const block = this.activeBlock
            if (!block) return
            this.pushHistory()
            const type = block.type || 'text'
            const hw = (block.w || 20) / 2
            const hh = (block.h || 15) / 2
            switch (direction) {
                case 'left':
                    if (type === 'text') { this.$set(block, 'align', 'left'); this.$set(block, 'x', 8) }
                    else if (type === 'shape' || type === 'image') this.$set(block, 'x', 8 + hw)
                    else this.$set(block, 'x', 8)
                    break
                case 'center-h':
                    if (type === 'text') this.$set(block, 'align', 'center')
                    this.$set(block, 'x', 50)
                    break
                case 'right':
                    if (type === 'text') { this.$set(block, 'align', 'right'); this.$set(block, 'x', 92) }
                    else if (type === 'shape' || type === 'image') this.$set(block, 'x', 92 - hw)
                    else this.$set(block, 'x', 92)
                    break
                case 'top':
                    this.$set(block, 'y', (type === 'shape' || type === 'image') ? 8 + hh : 8)
                    break
                case 'center-v':
                    this.$set(block, 'y', 50)
                    break
                case 'bottom':
                    this.$set(block, 'y', (type === 'shape' || type === 'image') ? 92 - hh : 92)
                    break
            }
            this.emitChange()
            this.contextMenu = null
        },
        cmCopy() {
            if (this.activeBlock) this.copiedBlock = JSON.parse(JSON.stringify(this.activeBlock))
            this.contextMenu = null
        },
        cmPaste() {
            this._pasteBlock()
            this.contextMenu = null
        },
        cmDelete() {
            if (this.contextMenu) this.removeBlock(this.contextMenu.blockId)
            this.contextMenu = null
        },
        cmLayer(direction) {
            if (!this.activeSlide || !this.activeBlockId) return
            const blocks = this.activeSlide.blocks
            const idx = blocks.findIndex(b => b.id === this.activeBlockId)
            if (idx < 0) return
            this.pushHistory()
            if (direction === 'front' && idx < blocks.length - 1) {
                const b = blocks.splice(idx, 1)[0]
                blocks.splice(idx + 1, 0, b)
            } else if (direction === 'back' && idx > 0) {
                const b = blocks.splice(idx, 1)[0]
                blocks.splice(idx - 1, 0, b)
            }
            this.emitChange()
            this.contextMenu = null
        },
        cmRotate(delta) {
            const block = this.activeBlock
            if (!block) return
            this.pushHistory()
            const newRot = (((block.rotation || 0) + delta) % 360 + 360) % 360
            this.$set(block, 'rotation', Math.round(newRot * 10) / 10)
            this.emitChange()
        },
        cmSetRotation(value) {
            const block = this.activeBlock
            if (!block) return
            const num = parseFloat(value)
            if (isNaN(num)) return
            this.pushHistory()
            this.$set(block, 'rotation', Math.round(((num % 360) + 360) % 360 * 10) / 10)
            this.emitChange()
        },
        cmSetPos(axis, value) {
            const block = this.activeBlock
            if (!block) return
            const num = parseFloat(value)
            if (isNaN(num)) return
            this.pushHistory()
            this.$set(block, axis, Math.round(Math.max(0, Math.min(100, num)) * 10) / 10)
            this.emitChange()
        },
        cmSetSize(axis, value) {
            const block = this.activeBlock
            if (!block) return
            const num = parseFloat(value)
            if (isNaN(num)) return
            this.pushHistory()
            const clamped = Math.round(Math.max(1, Math.min(100, num)) * 10) / 10
            this.$set(block, axis, clamped)
            if (block.type === 'image' && block.imageAspect) {
                if (axis === 'w') {
                    const newH = Math.round(Math.max(1, Math.min(100, clamped / block.imageAspect)) * 10) / 10
                    this.$set(block, 'h', newH)
                } else if (axis === 'h') {
                    const newW = Math.round(Math.max(1, Math.min(100, clamped * block.imageAspect)) * 10) / 10
                    this.$set(block, 'w', newW)
                }
            }
            this.emitChange()
        },
        cmEdit() {
            const block = this.activeBlock
            if (!block) return
            const type = block.type || 'text'
            if (type === 'text' || type === 'button') this.textEditMode = true
            else if (type === 'image') this.openImageForBlock(block.id)
            this.contextMenu = null
        },
        onCanvasSelectStart(e) {
            // e.target kann ein Textknoten sein → kein closest → Elternelement nehmen
            let el = e.target
            if (el && typeof el.closest !== 'function') el = el.parentElement
            if (el && el.closest('[contenteditable="true"]')) return
            e.preventDefault()
        },
        addBlockAndClose(type, opts) {
            this.addBlock(type, opts || {})
            this.showAddMenu = false
            this.showFormenMenu = false
        },
        setBlockProp(key, value) {
            if (!this.activeSlide || !this.activeBlockId) return
            this.pushHistory()
            const MOBILE_PROPS = ['fontSize', 'opacity']
            const ids = this.selectedBlockIds.length > 1 ? this.selectedBlockIds : [this.activeBlockId]
            ids.forEach(id => {
                const block = this.activeSlide.blocks.find(b => b.id === id)
                if (!block) return
                if (this.mobileEditMode && MOBILE_PROPS.includes(key)) {
                    if (!block.mobile) this.$set(block, 'mobile', {})
                    this.$set(block.mobile, key, value)
                } else {
                    this.$set(block, key, value)
                }
            })
            this.emitChange()
        },
        parseVideoInput(url) {
            if (!url) return { type: 'youtube', id: '' }
            url = url.trim()
            const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/)
            if (ytMatch) return { type: 'youtube', id: ytMatch[1] }
            const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)
            if (vimeoMatch) return { type: 'vimeo', id: vimeoMatch[1] }
            return { type: 'custom', id: '' }
        },
        setVideoUrl(rawUrl) {
            if (!this.activeSlide || !this.activeBlockId) return
            const block = this.activeSlide.blocks.find(b => b.id === this.activeBlockId)
            if (!block) return
            this.pushHistory()
            const parsed = this.parseVideoInput(rawUrl)
            this.$set(block, 'videoUrl', rawUrl)
            this.$set(block, 'videoType', parsed.type)
            this.$set(block, 'videoId', parsed.id)
            this.emitChange()
        },
        blockVideoEmbedUrl(block, editorMode) {
            if (!block.videoId) return ''
            const auto = !editorMode && block.autoplay ? 1 : 0
            const ctrl = block.controls !== false ? 1 : 0
            const loop = block.loop ? 1 : 0
            const muted = block.muted !== false ? 1 : 0
            if (block.videoType === 'youtube') {
                return `https://www.youtube.com/embed/${block.videoId}?autoplay=${auto}&mute=${muted}&loop=${loop}&controls=${ctrl}&playsinline=1&rel=0${loop ? '&playlist=' + block.videoId : ''}`
            }
            if (block.videoType === 'vimeo') {
                return `https://player.vimeo.com/video/${block.videoId}?autoplay=${auto}&muted=${muted}&loop=${loop}&controls=${ctrl}&dnt=1`
            }
            return ''
        },
        setFontSizeFromInput(event, defaultVal) {
            const raw = parseFloat(event.target.value)
            if (isNaN(raw)) return
            const val = Math.min(15, Math.max(0.2, Math.round(raw * 10) / 10))
            this.setBlockProp('fontSize', val)
        },
        setPxPropFromInput(event, prop, defaultVal, min, max) {
            const raw = parseInt(event.target.value, 10)
            if (isNaN(raw)) return
            this.setBlockProp(prop, Math.min(max, Math.max(min, raw)))
        },
        toggleFormat(prop, command) {
            if (this.textEditMode) {
                document.execCommand(command, false, null)
                const ref = this.$refs.activeEditable
                const el = Array.isArray(ref) ? ref[0] : ref
                if (el) {
                    const block = this.activeBlock
                    if (block) {
                        this.$set(block, 'html', el.innerHTML)
                        this.$set(block, 'text', el.innerText.replace(/\n+$/, ''))
                        this.scheduleEmit()
                    }
                }
            } else {
                this.setBlockProp(prop, !this.blockProps[prop])
            }
        },
        onContentEditableInput(blockId, event) {
            if (!this.typingHistoryPushed) {
                this.pushHistory()
                this.typingHistoryPushed = true
            }
            const slide = this.slides[this.activeSlideIndex]
            if (!slide) return
            const block = slide.blocks.find(b => b.id === blockId)
            if (!block) return
            this.$set(block, 'text', event.target.innerText.replace(/\n+$/, ''))
            this.$set(block, 'html', event.target.innerHTML)
            this.scheduleEmit()
        },
        onPlainTextPaste(event) {
            const text = (event.clipboardData || window.clipboardData).getData('text/plain')
            document.execCommand('insertText', false, text)
        },

        // ── slide settings ────────────────────────────────────────
        slideHasBgColor(slide) {
            if (!slide) return false
            if (slide.bgFillMode === 'gradient') return !!(slide.bgGradientColor1 || slide.bgGradientColor2)
            return !!slide.bgColor
        },
        slideBgColorStyle(slide) {
            if (!slide) return {}
            if (slide.bgFillMode === 'gradient') {
                const angle = slide.bgGradientAngle !== undefined ? slide.bgGradientAngle : 135
                const c1 = slide.bgGradientColor1 || '#000000'
                const c2 = slide.bgGradientColor2 || '#ffffff'
                const bg = slide.bgGradientType === 'radial'
                    ? `radial-gradient(circle, ${c1}, ${c2})`
                    : `linear-gradient(${angle}deg, ${c1}, ${c2})`
                return { background: bg }
            }
            return { background: slide.bgColor || 'transparent' }
        },
        setSlideColor(key, value, immediate = true) {
            if (!this.activeSlide) return
            this.$set(this.activeSlide, key, value)
            if (immediate) this.emitChange()
            else this.scheduleEmit()
        },
        setCanvasLayout(layout) {
            this.canvasLayout = layout
            this.emitChange()
        },
        setContainerBgMode(mode) {
            this.containerBgMode = mode
            this.emitChange()
        },
        setContainerBgColor(color) {
            this.containerBgColor = color
            this.emitChange()
        },
        onBannerHeightInput(val) {
            const n = parseInt(val)
            if (!isNaN(n) && n >= 10 && n <= 100) {
                this.bannerHeight = n
                this.emitChange()
            }
        },
        setBannerHeight(val) {
            const n = parseInt(val)
            if (!isNaN(n)) this.bannerHeight = Math.min(100, Math.max(10, n))
            this.emitChange()
        },
        onPreviewCanvasClick() {
            sessionStorage.setItem('be_open_at', String(this.previewSlideIndex))
            this.$emit('open-at', this.previewSlideIndex)
        },
        setSlideOverlay(color) {
            if (!this.activeSlide) return
            this.pushHistory()
            this.$set(this.activeSlide, 'overlayColor', color)
            this.emitChange()
        },

        // ── image library ─────────────────────────────────────────
        openImageForBlock(blockId) {
            this.pendingImageBlockId = blockId
            this.showImageLibrary = true
        },
        onImageSelect(image) {
            if (!this.activeSlide) return
            if (this.pendingImageBlockId) {
                const blockId = this.pendingImageBlockId
                const slide = this.slides[this.activeSlideIndex]
                const bIdx = slide ? slide.blocks.findIndex(b => b.id === blockId) : -1
                if (bIdx >= 0) {
                    const hadImage = !!slide.blocks[bIdx].imageUrl
                    this.$set(slide.blocks[bIdx], 'imageUrl', image.url)
                    this.$set(slide.blocks[bIdx], 'imageName', image.name || image.preferred_name || '')
                    this.$delete(slide.blocks[bIdx], 'imageCrop')
                    const img = new window.Image()
                    img.onload = () => {
                        const canvas = this.$refs.canvas
                        const rect = canvas ? canvas.getBoundingClientRect() : null
                        const canvasW = (rect && rect.width) ? rect.width : (this.canvasWidth || 700)
                        const canvasH = (rect && rect.height) ? rect.height : canvasW * (this.bannerHeight || 30) / 100
                        if (canvasW && canvasH && img.naturalWidth && img.naturalHeight) {
                            const bIdx2 = slide.blocks.findIndex(b => b.id === blockId)
                            if (bIdx2 < 0) return
                            const startW = hadImage ? (slide.blocks[bIdx2].w || 30) : 30
                            const maxH = hadImage ? 90 : 50
                            const ratio = (canvasW / canvasH) * (img.naturalHeight / img.naturalWidth)
                            let fw = startW
                            let fh = fw * ratio
                            if (fh > maxH) { fh = maxH; fw = fh / ratio }
                            fw = Math.max(2, Math.min(100, Math.round(fw * 10) / 10))
                            fh = Math.max(2, Math.min(maxH, Math.round(fh * 10) / 10))
                            this.$set(slide.blocks[bIdx2], 'w', fw)
                            this.$set(slide.blocks[bIdx2], 'h', fh)
                            this.$set(slide.blocks[bIdx2], 'imageAspect', fw / fh)
                            this.emitChange()
                        }
                    }
                    img.src = image.url
                }
                this.pendingImageBlockId = null
                this.emitChange()
                this.openCropModal(image.url, image.width || 0, image.height || 0, (crop) => {
                    const s = this.slides[this.activeSlideIndex]
                    const block = s && s.blocks.find(b => b.id === blockId)
                    if (block) {
                        this.$set(block, 'imageCrop', crop)
                        this._fitBlockToCrop(block, crop)
                        this.emitChange()
                    }
                })
            } else {
                this.pushHistory()
                const slideIdx = this.activeSlideIndex
                // Replace the entire slide in the array so Vue 2 definitely detects the change
                // (avoids the case where 'image' / 'video' are absent from old JSON and not yet reactive).
                this.$set(this.slides, slideIdx, {
                    ...this.slides[slideIdx],
                    image: {
                        url: image.url,
                        name: image.name || image.preferred_name || '',
                        id: image.id,
                        width: image.width,
                        height: image.height,
                        srcset: image.srcset || [],
                        files: image.files || [],
                        crop: null,
                    },
                    video: null,
                })
                this.emitChange()
            }
        },
        onImageSelectWithCrop(image) {
            if (!this.activeSlide) return
            this.pushHistory()
            const slideIdx = this.activeSlideIndex
            this.$set(this.slides, slideIdx, {
                ...this.slides[slideIdx],
                image: {
                    url: image.url,
                    name: image.name || image.preferred_name || '',
                    id: image.id,
                    width: image.width,
                    height: image.height,
                    srcset: image.srcset || [],
                    files: image.files || [],
                    crop: null,
                },
                video: null,
            })
            this.emitChange()
            const canvasAr = 100 / (this.bannerHeight || 30)
            this.$nextTick(() => {
                this.openCropModal(image.url, image.width || 0, image.height || 0, (crop) => {
                    const s = this.slides[slideIdx]
                    if (s && s.image) {
                        this.$set(s.image, 'crop', crop)
                        this.emitChange()
                    }
                }, canvasAr)
            })
        },
        markCropMoved() {
            this.cropModal.moved = true
        },
        openCropModal(src, origW, origH, onConfirm, aspectRatio) {
            this.cropModal = { visible: true, ready: false, moved: false, src, origW: origW || 0, origH: origH || 0, aspectRatio: aspectRatio || NaN, onConfirm: onConfirm || null }
        },
        onCropConfirm() {
            const cropper = this.$refs.beCropper && this.$refs.beCropper.cropper
            if (!cropper) { this.cropModal.visible = false; return }
            const data = cropper.getData(true)
            if (!data.width || !data.height) { this.cropModal.visible = false; return }
            const imageData = cropper.getImageData()
            const origW = this.cropModal.origW || imageData.naturalWidth || data.width
            const origH = this.cropModal.origH || imageData.naturalHeight || data.height
            const crop = {
                x: Math.max(0, data.x),
                y: Math.max(0, data.y),
                w: data.width,
                h: data.height,
                origW,
                origH,
            }
            if (this.cropModal.onConfirm) this.cropModal.onConfirm(crop)
            this.cropModal.visible = false
        },
        onCropSkip() {
            this.cropModal.visible = false
        },
        reopenCropForBlock(blockId) {
            const slide = this.slides[this.activeSlideIndex]
            const block = slide && slide.blocks.find(b => b.id === blockId)
            if (!block || !block.imageUrl) return
            const existingCrop = block.imageCrop
            const origW = existingCrop ? existingCrop.origW : 0
            const origH = existingCrop ? existingCrop.origH : 0
            this.openCropModal(block.imageUrl, origW, origH, (crop) => {
                this.$set(block, 'imageCrop', crop)
                this._fitBlockToCrop(block, crop)
                this.emitChange()
            })
        },
        reopenCropForBackground() {
            const slide = this.slides[this.activeSlideIndex]
            if (!slide || !slide.image || !slide.image.url) return
            const existingCrop = slide.image.crop
            const origW = existingCrop ? existingCrop.origW : (slide.image.width || 0)
            const origH = existingCrop ? existingCrop.origH : (slide.image.height || 0)
            const canvasAr = 100 / (this.bannerHeight || 30)
            const slideIdx = this.activeSlideIndex
            this.openCropModal(slide.image.url, origW, origH, (crop) => {
                const s = this.slides[slideIdx]
                if (s && s.image) {
                    this.$set(s.image, 'crop', crop)
                    this.emitChange()
                }
            }, canvasAr)
        },
        removeCropForBackground() {
            const slideIdx = this.activeSlideIndex
            const slide = this.slides[slideIdx]
            if (!slide || !slide.image) return
            this.pushHistory()
            this.$set(slide.image, 'crop', null)
            this.emitChange()
        },
        onBgDropdown(cmd) {
            if (cmd === 'image') this.showImageLibrary = true
            else if (cmd === 'video') this.showVideoLibrary = true
            else if (cmd === 'removeVideo') this.removeSlideVideo()
            else if (cmd === 'bgColor') {
                if (!this.activeSlide) return
                if (this.activeSlide.image?.url || this.activeSlide.video?.url) {
                    this.pushHistory()
                    this.$set(this.activeSlide, 'image', null)
                    this.$set(this.activeSlide, 'video', null)
                    this.emitChange()
                }
                this.showBgColorPanel = true
                this.showEffectsPanel = false
            }
            else if (cmd === 'removeBgColor') {
                if (!this.activeSlide) return
                this.pushHistory()
                this.$set(this.activeSlide, 'bgColor', null)
                this.$set(this.activeSlide, 'bgGradientColor1', null)
                this.$set(this.activeSlide, 'bgGradientColor2', null)
                this.showBgColorPanel = false
                this.emitChange()
            }
        },
        openVideoSourceDialog() {
            this.showAddMenu = false
            this.showFormenMenu = false
            this.videoSourceDialog.visible = true
        },
        cmAddVideo() {
            if (this.contextMenu) {
                this.ctxVideoPos = { x: this.contextMenu.canvasX || 50, y: this.contextMenu.canvasY || 50 }
            }
            this.contextMenu = null
            this.videoSourceDialog.visible = true
        },
        addVideoFromLibrary() {
            this.videoSourceDialog.visible = false
            if (!this.activeSlide) return
            this.pushHistory()
            const block = this._emptyBlock('video', {})
            const pos = this.ctxVideoPos
            this.ctxVideoPos = null
            block.x = pos ? pos.x : 50
            block.y = pos ? pos.y : 30 + this.activeSlide.blocks.length * 15
            block.videoType = 'custom'
            this.activeSlide.blocks.push(block)
            this.activeBlockId = block.id
            this._pendingVideoBlockId = block.id
            this.showVideoLibrary = true
        },
        addVideoExternal() {
            this.videoSourceDialog.visible = false
            const pos = this.ctxVideoPos
            this.ctxVideoPos = null
            if (pos && this.activeSlide) {
                this.pushHistory()
                const block = this._emptyBlock('video', {})
                block.x = pos.x
                block.y = pos.y
                this.activeSlide.blocks.push(block)
                this.activeBlockId = block.id
                this.emitChange()
            } else {
                this.addBlock('video')
            }
        },
        toggleAutoplay() {
            const newVal = !this.blockProps.autoplay
            this.setBlockProp('autoplay', newVal)
            if (newVal) this.setBlockProp('muted', true)
        },
        toggleMuted() {
            const newVal = !(this.blockProps.muted !== false)
            this.setBlockProp('muted', newVal)
            if (!newVal) this.setBlockProp('autoplay', false)
        },
        toggleVideoOrientation() {
            const block = this.activeBlock
            if (!block) return
            this.pushHistory()
            const isPortrait = block.videoOrientation === 'portrait'
            const orientation = isPortrait ? 'landscape' : 'portrait'
            const oldW = block.w || 20
            const oldH = block.h || 15
            this.$set(block, 'videoOrientation', orientation)
            this.$set(block, 'w', Math.min(100, Math.round(oldH * 10) / 10))
            this.$set(block, 'h', Math.min(100, Math.round(oldW * 10) / 10))
            this.emitChange()
        },
        onVideoSelect(file) {
            // Block-Video aus Mediathek
            if (this._pendingVideoBlockId && this.activeSlide) {
                const block = this.activeSlide.blocks.find(b => b.id === this._pendingVideoBlockId)
                this._pendingVideoBlockId = null
                if (block) {
                    this.$set(block, 'videoUrl', file.url)
                    this.$set(block, 'videoId', '')
                    this.$set(block, 'videoType', 'custom')
                    this.emitChange()
                }
                return
            }
            // Hintergrund-Video (bisheriges Verhalten)
            if (!this.activeSlide) return
            this.pushHistory()
            this.$set(this.activeSlide, 'video', {
                url: file.url,
                name: file.name || file.preferred_name || '',
            })
            this.$set(this.activeSlide, 'image', null)
            this.emitChange()
        },
        removeSlideVideo() {
            if (!this.activeSlide) return
            this.pushHistory()
            this.$set(this.activeSlide, 'video', null)
            this.emitChange()
        },

        // ── drag / resize ─────────────────────────────────────────
        onBlockMouseDown(blockId, event) {
            if (!(this.textEditMode && this.activeBlockId === blockId)) {
                event.preventDefault()
            }
            // dragIds VOR selectBlock erfassen, damit Mehrfachauswahl erhalten bleibt
            const isMulti = this.selectedBlockIds.length > 1 && this.selectedBlockIds.includes(blockId)
            const dragIds = isMulti ? [...this.selectedBlockIds] : [blockId]
            this.selectBlock(blockId, event.shiftKey)
            if (event.shiftKey) return
            const canvas = this.$refs.canvas
            if (!canvas) return
            const rect = canvas.getBoundingClientRect()
            const slide = this.slides[this.activeSlideIndex]
            if (!slide) return
            const block = slide.blocks.find(b => b.id === blockId)
            if (!block) return
            this.pushHistory()
            const startX = event.clientX, startY = event.clientY
            const cW = rect.width, cH = rect.height
            const slideIdx = this.activeSlideIndex
            const blockEl = event.currentTarget
            const bRect = blockEl ? blockEl.getBoundingClientRect() : null
            const measuredW = bRect ? (bRect.width / cW) * 100 : 20
            const measuredH = bRect ? (bRect.height / cH) * 100 : 10
            const useMobile = this.mobileEditMode
            const startPositions = dragIds.map(id => {
                const b = slide.blocks.find(b => b.id === id)
                const sx = (useMobile && b?.mobile?.x !== undefined) ? b.mobile.x : (b ? b.x : 0)
                const sy = (useMobile && b?.mobile?.y !== undefined) ? b.mobile.y : (b ? b.y : 0)
                return { id, x: sx, y: sy }
            })
            let moved = false
            this._onDragMove = (e) => {
                moved = true
                const dx = e.clientX - startX, dy = e.clientY - startY
                startPositions.forEach(({ id, x: sX, y: sY }) => {
                    const rawX = Math.max(0, Math.min(100, sX + (dx / cW) * 100))
                    const rawY = Math.max(0, Math.min(100, sY + (dy / cH) * 100))
                    const bIdx = this.slides[slideIdx].blocks.findIndex(b => b.id === id)
                    if (bIdx >= 0) {
                        const b = this.slides[slideIdx].blocks[bIdx]
                        const pos = (this.showGuides && dragIds.length === 1)
                            ? snapBlock(rawX, rawY, b, measuredW, measuredH)
                            : { x: Math.round(rawX * 10) / 10, y: Math.round(rawY * 10) / 10 }
                        if (useMobile) {
                            if (!b.mobile) this.$set(b, 'mobile', {})
                            this.$set(b.mobile, 'x', pos.x)
                            this.$set(b.mobile, 'y', pos.y)
                        } else {
                            this.$set(this.slides[slideIdx].blocks[bIdx], 'x', pos.x)
                            this.$set(this.slides[slideIdx].blocks[bIdx], 'y', pos.y)
                        }
                    }
                })
            }
            this._onDragEnd = () => {
                if (moved) this.emitChange()
                this._cleanupDragListeners()
                this._updateMultiSelectBbox()
            }
            document.addEventListener('mousemove', this._onDragMove)
            document.addEventListener('mouseup', this._onDragEnd)
        },
        onContentEditableMouseDown(blockId, event) {
            // Im Text-Edit-Modus: Propagation stoppen damit der Block nicht verschoben wird,
            // Browser übernimmt Cursor-Positionierung und Textauswahl
            event.stopPropagation()
        },
        onResizeMouseDown(blockId, event) {
            const startX = event.clientX, startY = event.clientY
            const slide = this.slides[this.activeSlideIndex]
            if (!slide) return
            const block = slide.blocks.find(b => b.id === blockId)
            if (!block) return
            const type = block.type || 'text'
            const slideIdx = this.activeSlideIndex
            this.pushHistory()
            const resizeMobile = this.mobileEditMode
            if (type === 'shape' || type === 'image' || type === 'video' || (type === 'button' && block.w && block.h)) {
                const canvas = this.$refs.canvas
                const rect = canvas ? canvas.getBoundingClientRect() : { width: 1, height: 1 }
                const mobSrc = (resizeMobile && block.mobile) ? block.mobile : block
                const startW = (resizeMobile && block.mobile?.w !== undefined ? block.mobile.w : block.w) || 20
                const startH = (resizeMobile && block.mobile?.h !== undefined ? block.mobile.h : block.h) || 15
                const startFontSize = type === 'button'
                    ? ((resizeMobile && block.mobile?.fontSize !== undefined) ? block.mobile.fontSize : (block.fontSize || 1.2))
                    : null
                const clipShapes = ['circle', 'triangle', 'diamond', 'star', 'hexagon']
                const isSquare = type === 'shape' && clipShapes.includes(block.shapeType)
                const imageAspect = (type === 'image' && block.imageAspect) ? block.imageAspect : null
                const videoAspect = type === 'video' ? (startW / startH) : null
                const buttonAspect = type === 'button' ? (startW / startH) : null
                // Multi-Select: Startgrößen aller selektierten Blöcke merken
                const multiIds = this.selectedBlockIds.length > 1 ? [...this.selectedBlockIds] : null
                const startSizes = multiIds ? multiIds.map(id => {
                    const b = slide.blocks.find(b => b.id === id)
                    const bw = (resizeMobile && b?.mobile?.w !== undefined) ? b.mobile.w : (b?.w || 20)
                    const bh = (resizeMobile && b?.mobile?.h !== undefined) ? b.mobile.h : (b?.h || 15)
                    return { id, w: bw, h: bh }
                }) : null
                const setWH = (bIdx, w, h) => {
                    const b = this.slides[slideIdx].blocks[bIdx]
                    if (resizeMobile) {
                        if (!b.mobile) this.$set(b, 'mobile', {})
                        this.$set(b.mobile, 'w', w)
                        if (h !== undefined) this.$set(b.mobile, 'h', h)
                    } else {
                        this.$set(b, 'w', w)
                        if (h !== undefined) this.$set(b, 'h', h)
                    }
                }
                const onMove = (e) => {
                    const free = e.shiftKey
                    const dx = e.clientX - startX, dy = e.clientY - startY
                    const locked = isSquare && !free
                    const delta = locked ? (dx + (startY - e.clientY)) / 2 : dx
                    const newW = Math.max(3, Math.min(100, startW + (delta / rect.width) * 100))
                    const scale = newW / startW
                    const bIdx = this.slides[slideIdx].blocks.findIndex(b => b.id === blockId)
                    if (bIdx >= 0) {
                        if (!resizeMobile) this.$set(this.slides[slideIdx].blocks[bIdx], 'freeAspect', free)
                        if (!locked) {
                            const newH = videoAspect
                                ? Math.max(2, Math.min(100, newW / videoAspect))
                                : buttonAspect
                                    ? Math.max(2, Math.min(100, newW / buttonAspect))
                                    : (!free && imageAspect)
                                        ? Math.max(2, Math.min(100, newW / imageAspect))
                                        : Math.max(2, Math.min(100, startH + (dy / rect.height) * 100))
                            setWH(bIdx, Math.round(newW * 10) / 10, Math.round(newH * 10) / 10)
                            if (startFontSize !== null && startH > 0) {
                                const newFs = Math.round(startFontSize * (newH / startH) * 100) / 100
                                const b = this.slides[slideIdx].blocks[bIdx]
                                if (resizeMobile) {
                                    if (!b.mobile) this.$set(b, 'mobile', {})
                                    this.$set(b.mobile, 'fontSize', Math.max(0.1, newFs))
                                } else {
                                    this.$set(b, 'fontSize', Math.max(0.1, newFs))
                                }
                            }
                        } else {
                            setWH(bIdx, Math.round(newW * 10) / 10, undefined)
                        }
                    }
                    // Alle anderen selektierten Blöcke proportional skalieren
                    if (multiIds && scale !== 1) {
                        startSizes.forEach(({ id, w: sW, h: sH }) => {
                            if (id === blockId) return
                            const idx = this.slides[slideIdx].blocks.findIndex(b => b.id === id)
                            if (idx < 0) return
                            setWH(idx, Math.round(Math.max(3, Math.min(100, sW * scale)) * 10) / 10, Math.round(Math.max(2, Math.min(100, sH * scale)) * 10) / 10)
                        })
                    }
                }
                const onEnd = () => { this.emitChange(); this._updateMultiSelectBbox(); document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onEnd) }
                document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onEnd)
            } else {
                const mobStartSize = resizeMobile && block.mobile?.fontSize !== undefined ? block.mobile.fontSize : undefined
                const startSize = mobStartSize ?? block.fontSize ?? (type === 'button' ? 1.2 : 2)
                const canvas = this.$refs.canvas
                const canvasRect = canvas ? canvas.getBoundingClientRect() : null
                const blockEl = canvas ? canvas.querySelector(`[data-block-id="${blockId}"]`) : null
                const initialRect = blockEl ? blockEl.getBoundingClientRect() : null
                // Anchor = top-left corner of block in canvas-% at drag start
                const anchorY = (initialRect && canvasRect) ? ((initialRect.top  - canvasRect.top)  / canvasRect.height) * 100 : null
                const initialH = (initialRect && canvasRect) ? (initialRect.height / canvasRect.height) * 100 : null
                const onMove = (e) => {
                    const delta = (e.clientX - startX) + (startY - e.clientY)
                    const newSize = Math.max(1, Math.min(15, Math.round((startSize + delta * 0.05) * 10) / 10))
                    const bIdx = this.slides[slideIdx].blocks.findIndex(b => b.id === blockId)
                    if (bIdx < 0) return
                    const b = this.slides[slideIdx].blocks[bIdx]
                    if (resizeMobile) {
                        if (!b.mobile) this.$set(b, 'mobile', {})
                        this.$set(b.mobile, 'fontSize', newSize)
                    } else {
                        this.$set(b, 'fontSize', newSize)
                    }
                    // Height scales proportionally with fontSize — correct y in the same tick
                    if (anchorY !== null && initialH !== null) {
                        const newH = initialH * (newSize / startSize)
                        const newY = Math.round((anchorY + newH / 2) * 10) / 10
                        if (resizeMobile) {
                            if (!b.mobile) this.$set(b, 'mobile', {})
                            this.$set(b.mobile, 'y', newY)
                        } else {
                            this.$set(this.slides[slideIdx].blocks[bIdx], 'y', newY)
                        }
                    }
                }
                const onEnd = () => { this.emitChange(); document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onEnd) }
                document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onEnd)
            }
        },
        onRotateMouseDown(blockId, event) {
            const slide = this.slides[this.activeSlideIndex]
            if (!slide) return
            const block = slide.blocks.find(b => b.id === blockId)
            if (!block) return
            this.pushHistory()
            const canvas = this.$refs.canvas
            const canvasRect = canvas ? canvas.getBoundingClientRect() : null
            if (!canvasRect) return
            const slideIdx = this.activeSlideIndex
            const multiIds = this.selectedBlockIds.length > 1 ? [...this.selectedBlockIds] : null
            // Drehzentrum: bei Multi-Select = Mittelpunkt der Bbox, sonst Block-Mitte
            let cx, cy
            if (multiIds && this.multiSelectBbox) {
                const b = this.multiSelectBbox
                cx = canvasRect.left + b.left + b.width / 2
                cy = canvasRect.top + b.top + b.height / 2
            } else {
                const blockEl = canvas.querySelector(`[data-block-id="${blockId}"]`)
                const blockRect = blockEl ? blockEl.getBoundingClientRect() : null
                if (!blockRect) return
                cx = blockRect.left + blockRect.width / 2
                cy = blockRect.top + blockRect.height / 2
            }
            const startAngle = Math.atan2(event.clientY - cy, event.clientX - cx) * (180 / Math.PI)
            const startRotation = block.rotation || 0
            // Startpositionen/-rotationen aller selektierten Blöcke
            const cW = canvasRect.width, cH = canvasRect.height
            const bH = this.bannerHeight || 30
            const canvasH = cW * (bH / 100)
            const startStates = multiIds ? multiIds.map(id => {
                const b = slide.blocks.find(b => b.id === id)
                return { id, rotation: b ? (b.rotation || 0) : 0, x: b ? b.x : 0, y: b ? b.y : 0 }
            }) : [{ id: blockId, rotation: startRotation, x: block.x, y: block.y }]
            // Pivot in canvas-% (normalisiert auf quadratisches Koordinatensystem)
            const pivotX_px = cx - canvasRect.left
            const pivotY_px = cy - canvasRect.top
            const onMove = (e) => {
                const angle = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI)
                const delta = angle - startAngle
                const snap = !e.shiftKey
                startStates.forEach(({ id, rotation: sRot, x: sX, y: sY }) => {
                    let newRot = ((sRot + delta) % 360 + 360) % 360
                    if (snap) newRot = Math.round(newRot / 10) * 10 % 360
                    const idx = this.slides[slideIdx].blocks.findIndex(b => b.id === id)
                    if (idx < 0) return
                    this.$set(this.slides[slideIdx].blocks[idx], 'rotation', Math.round(newRot * 10) / 10)
                    // Position um Pivot rotieren (nur bei Multi-Select)
                    if (multiIds && multiIds.length > 1) {
                        const rad = delta * Math.PI / 180
                        const dx_px = (sX / 100) * cW - pivotX_px
                        const dy_px = (sY / 100) * canvasH - pivotY_px
                        const newX_px = dx_px * Math.cos(rad) - dy_px * Math.sin(rad) + pivotX_px
                        const newY_px = dx_px * Math.sin(rad) + dy_px * Math.cos(rad) + pivotY_px
                        this.$set(this.slides[slideIdx].blocks[idx], 'x', Math.round((newX_px / cW) * 100 * 10) / 10)
                        this.$set(this.slides[slideIdx].blocks[idx], 'y', Math.round((newY_px / canvasH) * 100 * 10) / 10)
                    }
                })
            }
            const onEnd = () => { this.emitChange(); this._updateMultiSelectBbox(); document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onEnd) }
            document.addEventListener('mousemove', onMove)
            document.addEventListener('mouseup', onEnd)
        },
        _makeDraggable(el) {
            const handle = document.createElement('div')
            handle.className = 'be-cd-drag-handle'
            el.insertBefore(handle, el.firstChild)

            let posX = null, posY = null

            handle.addEventListener('mousedown', (e) => {
                if (e.button !== 0) return
                e.preventDefault()
                e.stopPropagation()

                if (posX === null) {
                    const rect = el.getBoundingClientRect()
                    posX = rect.left
                    posY = rect.top
                    el.style.position = 'fixed'
                    el.style.transform = 'none'
                    el.style.zIndex = '9999'
                    el.classList.add('is-dragged')
                }
                el.style.left = posX + 'px'
                el.style.top  = posY + 'px'

                const startX = e.clientX - posX
                const startY = e.clientY - posY

                const onMove = (e) => {
                    posX = e.clientX - startX
                    posY = e.clientY - startY
                    el.style.left = posX + 'px'
                    el.style.top  = posY + 'px'
                }
                const onUp = () => {
                    document.removeEventListener('mousemove', onMove)
                    document.removeEventListener('mouseup', onUp)
                }
                document.addEventListener('mousemove', onMove)
                document.addEventListener('mouseup', onUp)
            })
        },

        _cleanupDragListeners() {
            if (this._onDragMove) { document.removeEventListener('mousemove', this._onDragMove); this._onDragMove = null }
            if (this._onDragEnd) { document.removeEventListener('mouseup', this._onDragEnd); this._onDragEnd = null }
        },

        // ── canvas measurement ───────────────────────────────────
        _updateCanvasWidth() {
            this.$nextTick(() => {
                const editEl = this.$refs.canvas
                if (editEl) {
                    const w = editEl.getBoundingClientRect().width
                    if (w > 0) this.canvasWidth = w
                }
                const previewEl = this.$refs.previewCanvasEl
                if (previewEl) {
                    const w = previewEl.getBoundingClientRect().width
                    if (w > 0) this.previewCanvasWidth = w
                }
            })
        },

        // ── styles ────────────────────────────────────────────────
        blockStyle(block) {
            const type = block.type || 'text'
            const rot = block.rotation ? ` rotate(${block.rotation}deg)` : ''
            const mob = (this.mobileEditMode && block.mobile) ? block.mobile : {}
            const bx = mob.x !== undefined ? mob.x : block.x
            const by = mob.y !== undefined ? mob.y : block.y
            const base = { left: `${bx}%`, top: `${by}%` }
            if (type === 'shape' || type === 'image' || type === 'video') {
                const bw = mob.w !== undefined ? mob.w : (block.w || 20)
                const bh = mob.h !== undefined ? mob.h : (block.h || 15)
                const clipShapes = ['circle', 'triangle', 'diamond', 'star', 'hexagon']
                const isSquare = type === 'shape' && clipShapes.includes(block.shapeType) && !block.freeAspect
                const isNativeVideo = type === 'video' && block.videoType === 'custom'
                const isEmbedVideo = type === 'video' && !isNativeVideo
                const embedAr = isEmbedVideo ? (block.videoOrientation === 'portrait' ? '9/16' : '16/9') : undefined
                const radius = block.borderRadius ? `${block.borderRadius}px` : undefined
                const imgRound = type === 'image' && block.imageRound
                const vidRound = type === 'video' && block.videoRound
                return {
                    ...base,
                    width: `${bw}%`,
                    height: isSquare || isNativeVideo || isEmbedVideo || imgRound || vidRound ? 'auto' : `${bh}%`,
                    maxHeight: isNativeVideo ? '100%' : undefined,
                    aspectRatio: isSquare || imgRound || vidRound ? '1' : embedAr,
                    transform: `translate(-50%, -50%)${rot}`,
                    padding: '0',
                    ...(type === 'image' ? {
                        opacity: block.opacity !== undefined ? block.opacity : 1,
                        borderRadius: imgRound ? '50%' : radius,
                        filter: block.shadow
                            ? `drop-shadow(0 0 ${Math.round((block.shadowBlur !== undefined ? block.shadowBlur : 0.3) / 100 * (this.canvasWidth || 800))}px ${block.shadowColor || 'rgba(0,0,0,0.7)'})`
                            : undefined,
                    } : {}),
                    ...(vidRound ? { borderRadius: '50%' } : {}),
                }
            }
            if (type === 'button') {
                const bw = mob.w !== undefined ? mob.w : block.w
                const bh = mob.h !== undefined ? mob.h : block.h
                const hasSize = bw && bh
                return {
                    ...base,
                    width: bw ? `${bw}%` : 'max-content',
                    height: bh ? `${bh}%` : undefined,
                    transform: `translate(-50%, -50%)${rot}`,
                    padding: '0',
                }
            }
            const align = block.align || 'left'
            if (align === 'right') {
                return {
                    left: `${block.x}%`,
                    top: `${block.y}%`,
                    zIndex: block.zIndex || 0,
                    transform: `translate(-100%, -50%)${rot}`,
                    width: 'max-content',
                }
            }
            const tx = align === 'center' ? '-50%' : '0%'
            return { ...base, transform: `translate(${tx}, -50%)${rot}` }
        },
        blockVideoWrapStyle(block) {
            const isNative = block.videoType === 'custom'
            const br = block.videoRound ? '50%' : (block.borderRadius ? `${block.borderRadius}px` : undefined)
            const filterParts = []
            if (block.videoDuotone) filterParts.push(`url(#be-duotone-vid-${block.id})`)
            else if (block.videoGrayscale) filterParts.push('grayscale(100%)')
            else if (block.videoSepia) filterParts.push('sepia(100%)')
            return {
                width: '100%',
                height: isNative && !block.videoRound ? 'auto' : '100%',
                borderRadius: br,
                overflow: block.videoRound ? 'hidden' : undefined,
                opacity: block.opacity !== undefined ? block.opacity : 1,
                filter: filterParts.length ? filterParts.join(' ') : undefined,
            }
        },
        autoFitVideoBlock(block, event) {
            const video = event.target
            const vw = video.videoWidth
            const vh = video.videoHeight
            if (!vw || !vh || !block) return
            const bh = this.bannerHeight || 30
            const blockW = block.w || 25
            // Block-Höhe als % der Canvas-Höhe
            const blockHpct = (blockW * vh / vw) / (bh / 100)
            // Wenn Block mehr als 60% der Canvas-Höhe einnimmt → verkleinern
            const maxPct = 60
            if (blockHpct > maxPct) {
                const newW = Math.round(blockW * (maxPct / blockHpct) * 10) / 10
                this.$set(block, 'w', Math.max(5, newW))
                this.emitChange()
            }
        },
        makeBorderStyle(sides, width, style, color) {
            if (!width) return { border: 'none' }
            const allSides = ['top', 'right', 'bottom', 'left']
            const active = (sides && sides.length) ? sides : allSides
            const val = `${width}px ${style || 'solid'} ${color || '#ffffff'}`
            if (active.length === 4) return { border: val }
            return {
                borderTop:    active.includes('top')    ? val : '0',
                borderRight:  active.includes('right')  ? val : '0',
                borderBottom: active.includes('bottom') ? val : '0',
                borderLeft:   active.includes('left')   ? val : '0',
            }
        },
        borderSidesHas(prop, side) {
            const sides = this.blockProps[prop]
            return !sides || sides.length === 0 || sides.includes(side)
        },
        toggleBorderSide(prop, side) {
            const allSides = ['top', 'right', 'bottom', 'left']
            const current = (this.blockProps[prop] && this.blockProps[prop].length) ? [...this.blockProps[prop]] : [...allSides]
            let next = current.includes(side) ? current.filter(s => s !== side) : [...current, side]
            if (next.length === 0) next = [...allSides]
            this.setBlockProp(prop, next.length === 4 ? null : next.sort((a, b) => allSides.indexOf(a) - allSides.indexOf(b)))
        },
        blockImgStyle(block) {
            return {
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: block.bgColor || undefined,
            }
        },
        imgElementStyle(block) {
            const crop = block.imageCrop
            const filterParts = []
            if (block.imageDuotone) filterParts.push(`url(#be-duotone-img-${block.id})`)
            if (block.imageGrayscale && !block.imageDuotone) filterParts.push('grayscale(100%)')
            if (block.imageSepia && !block.imageDuotone) filterParts.push('sepia(100%)')
            const base = {
                display: 'block',
                pointerEvents: 'none',
                borderRadius: block.imageRound ? '50%' : (block.borderRadius ? `${block.borderRadius}px` : undefined),
                filter: filterParts.length ? filterParts.join(' ') : undefined,
                ...this.makeBorderStyle(block.borderSides, block.borderWidth, 'solid', block.borderColor || '#ffffff'),
            }
            if (block.imageRound) {
                return { ...base, width: '100%', height: '100%', objectFit: 'cover' }
            }
            if (crop && crop.w && crop.h && crop.origW && crop.origH) {
                return {
                    ...base,
                    position: 'absolute',
                    width: `${(crop.origW / crop.w) * 100}%`,
                    height: `${(crop.origH / crop.h) * 100}%`,
                    left: `${-(crop.x / crop.w) * 100}%`,
                    top: `${-(crop.y / crop.h) * 100}%`,
                    right: 'auto',
                    bottom: 'auto',
                    maxWidth: 'none',
                }
            }
            const fit = block.imageFit || 'contain'
            const isContain = fit === 'contain'
            return {
                ...base,
                ...(isContain ? {
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '100%',
                    maxHeight: '100%',
                } : {
                    width: '100%',
                    height: '100%',
                    objectFit: fit,
                }),
            }
        },
        shapeTextStyle(block) {
            const px = Math.round((block.fontSize || 1.5) / 100 * this.canvasWidth)
            const align = block.align || 'center'
            return {
                fontSize: `${px}px`,
                fontFamily: block.fontFamily || 'inherit',
                color: block.color || '#ffffff',
                fontWeight: block.bold ? 'bold' : 'normal',
                fontStyle: block.italic ? 'italic' : 'normal',
                textAlign: align,
                lineHeight: '1.2',
                justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
            }
        },
        blockTextStyle(block) {
            const mobFontSize = (this.mobileEditMode && block.mobile?.fontSize !== undefined) ? block.mobile.fontSize : undefined
            const px = Math.round((mobFontSize ?? block.fontSize ?? 2) / 100 * this.canvasWidth)
            const blurPx = block.shadow ? Math.max(1, Math.round((block.shadowBlur !== undefined ? block.shadowBlur : 0.3) / 100 * this.canvasWidth)) : 0
            const isGradientBg = block.bgFillMode === 'gradient'
            const hasBg = isGradientBg ? !!(block.bgGradientColor1 || block.bgGradientColor2) : !!block.bgColor
            const hasBorder = !!block.bgBorderWidth
            const hasDecoration = hasBg || hasBorder
            let bgValue
            if (isGradientBg) {
                const angle = block.bgGradientAngle !== undefined ? block.bgGradientAngle : 90
                const c1 = block.bgGradientColor1 || '#3b82f6'
                const c2 = block.bgGradientColor2 || 'transparent'
                bgValue = block.bgGradientType === 'radial'
                    ? `radial-gradient(circle, ${c1}, ${c2})`
                    : `linear-gradient(${angle}deg, ${c1}, ${c2})`
            } else {
                bgValue = hasBg ? this.hexToRgba(block.bgColor, block.bgOpacity !== undefined ? block.bgOpacity : 1) : 'transparent'
            }
            return {
                fontSize: `${px}px`,
                fontFamily: block.fontFamily || 'inherit',
                color: block.color || '#ffffff',
                fontWeight: block.bold ? 'bold' : 'normal',
                fontStyle: block.italic ? 'italic' : 'normal',
                textDecoration: [block.underline && 'underline', block.strikethrough && 'line-through'].filter(Boolean).join(' ') || undefined,
                textAlign: block.align || 'left',
                lineHeight: block.lineHeight !== undefined ? block.lineHeight : 1.2,
                letterSpacing: block.letterSpacing ? `${Math.round(block.letterSpacing / 100 * this.canvasWidth)}px` : undefined,
                opacity: block.opacity !== undefined ? block.opacity : 1,
                textShadow: block.shadow ? `0px 1px ${blurPx}px ${block.shadowColor || 'rgba(0,0,0,0.7)'}` : 'none',
                background: bgValue,
                padding: (() => {
                    if (!hasDecoration) return undefined
                    const fallback = block.bgPadding || 0
                    const py = block.bgPaddingY !== undefined ? block.bgPaddingY : fallback
                    const px = block.bgPaddingX !== undefined ? block.bgPaddingX : fallback
                    return (py || px) ? `${py}px ${px}px` : '4px 8px'
                })(),
                ...this.makeBorderStyle(hasBorder ? block.bgBorderSides : null, block.bgBorderWidth, block.bgBorderStyle, block.bgBorderColor || '#ffffff'),
                borderRadius: (hasDecoration && block.bgBorderRadius) ? `${block.bgBorderRadius}px` : undefined,
                boxDecorationBreak: hasDecoration ? 'clone' : undefined,
                WebkitBoxDecorationBreak: hasDecoration ? 'clone' : undefined,
            }
        },
        hexToRgba(hex, opacity) {
            if (!hex) return 'transparent'
            const clean = hex.replace('#', '')
            const r = parseInt(clean.slice(0, 2), 16)
            const g = parseInt(clean.slice(2, 4), 16)
            const b = parseInt(clean.slice(4, 6), 16)
            return `rgba(${r},${g},${b},${opacity !== undefined ? opacity : 1})`
        },
        blockAnimStyle(block) {
            const isUnsizedBtn = block.type === 'button' && !(block.w && block.h)
            const videoStyle = block.type === 'video' ? {
                borderRadius: block.videoRound ? '50%' : (block.borderRadius ? `${block.borderRadius}px` : undefined),
                overflow: 'hidden',
                filter: block.shadow ? `drop-shadow(0 0 ${Math.round((block.shadowBlur || 0.3) / 100 * this.canvasWidth)}px ${block.shadowColor || 'rgba(0,0,0,0.7)'})` : undefined,
                ...this.makeBorderStyle(block.borderSides, block.borderWidth, block.borderStyle, block.borderColor || '#ffffff'),
            } : {}
            const btnStyle = isUnsizedBtn ? { width: 'max-content', height: 'auto' } : {}
            if (!block.animationType || block.animationType === 'none') return { ...videoStyle, ...btnStyle }
            const dur = block.animationDuration !== undefined ? block.animationDuration : 0.6
            const delay = block.animationDelay !== undefined ? block.animationDelay / 1000 : 0
            const easing = block.animationEasing || 'ease'
            return { ...videoStyle, ...btnStyle, animation: `be-anim-${block.animationType} ${dur}s ${easing} both ${delay}s` }
        },
        triggerAnimationPreview(blockId) {
            this.$set(this.animPreviewKeys, blockId, (this.animPreviewKeys[blockId] || 0) + 1)
        },

        // ── Timeline helpers ──────────────────────────────────────
        tlBarStyle(block) {
            const delay = block.animationDelay || 0
            const dur = (block.animationDuration !== undefined ? block.animationDuration : 0.6) * 1000
            const left = (delay / this.timelineMax * 100)
            const width = Math.max(1, dur / this.timelineMax * 100)
            const colors = { text: '#3b82f6', shape: '#8b5cf6', image: '#10b981', button: '#f59e0b' }
            const bg = colors[block.type || 'text'] || '#3b82f6'
            return { left: left + '%', width: width + '%', background: bg }
        },
        tlAnimLabel(type) {
            const map = {
                fadeIn:     this.$t('Fade in'),
                slideUp:    this.$t('From below'),
                slideDown:  this.$t('From above'),
                slideLeft:  this.$t('From right'),
                slideRight: this.$t('From left'),
                zoomIn:     this.$t('Zoom in'),
                zoomOut:    this.$t('Zoom out'),
                flyTop:     this.$t('Fly from top'),
                flyBottom:  this.$t('Fly from bottom'),
                flyLeft:    this.$t('Fly from left'),
                flyRight:   this.$t('Fly from right'),
            }
            return map[type] || type
        },
        tlBlockIcon(block) {
            const map = { text: 'el-icon-fa-font fas', shape: 'el-icon-fa-shapes fas', image: 'el-icon-fa-image far', button: 'el-icon-fa-hand-pointer far', video: 'el-icon-fa-video fas' }
            return map[block.type || 'text'] || 'el-icon-fa-font fas'
        },
        tlBlockLabel(block) {
            if (!block.type || block.type === 'text') return (block.text || this.$t('Text')).slice(0, 18)
            if (block.type === 'button') return (block.text || 'Button').slice(0, 18)
            if (block.type === 'shape') {
                const names = {
                    rect: this.$t('Rectangle'),
                    circle: this.$t('Circle'),
                    triangle: this.$t('Triangle'),
                    diamond: this.$t('Diamond'),
                    star: this.$t('Star'),
                    hexagon: this.$t('Hexagon'),
                    arrow: this.$t('Arrow'),
                }
                return names[block.shapeType] || this.$t('Shape')
            }
            if (block.type === 'video') return this.$t('Video')
            return this.$t('Image')
        },
        // ── Tour ─────────────────────────────────────────────────
        tourCheckFirst() {
            try {
                if (!localStorage.getItem('be_tour_done')) {
                    setTimeout(() => { this.tourAsk = true }, 600)
                }
            } catch (e) { /* ignoriert */ }
        },
        tourStart() {
            this.tourAsk = false
            this.tourStep = 0
            this._tourUpdateSpotlight()
        },
        tourDecline() {
            this.tourAsk = false
            try { localStorage.setItem('be_tour_done', '1') } catch (e) { /* ignoriert */ }
        },
        tourEnd() {
            this.tourStep = -1
            this.tourSpotlight = null
            try { localStorage.setItem('be_tour_done', '1') } catch (e) { /* ignoriert */ }
        },
        tourNext() {
            if (this.tourStep >= this.tourSteps.length - 1) {
                this.tourEnd()
                return
            }
            this.tourStep++
            this._tourUpdateSpotlight()
        },
        _tourUpdateSpotlight() {
            this.$nextTick(() => {
                const step = this.tourSteps[this.tourStep]
                if (!step || !step.ref) { this.tourSpotlight = null; return }
                let el = this.$refs[step.ref]
                if (!el) { this.tourSpotlight = null; return }
                if (el.$el) el = el.$el
                if (Array.isArray(el)) el = el[0]
                const r = el.getBoundingClientRect()
                this.tourSpotlight = { top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom }
            })
        },
        setBlockPropById(blockId, key, value) {
            const slide = this.slides[this.activeSlideIndex]
            if (!slide) return
            const bIdx = slide.blocks.findIndex(b => b.id === blockId)
            if (bIdx < 0) return
            this.$set(slide.blocks[bIdx], key, value)
            this.pushHistory()
            this.emitChange()
        },
        tlPlayAll() {
            const slide = this.slides[this.activeSlideIndex]
            if (!slide) return
            slide.blocks.forEach(b => {
                if (b.animationType && b.animationType !== 'none') {
                    this.triggerAnimationPreview(b.id)
                }
            })
        },
        setAnimForBlock(blockId) {
            const slide = this.slides[this.activeSlideIndex]
            if (!slide) return
            const bIdx = slide.blocks.findIndex(b => b.id === blockId)
            if (bIdx < 0) return
            this.$set(slide.blocks[bIdx], 'animationType', 'fadeIn')
            this.$set(slide.blocks[bIdx], 'animationDelay', 0)
            this.$set(slide.blocks[bIdx], 'animationDuration', 0.6)
            this.activeBlockId = blockId
            this.pushHistory()
            this.emitChange()
        },
        onTlTrackMouseDown(blockId, event) {
            const track = event.currentTarget
            const rect = track.getBoundingClientRect()
            const relX = event.clientX - rect.left
            const ms = Math.max(0, Math.round((relX / rect.width) * this.timelineMax / 100) * 100)
            const slide = this.slides[this.activeSlideIndex]
            if (!slide) return
            const bIdx = slide.blocks.findIndex(b => b.id === blockId)
            if (bIdx < 0) return
            const block = slide.blocks[bIdx]
            if (!block.animationType || block.animationType === 'none') {
                this.setAnimForBlock(blockId)
                this.$set(slide.blocks[bIdx], 'animationDelay', ms)
                this.emitChange()
            }
        },
        onTlBarMouseDown(blockId, event) {
            event.preventDefault()
            const slide = this.slides[this.activeSlideIndex]
            if (!slide) return
            const bIdx = slide.blocks.findIndex(b => b.id === blockId)
            if (bIdx < 0) return
            const block = slide.blocks[bIdx]
            const startX = event.clientX
            const startDelay = block.animationDelay || 0
            const trackEl = event.currentTarget.closest('.be-tl-track')
            const trackW = trackEl ? trackEl.getBoundingClientRect().width : 800
            const onMove = (e) => {
                const dx = e.clientX - startX
                const dMs = Math.round((dx / trackW) * this.timelineMax / 100) * 100
                const newDelay = Math.max(0, Math.min(this.timelineMax - 100, startDelay + dMs))
                this.$set(slide.blocks[bIdx], 'animationDelay', newDelay)
            }
            const onUp = () => {
                document.removeEventListener('mousemove', onMove)
                document.removeEventListener('mouseup', onUp)
                this.pushHistory()
                this.emitChange()
            }
            document.addEventListener('mousemove', onMove)
            document.addEventListener('mouseup', onUp)
        },
        onTlBarResizeMouseDown(blockId, event) {
            event.preventDefault()
            const slide = this.slides[this.activeSlideIndex]
            if (!slide) return
            const bIdx = slide.blocks.findIndex(b => b.id === blockId)
            if (bIdx < 0) return
            const block = slide.blocks[bIdx]
            const startX = event.clientX
            const startDur = (block.animationDuration !== undefined ? block.animationDuration : 0.6) * 1000
            const trackEl = event.currentTarget.closest('.be-tl-track')
            const trackW = trackEl ? trackEl.getBoundingClientRect().width : 800
            const onMove = (e) => {
                const dx = e.clientX - startX
                const dMs = Math.round((dx / trackW) * this.timelineMax / 100) * 100
                const newDurMs = Math.max(100, Math.min(5000, startDur + dMs))
                this.$set(slide.blocks[bIdx], 'animationDuration', Math.round(newDurMs / 100) / 10)
            }
            const onUp = () => {
                document.removeEventListener('mousemove', onMove)
                document.removeEventListener('mouseup', onUp)
                this.pushHistory()
                this.emitChange()
            }
            document.addEventListener('mousemove', onMove)
            document.addEventListener('mouseup', onUp)
        },
        shapeInnerStyle(block) {
            const clipPaths = {
                triangle:  'polygon(50% 0%, 0% 100%, 100% 100%)',
                diamond:   'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
                star:      'polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%)',
                hexagon:   'polygon(25% 0%,75% 0%,100% 50%,75% 100%,25% 100%,0% 50%)',
                arrow:     'polygon(0% 25%,60% 25%,60% 0%,100% 50%,60% 100%,60% 75%,0% 75%)',
            }
            const clip = clipPaths[block.shapeType]
            const shadowVal = block.shadow
                ? `0 0 ${Math.round((block.shadowBlur !== undefined ? block.shadowBlur : 0.3) / 100 * (this.canvasWidth || 800))}px ${block.shadowColor || 'rgba(0,0,0,0.7)'}`
                : undefined
            const isGradient = block.fillMode === 'gradient'
            const angle = block.gradientAngle !== undefined ? block.gradientAngle : 90
            const c1 = block.gradientColor1 || '#3b82f6'
            const c2 = block.gradientColor2 || 'transparent'
            const bg = isGradient
                ? (block.gradientType === 'radial'
                    ? `radial-gradient(circle, ${c1}, ${c2})`
                    : `linear-gradient(${angle}deg, ${c1}, ${c2})`)
                : (block.fillColor || '#3b82f6')
            return {
                width: '100%',
                height: '100%',
                background: bg,
                borderRadius: block.shapeType === 'circle' ? '50%' : (clip ? '0' : `${block.borderRadius || 0}px`),
                ...(!clip ? this.makeBorderStyle(block.borderSides, block.borderWidth, block.borderStyle, block.borderColor || '#ffffff') : { border: 'none' }),
                opacity: block.opacity !== undefined ? block.opacity : 1,
                boxSizing: 'border-box',
                clipPath: clip || undefined,
                filter: shadowVal ? `drop-shadow(${shadowVal})` : undefined,
            }
        },
        buttonContainerStyle(block) {
            const shadowPx = block.shadow
                ? `0 2px ${Math.round((block.shadowBlur || 0.3) / 100 * this.canvasWidth)}px ${block.shadowColor || 'rgba(0,0,0,0.7)'}`
                : undefined
            let bgVal
            if (block.bgFillMode === 'gradient') {
                const angle = block.bgGradientAngle !== undefined ? block.bgGradientAngle : 90
                const c1 = block.bgGradientColor1 || '#3b82f6'
                const c2 = block.bgGradientColor2 || 'transparent'
                bgVal = block.bgGradientType === 'radial'
                    ? `radial-gradient(circle, ${c1}, ${c2})`
                    : `linear-gradient(${angle}deg, ${c1}, ${c2})`
            } else {
                bgVal = block.bgColor || '#3b82f6'
            }
            const hasSize = block.w && block.h
            return {
                background: bgVal,
                borderRadius: `${block.borderRadius !== undefined ? block.borderRadius : 6}px`,
                overflow: 'hidden',
                ...(hasSize ? {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    padding: (() => {
                        const py = block.bgPaddingY !== undefined ? Math.round(block.bgPaddingY / 900 * this.canvasWidth) : 0
                        const px = block.bgPaddingX !== undefined ? Math.round(block.bgPaddingX / 900 * this.canvasWidth) : 0
                        return (py || px) ? `${py}px ${px}px` : undefined
                    })(),
                } : {
                    padding: (() => {
                        const py = block.bgPaddingY !== undefined ? Math.round(block.bgPaddingY / 900 * this.canvasWidth) : Math.round(0.6 * this.canvasWidth / 100)
                        const px = block.bgPaddingX !== undefined ? Math.round(block.bgPaddingX / 900 * this.canvasWidth) : Math.round(1.5 * this.canvasWidth / 100)
                        return `${py}px ${px}px`
                    })(),
                    display: 'inline-block',
                    width: 'max-content',
                    whiteSpace: 'nowrap',
                    lineHeight: '1.25',
                }),
                cursor: 'inherit',
                boxShadow: shadowPx,
                ...this.makeBorderStyle(block.borderSides, block.borderWidth, block.borderStyle, block.borderColor || '#ffffff'),
            }
        },
        btnTextStyle(block) {
            const hasSize = block.w && block.h
            const mobFs = (this.mobileEditMode && block.mobile?.fontSize !== undefined) ? block.mobile.fontSize : undefined
            const fs = mobFs ?? block.fontSize ?? 1.2
            const px = Math.round(fs / 100 * this.canvasWidth)
            return {
                fontSize: `${px}px`,
                fontFamily: block.fontFamily || 'inherit',
                color: block.color || '#ffffff',
                fontWeight: block.bold ? 'bold' : 'normal',
                fontStyle: block.italic ? 'italic' : 'normal',
                textDecoration: [block.underline && 'underline', block.strikethrough && 'line-through'].filter(Boolean).join(' ') || undefined,
                display: 'block',
                whiteSpace: hasSize ? 'normal' : 'nowrap',
                textAlign: hasSize ? 'center' : undefined,
                overflowWrap: hasSize ? 'break-word' : undefined,
                overflow: hasSize ? 'hidden' : undefined,
            }
        },
        _slideBgBase(img, W, H) {
            const crop = img.crop
            if (crop && crop.w && crop.h && crop.origW && crop.origH && W && H) {
                // Cover-Skalierung – kein Verzerren, kein Graurand, Crop-Mitte zentriert
                const scale = Math.max(W / crop.w, H / crop.h)
                const bw = (crop.origW * scale).toFixed(1)
                const bh = (crop.origH * scale).toFixed(1)
                // Crop-Region zentrieren (Überlauf wird durch overflow:hidden abgeschnitten)
                const px = (-(crop.x * scale) + (W - crop.w * scale) / 2).toFixed(1)
                const py = (-(crop.y * scale) + (H - crop.h * scale) / 2).toFixed(1)
                return {
                    backgroundImage: `url(${img.url})`,
                    backgroundSize: `${bw}px ${bh}px`,
                    backgroundPosition: `${px}px ${py}px`,
                    backgroundRepeat: 'no-repeat',
                }
            }
            return {
                backgroundImage: `url(${img.url})`,
                backgroundSize: 'cover',
                backgroundPosition: img.bgPosition || 'center',
            }
        },
        _duotoneValues(slide) {
            if (!slide || !slide.bgDuotone) return { r: '0 1', g: '0 1', b: '0 1' }
            const preset = SLIDE_DUOTONE_MAP[slide.bgDuotone]
            const c1 = preset ? preset.color1 : (slide.bgDuotoneColor1 || '#000000')
            const c2 = preset ? preset.color2 : (slide.bgDuotoneColor2 || '#ffffff')
            return duotoneSvgValues(c1, c2)
        },
        setDuotone(key) {
            this.pushHistory()
            this.setSlideColor('bgDuotone', key || null, false)
            if (key && !SLIDE_DUOTONE_MAP[key] && key !== 'custom') return
            this.emitChange()
        },
        onDuotoneSwitchChange(val) {
            this.showDuotoneGrid = val
            if (!val) this.setDuotone(null)
        },
        setDuotoneColor(field, val) {
            let v = (val || '').trim()
            if (!v.startsWith('#')) v = '#' + v
            if (/^#[0-9a-fA-F]{3}$/.test(v)) v = '#' + v[1]+v[1]+v[2]+v[2]+v[3]+v[3]
            if (!/^#[0-9a-fA-F]{6}$/i.test(v)) return
            this.setSlideColor(field, v)
        },
        setImgDuotone(key) {
            const block = this.activeBlock
            if (!block) return
            this.pushHistory()
            this.$set(block, 'imageDuotone', key || null)
            if (key) {
                this.$set(block, 'imageGrayscale', false)
                this.$set(block, 'imageSepia', false)
            }
            this.emitChange()
        },
        setImgDuotoneColor(field, val) {
            let v = (val || '').trim()
            if (!v.startsWith('#')) v = '#' + v
            if (/^#[0-9a-fA-F]{3}$/.test(v)) v = '#' + v[1]+v[1]+v[2]+v[2]+v[3]+v[3]
            if (!/^#[0-9a-fA-F]{6}$/i.test(v)) return
            const block = this.activeBlock
            if (!block) return
            this.$set(block, field, v)
            this.emitChange()
        },
        setVidDuotone(key) {
            const block = this.activeBlock
            if (!block) return
            this.pushHistory()
            this.$set(block, 'videoDuotone', key || null)
            if (key) {
                this.$set(block, 'videoGrayscale', false)
                this.$set(block, 'videoSepia', false)
            }
            this.emitChange()
        },
        setVidDuotoneColor(field, val) {
            let v = (val || '').trim()
            if (!v.startsWith('#')) v = '#' + v
            if (/^#[0-9a-fA-F]{3}$/.test(v)) v = '#' + v[1]+v[1]+v[2]+v[2]+v[3]+v[3]
            if (!/^#[0-9a-fA-F]{6}$/i.test(v)) return
            const block = this.activeBlock
            if (!block) return
            this.$set(block, field, v)
            this.emitChange()
        },
        slideThumbBgStyle(slide) {
            if (!slide || !slide.image || !slide.image.url) return {}
            const base = this._slideBgBase(slide.image, 72, 42)
            const parts = []
            if (slide.bgDuotone) parts.push(`url(#be-duotone-thumb-${slide.id})`)
            if (slide.bgGrayscale) parts.push('grayscale(100%)')
            if (slide.bgSepia) parts.push('sepia(100%)')
            const transforms = []
            if (slide.bgFlipH) transforms.push('scaleX(-1)')
            if (slide.bgFlipV) transforms.push('scaleY(-1)')
            return {
                ...base,
                filter: parts.length ? parts.join(' ') : undefined,
                transform: transforms.length ? transforms.join(' ') : undefined,
            }
        },
        slideThumbVideoStyle(slide) {
            if (!slide) return {}
            const parts = []
            if (slide.bgDuotone) parts.push(`url(#be-duotone-thumb-${slide.id})`)
            if (slide.bgGrayscale) parts.push('grayscale(100%)')
            if (slide.bgSepia) parts.push('sepia(100%)')
            const transforms = []
            if (slide.bgFlipH) transforms.push('scaleX(-1)')
            if (slide.bgFlipV) transforms.push('scaleY(-1)')
            return {
                filter: parts.length ? parts.join(' ') : undefined,
                transform: transforms.length ? transforms.join(' ') : undefined,
            }
        },
        previewBgWrapStyle(slide) {
            if (!slide || !slide.image || !slide.image.url) return {}
            const parts = []
            if (slide.bgDuotone) parts.push('url(#be-duotone-preview)')
            if (slide.bgBlur) parts.push(`blur(${slide.bgBlur}px)`)
            if (slide.bgGrayscale) parts.push('grayscale(100%)')
            if (slide.bgSepia) parts.push('sepia(100%)')
            const transforms = []
            if (slide.bgBlur) transforms.push('scale(1.05)')
            if (slide.bgFlipH) transforms.push('scaleX(-1)')
            if (slide.bgFlipV) transforms.push('scaleY(-1)')
            return {
                filter: parts.length ? parts.join(' ') : undefined,
                transform: transforms.length ? transforms.join(' ') : undefined,
            }
        },
        previewBgImgStyle(slide) {
            if (!slide || !slide.image) return {}
            const crop = slide.image.crop
            if (crop && crop.w && crop.h && crop.origW && crop.origH) {
                return {
                    position: 'absolute',
                    width: `${(crop.origW / crop.w) * 100}%`,
                    height: `${(crop.origH / crop.h) * 100}%`,
                    left: `${-(crop.x / crop.w) * 100}%`,
                    top: `${-(crop.y / crop.h) * 100}%`,
                    right: 'auto',
                    bottom: 'auto',
                    maxWidth: 'none',
                    maxHeight: 'none',
                    objectFit: 'fill',
                }
            }
            return {
                position: 'absolute',
                inset: '0',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: slide.bgPosition || 'center',
            }
        },
        blockPreviewStyle(block) {
            const type = block.type || 'text'
            const rot = block.rotation ? ` rotate(${block.rotation}deg)` : ''
            const pW = this.previewCanvasWidth || this.canvasWidth * 0.12
            const hScale = 1
            const base = { position: 'absolute', left: `${block.x}%`, top: `${block.y}%` }
            if (type === 'shape' || type === 'image' || type === 'video') {
                const clipShapes = ['circle', 'triangle', 'diamond', 'star', 'hexagon']
                const isSquare = type === 'shape' && clipShapes.includes(block.shapeType) && !block.freeAspect
                const radius = block.borderRadius ? `${block.borderRadius}px` : undefined
                const imgRound = type === 'image' && block.imageRound
                const vidRound = type === 'video' && block.videoRound
                return {
                    ...base,
                    width: `${block.w || 20}%`,
                    height: isSquare || imgRound || vidRound ? 'auto' : `${Math.round((block.h || 15) * hScale * 100) / 100}%`,
                    aspectRatio: isSquare || imgRound || vidRound ? '1' : undefined,
                    transform: `translate(-50%, -50%)${rot}`,
                    ...(type === 'video' ? { borderRadius: vidRound ? '50%' : radius, ...this.makeBorderStyle(block.borderSides, block.borderWidth, block.borderStyle, block.borderColor || '#ffffff') } : {}),
                    ...(type === 'image' ? {
                        opacity: block.opacity !== undefined ? block.opacity : 1,
                        borderRadius: imgRound ? '50%' : radius,
                        filter: block.shadow
                            ? `drop-shadow(0 0 ${Math.max(1, Math.round((block.shadowBlur !== undefined ? block.shadowBlur : 0.3) / 100 * pW))}px ${block.shadowColor || 'rgba(0,0,0,0.7)'})`
                            : undefined,
                    } : {}),
                }
            }
            if (type === 'button') {
                const hasSize = block.w && block.h
                if (hasSize) {
                    const scale = pW / (this.canvasWidth || 800)
                    const radius = Math.max(1, Math.round((block.borderRadius !== undefined ? block.borderRadius : 6) * scale))
                    let bgVal
                    if (block.bgFillMode === 'gradient') {
                        const angle = block.bgGradientAngle !== undefined ? block.bgGradientAngle : 90
                        const c1 = block.bgGradientColor1 || '#3b82f6'
                        const c2 = block.bgGradientColor2 || 'transparent'
                        bgVal = block.bgGradientType === 'radial'
                            ? `radial-gradient(circle, ${c1}, ${c2})`
                            : `linear-gradient(${angle}deg, ${c1}, ${c2})`
                    } else {
                        bgVal = block.bgColor || '#3b82f6'
                    }
                    return {
                        ...base,
                        width: `${block.w}%`,
                        height: `${Math.round(block.h * hScale * 100) / 100}%`,
                        transform: `translate(-50%, -50%)${rot}`,
                        background: bgVal,
                        borderRadius: `${radius}px`,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ...this.makeBorderStyle(block.borderSides, block.borderWidth, block.borderStyle, block.borderColor || '#ffffff'),
                    }
                }
                return { ...base, transform: `translate(-50%, -50%)${rot}` }
            }
            const align = block.align || 'left'
            const isGradientBg = block.bgFillMode === 'gradient'
            const hasBg = isGradientBg ? !!(block.bgGradientColor1 || block.bgGradientColor2) : !!block.bgColor
            const hasBorder = !!block.bgBorderWidth
            const hasDecoration = hasBg || hasBorder || isGradientBg
            const fallbackPad = block.bgPadding || 0
            const py = block.bgPaddingY !== undefined ? block.bgPaddingY : fallbackPad
            const px = block.bgPaddingX !== undefined ? block.bgPaddingX : fallbackPad
            let bgValue
            if (isGradientBg) {
                const angle = block.bgGradientAngle !== undefined ? block.bgGradientAngle : 90
                const c1 = block.bgGradientColor1 || '#3b82f6'
                const c2 = block.bgGradientColor2 || 'transparent'
                bgValue = block.bgGradientType === 'radial'
                    ? `radial-gradient(circle, ${c1}, ${c2})`
                    : `linear-gradient(${angle}deg, ${c1}, ${c2})`
            } else if (hasBg) {
                bgValue = this.hexToRgba(block.bgColor, block.bgOpacity !== undefined ? block.bgOpacity : 1)
            }
            const posStyle = align === 'right'
                ? { ...base, transform: `translate(-100%, -50%)${rot}`, width: 'max-content' }
                : { ...base, transform: `translate(${align === 'center' ? '-50%' : '0%'}, -50%)${rot}` }
            return {
                ...posStyle,
                fontSize: `${Math.max(1, Math.round((block.fontSize || 2) / 100 * pW))}px`,
                fontFamily: block.fontFamily || 'inherit',
                color: block.color || '#ffffff',
                fontWeight: block.bold ? 'bold' : 'normal',
                fontStyle: block.italic ? 'italic' : 'normal',
                textDecoration: [block.underline && 'underline', block.strikethrough && 'line-through'].filter(Boolean).join(' ') || undefined,
                textAlign: block.align || 'left',
                lineHeight: block.lineHeight !== undefined ? block.lineHeight : 1.2,
                letterSpacing: block.letterSpacing ? `${Math.max(1, Math.round(block.letterSpacing / 100 * pW))}px` : undefined,
                opacity: block.opacity !== undefined ? block.opacity : 1,
                textShadow: block.shadow ? `0px 1px ${Math.max(1, Math.round((block.shadowBlur !== undefined ? block.shadowBlur : 0.3) / 100 * pW))}px ${block.shadowColor || 'rgba(0,0,0,0.7)'}` : 'none',
                background: bgValue,
                padding: (() => {
                    if (!hasDecoration) return undefined
                    const scale = pW / (this.canvasWidth || 900)
                    const sPy = Math.max(0, Math.round(py * scale))
                    const sPx = Math.max(0, Math.round(px * scale))
                    return (py || px) ? `${sPy}px ${sPx}px` : `${Math.round(4 * scale)}px ${Math.round(8 * scale)}px`
                })(),
                ...(hasBorder ? this.makeBorderStyle(block.bgBorderSides, block.bgBorderWidth, block.bgBorderStyle, block.bgBorderColor || '#ffffff') : {}),
                borderRadius: (hasDecoration && block.bgBorderRadius) ? `${Math.max(0, Math.round(block.bgBorderRadius * pW / (this.canvasWidth || 900)))}px` : undefined,
                boxDecorationBreak: hasDecoration ? 'clone' : undefined,
                WebkitBoxDecorationBreak: hasDecoration ? 'clone' : undefined,
            }
        },
        btnPreviewStyle(block) {
            const pW = this.previewCanvasWidth || this.canvasWidth * 0.12
            const scale = pW / (this.canvasWidth || 800)
            const fontSize = Math.max(1, Math.round((block.fontSize || 1.2) / 100 * pW))
            const hasSize = block.w && block.h
            if (hasSize) {
                return {
                    color: block.color || '#ffffff',
                    fontSize: `${fontSize}px`,
                    fontFamily: block.fontFamily || 'inherit',
                    fontWeight: block.bold ? 'bold' : 'normal',
                    fontStyle: block.italic ? 'italic' : 'normal',
                    textDecoration: [block.underline && 'underline', block.strikethrough && 'line-through'].filter(Boolean).join(' ') || undefined,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }
            }
            let bgVal
            if (block.bgFillMode === 'gradient') {
                const angle = block.bgGradientAngle !== undefined ? block.bgGradientAngle : 90
                const c1 = block.bgGradientColor1 || '#3b82f6'
                const c2 = block.bgGradientColor2 || 'transparent'
                bgVal = block.bgGradientType === 'radial'
                    ? `radial-gradient(circle, ${c1}, ${c2})`
                    : `linear-gradient(${angle}deg, ${c1}, ${c2})`
            } else {
                bgVal = block.bgColor || '#3b82f6'
            }
            const shadowPx = block.shadow
                ? `0 ${Math.max(1, Math.round(2 * scale))}px ${Math.max(1, Math.round((block.shadowBlur || 0.3) / 100 * pW))}px ${block.shadowColor || 'rgba(0,0,0,0.7)'}`
                : undefined
            const padV = Math.max(1, Math.round(0.6 * 13 * scale))
            const padH = Math.max(2, Math.round(1.5 * 13 * scale))
            const radius = Math.max(1, Math.round((block.borderRadius !== undefined ? block.borderRadius : 6) * scale))
            return {
                background: bgVal,
                borderRadius: `${radius}px`,
                padding: `${padV}px ${padH}px`,
                color: block.color || '#ffffff',
                fontSize: `${fontSize}px`,
                fontFamily: block.fontFamily || 'inherit',
                display: 'inline-block',
                lineHeight: '1.25',
                fontWeight: block.bold ? 'bold' : 'normal',
                fontStyle: block.italic ? 'italic' : 'normal',
                textDecoration: [block.underline && 'underline', block.strikethrough && 'line-through'].filter(Boolean).join(' ') || undefined,
                whiteSpace: 'nowrap',
                boxShadow: shadowPx,
                ...this.makeBorderStyle(block.borderSides, block.borderWidth, block.borderStyle, block.borderColor || '#ffffff'),
            }
        },
    },
}
</script>

<style lang="sass">
.banner-editor
    border: 1px solid #e5e7eb
    border-radius: 6px
    overflow: hidden
    font-size: 13px
    user-select: none

    .be-toolbar
        display: flex
        align-items: center
        gap: 4px
        padding: 8px 10px
        background: #f9fafb
        border-bottom: 1px solid #e5e7eb

        .be-toolbar-sep
            width: 1px
            height: 18px
            background: #e5e7eb
            margin: 0 4px

        .be-height-field
            display: flex
            align-items: center
            gap: 3px

        .be-height-input
            width: 42px
            height: 24px
            border: 1px solid #d1d5db
            border-radius: 4px
            font-size: 12px
            text-align: center
            padding: 0 2px
            -moz-appearance: textfield
            &::-webkit-inner-spin-button,
            &::-webkit-outer-spin-button
                -webkit-appearance: none
                margin: 0
            &:focus
                outline: none
                border-color: #3b82f6

        .be-layout-select
            height: 24px
            border: 1px solid #d1d5db
            border-radius: 4px
            font-size: 12px
            padding: 0 4px
            color: #374151
            background: #fff
            cursor: pointer
            &:focus
                outline: none
                border-color: #3b82f6

        .be-height-unit
            font-size: 11px
            color: #6b7280
            white-space: nowrap

        .be-vw-hint
            font-size: 12px
            color: #9ca3af
            cursor: help
            &:hover
                color: #6b7280

        .be-cbg-color-label
            display: inline-flex
            align-items: center
            cursor: pointer
            position: relative
            margin-left: 2px

        .be-cbg-color-swatch
            display: inline-block
            width: 22px
            height: 22px
            border-radius: 4px
            border: 1px solid #d1d5db
            pointer-events: none
            flex-shrink: 0

        .be-cbg-color-input
            position: absolute
            inset: 0
            opacity: 0
            cursor: pointer
            width: 100%
            height: 100%
            padding: 0
            border: none

        .be-cbg-fill-btns
            display: flex
            gap: 3px
            button
                flex: 1
                padding: 2px 8px
                font-size: 11px
                border: 1px solid #d1d5db
                border-radius: 4px
                background: #f9fafb
                cursor: pointer
                color: #374151
                &.is-on
                    background: #dbeafe
                    border-color: #3b82f6
                    color: #1d4ed8

        .be-cbg-angle-wrap
            display: flex
            align-items: center
            gap: 6px
            flex: 1

        .be-cbg-angle-slider
            flex: 1
            accent-color: #3b82f6

        .be-cbg-angle-val
            font-size: 11px
            color: #6b7280
            min-width: 30px
            text-align: right

        .be-cbg-presets
            display: flex
            flex-wrap: wrap
            gap: 4px
            margin-top: 2px
            .be-cbg-preset
                width: 28px
                height: 18px
                border-radius: 3px
                border: 1.5px solid transparent
                cursor: pointer
                padding: 0
                transition: border-color 0.1s
                &:hover
                    border-color: #6366f1

    .be-body
        display: flex
        min-height: 360px
        position: relative

    // ── Slide list ──────────────────────────────────────────────
    .be-slide-list
        width: 96px
        min-width: 96px
        border-right: 1px solid #e5e7eb
        background: #f9fafb
        padding: 8px 6px
        display: flex
        flex-direction: column
        gap: 6px
        overflow-y: auto

        .be-slide-item
            position: relative
            display: flex
            flex-direction: column
            align-items: center
            gap: 3px
            padding: 5px 4px
            border-radius: 5px
            cursor: pointer
            border: 2px solid transparent
            background: transparent

            &:hover
                background: #eff6ff

                .be-slide-dup,
                .be-slide-del
                    opacity: 1

            &.is-active
                border-color: #3b82f6
                background: #eff6ff

            .be-slide-handle
                cursor: grab
                color: #9ca3af
                line-height: 0
                &:active
                    cursor: grabbing

            .be-slide-thumb
                width: 72px
                height: 42px
                border-radius: 3px
                background: #e5e7eb
                overflow: hidden
                display: flex
                align-items: center
                justify-content: center
                flex-shrink: 0

                .be-slide-thumb-bg
                    width: 100%
                    height: 100%
                    background-size: cover
                    background-position: center
                    background-repeat: no-repeat

                .be-slide-thumb-video
                    width: 100%
                    height: 100%
                    object-fit: cover
                    display: block
                    pointer-events: none

                .be-slide-thumb-color
                    width: 100%
                    height: 100%
                    border-radius: 2px

                .el-icon-picture-outline
                    font-size: 18px
                    color: #9ca3af

            .be-slide-nr
                font-size: 11px
                color: #6b7280
                font-weight: 600
                line-height: 1

            .be-slide-dup
                position: absolute
                top: 2px
                right: 18px
                width: 15px
                height: 15px
                padding: 0
                border: none
                background: none
                cursor: pointer
                color: #6b7280
                display: flex
                align-items: center
                justify-content: center
                border-radius: 3px
                opacity: 0
                transition: opacity 0.15s

                i
                    font-size: 10px

                &:hover
                    background: #dbeafe
                    color: #3b82f6

            .be-slide-del
                position: absolute
                top: 2px
                right: 2px
                width: 15px
                height: 15px
                padding: 0
                border: none
                background: none
                cursor: pointer
                color: #6b7280
                display: flex
                align-items: center
                justify-content: center
                border-radius: 3px
                opacity: 0
                transition: opacity 0.15s

                i
                    font-size: 10px

                &:hover
                    background: #fee2e2
                    color: #ef4444

        .be-slide-ghost
            opacity: 0.4

        .be-slide-add
            display: flex
            flex-direction: column
            align-items: center
            gap: 4px
            padding: 8px 4px
            border-radius: 5px
            border: 2px dashed #d1d5db
            background: transparent
            color: #6b7280
            font-size: 11px
            cursor: pointer
            width: 100%
            transition: border-color 0.15s, color 0.15s

            i
                font-size: 13px

            &:hover
                border-color: #3b82f6
                color: #3b82f6

    // ── Main area ───────────────────────────────────────────────
    .be-main
        flex: 1
        display: flex
        flex-direction: column
        gap: 10px
        padding: 12px
        min-width: 0
        overflow: auto

    // ── Canvas ──────────────────────────────────────────────────
    .be-thumb-duotone-defs
        position: absolute
        width: 0
        height: 0
        overflow: hidden
        pointer-events: none

    .be-viewport-row
        display: flex
        align-items: center
        gap: 10px
        justify-content: space-between

    .be-viewport-toggle
        display: flex
        align-items: center
        gap: 0
        border: 1px solid #e5e7eb
        border-radius: 6px
        overflow: hidden
        background: #f9fafb

    .be-zoom-label
        font-size: 11px
        font-weight: 600
        color: #6b7280
        white-space: nowrap

    .be-vp-spacer
        flex: 1
        min-width: 0

    .be-vp-visibility
        display: flex
        align-items: center
        gap: 5px

    .be-vp-vis-btns
        display: flex
        align-items: center
        height: 25px
        border: 1px solid #e5e7eb
        border-radius: 5px
        overflow: hidden
        background: #f9fafb

    .be-vis-btn
        height: 100%
        width: 28px
        border: none
        background: transparent
        cursor: pointer
        color: #6b7280
        font-size: 11px
        display: flex
        align-items: center
        justify-content: center
        padding: 0
        transition: background 0.15s, color 0.15s
        & + .be-vis-btn
            border-left: 1px solid #e5e7eb
        &:hover
            background: #f3f4f6
            color: #374151
        &.is-on
            background: #f59e0b
            color: #fff

    .be-mobile-height
        display: flex
        align-items: center
        gap: 5px

    .be-mh-switch-wrap
        display: flex
        align-items: center
        gap: 8px

    .be-mh-switch-label
        font-size: 11px
        font-weight: 600
        color: #6b7280
        white-space: nowrap

    .be-mh-label-wrap
        display: flex
        align-items: center
        gap: 3px
        white-space: nowrap

    .be-mh-help
        font-size: 11px
        color: #9ca3af
        cursor: pointer
        &:hover
            color: #6b7280

    .be-mh-wrap
        display: flex
        align-items: center
        gap: 0
        height: 25px
        border: 1px solid #e5e7eb
        border-radius: 5px
        overflow: hidden
        background: #f9fafb

    .be-mh-btn
        width: 20px
        height: 100%
        border: none
        background: transparent
        cursor: pointer
        color: #6b7280
        font-size: 11px
        display: flex
        align-items: center
        justify-content: center
        padding: 0
        flex-shrink: 0
        &:hover
            background: #f3f4f6
            color: #374151

    .be-mh-input
        width: 32px
        height: 100%
        border: none
        border-left: 1px solid #e5e7eb
        border-right: 1px solid #e5e7eb
        background: #fff
        text-align: center
        font-size: 11px
        font-weight: 600
        color: #374151
        padding: 0
        outline: none
        -moz-appearance: textfield
        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button
            -webkit-appearance: none

    .be-mh-unit
        font-size: 10px
        color: #9ca3af
        padding: 0 3px
        border-left: 1px solid #e5e7eb
        line-height: 1

    .be-vt-btn
        display: flex
        align-items: center
        gap: 5px
        padding: 5px 12px
        font-size: 12px
        color: #6b7280
        background: transparent
        border: none
        cursor: pointer
        transition: background 0.15s, color 0.15s
        i
            font-size: 11px
        &:hover
            background: #f3f4f6
            color: #374151
        &.is-active
            background: #3b82f6
            color: #fff

    .be-canvas-outer
        position: relative
        width: 100%

        &.has-outer-bg
            overflow: hidden
            border-radius: 5px

        &.has-outer-bg .be-canvas-container
            width: 80%
            margin: 0 auto
            position: relative
            z-index: 1

        &.is-mobile-preview
            background: #e2e8f0
            border-radius: 5px
            .be-canvas
                left: 50%
                right: auto
                width: 550px
                transform: translateX(-50%)
                border-radius: 0
                overflow: hidden

    .be-canvas-outer-bg
        position: absolute
        inset: -20px
        background-size: cover
        background-position: center

    .be-canvas-container
        position: relative
        width: 100%
        overflow: visible
        pointer-events: none

    .be-canvas
        position: absolute
        top: 0
        left: 0
        right: 0
        bottom: 0
        background: #1e293b
        border-radius: 5px
        overflow: hidden
        cursor: default

        .be-canvas-bg
            position: absolute
            inset: 0
            overflow: hidden

        .be-canvas-bg-img
            display: block

        .be-canvas-video
            position: absolute
            inset: 0
            width: 100%
            height: 100%
            object-fit: cover
            pointer-events: none

        .be-canvas-overlay
            position: absolute
            inset: 0
            pointer-events: none

        .be-guides
            position: absolute
            inset: 0
            width: 100%
            height: 100%
            pointer-events: none
            z-index: 1

        .be-canvas-empty
            position: absolute
            inset: 0
            display: flex
            flex-direction: column
            align-items: center
            justify-content: center
            color: #64748b
            gap: 6px
            cursor: pointer
            transition: background 0.15s

            &:hover
                background: rgba(255,255,255,0.06)
                color: #94a3b8

            i
                font-size: 32px

            span
                font-size: 13px
                font-weight: 500

            .be-canvas-empty-hint
                font-size: 11px
                color: #475569
                margin-top: 2px


        // ── Blocks ──────────────────────────────────────────────
        .be-block--mobile-hidden
            opacity: 0.3 !important
            outline: 2px dashed #f59e0b !important

        .be-block--desktop-hidden
            opacity: 0.3 !important
            outline: 2px dashed #6366f1 !important

        .be-block
            position: absolute
            cursor: move
            padding: 0
            border-radius: 2px
            outline: 2px solid transparent
            transition: outline-color 0.1s
            z-index: 2

            &:hover
                outline-color: rgba(59, 130, 246, 0.6)

            &.is-active
                outline-color: #3b82f6

            &.is-selected
                outline-color: rgba(99, 102, 241, 0.7)

            &.be-block--shape,
            &.be-block--image,
            &.be-block--button
                padding: 0

            .be-block-anim-wrap
                width: 100%
                height: 100%

            .be-block-text
                white-space: pre-wrap
                line-height: 1.25
                text-shadow: 0 1px 3px rgba(0,0,0,0.5)

                p, div
                    margin: 0

                &[contenteditable]
                    user-select: text !important
                    -webkit-user-select: text !important
                    cursor: text
                    outline: none

            .be-block-shape
                pointer-events: none

            .be-block-shape-text
                position: absolute
                inset: 0
                display: flex
                align-items: center
                padding: 4px
                pointer-events: none
                white-space: pre-wrap
                word-break: break-word
                overflow: hidden
                &[contenteditable]
                    user-select: text !important
                    -webkit-user-select: text !important
                    cursor: text
                    outline: none
                    pointer-events: auto

            .be-block-btn
                pointer-events: none

                .be-block-btn-text
                    &[contenteditable]
                        user-select: text !important
                        -webkit-user-select: text !important
                        cursor: text
                        outline: none
                        pointer-events: auto

            .be-block-img
                width: 100%
                height: 100%
                overflow: hidden
                border-radius: inherit
                display: flex
                align-items: center
                justify-content: center

                .be-block-img-empty
                    width: 100%
                    height: 100%
                    display: flex
                    flex-direction: column
                    align-items: center
                    justify-content: center
                    background: rgba(255,255,255,0.1)
                    border: 2px dashed rgba(255,255,255,0.4)
                    border-radius: 4px
                    color: rgba(255,255,255,0.7)
                    gap: 4px
                    cursor: pointer
                    font-size: 11px

                    i
                        font-size: 18px

            .be-block-video
                width: 100%
                height: 100%
                min-height: 0
                position: relative
                overflow: hidden
                border-radius: inherit
                background: #000

                .be-block-video-frame
                    position: absolute
                    inset: 0
                    width: 100%
                    height: 100%
                    border: none
                    display: block
                .be-block-video-native
                    width: 100%
                    height: auto
                    border: none
                    display: block

                .be-block-video-empty
                    position: absolute
                    inset: 0
                    display: flex
                    flex-direction: column
                    align-items: center
                    justify-content: center
                    color: rgba(255,255,255,0.7)
                    gap: 6px
                    font-size: 11px
                    pointer-events: none

                    i
                        font-size: 20px

                .be-block-video-overlay
                    position: absolute
                    inset: 0
                    cursor: move

            .be-block-link-badge
                position: absolute
                top: -7px
                right: -7px
                width: 16px
                height: 16px
                background: #10b981
                border: 2px solid #fff
                border-radius: 50%
                z-index: 9
                display: flex
                align-items: center
                justify-content: center
                pointer-events: none

                i
                    font-size: 8px
                    color: #fff

            .be-block-delete
                position: absolute
                top: -8px
                left: -8px
                width: 18px
                height: 18px
                background: #ef4444
                border: 2px solid #fff
                border-radius: 4px
                cursor: pointer
                z-index: 10
                display: flex
                align-items: center
                justify-content: center

                i
                    font-size: 9px
                    color: #fff
                    pointer-events: none

                &:hover
                    background: #dc2626

            .be-block-rotate
                position: absolute
                top: -8px
                right: -8px
                width: 18px
                height: 18px
                background: #10b981
                border: 2px solid #fff
                border-radius: 4px
                cursor: grab
                z-index: 10
                display: flex
                align-items: center
                justify-content: center

                i
                    font-size: 9px
                    color: #fff
                    pointer-events: none

                &:active
                    cursor: grabbing

                &:hover
                    background: #059669

            .be-block-resize
                position: absolute
                bottom: -8px
                right: -8px
                width: 18px
                height: 18px
                background: #3b82f6
                border: 2px solid #fff
                border-radius: 4px
                cursor: nwse-resize
                z-index: 10
                display: flex
                align-items: center
                justify-content: center

                i
                    font-size: 9px
                    color: #fff
                    transform: rotate(90deg)
                    pointer-events: none

                &:hover
                    background: #3b82f6

            // Text-Block: Handles vollständig außerhalb des Textbereichs
            &.be-block--text
                .be-block-delete
                    top: -20px
                    left: -20px
                .be-block-rotate
                    top: -20px
                    right: -20px
                .be-block-resize
                    bottom: -20px
                    right: -20px

    // ── Multi-Select Bbox ──────────────────────────────────────────
    .be-multi-bbox
        outline: 2px dashed #6366f1
        outline-offset: 2px

        .be-block-delete
            position: absolute
            top: -8px
            left: -8px
            width: 18px
            height: 18px
            background: #ef4444
            border: 2px solid #fff
            border-radius: 4px
            cursor: pointer
            z-index: 10
            display: flex
            align-items: center
            justify-content: center
            &:hover
                background: #dc2626
            i
                font-size: 9px
                color: #fff
                pointer-events: none

        .be-block-rotate
            position: absolute
            top: -8px
            right: -8px
            width: 18px
            height: 18px
            background: #10b981
            border: 2px solid #fff
            border-radius: 4px
            cursor: grab
            z-index: 10
            display: flex
            align-items: center
            justify-content: center
            &:active
                cursor: grabbing
            &:hover
                background: #059669
            i
                font-size: 9px
                color: #fff
                pointer-events: none

        .be-block-resize
            position: absolute
            bottom: -8px
            right: -8px
            width: 18px
            height: 18px
            background: #3b82f6
            border: 2px solid #fff
            border-radius: 4px
            cursor: nwse-resize
            z-index: 10
            display: flex
            align-items: center
            justify-content: center
            &:hover
                background: #3b82f6
            i
                font-size: 9px
                color: #fff
                transform: rotate(90deg)
                pointer-events: none

    // ── Add menu ─────────────────────────────────────────────────
    .be-add-wrap
        position: relative

    .be-add-dropdown
        position: absolute
        bottom: calc(100% + 6px)
        right: 0
        background: #fff
        border: 1px solid #e5e7eb
        border-radius: 8px
        box-shadow: 0 4px 16px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)
        padding: 4px
        display: flex
        flex-direction: column
        min-width: 148px
        z-index: 50

    .be-add-opt
        display: flex
        align-items: center
        gap: 9px
        padding: 7px 11px
        border: none
        background: transparent
        color: #374151
        border-radius: 5px
        cursor: pointer
        font-size: 12px
        line-height: 1
        text-align: left
        width: 100%
        box-sizing: border-box
        transition: background 0.1s

        &:hover
            background: #eff6ff
            color: #1d4ed8

        i
            width: 13px
            text-align: center
            font-size: 11px
            color: #6b7280
            flex-shrink: 0

        &:hover i
            color: #3b82f6

    .be-add-trigger
        height: 28px
        padding: 0 10px
        border-radius: 4px
        background: #3b82f6
        border: none
        color: #fff
        cursor: pointer
        display: flex
        align-items: center
        gap: 5px
        font-size: 12px
        font-weight: 500
        transition: background 0.15s
        white-space: nowrap

        &:hover
            background: #3b82f6

        &.is-open
            background: #1d4ed8

            i.el-icon-fa-plus
                transform: rotate(45deg)

        i
            pointer-events: none
            font-size: 11px
            transition: transform 0.2s

    .be-add-sub-wrap
        position: relative

    .be-add-opt.has-sub
        justify-content: space-between
        .be-add-arrow
            margin-left: auto
            font-size: 9px
            color: #9ca3af
            transition: transform 0.15s
            &.is-open
                transform: rotate(90deg)

    .be-add-sub
        position: absolute
        bottom: 0
        right: calc(100% + 2px)
        background: #fff
        border: 1px solid #e5e7eb
        border-radius: 8px
        box-shadow: 0 4px 16px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)
        padding: 4px
        flex-direction: column
        min-width: 148px
        z-index: 60

    .be-add-sub-opt
        display: flex
        align-items: center
        gap: 9px
        padding: 6px 10px
        border: none
        background: transparent
        color: #374151
        border-radius: 5px
        cursor: pointer
        font-size: 12px
        text-align: left
        width: 100%
        transition: background 0.1s

        &:hover
            background: #eff6ff
            color: #1d4ed8

        .be-add-shape-icon
            width: 14px
            height: 14px
            flex-shrink: 0
            color: #6b7280
            display: flex
            align-items: center
            justify-content: center
            svg
                width: 100%
                height: 100%

        &:hover .be-add-shape-icon
            color: #3b82f6

    // ── Kontextmenü ──────────────────────────────────────────────
    .be-context-menu
        position: fixed
        background: #fff
        border: 1px solid #e5e7eb
        border-radius: 8px
        box-shadow: 0 8px 24px rgba(0,0,0,0.13), 0 2px 6px rgba(0,0,0,0.07)
        padding: 4px
        min-width: 210px
        z-index: 9999

        .be-cm-title
            font-size: 10px
            font-weight: 600
            color: #9ca3af
            text-transform: uppercase
            letter-spacing: 0.05em
            padding: 5px 10px 2px
            margin: 0

        .be-cm-sep
            height: 1px
            background: #f3f4f6
            margin: 3px 0

        .be-cm-pos-row
            display: flex
            gap: 6px
            padding: 5px 10px 6px

        .be-cm-pos-field
            display: flex
            align-items: center
            gap: 4px
            flex: 1

            label
                font-size: 11px
                font-weight: 600
                color: #6b7280
                min-width: 10px
                user-select: none

            span
                font-size: 11px
                color: #9ca3af

        .be-cm-pos-input,
        .be-cm-pos-select
            flex: 1
            min-width: 0
            height: 24px
            padding: 0 5px
            border: 1px solid #e5e7eb
            border-radius: 4px
            font-size: 12px
            color: #374151
            background: #fff

            &:focus
                outline: none
                border-color: #3b82f6
                box-shadow: 0 0 0 2px rgba(59,130,246,0.15)

        .be-cm-pos-input
            text-align: right
            -moz-appearance: textfield

            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button
                -webkit-appearance: none
                margin: 0

        .be-cm-item
            display: flex
            align-items: center
            gap: 9px
            padding: 6px 10px
            border: none
            background: transparent
            color: #374151
            border-radius: 5px
            cursor: pointer
            font-size: 12px
            line-height: 1
            font-family: inherit
            text-align: left
            width: 100%
            box-sizing: border-box
            transition: background 0.1s

            &:hover
                background: #eff6ff
                color: #1d4ed8

            i
                width: 13px
                text-align: center
                font-size: 10px
                color: #9ca3af
                flex-shrink: 0

            &:hover i
                color: #3b82f6

            .be-cm-shape-icon
                width: 13px
                height: 13px
                flex-shrink: 0
                color: #9ca3af
                display: flex
                align-items: center
                justify-content: center
                svg
                    width: 100%
                    height: 100%

            &:hover .be-cm-shape-icon
                color: #3b82f6

            &.is-danger
                color: #ef4444

                i
                    color: #fca5a5

                &:hover
                    background: #fef2f2
                    color: #dc2626

                &:hover i
                    color: #dc2626

        .be-cm-sub-wrap
            position: relative

            .be-cm-item.has-sub
                cursor: default

                .be-cm-arrow
                    margin-left: auto
                    font-size: 8px !important
                    width: auto !important
                    color: #d1d5db !important

            .be-cm-sub
                position: absolute
                top: -4px
                left: calc(100% + 2px)
                background: #fff
                border: 1px solid #e5e7eb
                border-radius: 8px
                box-shadow: 0 8px 24px rgba(0,0,0,0.13), 0 2px 6px rgba(0,0,0,0.07)
                padding: 4px
                min-width: 190px
                z-index: 10000
                display: none

                &::before
                    content: ''
                    position: absolute
                    top: 0
                    right: 100%
                    width: 8px
                    height: 100%

            &:hover > .be-cm-sub,
            &:focus-within > .be-cm-sub
                display: block

        &.sub-left .be-cm-sub-wrap .be-cm-sub
            left: auto
            right: calc(100% + 2px)

            &::before
                right: auto
                left: 100%

        .be-cm-anim-sub
            min-width: 200px !important

        .be-cm-anim-list
            max-height: 280px
            overflow-y: auto

        .be-cm-item.is-on
            background: #eff6ff
            color: #1d4ed8

            i
                color: #3b82f6

    // ── Block toolbar bar ────────────────────────────────────────
    .be-block-bar
        display: flex
        align-items: center
        gap: 2px
        padding: 0 8px
        background: #fff
        border: 1px solid #e5e7eb
        border-radius: 8px
        box-shadow: 0 2px 8px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)
        flex-wrap: nowrap
        height: 42px
        flex-shrink: 0
        margin-bottom: 8px

        &.is-disabled
            opacity: 0.4
            pointer-events: none

        &.be-block-bar--multi
            gap: 8px
            padding: 0 12px
            border-color: #c7d2fe
            background: #f5f3ff

    .be-multi-label
        font-size: 13px
        font-weight: 500
        color: #4f46e5
        white-space: nowrap

    .be-tb-sep
        width: 1px
        height: 16px
        background: #e5e7eb
        margin: 0 4px
        flex-shrink: 0

    .be-tb-label
        font-size: 11px
        color: #6b7280
        white-space: nowrap
        flex-shrink: 0

    .be-tb-font-select
        width: 120px
        flex-shrink: 0

        .el-input__inner
            height: 26px
            line-height: 26px
            font-size: 12px
            padding: 0 22px 0 7px

        .el-input__suffix
            right: 4px

    .be-tb-size
        font-size: 11px
        color: #6b7280
        min-width: 36px
        text-align: center
        flex-shrink: 0

    .be-tb-size-wrap
        display: flex
        align-items: center
        height: 22px
        border: 1px solid #d1d5db
        border-radius: 3px
        background: #fff
        flex-shrink: 0
        padding: 0 6px 0 4px
        gap: 2px
        &:focus-within
            border-color: #409eff

    .be-tb-size-input
        width: 32px
        height: 100%
        font-size: 11px
        color: #374151
        text-align: center
        border: none
        background: transparent
        padding: 0
        outline: none
        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button
            -webkit-appearance: none
            margin: 0
        -moz-appearance: textfield

    .be-tb-size-unit
        font-size: 11px
        color: #9ca3af
        line-height: 1
        white-space: nowrap

    .be-tb-video-url-wrap
        flex: 1
        min-width: 0
        max-width: 280px

        .be-tb-video-url-input
            width: 100%
            height: 26px
            border: 1px solid #d1d5db
            border-radius: 3px
            padding: 0 8px
            font-size: 11px
            color: #374151
            outline: none
            background: #fff
            &:focus
                border-color: #409eff
            &::placeholder
                color: #9ca3af

    .be-tb-btn
        width: 26px
        height: 26px
        padding: 0
        border: none
        background: transparent
        cursor: pointer
        border-radius: 4px
        display: flex
        align-items: center
        justify-content: center
        font-size: 12px
        color: #374151
        line-height: 1
        flex-shrink: 0

        &:hover
            background: #f3f4f6
            color: #111827

        &.is-on
            background: #dbeafe
            color: #1d4ed8

        &.be-tb-del:hover
            background: #fee2e2
            color: #dc2626

    .be-tb-shape-icon
        width: 13px
        height: 13px
        display: flex
        align-items: center
        justify-content: center
        svg
            width: 100%
            height: 100%

    .be-tb-color
        width: 16px
        height: 16px
        border-radius: 3px
        border: 2px solid #e5e7eb
        cursor: pointer
        flex-shrink: 0
        transition: transform 0.1s

        &:hover
            transform: scale(1.25)

        &.is-on
            border-color: #374151
            box-shadow: 0 0 0 2px #3b82f6

    // ── Color picker dropdown ─────────────────────────────────────
    .be-tb-color-wrap
        position: relative
        display: inline-flex
        align-items: center

    .be-tb-color-btn
        width: auto
        padding: 0 6px
        gap: 4px

        .be-tb-color-swatch
            width: 14px
            height: 14px
            border-radius: 3px
            border: 1.5px solid rgba(0,0,0,0.18)
            flex-shrink: 0

        .be-tb-caret
            font-size: 7px
            color: #9ca3af
            pointer-events: none

    .be-color-dropdown
        position: absolute
        top: calc(100% + 8px)
        left: 50%
        transform: translateX(-50%)
        background: #fff
        border: 1px solid #e5e7eb
        border-radius: 8px
        box-shadow: 0 4px 16px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)
        padding: 10px
        z-index: 200
        min-width: 196px

        &::before
            content: ''
            position: absolute
            top: -5px
            left: var(--arrow-left, 50%)
            transform: translateX(-50%)
            border-left: 5px solid transparent
            border-right: 5px solid transparent
            border-bottom: 5px solid #e5e7eb

        &::after
            content: ''
            position: absolute
            top: -4px
            left: var(--arrow-left, 50%)
            transform: translateX(-50%)
            border-left: 4px solid transparent
            border-right: 4px solid transparent
            border-bottom: 4px solid #fff

        &.is-dragged
            &::before, &::after
                display: none

        .be-cd-drag-handle
            height: 14px
            display: flex
            align-items: center
            justify-content: center
            cursor: grab
            margin: -10px -10px 8px
            border-radius: 7px 7px 0 0
            background: #f3f4f6
            border-bottom: 1px solid #e5e7eb
            flex-shrink: 0
            user-select: none

            &::before
                content: ''
                width: 28px
                height: 3px
                border-radius: 2px
                background: #d1d5db

            &:hover::before
                background: #9ca3af

            &:active
                cursor: grabbing

        // padding:0 dropdowns (two-column layouts)
        &[style*="padding:0"] .be-cd-drag-handle,
        &[style*="padding: 0"] .be-cd-drag-handle
            margin: 0 0 0 0
            border-radius: 7px 7px 0 0

        .be-cd-section
            margin-bottom: 4px

        .be-cd-label
            font-size: 10px
            font-weight: 600
            color: #9ca3af
            text-transform: uppercase
            letter-spacing: 0.04em
            margin-bottom: 6px
            display: block

        .be-cd-swatches
            display: flex
            flex-wrap: wrap
            gap: 5px

        .be-cd-swatch
            width: 20px
            height: 20px
            border-radius: 4px
            border: 2px solid transparent
            cursor: pointer
            transition: transform 0.1s
            flex-shrink: 0
            padding: 0

            &:hover
                transform: scale(1.2)

            &.is-on
                border-color: #374151
                box-shadow: 0 0 0 2px #3b82f6

            &--none
                background: #f3f4f6 !important
                border-color: #e5e7eb
                color: #9ca3af
                font-size: 11px
                display: flex
                align-items: center
                justify-content: center

        .be-cd-sep
            height: 1px
            background: #f3f4f6
            margin: 8px 0

        .be-cd-custom
            display: flex
            flex-direction: column
            gap: 5px

            .be-cd-label
                margin-bottom: 0
                white-space: nowrap

            .be-cd-custom-row
                display: flex
                align-items: center
                gap: 6px

        .be-cd-switch-row
            display: flex
            align-items: center
            justify-content: space-between
            margin-top: 4px

            &.is-on
                background: #dbeafe
                border-color: #93c5fd
                color: #1d4ed8

        .be-cd-two-col
            display: flex
            align-items: stretch

        .be-cd-col
            flex: 1
            padding: 10px
            display: flex
            flex-direction: column
            gap: 0

        .be-cd-col-sep
            width: 1px
            background: #f3f4f6
            flex-shrink: 0

        .be-cd-blur-row, .be-cd-xy-row
            display: flex
            align-items: center
            gap: 6px
            margin-top: 4px

        .be-cd-xy-label
            font-size: 10px
            font-weight: 600
            color: #6b7280
            width: 10px
            flex-shrink: 0

        .be-cd-blur-btn
            width: 24px
            height: 24px
            border: 1px solid #e5e7eb
            border-radius: 4px
            background: #f3f4f6
            cursor: pointer
            font-size: 14px
            display: flex
            align-items: center
            justify-content: center
            flex-shrink: 0

            &:hover
                background: #e5e7eb

        .be-cd-blur-val
            flex: 1
            text-align: center
            font-size: 12px
            color: #374151
            border: 1px solid #e5e7eb
            border-radius: 4px
            background: #fff
            padding: 0 2px
            height: 24px
            min-width: 0
            -moz-appearance: textfield
            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button
                -webkit-appearance: none
            &:focus
                outline: none
                border-color: #3b82f6

        .be-cd-opacity-row
            display: flex
            align-items: center
            gap: 8px
            margin-top: 4px

        .be-cd-opacity-slider
            flex: 1
            height: 4px
            cursor: pointer
            accent-color: #3b82f6

        .be-cd-opacity-val
            font-size: 11px
            color: #6b7280
            min-width: 32px
            text-align: right
            flex-shrink: 0

        .be-cd-hex-input
            flex: 1
            min-width: 0
            height: 26px
            padding: 0 7px
            border: 1px solid #e5e7eb
            border-radius: 4px
            font-size: 12px
            font-family: ui-monospace, monospace
            color: #374151

            &:focus
                outline: none
                border-color: #3b82f6
                box-shadow: 0 0 0 2px rgba(59,130,246,0.15)

        .be-cd-color-input
            width: 26px
            height: 26px
            padding: 1px 2px
            border: 1px solid #e5e7eb
            border-radius: 4px
            cursor: pointer
            background: transparent
            flex-shrink: 0

        .be-cd-fill-mode-switch
            display: flex
            gap: 4px
            margin-bottom: 6px

            button
                flex: 1
                height: 24px
                border: 1px solid #e5e7eb
                border-radius: 4px
                background: #f3f4f6
                font-size: 11px
                cursor: pointer
                color: #374151

                &:hover
                    background: #e5e7eb

                &.is-on
                    background: #3b82f6
                    border-color: #3b82f6
                    color: #fff

        .be-cd-border-style-row
            display: flex
            gap: 4px
            margin-top: 2px

            button
                flex: 1
                height: 28px
                border: 1px solid #e5e7eb
                border-radius: 4px
                background: #f3f4f6
                cursor: pointer
                display: flex
                align-items: center
                justify-content: center
                padding: 0 4px

                &:hover
                    background: #e5e7eb

                &.is-on
                    background: #dbeafe
                    border-color: #3b82f6

        .be-cd-border-preview
            width: 28px
            height: 0
            border-bottom-width: 2px
            border-bottom-color: #374151

        .be-cd-border-sides-row
            display: flex
            gap: 4px
            margin-top: 4px

        .be-bside-btn
            flex: 1
            height: 28px
            border: 1px solid #e5e7eb
            border-radius: 4px
            background: #f9fafb
            cursor: pointer
            display: flex
            align-items: center
            justify-content: center
            padding: 0
            transition: background 0.1s, border-color 0.1s

            &:hover
                background: #e5e7eb

            &.is-on
                background: #dbeafe
                border-color: #3b82f6

                .be-bside-icon::after
                    background: #3b82f6

        .be-bside-icon
            width: 14px
            height: 14px
            border: 1.5px solid #d1d5db
            border-radius: 1px
            position: relative

            &::after
                content: ''
                position: absolute
                background: #9ca3af

            &.be-bside--top::after
                top: -2px
                left: 0
                right: 0
                height: 2px

            &.be-bside--bottom::after
                bottom: -2px
                left: 0
                right: 0
                height: 2px

            &.be-bside--left::after
                left: -2px
                top: 0
                bottom: 0
                width: 2px

            &.be-bside--right::after
                right: -2px
                top: 0
                bottom: 0
                width: 2px

        .be-cd-filter-row
            display: flex
            gap: 6px
            margin-top: 4px

        .be-cd-filter-btn
            flex: 1
            height: 36px
            border: 1px solid #e5e7eb
            border-radius: 4px
            background: #f9fafb
            cursor: pointer
            display: flex
            align-items: center
            justify-content: center
            gap: 5px
            font-size: 11px
            font-weight: 600
            color: #374151
            transition: background 0.1s, border-color 0.1s

            &:hover
                background: #e5e7eb

            &.is-on
                background: #dbeafe
                border-color: #3b82f6
                color: #1d4ed8

        .be-cd-filter-swatch
            width: 22px
            height: 14px
            border-radius: 2px
            background: linear-gradient(135deg, #f87171 0%, #60a5fa 50%, #4ade80 100%)
            flex-shrink: 0

            &.be-cd-filter--bw
                filter: grayscale(100%)

            &.be-cd-filter--sepia
                filter: sepia(100%)

        .be-cd-dt-swatch-wrap
            position: relative
            flex-shrink: 0

        .be-cd-dt-badge
            position: absolute
            top: -5px
            right: -5px
            background: #3b82f6
            color: #fff
            font-size: 7px
            font-weight: 600
            line-height: 1
            padding: 2px 3px
            border-radius: 3px
            border: 1.5px solid #fff
            pointer-events: none
            white-space: nowrap

        .be-link-dropdown
            width: 320px
            padding: 0
            overflow: visible

    // ── Canvas footer ─────────────────────────────────────────────
    .be-canvas-footer
        display: flex
        align-items: center
        margin-top: 8px
        background: #f9fafb
        border: 1px solid #e5e7eb
        border-radius: 6px
        padding: 5px 10px
        gap: 0

        .be-footer-group
            display: flex
            align-items: center
            gap: 5px

        .be-footer-spacer
            flex: 1

        .be-footer-sep
            width: 1px
            height: 18px
            background: #e5e7eb
            margin: 0 10px
            flex-shrink: 0

        .be-overlay-label
            font-size: 11px
            color: #6b7280
            flex-shrink: 0

        .be-blur-btn
            width: 20px
            height: 20px
            border: 1px solid #d1d5db
            border-radius: 3px
            background: #fff
            font-size: 13px
            cursor: pointer
            display: flex
            align-items: center
            justify-content: center
            padding: 0
            line-height: 1
            flex-shrink: 0
            &:hover
                background: #f3f4f6

        .be-blur-input
            width: 36px
            height: 20px
            font-size: 11px
            border: 1px solid #d1d5db
            border-radius: 3px
            padding: 0 4px
            text-align: center
            flex-shrink: 0
            &:focus
                outline: none
                border-color: #3b82f6
            &::-webkit-inner-spin-button,
            &::-webkit-outer-spin-button
                -webkit-appearance: none
                margin: 0
            -moz-appearance: textfield

    .be-ep
        position: relative
        margin-top: 6px
        background: #f9fafb
        border: 1px solid #e5e7eb
        border-radius: 6px
        padding: 8px 26px 8px 8px

        .be-ep-close
            position: absolute
            top: 4px
            right: 4px
            width: 18px
            height: 18px
            border: none
            background: none
            cursor: pointer
            color: #9ca3af
            font-size: 14px
            display: flex
            align-items: center
            justify-content: center
            border-radius: 3px
            padding: 0
            line-height: 1
            z-index: 1
            &:hover
                background: #e5e7eb
                color: #374151

        .be-ep-groups
            display: flex
            gap: 8px
            align-items: stretch
            flex-wrap: wrap

        .be-ep-group
            background: #fff
            border: 1px solid #e5e7eb
            border-radius: 7px
            padding: 8px 10px
            display: flex
            flex-direction: column
            gap: 7px
            flex-shrink: 0
            &--fill
                flex: 1

        .be-ep-group-title
            font-size: 10px
            font-weight: 600
            color: #9ca3af
            text-transform: uppercase
            letter-spacing: 0.06em

        .be-ep-group-body
            display: flex
            gap: 0
            align-items: flex-start

        .be-ep-sub-col
            display: flex
            flex-direction: column
            gap: 6px

        .be-ep-sub-divider
            width: 1px
            background: #f0f0f0
            align-self: stretch
            margin: 0 10px

        .be-ep-pos-btns
            display: flex
            gap: 4px

        .be-ep-pos-btn
            width: 30px
            height: 22px
            border: 2px solid transparent
            border-radius: 4px
            background: #f3f4f6
            cursor: pointer
            padding: 0
            display: flex
            align-items: center
            justify-content: center
            color: #9ca3af
            outline: 1px solid rgba(0,0,0,0.1)
            transition: color 0.1s, border-color 0.1s
            &:hover
                color: #374151
            &.is-active
                border-color: #3b82f6
                outline-color: #3b82f6
                color: #3b82f6

        .be-ep-filter-btns
            display: flex
            gap: 5px

        .be-ep-filter-btn
            display: flex
            flex-direction: column
            align-items: center
            gap: 3px
            padding: 4px
            background: none
            border: 2px solid transparent
            border-radius: 5px
            cursor: pointer
            outline: 1px solid rgba(0,0,0,0.08)
            transition: border-color 0.1s, outline-color 0.1s
            color: #374151
            &:hover
                outline-color: #9ca3af
            &.is-active
                border-color: #3b82f6
                outline-color: #3b82f6
            span
                font-size: 10px
                line-height: 1.2
                white-space: nowrap

        .be-ep-filter-dt-swatch-wrap
            position: relative

        .be-ep-filter-dt-swatch
            width: 36px
            height: 22px
            border-radius: 3px

        .be-ep-dt-badge
            position: absolute
            top: -5px
            right: -5px
            background: #3b82f6
            color: #fff
            font-size: 8px
            font-weight: 600
            line-height: 1
            padding: 2px 4px
            border-radius: 3px
            border: 1.5px solid #fff
            pointer-events: none
            white-space: nowrap

        .be-ep-crop-divider
            height: 1px
            background: #e5e7eb
            margin: 2px 0

        .be-ep-crop-btns
            display: flex
            flex-direction: column
            gap: 4px

        .be-ep-crop-btn
            display: inline-flex
            align-items: center
            font-size: 11px
            padding: 3px 7px
            border-radius: 4px
            border: 1px solid #d1d5db
            background: #f9fafb
            color: #374151
            cursor: pointer
            white-space: nowrap
            transition: background 0.1s, border-color 0.1s
            &:hover
                background: #f3f4f6
                border-color: #9ca3af
            &.is-danger
                color: #ef4444
                border-color: #fca5a5
                background: #fff5f5
                &:hover
                    background: #fee2e2
                    border-color: #ef4444

        .be-ep-vdivider
            width: 1px
            background: #e5e7eb
            flex-shrink: 0
            margin: 0 10px

        .be-ep-dt-preview-swatch
            width: 20px
            height: 14px
            border-radius: 2px
            flex-shrink: 0

        .be-ep-dt-panel
            border-top: 1px solid #e5e7eb
            margin-top: 8px
            padding-top: 8px


        .be-ep-dt-panel-header
            display: flex
            align-items: center
            justify-content: space-between
            margin-bottom: 6px

        .be-ep-duotone-clear
            display: inline-flex
            align-items: center
            font-size: 11px
            padding: 3px 7px
            border-radius: 4px
            border: 1px solid #fca5a5
            background: #fff5f5
            color: #ef4444
            cursor: pointer
            white-space: nowrap
            transition: background 0.1s, border-color 0.1s
            &:hover
                background: #fee2e2
                border-color: #ef4444

        .be-ep-dt-grid
            display: grid
            grid-template-columns: repeat(7, 1fr)
            gap: 3px

        .be-ep-dt-btn
            display: flex
            flex-direction: column
            align-items: center
            gap: 2px
            background: none
            border: 2px solid transparent
            border-radius: 4px
            padding: 2px
            cursor: pointer
            outline: 1px solid rgba(0,0,0,0.08)
            transition: border-color 0.1s, outline-color 0.1s
            &:hover
                outline-color: #9ca3af
            &.is-active
                border-color: #6366f1
                outline-color: #6366f1
            span
                font-size: 8px
                color: #374151
                line-height: 1.2
                text-align: center
                white-space: nowrap
                overflow: hidden
                text-overflow: ellipsis
                max-width: 100%

        .be-ep-dt-defs
            position: absolute
            width: 0
            height: 0
            overflow: hidden
            pointer-events: none

        .be-ep-dt-swatch
            width: 100%
            border-radius: 3px
            overflow: hidden
            position: relative

        .be-ep-dt-img
            width: 100%
            height: 100%
            object-fit: cover
            display: block

        .be-ep-dt-img-fallback
            width: 100%
            height: 100%

        .be-ep-duotone-custom
            display: flex
            flex-direction: row
            gap: 10px
            padding: 6px 0
            border-bottom: 1px solid #e5e7eb

        .be-ep-dc-row
            display: flex
            align-items: center
            gap: 4px
            flex: 1
            min-width: 0
            label
                font-size: 10px
                color: #6b7280
                flex-shrink: 0
            .be-ep-dc-inputs
                display: flex
                align-items: center
                gap: 4px
                flex: 1
                min-width: 0
            input[type="color"]
                width: 28px
                height: 22px
                border: 1px solid #d1d5db
                border-radius: 3px
                padding: 1px 2px
                cursor: pointer
                background: #fff
                flex-shrink: 0

        .be-ep-dc-hex
            flex: 1
            min-width: 0
            height: 22px
            font-size: 11px
            font-family: monospace
            border: 1px solid #d1d5db
            border-radius: 3px
            padding: 2px 5px
            color: #374151
            &:focus
                outline: none
                border-color: #6366f1

        .be-duotone-defs
            position: absolute
            width: 0
            height: 0
            overflow: hidden
            pointer-events: none

        .be-ep-label
            font-size: 11px
            color: #6b7280
            font-weight: 600
            padding-right: 20px

        .be-ep-row
            display: flex
            align-items: center
            gap: 4px

            input[type="number"]
                width: 38px
                height: 22px
                font-size: 12px
                border: 1px solid #d1d5db
                border-radius: 4px
                padding: 0 4px
                text-align: center
                background: #fff
                -moz-appearance: textfield
                &::-webkit-inner-spin-button,
                &::-webkit-outer-spin-button
                    -webkit-appearance: none
                    margin: 0
                &:focus
                    outline: none
                    border-color: #3b82f6

        .be-ep-swatches
            display: flex
            flex-wrap: wrap
            gap: 4px

        .be-ep-sw
            width: 20px
            height: 20px
            border-radius: 3px
            border: 2px solid transparent
            cursor: pointer
            padding: 0
            flex-shrink: 0
            box-sizing: border-box
            outline: 1px solid rgba(0,0,0,0.12)
            transition: transform 0.1s
            &:hover
                transform: scale(1.15)
                z-index: 1
                position: relative
            &.is-on
                border-color: #3b82f6
                outline-color: #3b82f6
            &.be-ep-sw--none
                background: repeating-linear-gradient(45deg, #e5e7eb, #e5e7eb 2px, #fff 2px, #fff 6px)

        .be-ep-custom
            display: flex
            align-items: center
            gap: 4px

            input[type="color"]
                width: 22px
                height: 22px
                padding: 1px 2px
                border: 1px solid #e5e7eb
                border-radius: 4px
                cursor: pointer
                background: transparent
                flex-shrink: 0

            input[type="text"]
                flex: 1
                min-width: 0
                height: 22px
                padding: 0 5px
                border: 1px solid #e5e7eb
                border-radius: 4px
                font-size: 11px
                font-family: ui-monospace, monospace
                color: #374151
                background: #fff
                &:focus
                    outline: none
                    border-color: #3b82f6

    .be-bcp
        margin-top: 6px
        background: #f9fafb
        border: 1px solid #e5e7eb
        border-radius: 6px
        padding: 8px 12px
        display: flex
        flex-direction: column
        gap: 8px

        .be-bcp-header
            display: flex
            align-items: center
            justify-content: space-between

        .be-bcp-fill-switch
            display: flex
            border: 1px solid #e5e7eb
            border-radius: 5px
            overflow: hidden
            flex-shrink: 0

        .be-bcp-fill-btn
            padding: 4px 10px
            font-size: 11px
            background: none
            border: none
            cursor: pointer
            color: #6b7280
            white-space: nowrap
            &.is-active
                background: #3b82f6
                color: #fff
            &:hover:not(.is-active)
                background: #f3f4f6
                color: #374151

        .be-bcp-close
            width: 22px
            height: 22px
            border: none
            background: none
            cursor: pointer
            color: #9ca3af
            font-size: 16px
            line-height: 1
            display: flex
            align-items: center
            justify-content: center
            border-radius: 4px
            padding: 0
            flex-shrink: 0
            &:hover
                background: #e5e7eb
                color: #374151

        .be-bcp-swatches
            display: flex
            flex-wrap: wrap
            gap: 4px

        .be-bcp-sw
            width: 20px
            height: 20px
            border-radius: 4px
            border: 2px solid transparent
            cursor: pointer
            padding: 0
            flex-shrink: 0
            box-sizing: border-box
            transition: transform 0.1s
            outline: 1px solid rgba(0,0,0,0.08)
            &:hover
                transform: scale(1.2)
                z-index: 1
                position: relative
            &.is-on
                border-color: #3b82f6
                outline-color: #3b82f6

        .be-bcp-gsw
            width: 36px
            height: 20px
            border-radius: 4px
            border: 2px solid transparent
            cursor: pointer
            padding: 0
            flex-shrink: 0
            box-sizing: border-box
            transition: transform 0.1s
            outline: 1px solid rgba(0,0,0,0.08)
            &:hover
                transform: scale(1.15)
                z-index: 1
                position: relative
            &.is-on
                border-color: #3b82f6
                outline-color: #3b82f6

        .be-bcp-body
            display: flex
            align-items: center
            gap: 6px
            flex-wrap: wrap

        .be-bcp-hex
            flex: 1
            min-width: 80px

        .be-bcp-grad-preview
            width: 60px
            height: 26px
            border-radius: 4px
            border: 1px solid #e5e7eb
            flex-shrink: 0

        .be-bcp-vsp
            width: 1px
            height: 18px
            background: #e5e7eb
            flex-shrink: 0

        .be-bcp-type-switch
            display: flex
            border: 1px solid #e5e7eb
            border-radius: 5px
            overflow: hidden
            flex-shrink: 0

        .be-bcp-type-btn
            padding: 3px 8px
            font-size: 11px
            background: none
            border: none
            cursor: pointer
            color: #6b7280
            &.is-active
                background: #3b82f6
                color: #fff
            &:hover:not(.is-active)
                background: #f3f4f6

        .be-bcp-angle
            display: flex
            align-items: center
            gap: 2px
            span
                font-size: 11px
                color: #9ca3af

        .be-bcp-angle-input
            width: 58px
            height: 26px
            font-size: 12px
            border: 1px solid #e5e7eb
            border-radius: 4px
            padding: 0 4px
            text-align: center
            &:focus
                outline: none
                border-color: #3b82f6

        .be-bcp-arrow
            font-size: 10px
            color: #9ca3af
            flex-shrink: 0

    .be-lp-wrap
        position: relative

    .be-lp-backdrop
        position: fixed
        inset: 0
        z-index: 199

    .be-lp
        position: absolute
        top: calc(100% + 4px)
        left: 0
        z-index: 200
        background: #f9fafb
        border: 1px solid #e5e7eb
        border-radius: 6px
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.10), 0 1px 4px rgba(0, 0, 0, 0.06)
        padding: 10px 12px
        min-width: 240px
        display: flex
        flex-direction: column
        gap: 5px

        .be-lp-divider
            height: 1px
            background: #e5e7eb
            margin: 3px 0

        .be-lp-row
            display: flex
            align-items: center
            gap: 8px

        .be-lp-label
            font-size: 11px
            font-weight: 500
            color: #374151
            white-space: nowrap
            min-width: 88px

        .be-lp-unit
            font-size: 11px
            color: #9ca3af

        .be-lp-select
            flex: 1
            height: 26px
            font-size: 12px
            border: 1px solid #d1d5db
            border-radius: 4px
            background: #fff
            color: #374151
            padding: 0 4px
            cursor: pointer
            &:focus
                outline: none
                border-color: #6b7280

    .be-canvas-bg-color
        position: absolute
        inset: 0

    .be-swatch
        width: 20px
        height: 20px
        border-radius: 3px
        border: 2px solid transparent
        cursor: pointer
        transition: transform 0.1s
        box-sizing: border-box
        flex-shrink: 0

        &:hover
            transform: scale(1.2)

        &.is-selected
            border-color: #3b82f6

        &.be-swatch--transparent
            background: repeating-conic-gradient(#d1d5db 0% 25%, #fff 0% 50%) 0 0 / 8px 8px

    // ── Preview mode ─────────────────────────────────────────────
    .be-preview
        border-radius: 4px
        overflow: hidden
        position: relative

        .be-preview-canvas
            position: relative
            background: #1e293b
            overflow: hidden

            .be-preview-bg-wrap
                position: absolute
                inset: 0
                overflow: hidden

                .be-preview-bg-img
                    position: absolute
                    inset: 0
                    width: 100%
                    height: 100%
                    object-fit: cover

            .be-preview-video
                position: absolute
                inset: 0
                width: 100%
                height: 100%
                object-fit: cover
                pointer-events: none

            .be-preview-bg-color
                position: absolute
                inset: 0

            .be-preview-overlay
                position: absolute
                inset: 0

            .be-preview-block
                position: absolute

            .be-preview-text
                white-space: pre-wrap
                text-shadow: 0 1px 2px rgba(0,0,0,0.5)
                line-height: 1.2
                p, div
                    margin: 0

            .be-preview-video-block
                background: rgba(0,0,0,0.6)
                display: flex
                align-items: center
                justify-content: center
                &::after
                    content: '▶'
                    color: rgba(255,255,255,0.6)
                    font-size: 10px

            .be-preview-img-inner
                width: 100%
                height: 100%
                overflow: hidden
                border-radius: inherit
                display: flex
                align-items: center
                justify-content: center

            .be-preview-nav-btn
                --be-nav-size: 26px
                position: absolute
                top: 50%
                transform: translateY(-50%)
                width: var(--be-nav-size)
                height: var(--be-nav-size)
                border-radius: 50%
                background: rgba(255,255,255,0.85)
                border: none
                cursor: pointer
                z-index: 10
                padding: 0
                pointer-events: auto
                &::after
                    content: ''
                    display: block
                    width: calc(var(--be-nav-size) * 0.27)
                    height: calc(var(--be-nav-size) * 0.27)
                    border-top: 2px solid #4b5563
                    border-right: 2px solid #4b5563
                    position: absolute
                    top: 50%
                &:hover
                    background: rgba(255,255,255,1)

            .be-preview-nav-prev
                left: calc(var(--be-nav-size) * 16 / 44)
                &::after
                    transform: translateY(-50%) rotate(-135deg)
                    left: calc(50% - 3px)

            .be-preview-nav-next
                right: calc(var(--be-nav-size) * 16 / 44)
                &::after
                    transform: translateY(-50%) rotate(45deg)
                    right: calc(50% - 3px)

        .be-preview-badge
            display: flex
            align-items: center
            gap: 6px
            padding: 4px 10px
            background: #f9fafb
            border: 1px solid #e5e7eb
            border-top: none
            font-size: 12px
            color: #6b7280

            .be-preview-dots
                display: flex
                align-items: center
                gap: 4px
                margin-left: auto

            .be-preview-dot
                width: 7px
                height: 7px
                border-radius: 50%
                background: #d1d5db
                cursor: pointer
                transition: background 0.15s, transform 0.15s
                flex-shrink: 0

                &:hover
                    background: #9ca3af

                &.is-active
                    background: #3b82f6
                    transform: scale(1.2)

        .be-preview-placeholder
            display: flex
            align-items: center
            gap: 8px
            padding: 14px
            color: #9ca3af
            font-size: 13px
            background: #f9fafb
            border: 1px solid #e5e7eb
            border-radius: 4px

    // ── Animation UI ─────────────────────────────────────────────
    .be-cd-anim-select
        display: flex
        flex-direction: column
        gap: 2px

    .be-cd-anim-opt
        width: 100%
        text-align: left
        padding: 5px 8px
        border: 1px solid #e5e7eb
        border-radius: 4px
        background: #f9fafb
        font-size: 12px
        cursor: pointer
        color: #374151

        &:hover
            background: #e5e7eb

        &.is-on
            background: #dbeafe
            border-color: #3b82f6
            color: #1d4ed8
            font-weight: 600

    .be-cd-preview-btn
        display: flex
        align-items: center
        gap: 6px
        width: 100%
        padding: 6px 10px
        border: 1px solid #3b82f6
        border-radius: 4px
        background: #eff6ff
        color: #1d4ed8
        font-size: 12px
        cursor: pointer

        &:hover
            background: #dbeafe

    // ── Animations-Zeitleiste ──────────────────────────────────
    .be-timeline
        background: #111827
        border: 1px solid #374151
        border-radius: 6px
        overflow: hidden
        user-select: none

        .be-tl-header
            display: flex
            height: 24px
            border-bottom: 1px solid #374151

            .be-tl-label-col
                width: 110px
                flex-shrink: 0
                border-right: 1px solid #374151
                background: #1f2937
                display: flex
                align-items: center
                justify-content: center

            .be-tl-type-col
                width: 150px
                flex-shrink: 0
                border-right: 1px solid #374151
                background: #1f2937

            .be-tl-ruler
                flex: 1
                position: relative
                background: #1f2937

                .be-tl-ruler-tick
                    position: absolute
                    top: 0
                    bottom: 0
                    display: flex
                    align-items: center
                    padding-left: 4px
                    border-left: 1px solid #374151

                    span
                        font-size: 10px
                        color: #6b7280
                        white-space: nowrap

        .be-tl-play-all
            background: none
            border: none
            color: #6b7280
            cursor: pointer
            font-size: 10px
            padding: 0 6px
            height: 100%
            display: flex
            align-items: center
            gap: 4px

            &:hover
                color: #10b981

        .be-tl-rows
            overflow-y: auto
            max-height: 220px

        .be-tl-row
            display: flex
            height: 34px
            border-bottom: 1px solid #1f2937
            cursor: pointer

            &:last-child
                border-bottom: none

            &:hover
                background: rgba(255,255,255,0.02)

            &.is-active
                background: rgba(59,130,246,0.08)

                .be-tl-label
                    color: #93c5fd

            .be-tl-label
                width: 110px
                flex-shrink: 0
                display: flex
                align-items: center
                gap: 6px
                padding: 0 10px
                font-size: 11px
                color: #9ca3af
                border-right: 1px solid #374151
                overflow: hidden
                white-space: nowrap

                span
                    overflow: hidden
                    text-overflow: ellipsis

            .be-tl-type-pick
                width: 150px
                flex-shrink: 0
                display: flex
                align-items: center
                gap: 4px
                padding: 0 6px
                border-right: 1px solid #374151

            .be-tl-type-select
                flex: 1
                height: 22px
                background: #1f2937
                border: 1px solid #374151
                border-radius: 3px
                color: #d1d5db
                font-size: 11px
                cursor: pointer
                padding: 0 4px
                min-width: 0

                &:focus
                    outline: none
                    border-color: #3b82f6

            .be-tl-play-btn
                width: 22px
                height: 22px
                flex-shrink: 0
                background: #374151
                border: none
                border-radius: 3px
                color: #9ca3af
                cursor: pointer
                display: flex
                align-items: center
                justify-content: center
                font-size: 9px

                &:hover:not(:disabled)
                    background: #10b981
                    color: #fff

                &:disabled
                    opacity: 0.3
                    cursor: default

            .be-tl-track
                flex: 1
                position: relative
                cursor: crosshair

            .be-tl-bar
                position: absolute
                top: 6px
                height: 22px
                border-radius: 4px
                cursor: grab
                display: flex
                align-items: center
                overflow: hidden
                min-width: 8px
                box-sizing: border-box

                &:active
                    cursor: grabbing

                .be-tl-bar-resize
                    width: 8px
                    height: 100%
                    cursor: col-resize
                    flex-shrink: 0
                    margin-left: auto
                    background: rgba(0,0,0,0.25)
                    display: flex
                    align-items: center
                    justify-content: center

                    &::after
                        content: ''
                        width: 2px
                        height: 12px
                        background: rgba(255,255,255,0.5)
                        border-radius: 1px

            .be-tl-no-anim
                position: absolute
                inset: 0
                display: flex
                align-items: center
                padding-left: 10px
                gap: 4px
                font-size: 11px
                color: #374151
                cursor: pointer

                &:hover
                    color: #6b7280

// ── Toolbar spacer ────────────────────────────────────────────
.be-toolbar-spacer
    flex: 1

// ── Hilfe-Dialog ──────────────────────────────────────────────
.be-help-dialog
    .el-dialog__body
        padding: 16px 20px 0
        max-height: 70vh
        overflow-y: auto

    .be-help-body
        display: flex
        flex-direction: column
        gap: 0

    .be-help-divider
        height: 1px
        background: #f1f5f9
        margin: 16px 0

    .be-help-section
        .be-help-section-title
            display: flex
            align-items: center
            gap: 8px
            font-size: 11px
            font-weight: 700
            text-transform: uppercase
            letter-spacing: 0.06em
            color: #94a3b8
            margin-bottom: 10px

            i
                font-size: 11px

        .be-help-steps
            display: flex
            flex-direction: column
            gap: 6px

        .be-help-step
            display: flex
            align-items: flex-start
            gap: 12px
            padding: 10px 12px
            background: #f8fafc
            border-radius: 8px
            border: 1px solid #f1f5f9

            .be-help-step-num
                width: 24px
                height: 24px
                flex-shrink: 0
                background: #3b82f6
                color: #fff
                border-radius: 50%
                display: flex
                align-items: center
                justify-content: center
                font-size: 12px
                font-weight: 700
                margin-top: 1px

            .be-help-step-body
                display: flex
                flex-direction: column
                gap: 2px

                strong
                    font-size: 13px
                    font-weight: 600
                    color: #1e293b
                    display: block

                span
                    font-size: 12px
                    color: #64748b
                    line-height: 1.5

        .be-help-grid
            display: grid
            grid-template-columns: 1fr 1fr
            gap: 8px

        .be-help-item
            display: flex
            align-items: flex-start
            gap: 10px
            padding: 10px 12px
            background: #f8fafc
            border-radius: 8px
            border: 1px solid #f1f5f9

            .be-help-item-icon
                width: 30px
                height: 30px
                flex-shrink: 0
                background: #eff6ff
                border-radius: 7px
                display: flex
                align-items: center
                justify-content: center
                color: #3b82f6
                font-size: 12px
                font-weight: 700

                &.be-help-item-icon--text
                    font-size: 15px
                    font-family: Georgia, serif

            .be-help-item-body
                display: flex
                flex-direction: column
                gap: 2px
                min-width: 0

                strong
                    font-size: 13px
                    font-weight: 600
                    color: #1e293b
                    display: block

                span
                    font-size: 12px
                    color: #64748b
                    line-height: 1.5

    .be-help-footer
        display: flex
        align-items: center
        justify-content: space-between
        gap: 10px

// ── Tour ─────────────────────────────────────────────────────
.be-tour-ask
    position: fixed
    inset: 0
    z-index: 10000
    background: rgba(15,23,42,0.7)
    display: flex
    align-items: center
    justify-content: center

    .be-tour-ask-card
        background: #fff
        border-radius: 16px
        padding: 36px 40px 32px
        max-width: 420px
        width: 90%
        text-align: center
        box-shadow: 0 24px 60px rgba(0,0,0,0.25)

        .be-tour-ask-icon
            font-size: 36px
            color: #3b82f6
            margin-bottom: 16px

        h3
            font-size: 20px
            font-weight: 700
            color: #111827
            margin: 0 0 10px

        p
            font-size: 14px
            color: #6b7280
            margin: 0 0 28px
            line-height: 1.6

        .be-tour-ask-actions
            display: flex
            gap: 12px
            justify-content: center

.be-tour-btn-skip
    padding: 8px 20px
    border: 1px solid #e5e7eb
    border-radius: 8px
    background: #fff
    color: #6b7280
    font-size: 13px
    cursor: pointer

    &:hover
        background: #f9fafb

.be-tour-btn-start
    padding: 8px 24px
    border: none
    border-radius: 8px
    background: #3b82f6
    color: #fff
    font-size: 13px
    font-weight: 600
    cursor: pointer

    &:hover
        background: #3b82f6

.be-tour
    position: fixed
    inset: 0
    z-index: 9999
    pointer-events: none

    .be-tour-spotlight
        position: fixed
        border-radius: 8px
        box-shadow: 0 0 0 9999px rgba(15,23,42,0.6)
        border: 2px solid #3b82f6
        pointer-events: none
        transition: all 0.3s ease

    .be-tour-card
        position: fixed
        background: #fff
        border-radius: 12px
        padding: 20px 22px 18px
        width: 320px
        box-shadow: 0 12px 40px rgba(0,0,0,0.2)
        pointer-events: all
        z-index: 10000

        .be-tour-progress
            display: flex
            gap: 5px
            margin-bottom: 12px

        .be-tour-dot
            width: 6px
            height: 6px
            border-radius: 50%
            background: #e5e7eb
            transition: background 0.2s

            &.is-active
                background: #3b82f6
                width: 18px
                border-radius: 3px

        .be-tour-card-header
            display: flex
            align-items: baseline
            gap: 8px
            margin-bottom: 8px

            .be-tour-step-num
                font-size: 11px
                color: #9ca3af
                flex-shrink: 0

            h3
                font-size: 15px
                font-weight: 700
                color: #111827
                margin: 0

        .be-tour-card-text
            font-size: 13px
            color: #4b5563
            line-height: 1.6
            margin: 0 0 18px

        .be-tour-card-actions
            display: flex
            justify-content: space-between
            align-items: center

// ── Animation keyframes ───────────────────────────────────────
@keyframes be-anim-fadeIn
    from
        opacity: 0
    to
        opacity: 1

@keyframes be-anim-slideUp
    from
        opacity: 0
        transform: translateY(20px)
    to
        opacity: 1
        transform: translateY(0)

@keyframes be-anim-slideDown
    from
        opacity: 0
        transform: translateY(-20px)
    to
        opacity: 1
        transform: translateY(0)

@keyframes be-anim-slideLeft
    from
        opacity: 0
        transform: translateX(20px)
    to
        opacity: 1
        transform: translateX(0)

@keyframes be-anim-slideRight
    from
        opacity: 0
        transform: translateX(-20px)
    to
        opacity: 1
        transform: translateX(0)

@keyframes be-anim-zoomIn
    from
        opacity: 0
        transform: scale(0.75)
    to
        opacity: 1
        transform: scale(1)

@keyframes be-anim-zoomOut
    from
        opacity: 0
        transform: scale(1.25)
    to
        opacity: 1
        transform: scale(1)

@keyframes be-anim-flipX
    from
        opacity: 0
        transform: scaleX(0)
    to
        opacity: 1
        transform: scaleX(1)

@keyframes be-anim-flipY
    from
        opacity: 0
        transform: scaleY(0)
    to
        opacity: 1
        transform: scaleY(1)

@keyframes be-anim-bounceIn
    0%
        opacity: 0
        transform: scale(0.5)
    60%
        opacity: 1
        transform: scale(1.15)
    80%
        transform: scale(0.95)
    100%
        transform: scale(1)

@keyframes be-anim-rotateIn
    from
        opacity: 0
        transform: rotate(-180deg) scale(0.6)
    to
        opacity: 1
        transform: rotate(0deg) scale(1)

@keyframes be-anim-blurIn
    from
        opacity: 0
        filter: blur(16px)
    to
        opacity: 1
        filter: blur(0)

@keyframes be-anim-slideUpFade
    from
        opacity: 0
        transform: translateY(40px)
    50%
        opacity: 1
    to
        opacity: 1
        transform: translateY(0)

@keyframes be-anim-flyTop
    from
        opacity: 1
        transform: translateY(-150vh)
    to
        opacity: 1
        transform: translateY(0)

@keyframes be-anim-flyBottom
    from
        opacity: 1
        transform: translateY(150vh)
    to
        opacity: 1
        transform: translateY(0)

@keyframes be-anim-flyLeft
    from
        opacity: 1
        transform: translateX(-150vw)
    to
        opacity: 1
        transform: translateX(0)

@keyframes be-anim-flyRight
    from
        opacity: 1
        transform: translateX(150vw)
    to
        opacity: 1
        transform: translateX(0)

// ── Self-hosted Template Fonts ────────────────────────────────
@font-face
    font-family: 'Roboto Flex'
    font-display: swap
    font-weight: 100 900
    src: url('/assets/fonts/RobotoFlex/RobotoFlex-VariableFont.woff2') format('woff2-variations')

@font-face
    font-family: 'Dancing Script'
    font-display: swap
    font-weight: 400 700
    src: url('/assets/fonts/DancingScript/DancingScript-VariableFont.woff2') format('woff2-variations')

@font-face
    font-family: 'Libre Franklin'
    font-display: swap
    font-style: normal
    font-weight: 100 900
    src: url('/assets/fonts/LibreFranklin/LibreFranklin-VariableFont.woff2') format('woff2-variations')

@font-face
    font-family: 'Libre Franklin'
    font-display: swap
    font-style: italic
    font-weight: 100 900
    src: url('/assets/fonts/LibreFranklin/LibreFranklin-Italic-VariableFont.woff2') format('woff2-variations')

@font-face
    font-family: 'Rubik'
    font-display: swap
    font-style: normal
    font-weight: 300 900
    src: url('/assets/fonts/Rubik/VariableFont.woff2') format('woff2-variations')

@font-face
    font-family: 'Rubik'
    font-display: swap
    font-style: italic
    font-weight: 300 900
    src: url('/assets/fonts/Rubik/Italic-VariableFont.woff2') format('woff2-variations')

@font-face
    font-family: 'Work Sans'
    font-display: swap
    font-style: normal
    font-weight: 100 900
    src: url('/assets/fonts/WorkSans/VariableFont.woff2') format('woff2-variations')

@font-face
    font-family: 'Work Sans'
    font-display: swap
    font-style: italic
    font-weight: 100 900
    src: url('/assets/fonts/WorkSans/Italic-VariableFont.woff2') format('woff2-variations')

@font-face
    font-family: 'Shantell Sans'
    font-display: swap
    font-style: normal
    font-weight: 300 800
    src: url('/assets/fonts/ShantellSans/VariableFont.woff2') format('woff2-variations')

@font-face
    font-family: 'Shantell Sans'
    font-display: swap
    font-style: italic
    font-weight: 300 800
    src: url('/assets/fonts/ShantellSans/Italic-VariableFont.woff2') format('woff2-variations')

@font-face
    font-family: 'Oswald'
    font-display: swap
    font-style: normal
    font-weight: 200 700
    src: url('/assets/fonts/Oswald/oswald-latin-wght-normal.woff2') format('woff2-variations')

@font-face
    font-family: 'Open Sans'
    font-display: swap
    font-style: normal
    font-weight: 300 800
    src: url('/assets/fonts/OpenSans/VariableFont.woff2') format('woff2-variations')

@font-face
    font-family: 'Open Sans'
    font-display: swap
    font-style: italic
    font-weight: 300 800
    src: url('/assets/fonts/OpenSans/ItalicVariableFont.woff2') format('woff2-variations')

.be-video-source-dialog
    &::v-deep .el-dialog__body
        padding: 12px 16px 20px

.be-vsd-options
    display: flex
    gap: 12px

.be-vsd-opt
    flex: 1
    display: flex
    flex-direction: column
    align-items: center
    gap: 8px
    padding: 20px 12px
    border: 1px solid #e5e7eb
    border-radius: 8px
    background: #f9fafb
    cursor: pointer
    transition: border-color 0.15s, background 0.15s
    &:hover
        border-color: #3b82f6
        background: #eff6ff

    i
        font-size: 22px
        color: #3b82f6

    span
        font-size: 13px
        font-weight: 600
        color: #374151

    small
        font-size: 11px
        color: #9ca3af
        text-align: center
        line-height: 1.3

.be-crop-dialog
    &::v-deep .el-dialog__body
        padding: 0

.be-crop-body
    height: 420px
    background: #1a1a1a
    overflow: hidden
    display: flex
    align-items: center
    justify-content: center

.be-crop-inner
    padding: 20px
    width: 100%
    overflow: visible

.be-crop-body .vue-cropper
    width: 100%
    max-height: 380px
    overflow: visible

.be-crop-body .cropper-container
    overflow: visible !important

.be-crop-body .cropper-point
    width: 10px !important
    height: 10px !important
    opacity: 0.9 !important

.be-crop-body .cropper-point.point-nw
    left: -5px !important
    top: -5px !important

.be-crop-body .cropper-point.point-ne
    right: -5px !important
    top: -5px !important

.be-crop-body .cropper-point.point-sw
    left: -5px !important
    bottom: -5px !important

.be-crop-body .cropper-point.point-se
    right: -5px !important
    bottom: -5px !important
    width: 10px !important
    height: 10px !important

.be-img-dt-panel,
.be-vid-dt-panel
    border-top: 1px solid #e5e7eb
    margin-top: 8px
    padding-top: 10px
    width: 100%
    max-width: 278px
    overflow: hidden

.be-img-dt-header
    display: flex
    align-items: center
    justify-content: space-between
    margin-bottom: 8px

.be-img-dt-title
    font-size: 11px
    font-weight: 600
    color: #374151

.be-img-dt-grid
    display: grid
    width: 100%
    padding: 1px
    grid-template-columns: repeat(5, minmax(0, 1fr))
    gap: 4px

.be-img-dt-btn
    width: 100%
    min-width: 0
    display: flex
    flex-direction: column
    align-items: stretch
    gap: 3px
    background: none
    border: 2px solid transparent
    border-radius: 4px
    padding: 2px
    cursor: pointer
    outline: 1px solid rgba(0,0,0,0.1)
    transition: border-color 0.1s, outline-color 0.1s
    &:hover
        outline-color: #9ca3af
    &.is-active
        border-color: #6366f1
        outline-color: #6366f1
    span
        font-size: 9px
        color: #374151
        line-height: 1.2
        text-align: center
        white-space: nowrap
        overflow: hidden
        text-overflow: ellipsis

.be-img-dt-thumb
    width: 100%
    height: 28px
    border-radius: 2px
    background: #e5e7eb center / cover no-repeat
    flex-shrink: 0

.be-img-dt-panel .be-ep-duotone-clear,
.be-vid-dt-panel .be-ep-duotone-clear
    display: inline-flex
    align-items: center
    font-size: 11px
    padding: 3px 7px
    border-radius: 4px
    border: 1px solid #fca5a5
    background: #fff5f5
    color: #ef4444
    cursor: pointer
    white-space: nowrap
    transition: background 0.1s, border-color 0.1s
    &:hover
        background: #fee2e2
        border-color: #ef4444

.be-img-dt-panel .be-ep-duotone-custom,
.be-vid-dt-panel .be-ep-duotone-custom
    display: flex
    flex-direction: row
    gap: 10px
    padding: 6px 0
    border-bottom: 1px solid #e5e7eb

.be-img-dt-panel .be-ep-dc-row,
.be-vid-dt-panel .be-ep-dc-row
    display: flex
    align-items: center
    gap: 4px
    flex: 1
    min-width: 0
    label
        font-size: 10px
        color: #6b7280
        flex-shrink: 0
    .be-ep-dc-inputs
        display: flex
        align-items: center
        gap: 4px
        flex: 1
        min-width: 0
    input[type="color"]
        width: 28px
        height: 22px
        border: 1px solid #d1d5db
        border-radius: 3px
        padding: 1px 2px
        cursor: pointer
        background: #fff
        flex-shrink: 0

.be-img-dt-panel .be-ep-dc-hex,
.be-vid-dt-panel .be-ep-dc-hex
    flex: 1
    min-width: 0
    height: 22px
    font-size: 11px
    font-family: monospace
    border: 1px solid #d1d5db
    border-radius: 3px
    padding: 2px 5px
    color: #374151
    &:focus
        outline: none
        border-color: #6366f1
</style>

<i18n>
{
  "en": {
    "\"Centered (in container)\" = slider centered in the page width. \"Full width\" = slider across the full page width. \"Full width (container)\" = full width, but content limited to container.": "\"Centered (in container)\" = slider centered in the page width. \"Full width\" = slider across the full page width. \"Full width (container)\" = full width, but content limited to container.",
    "Add animation (optional)": "Add animation (optional)",
    "Add animation": "Add animation",
    "Add element": "Add element",
    "Add elements": "Add elements",
    "Add video": "Add video",
    "Media library": "Media library",
    "MP4, WebM from own library": "MP4, WebM from own library",
    "External service": "External service",
    "YouTube or Vimeo URL": "YouTube or Vimeo URL",
    "Add link": "Add link",
    "Add slide": "Add slide",
    "Align center": "Align center",
    "Align left": "Align left",
    "Align right": "Align right",
    "All changes can be undone with Ctrl+Z (Ctrl+Y to redo). Finally click \"Save\" outside the editor to apply all slides. Done!": "All changes can be undone with Ctrl+Z (Ctrl+Y to redo). Finally click \"Save\" outside the editor to apply all slides. Done!",
    "All size and position values are relative to the slider width (vw = 1% of the width). This means all elements scale proportionally on every device – no fixed pixel values needed.": "All size and position values are relative to the slider width (vw = 1% of the width). This means all elements scale proportionally on every device – no fixed pixel values needed.",
    "Angle": "Angle",
    "Animation timeline": "Animation timeline",
    "Animation": "Animation",
    "Animations": "Animations",
    "Arrow right": "Arrow right",
    "Arrow": "Arrow",
    "Assign animation": "Assign animation",
    "Assign link (optional)": "Assign link (optional)",
    "B&W": "B&W",
    "Background / Border": "Background / Border",
    "Background color / Gradient": "Background color / Gradient",
    "Background color with adjustable opacity and optional padding. Border: width, style (Solid, Dashed, Dotted), color and corner radius.": "Background color with adjustable opacity and optional padding. Border: width, style (Solid, Dashed, Dotted), color and corner radius.",
    "Background color": "Background color",
    "Background image": "Background image",
    "Background options": "Background options",
    "Background video": "Background video",
    "Background": "Background",
    "Banner height": "Banner height",
    "Banner Slider": "Banner Slider",
    "Black & White": "Black & White",
    "Block toolbar": "Block toolbar",
    "Block types": "Block types",
    "Blur in": "Blur in",
    "Bildausschnitt": "Image framing",
    "Effekte": "Effects",
    "Zuschneiden": "Crop",
    "Blur": "Blur",
    "Blurred": "Blurred",
    "Bold / Italic / Underline / Strikethrough": "Bold / Italic / Underline / Strikethrough",
    "Bold": "Bold",
    "Border color": "Border color",
    "Border width in px, style (Solid, Dashed, Dotted) and border color. CI colors are available as quick selection.": "Border width in px, style (Solid, Dashed, Dotted) and border color. CI colors are available as quick selection.",
    "Border": "Border",
    "Bottom edge": "Bottom edge",
    "Bottom": "Bottom",
    "Bouncing": "Bouncing",
    "Bring to front": "Bring to front",
    "Button background": "Button background",
    "Button block": "Button block",
    "Button border": "Button border",
    "Button": "Button",
    "Canvas layout": "Canvas layout",
    "Center horizontally": "Center horizontally",
    "Center vertically": "Center vertically",
    "Center": "Center",
    "Centered (in container)": "Centered (in container)",
    "Change background image": "Change background image",
    "Change image": "Change image",
    "Change background video": "Change background video",
    "Change image": "Change image",
    "Change order": "Change order",
    "Change video": "Change video",
    "Choose a font from the dropdown. Enter font size in vw – adapts automatically to every screen size (e.g. 2 vw ≈ 38 px on Full HD).": "Choose a font from the dropdown. Enter font size in vw – adapts automatically to every screen size (e.g. 2 vw ≈ 38 px on Full HD).",
    "Choose a video from the media library. The video plays automatically muted in a loop.": "Choose a video from the media library. The video plays automatically muted in a loop.",
    "Choose an image from the media library. The position (Top / Center / Bottom) can be set under \"Background Options\".": "Choose an image from the media library. The position (Top / Center / Bottom) can be set under \"Background Options\".",
    "Choose background image": "Choose background image",
    "Choose background video": "Choose background video",
    "Choose image": "Choose image",
    "CI colors": "CI colors",
    "Circle": "Circle",
    "Click \"+ Element\" at the bottom → choose block type: Text, Shape, Button or Image. The new block appears centered on the slide and is immediately selected.": "Click \"+ Element\" at the bottom → choose block type: Text, Shape, Button or Image. The new block appears centered on the slide and is immediately selected.",
    "Click \"Background\" at the bottom → choose image, video or color/gradient. Via \"Background Options\" you can set overlay color, blur, position, grayscale and sepia filters.": "Click \"Background\" at the bottom → choose image, video or color/gradient. Via \"Background Options\" you can set overlay color, blur, position, grayscale and sepia filters.",
    "Click \"Save\" outside the editor to apply all changes. Undo / Redo at any time via Ctrl+Z / Ctrl+Y or the toolbar buttons.": "Click \"Save\" outside the editor to apply all changes. Undo / Redo at any time via Ctrl+Z / Ctrl+Y or the toolbar buttons.",
    "Click \"Slide\" in the top left to add a new empty slide. An existing slide can be copied via the duplicate icon or reordered by drag & drop in the left sidebar.": "Click \"Slide\" in the top left to add a new empty slide. An existing slide can be copied via the duplicate icon or reordered by drag & drop in the left sidebar.",
    "Click a block and drag it to the desired position. Resize using the white corner handles. Arrow keys move by 0.5% (with Shift: 2%). Enable guides (toolbar) – elements snap automatically to grid and center lines.": "Click a block and drag it to the desired position. Resize using the white corner handles. Arrow keys move by 0.5% (with Shift: 2%). Enable guides (toolbar) – elements snap automatically to grid and center lines.",
    "Click and drag a block. Alternatively: arrow keys (0.5% per step), hold Shift (2% per step).": "Click and drag a block. Alternatively: arrow keys (0.5% per step), hold Shift (2% per step).",
    "Click the rotation button (circular arrow) in the block toolbar and enter the angle in degrees.": "Click the rotation button (circular arrow) in the block toolbar and enter the angle in degrees.",
    "Clickable button with an assigned link. Double-click to edit text. Background solid or as gradient (linear/radial). Font: size, color, family, Bold, Italic, Underline, Strikethrough. Border (width, style, color), rounded corners and shadow also configurable.": "Clickable button with an assigned link. Double-click to edit text. Background solid or as gradient (linear/radial). Font: size, color, family, Bold, Italic, Underline, Strikethrough. Border (width, style, color), rounded corners and shadow also configurable.",
    "Close": "Close",
    "Color / Gradient": "Color / Gradient",
    "Color 1": "Color 1",
    "Color 2": "Color 2",
    "Color overlay (tint), blur, image position, grayscale filter and sepia filter. Active options are shown counted in parentheses.": "Color overlay (tint), blur, image position, grayscale filter and sepia filter. Active options are shown counted in parentheses.",
    "Color": "Color",
    "Contain": "Contain",
    "Copy the active slide with all blocks, animations and settings – ideal for similar slides.": "Copy the active slide with all blocks, animations and settings – ideal for similar slides.",
    "Copy": "Copy",
    "Cover": "Cover",
    "Create slide": "Create slide",
    "Crop": "Crop",
    "Ctrl+Z / Ctrl+Y or the arrow buttons in the toolbar. The history covers all changes of the current session.": "Ctrl+Z / Ctrl+Y or the arrow buttons in the toolbar. The history covers all changes of the current session.",
    "Custom color": "Custom color",
    "Customize appearance": "Customize appearance",
    "Dashed": "Dashed",
    "Decrease font size": "Decrease font size",
    "Define a solid background or a linear/radial gradient with two colors and adjustable angle.": "Define a solid background or a linear/radial gradient with two colors and adjustable angle.",
    "Del = delete element. Arrow keys = move element (+ Shift = larger steps). Ctrl+Z / Ctrl+Y = Undo / Redo.": "Del = delete element. Arrow keys = move element (+ Shift = larger steps). Ctrl+Z / Ctrl+Y = Undo / Redo.",
    "Delay & duration": "Delay & duration",
    "Delay": "Delay",
    "Delete active slide": "Delete active slide",
    "Delete block": "Delete block",
    "Delete slide": "Delete slide",
    "Delete": "Delete",
    "Diamond": "Diamond",
    "Done ✓": "Done ✓",
    "Dotted": "Dotted",
    "Drag font size": "Drag font size",
    "Drag the white corner handles. Width and height are stored as a percentage of the slide size and scale proportionally on every device.": "Drag the white corner handles. Width and height are stored as a percentage of the slide size and scale proportionally on every device.",
    "Drag to resize": "Drag to resize",
    "Duplicate active slide": "Duplicate active slide",
    "Duplicate slide": "Duplicate slide",
    "Duration": "Duration",
    "Each slide has its own background. Click this button to select an image, a video or a background color or gradient.": "Each slide has its own background. Click this button to select an image, a video or a background color or gradient.",
    "Ease in & out": "Ease in & out",
    "Ease in": "Ease in",
    "Ease out": "Ease out",
    "Easing (motion curve)": "Easing (motion curve)",
    "Easing": "Easing",
    "Edit": "Edit",
    "Effect": "Effect",
    "Elastic": "Elastic",
    "Element": "Element",
    "Empty": "Empty",
    "Fade in": "Fade in",
    "Fill / Border": "Fill / Border",
    "Fill color": "Fill color",
    "Filter": "Filter",
    "Filter Duo Tone": "Filter Duo Tone",
    "Flip horizontal": "Flip horizontal",
    "Flip vertical": "Flip vertical",
    "Font family & size": "Font family & size",
    "Font family": "Font family",
    "Template Font": "Template Font",
    "Formatting buttons directly in the block toolbar. For text blocks, the formatting applies to the entire block.": "Formatting buttons directly in the block toolbar. For text blocks, the formatting applies to the entire block.",
    "Freely position and scale an image from the media library. Rounded corners, opacity and shadow are adjustable.": "Freely position and scale an image from the media library. Rounded corners, opacity and shadow are adjustable.",
    "From above": "From above",
    "From below": "From below",
    "From left": "From left",
    "From right": "From right",
    "Full width (container)": "Full width (container)",
    "Full width": "Full width",
    "Geometric shapes: Rectangle, Circle, Triangle, Diamond, Star, Hexagon, Arrow. Solid fill or linear/radial gradient. Border (width, style, color) only for Rectangle and Circle. Rounded corners only for Rectangle. Opacity and shadow for all shapes.": "Geometric shapes: Rectangle, Circle, Triangle, Diamond, Star, Hexagon, Arrow. Solid fill or linear/radial gradient. Border (width, style, color) only for Rectangle and Circle. Rounded corners only for Rectangle. Opacity and shadow for all shapes.",
    "Gradient": "Gradient",
    "Guides": "Guides",
    "Height relative to screen width. E.g. 30 = the banner is 30% as tall as the screen is wide.": "Height relative to screen width. E.g. 30 = the banner is 30% as tall as the screen is wide.",
    "Help & Guide": "Help & Guide",
    "Here you assign a fade-in effect to each element and control delay and duration. In the timeline you can drag the bars to move them and adjust their length.": "Here you assign a fade-in effect to each element and control delay and duration. In the timeline you can drag the bars to move them and adjust their length.",
    "Here you can add, duplicate or delete slides. Undo (Ctrl+Z) and Redo (Ctrl+Y) are also available here.": "Here you can add, duplicate or delete slides. Undo (Ctrl+Z) and Redo (Ctrl+Y) are also available here.",
    "Here you set color overlay (tint), blur, image position (top / center / bottom), grayscale and sepia filters. The number of active options is shown in brackets.": "Here you set color overlay (tint), blur, image position (top / center / bottom), grayscale and sepia filters. The number of active options is shown in brackets.",
    "Hexagon": "Hexagon",
    "History & shortcuts": "History & shortcuts",
    "Image block": "Image block",
    "Image size": "Image size",
    "Image": "Image",
    "In the right-click menu under \"Animation\" or directly in the timeline (drag bar = delay, drag right edge = duration).": "In the right-click menu under \"Animation\" or directly in the timeline (drag bar = delay, drag right edge = duration).",
    "Increase font size": "Increase font size",
    "Insert a new empty slide at the end. Each slide has its own background, elements and settings.": "Insert a new empty slide at the end. Each slide has its own background, elements and settings.",
    "Italic": "Italic",
    "Keyboard shortcuts": "Keyboard shortcuts",
    "Layer order": "Layer order",
    "Layer": "Layer",
    "Layout & Settings": "Layout & Settings",
    "Layout": "Layout",
    "Left, center or right – for text blocks only.": "Left, center or right – for text blocks only.",
    "Left": "Left",
    "Less rounded corners": "Less rounded corners",
    "Letter spacing": "Letter spacing",
    "Line height & letter spacing": "Line height & letter spacing",
    "Line height": "Line height",
    "Linear": "Linear",
    "Link": "Link",
    "Manage slides": "Manage slides",
    "More rounded corners": "More rounded corners",
    "Move blocks via drag & drop. Resize at the corner handles. Arrow keys: 0.5% per step (Shift = 2%). With the guide grid visible, elements snap automatically to center and third lines.": "Move blocks via drag & drop. Resize at the corner handles. Arrow keys: 0.5% per step (Shift = 2%). With the guide grid visible, elements snap automatically to center and third lines.",
    "Move": "Move",
    "Multi-line body text. Double-click to edit. Supports Bold, Italic, Underline, Strikethrough, font family, font size, color, alignment, line-height and letter-spacing, background color/gradient, border (width, style, color, corners) and shadow.": "Multi-line body text. Double-click to edit. Supports Bold, Italic, Underline, Strikethrough, font family, font size, color, alignment, line-height and letter-spacing, background color/gradient, border (width, style, color, corners) and shadow.",
    "Next →": "Next →",
    "No background / border": "No background / border",
    "No fill": "No fill",
    "No overlay": "No overlay",
    "No thanks": "No thanks",
    "None": "None",
    "On the left you can see all slides of the slider. Use the buttons at the top left to add new slides, duplicate or delete them. You can change the order via drag & drop in the sidebar.": "On the left you can see all slides of the slider. Use the buttons at the top left to add new slides, duplicate or delete them. You can change the order via drag & drop in the sidebar.",
    "Once an element is selected, this toolbar appears. Depending on the block type, the following are available: font, size, color, bold, italic, underline, strikethrough, alignment, background (solid or gradient), border (width, style, color), corners, shadow, opacity, rotation and link.": "Once an element is selected, this toolbar appears. Depending on the block type, the following are available: font, size, color, bold, italic, underline, strikethrough, alignment, background (solid or gradient), border (width, style, color), corners, shadow, opacity, rotation and link.",
    "Opacity": "Opacity",
    "Opacity / Filter": "Opacity / Filter",
    "Outer area (container layout)": "Outer area (container layout)",
    "Outer area color": "Outer area color",
    "Outer area": "Outer area",
    "Overlay": "Overlay",
    "Overshoot": "Overshoot",
    "Padding": "Padding",
    "Paste": "Paste",
    "Play preview": "Play preview",
    "Position & scale element": "Position & scale element",
    "Position & Scale": "Position & Scale",
    "Position": "Position",
    "Radial": "Radial",
    "Rectangle": "Rectangle",
    "Redo (Ctrl+Y)": "Redo (Ctrl+Y)",
    "Remove background color": "Remove background color",
    "Remove background video": "Remove background video",
    "Remove the active slide. At least one slide must always remain.": "Remove the active slide. At least one slide must always remain.",
    "Remove video": "Remove video",
    "Reorder slides in the left sidebar via drag & drop.": "Reorder slides in the left sidebar via drag & drop.",
    "Reset": "Reset",
    "Right-click → Animation → Type. 8 curves from \"Ease\" to \"Elastic\" selectable.": "Right-click → Animation → Type. 8 curves from \"Ease\" to \"Elastic\" selectable.",
    "Right-click → Link or link icon in the button toolbar. Link types: Internal (page), External (URL), Email, Phone, Media Library file.": "Right-click → Link or link icon in the button toolbar. Link types: Internal (page), External (URL), Email, Phone, Media Library file.",
    "Right-click menu": "Right-click menu",
    "Right-click on an element → \"Bring forward\" / \"Send backward\" to change the stacking order.": "Right-click on an element → \"Bring forward\" / \"Send backward\" to change the stacking order.",
    "Right-click on an element → Animation → select effect. 18 entrance effects available (Slide, Fade, Zoom, Bounce, Fly in, etc.).": "Right-click on an element → Animation → select effect. 18 entrance effects available (Slide, Fade, Zoom, Bounce, Fly in, etc.).",
    "Fly-in animations": "Fly-in animations",
    "4 new effects: \"Fly from top / bottom / left / right\". The element starts completely outside the visible area and flies into its position – no fade, pure movement.": "4 new effects: \"Fly from top / bottom / left / right\". The element starts completely outside the visible area and flies into its position – no fade, pure movement.",
    "Right-click on an element → Animation → select effect. Set delay and duration in the same menu or adjust by dragging in the \"Animation Timeline\" (button at the bottom).": "Right-click on an element → Animation → select effect. Set delay and duration in the same menu or adjust by dragging in the \"Animation Timeline\" (button at the bottom).",
    "Right-click on an element → Link. Available types: Internal, External, Email, Phone and Media Library file. Button blocks also have a link icon directly in the toolbar.": "Right-click on an element → Link. Available types: Internal, External, Email, Phone and Media Library file. Button blocks also have a link icon directly in the toolbar.",
    "Right-click on an element opens: Duplicate, Delete, Bring forward/backward, Link, Animation.": "Right-click on an element opens: Duplicate, Delete, Bring forward/backward, Link, Animation.",
    "Right-clicking on an element opens the context menu: duplicate, delete, move layer forward/backward, assign link and configure animation – all in one place.": "Right-clicking on an element opens the context menu: duplicate, delete, move layer forward/backward, assign link and configure animation – all in one place.",
    "Right": "Right",
    "Rise up": "Rise up",
    "Fly from top": "Fly from top",
    "Fly from bottom": "Fly from bottom",
    "Fly from left": "Fly from left",
    "Fly from right": "Fly from right",
    "Rotate in": "Rotate in",
    "Rotate": "Rotate",
    "Rotation": "Rotation",
    "Border / Corners": "Border / Corners",
    "Round corners": "Round corners",
    "Safety frame (8% margin), center lines (50%) and third lines (33 / 66%). Elements snap automatically to these lines when dragging.": "Safety frame (8% margin), center lines (50%) and third lines (33 / 66%). Elements snap automatically to these lines when dragging.",
    "Save": "Save",
    "Scale": "Scale",
    "Select a block → the block toolbar appears automatically at the top. Depending on type: font, color, gradient, border, shadow, corners, opacity, rotation etc. Double-click on text or button blocks to edit the text directly.": "Select a block → the block toolbar appears automatically at the top. Depending on type: font, color, gradient, border, shadow, corners, opacity, rotation etc. Double-click on text or button blocks to edit the text directly.",
    "Select background": "Select background",
    "Select image": "Select image",
    "Select video": "Select video",
    "Send to back": "Send to back",
    "Sepia": "Sepia",
    "Set color": "Set color",
    "Set line-height and letter-spacing in the typography dropdown (Aa icon).": "Set line-height and letter-spacing in the typography dropdown (Aa icon).",
    "Set the height in vw in the layout panel (toolbar) (10–100). The banner is then x% as tall as the screen is wide.": "Set the height in vw in the layout panel (toolbar) (10–100). The banner is then x% as tall as the screen is wide.",
    "Set up background": "Set up background",
    "Duotone": "Duotone",
    "Custom": "Custom",
    "Highlight": "Highlight",
    "Remove": "Remove",
    "Kirsche": "Cherry",
    "Fuchsia": "Fuchsia",
    "Pop": "Pop",
    "Violett": "Violet",
    "Ozeanblau": "Ocean Blue",
    "Ozeangrün": "Ocean Green",
    "Senfgelb": "Mustard",
    "Bernstein": "Amber",
    "Pomelo": "Pomelo",
    "Blush": "Blush",
    "Pfefferminz": "Peppermint",
    "Mystisch": "Mystic",
    "Pastell": "Pastel",
    "Koralle": "Coral",
    "Shadow": "Shadow",
    "Spiegeln": "Mirror",
    "Horizontal": "Horizontal",
    "Vertikal": "Vertical",
    "Shape block": "Shape block",
    "Shape": "Shape",
    "Shapes": "Shapes",
    "Show button at the bottom. Displays all blocks of the active slide in a timeline – change delay and duration by dragging.": "Show button at the bottom. Displays all blocks of the active slide in a timeline – change delay and duration by dragging.",
    "Show/hide guides": "Show/hide guides",
    "Crop image": "Crop image",
    "Change crop": "Change crop",
    "Remove crop": "Remove crop",
    "Remove duotone": "Remove duotone",
    "Set crop": "Set crop",
    "Skip": "Skip",
    "Slide background": "Slide background",
    "Slide toolbar": "Slide toolbar",
    "Slide": "Slide",
    "Slides": "Slides",
    "Smooth": "Smooth",
    "Solid or gradient (linear with angle or radial). Two colors freely selectable, CI colors are offered as quick selection.": "Solid or gradient (linear with angle or radial). Two colors freely selectable, CI colors are offered as quick selection.",
    "Solid": "Solid",
    "Standard colors": "Standard colors",
    "Star": "Star",
    "Stretch": "Stretch",
    "Start guided tour": "Start guided tour",
    "Start tour": "Start tour",
    "Step-by-step guide": "Step-by-step guide",
    "Stepwise": "Stepwise",
    "Strikethrough": "Strikethrough",
    "Style": "Style",
    "Text & Button toolbar": "Text & Button toolbar",
    "Text alignment": "Text alignment",
    "Text background": "Text background",
    "Text block": "Text block",
    "Text color": "Text color",
    "Text": "Text",
    "This short tour shows you all the important features. You can click \"Skip\" at any time or restart the tour via \"Help & Instructions\".": "This short tour shows you all the important features. You can click \"Skip\" at any time or restart the tour via \"Help & Instructions\".",
    "Top edge": "Top edge",
    "Top": "Top",
    "Transparent": "Transparent",
    "Triangle": "Triangle",
    "Type": "Type",
    "Typography": "Typography",
    "Underline": "Underline",
    "Undo (Ctrl+Z)": "Undo (Ctrl+Z)",
    "Undo / Redo": "Undo / Redo",
    "Undo & Save": "Undo & Save",
    "Unit vw": "Unit vw",
    "Use the \"+ Element\" button at the bottom right to add new blocks to the slide. Options: Text, Shapes (Rectangle, Circle, Triangle, Diamond, Star, Hexagon, Arrow), Button and Image. The button appears once a background is set.": "Use the \"+ Element\" button at the bottom right to add new blocks to the slide. Options: Text, Shapes (Rectangle, Circle, Triangle, Diamond, Star, Hexagon, Arrow), Button and Image. The button appears once a background is set.",
    "Video": "Video",
    "Enter video URL": "Enter video URL",
    "YouTube, Vimeo or video URL...": "YouTube, Vimeo or video URL...",
    "Autoplay": "Autoplay",
    "Mute": "Mute",
    "Loop": "Loop",
    "Controls": "Controls",
    "Portrait / Landscape": "Portrait / Landscape",
    "Corner radius": "Corner radius",
    "Welcome to the Slider Editor!": "Welcome to the Slider Editor!",
    "Width": "Width",
    "With \"Centered\" layout the side outer area can be left empty, colored or blurred (image blur).": "With \"Centered\" layout the side outer area can be left empty, colored or blurred (image blur).",
    "Would you like a brief introduction to the most important features?": "Would you like a brief introduction to the most important features?",
    "Zoom in": "Zoom in",
    "Zoom out": "Zoom out"
  },
  "de": {
    "\"Centered (in container)\" = slider centered in the page width. \"Full width\" = slider across the full page width. \"Full width (container)\" = full width, but content limited to container.": "\"Zentriert (im Container)\" = Slider zentriert in der Seitenbreite. \"Volle Breite\" = Slider über die volle Seitenbreite. \"Volle Breite (Container)\" = volle Breite, aber Inhalt auf Container begrenzt.",
    "Add animation (optional)": "Animation hinzufügen (optional)",
    "Add animation": "Animation hinzufügen",
    "Add element": "Element hinzufügen",
    "Add elements": "Elemente hinzufügen",
    "Add video": "Video hinzufügen",
    "Media library": "Mediathek",
    "MP4, WebM from own library": "MP4, WebM aus eigener Mediathek",
    "External service": "Externer Dienst",
    "YouTube or Vimeo URL": "YouTube- oder Vimeo-URL",
    "Add link": "Link hinzufügen",
    "Add slide": "Folie hinzufügen",
    "Align center": "Zentriert",
    "Align left": "Linksbündig",
    "Align right": "Rechtsbündig",
    "All changes can be undone with Ctrl+Z (Ctrl+Y to redo). Finally click \"Save\" outside the editor to apply all slides. Done!": "Alle Änderungen können per Strg+Z rückgängig gemacht werden (Strg+Y zum Wiederholen). Klicke abschließend auf „Speichern\" außerhalb des Editors, um alle Folien zu übernehmen. Fertig!",
    "All size and position values are relative to the slider width (vw = 1% of the width). This means all elements scale proportionally on every device – no fixed pixel values needed.": "Alle Größen- und Positionswerte sind relativ zur Slider-Breite (vw = 1 % der Breite). Dadurch skalieren alle Elemente auf jedem Gerät proportional – keine festen Pixelwerte nötig.",
    "Angle": "Winkel",
    "Animation timeline": "Animations-Zeitleiste",
    "Animation": "Animation",
    "Animations": "Animationen",
    "Arrow right": "Pfeil rechts",
    "Arrow": "Pfeil",
    "Assign animation": "Animation zuweisen",
    "Assign link (optional)": "Link zuweisen (optional)",
    "B&W": "S/W",
    "Background / Border": "Hintergrund / Rahmen",
    "Background color / Gradient": "Hintergrundfarbe / Verlauf",
    "Background color with adjustable opacity and optional padding. Border: width, style (Solid, Dashed, Dotted), color and corner radius.": "Hintergrundfarbe mit einstellbarer Deckkraft und optionalem Innenabstand. Rahmen: Breite, Stil (Durchgehend, Gestrichelt, Gepunktet), Farbe und Eckenradius.",
    "Background color": "Hintergrundfarbe",
    "Background image": "Hintergrundbild",
    "Background options": "Hintergrund-Optionen",
    "Background video": "Hintergrundvideo",
    "Background": "Hintergrund",
    "Banner height": "Banner-Höhe",
    "Banner Slider": "Banner-Slider",
    "Black & White": "Schwarzweiß",
    "Block toolbar": "Block-Toolbar",
    "Block types": "Block-Typen",
    "Blur in": "Einblenden (Unschärfe)",
    "Bildausschnitt": "Bildausschnitt",
    "Effekte": "Effekte",
    "Zuschneiden": "Zuschneiden",
    "Blur": "Weichzeichner",
    "Blurred": "Weichgezeichnet",
    "Bold / Italic / Underline / Strikethrough": "Fett / Kursiv / Unterstrichen / Durchgestrichen",
    "Bold": "Fett",
    "Border color": "Rahmenfarbe",
    "Border width in px, style (Solid, Dashed, Dotted) and border color. CI colors are available as quick selection.": "Rahmenbreite in px, Stil (Durchgehend, Gestrichelt, Gepunktet) und Rahmenfarbe. CI-Farben stehen als Schnellauswahl zur Verfügung.",
    "Border": "Rahmen",
    "Bottom edge": "Unterkante",
    "Bottom": "Unten",
    "Bouncing": "Bouncing",
    "Bring to front": "Nach vorne",
    "Button background": "Schaltflächen-Hintergrund",
    "Button block": "Button-Block",
    "Button border": "Schaltflächen-Rahmen",
    "Button": "Button",
    "Canvas layout": "Canvas-Layout",
    "Center horizontally": "Horizontal zentriert",
    "Center vertically": "Vertikal zentriert",
    "Center": "Mitte",
    "Centered (in container)": "Zentriert (im Container)",
    "Change background image": "Hintergrundbild ändern",
    "Change image": "Bild tauschen",
    "Change background video": "Hintergrundvideo ändern",
    "Change image": "Bild ändern",
    "Change order": "Reihenfolge ändern",
    "Change video": "Video ändern",
    "Choose a font from the dropdown. Enter font size in vw – adapts automatically to every screen size (e.g. 2 vw ≈ 38 px on Full HD).": "Schriftart aus dem Dropdown wählen. Schriftgröße in vw eingeben – passt sich automatisch an jede Bildschirmgröße an (z. B. 2 vw ≈ 38 px auf Full HD).",
    "Choose a video from the media library. The video plays automatically muted in a loop.": "Ein Video aus der Mediathek wählen. Das Video läuft automatisch stumm in einer Schleife.",
    "Choose an image from the media library. The position (Top / Center / Bottom) can be set under \"Background Options\".": "Ein Bild aus der Mediathek wählen. Die Position (Oben / Mitte / Unten) kann unter „Hintergrund-Optionen\" eingestellt werden.",
    "Choose background image": "Hintergrundbild wählen",
    "Choose background video": "Hintergrundvideo wählen",
    "Choose image": "Bild wählen",
    "CI colors": "CI-Farben",
    "Circle": "Kreis",
    "Click \"+ Element\" at the bottom → choose block type: Text, Shape, Button or Image. The new block appears centered on the slide and is immediately selected.": "Unten auf „+ Element\" klicken → Block-Typ auswählen: Text, Form, Button oder Bild. Der neue Block erscheint zentriert auf der Folie und ist sofort ausgewählt.",
    "Click \"Background\" at the bottom → choose image, video or color/gradient. Via \"Background Options\" you can set overlay color, blur, position, grayscale and sepia filters.": "Unten auf „Hintergrund\" klicken → Bild, Video oder Farbe/Verlauf auswählen. Über „Hintergrund-Optionen\" können Überlagerungsfarbe, Weichzeichner, Position, Graustufen- und Sepia-Filter eingestellt werden.",
    "Click \"Save\" outside the editor to apply all changes. Undo / Redo at any time via Ctrl+Z / Ctrl+Y or the toolbar buttons.": "Auf „Speichern\" außerhalb des Editors klicken, um alle Änderungen zu übernehmen. Rückgängig / Wiederholen jederzeit per Strg+Z / Strg+Y oder den Toolbar-Buttons.",
    "Click \"Slide\" in the top left to add a new empty slide. An existing slide can be copied via the duplicate icon or reordered by drag & drop in the left sidebar.": "Oben links auf „Folie\" klicken, um eine neue leere Folie hinzuzufügen. Eine bestehende Folie kann über das Duplizieren-Symbol kopiert oder per Drag & Drop in der linken Seitenleiste neu angeordnet werden.",
    "Click a block and drag it to the desired position. Resize using the white corner handles. Arrow keys move by 0.5% (with Shift: 2%). Enable guides (toolbar) – elements snap automatically to grid and center lines.": "Block anklicken und an die gewünschte Position ziehen. Mit den weißen Ecken-Handles die Größe anpassen. Pfeiltasten bewegen um 0,5 % (mit Shift: 2 %). Hilfslinien aktivieren (Toolbar) – Elemente rasten automatisch an Raster- und Mittellinien ein.",
    "Click and drag a block. Alternatively: arrow keys (0.5% per step), hold Shift (2% per step).": "Block anklicken und ziehen. Alternativ: Pfeiltasten (0,5 % pro Schritt), Shift halten (2 % pro Schritt).",
    "Click the rotation button (circular arrow) in the block toolbar and enter the angle in degrees.": "Auf den Rotations-Button (Kreispfeil) in der Block-Toolbar klicken und den Winkel in Grad eingeben.",
    "Clickable button with an assigned link. Double-click to edit text. Background solid or as gradient (linear/radial). Font: size, color, family, Bold, Italic, Underline, Strikethrough. Border (width, style, color), rounded corners and shadow also configurable.": "Klickbarer Button mit zugewiesenem Link. Doppelklick zum Text bearbeiten. Hintergrund einfarbig oder als Verlauf (linear/radial). Schrift: Größe, Farbe, Familie, Fett, Kursiv, Unterstrichen, Durchgestrichen. Rahmen (Breite, Stil, Farbe), Eckenradius und Schatten ebenfalls einstellbar.",
    "Close": "Schließen",
    "Color / Gradient": "Farbe / Verlauf",
    "Color 1": "Farbe 1",
    "Color 2": "Farbe 2",
    "Color overlay (tint), blur, image position, grayscale filter and sepia filter. Active options are shown counted in parentheses.": "Farbüberlagerung (Tint), Weichzeichner, Bildposition, Graustufen- und Sepia-Filter. Aktive Optionen werden in Klammern gezählt angezeigt.",
    "Color": "Farbe",
    "Contain": "Enthalten",
    "Copy the active slide with all blocks, animations and settings – ideal for similar slides.": "Die aktive Folie mit allen Blöcken, Animationen und Einstellungen kopieren – ideal für ähnliche Folien.",
    "Copy": "Kopieren",
    "Cover": "Abdecken",
    "Create slide": "Folie erstellen",
    "Crop": "Zuschneiden",
    "Ctrl+Z / Ctrl+Y or the arrow buttons in the toolbar. The history covers all changes of the current session.": "Strg+Z / Strg+Y oder die Pfeil-Buttons in der Toolbar. Der Verlauf umfasst alle Änderungen der aktuellen Sitzung.",
    "Custom color": "Eigene Farbe",
    "Customize appearance": "Aussehen anpassen",
    "Dashed": "Gestrichelt",
    "Decrease font size": "Schriftgröße verringern",
    "Define a solid background or a linear/radial gradient with two colors and adjustable angle.": "Einen einfarbigen Hintergrund oder einen linearen/radialen Verlauf mit zwei Farben und einstellbarem Winkel definieren.",
    "Del = delete element. Arrow keys = move element (+ Shift = larger steps). Ctrl+Z / Ctrl+Y = Undo / Redo.": "Entf = Element löschen. Pfeiltasten = Element verschieben (+ Shift = größere Schritte). Strg+Z / Strg+Y = Rückgängig / Wiederholen.",
    "Delay & duration": "Verzögerung & Dauer",
    "Delay": "Verzögerung",
    "Delete active slide": "Aktive Folie löschen",
    "Delete block": "Block löschen",
    "Delete slide": "Folie löschen",
    "Delete": "Löschen",
    "Diamond": "Raute",
    "Done ✓": "Fertig ✓",
    "Dotted": "Gepunktet",
    "Drag font size": "Schriftgröße ziehen",
    "Drag the white corner handles. Width and height are stored as a percentage of the slide size and scale proportionally on every device.": "Die weißen Ecken-Handles ziehen. Breite und Höhe werden als Prozentsatz der Foliengröße gespeichert und skalieren auf jedem Gerät proportional.",
    "Drag to resize": "Zum Skalieren ziehen",
    "Duplicate active slide": "Aktive Folie duplizieren",
    "Duplicate slide": "Folie duplizieren",
    "Duration": "Dauer",
    "Each slide has its own background. Click this button to select an image, a video or a background color or gradient.": "Jede Folie hat einen eigenen Hintergrund. Klicke auf diesen Button, um ein Bild, ein Video oder eine Hintergrundfarbe bzw. einen Farbverlauf auszuwählen.",
    "Ease in & out": "Sanft ein & aus",
    "Ease in": "Einsteigen",
    "Ease out": "Aussteigen",
    "Easing (motion curve)": "Easing (Bewegungskurve)",
    "Easing": "Art",
    "Edit": "Editieren",
    "Effect": "Effekt",
    "Elastic": "Elastic",
    "Element": "Element",
    "Empty": "Leer",
    "Fade in": "Einblenden",
    "Fill / Border": "Füllung / Rahmen",
    "Fill color": "Füllfarbe",
    "Filter": "Filter",
    "Filter Duo Tone": "Filter Duo Tone",
    "Flip horizontal": "Klappen horizontal",
    "Flip vertical": "Klappen vertikal",
    "Font family & size": "Schriftfamilie & Größe",
    "Font family": "Schriftfamilie",
    "Template Font": "Schriftart Template",
    "Formatting buttons directly in the block toolbar. For text blocks, the formatting applies to the entire block.": "Formatierungs-Buttons direkt in der Block-Toolbar. Bei Text-Blöcken gilt die Formatierung für den gesamten Block.",
    "Freely position and scale an image from the media library. Rounded corners, opacity and shadow are adjustable.": "Ein Bild aus der Mediathek frei positionieren und skalieren. Abgerundete Ecken, Deckkraft und Schatten sind einstellbar.",
    "From above": "Von oben",
    "From below": "Von unten",
    "From left": "Von links",
    "From right": "Von rechts",
    "Full width (container)": "Volle Breite (Container)",
    "Full width": "Volle Breite",
    "Geometric shapes: Rectangle, Circle, Triangle, Diamond, Star, Hexagon, Arrow. Solid fill or linear/radial gradient. Border (width, style, color) only for Rectangle and Circle. Rounded corners only for Rectangle. Opacity and shadow for all shapes.": "Geometrische Formen: Rechteck, Kreis, Dreieck, Raute, Stern, Hexagon, Pfeil. Einfarbige Füllung oder linearer/radialer Verlauf. Rahmen (Breite, Stil, Farbe) nur für Rechteck und Kreis. Abgerundete Ecken nur für Rechteck. Deckkraft und Schatten für alle Formen.",
    "Gradient": "Verlauf",
    "Guides": "Hilfslinien",
    "Height relative to screen width. E.g. 30 = the banner is 30% as tall as the screen is wide.": "Höhe relativ zur Bildschirmbreite. Z. B. 30 = der Banner ist 30 % so hoch wie der Bildschirm breit ist.",
    "Help & Guide": "Hilfe & Anleitung",
    "Here you assign a fade-in effect to each element and control delay and duration. In the timeline you can drag the bars to move them and adjust their length.": "Hier weist du jedem Element einen Einblend-Effekt zu und steuerst Verzögerung und Dauer. In der Zeitleiste kannst du die Balken per Drag verschieben und deren Länge anpassen.",
    "Here you can add, duplicate or delete slides. Undo (Ctrl+Z) and Redo (Ctrl+Y) are also available here.": "Hier kannst du Folien hinzufügen, duplizieren oder löschen. Rückgängig (Strg+Z) und Wiederholen (Strg+Y) stehen ebenfalls hier zur Verfügung.",
    "Here you set color overlay (tint), blur, image position (top / center / bottom), grayscale and sepia filters. The number of active options is shown in brackets.": "Hier stellst du Farbüberlagerung (Tint), Weichzeichner (Blur), Bildposition (Oben / Mitte / Unten), Graustufen- und Sepia-Filter ein. Die Anzahl aktiver Optionen wird in Klammern angezeigt.",
    "Hexagon": "Hexagon",
    "History & shortcuts": "Verlauf & Tastenkürzel",
    "Image block": "Bild-Block",
    "Image size": "Bildgröße",
    "Image": "Bild",
    "In the right-click menu under \"Animation\" or directly in the timeline (drag bar = delay, drag right edge = duration).": "Im Rechtsklick-Menü unter „Animation\" oder direkt in der Zeitleiste (Balken ziehen = Verzögerung, rechte Kante ziehen = Dauer).",
    "Increase font size": "Schriftgröße erhöhen",
    "Insert a new empty slide at the end. Each slide has its own background, elements and settings.": "Eine neue leere Folie am Ende einfügen. Jede Folie hat einen eigenen Hintergrund, eigene Elemente und Einstellungen.",
    "Italic": "Kursiv",
    "Keyboard shortcuts": "Tastenkürzel",
    "Layer order": "Ebenenreihenfolge",
    "Layer": "Ebene",
    "Layout & Settings": "Layout & Einstellungen",
    "Layout": "Layout",
    "Left, center or right – for text blocks only.": "Links, zentriert oder rechts – nur für Text-Blöcke.",
    "Left": "Links",
    "Less rounded corners": "Weniger abgerundete Ecken",
    "Letter spacing": "Zeichenabstand",
    "Line height & letter spacing": "Zeilenhöhe & Zeichenabstand",
    "Line height": "Zeilenhöhe",
    "Linear": "Linear",
    "Link": "Link",
    "Manage slides": "Folien verwalten",
    "More rounded corners": "Mehr abgerundete Ecken",
    "Move blocks via drag & drop. Resize at the corner handles. Arrow keys: 0.5% per step (Shift = 2%). With the guide grid visible, elements snap automatically to center and third lines.": "Blöcke per Drag & Drop verschieben. An den Ecken-Handles die Größe anpassen. Pfeiltasten: 0,5 % pro Schritt (Shift = 2 %). Mit eingeblendetem Hilfslinien-Raster rasten Elemente automatisch an Mittel- und Drittel-Linien ein.",
    "Move": "Verschieben",
    "Multi-line body text. Double-click to edit. Supports Bold, Italic, Underline, Strikethrough, font family, font size, color, alignment, line-height and letter-spacing, background color/gradient, border (width, style, color, corners) and shadow.": "Mehrzeiliger Fließtext. Doppelklick zum Bearbeiten. Unterstützt Fett, Kursiv, Unterstrichen, Durchgestrichen, Schriftfamilie, Schriftgröße, Farbe, Ausrichtung, Zeilenhöhe und Zeichenabstand, Hintergrundfarbe/-verlauf, Rahmen (Breite, Stil, Farbe, Ecken) und Schatten.",
    "Next →": "Weiter →",
    "No background / border": "Kein Hintergrund / Rahmen",
    "No fill": "Keine Füllung",
    "No overlay": "Keine Überlagerung",
    "No thanks": "Nein, danke",
    "None": "Keine",
    "On the left you can see all slides of the slider. Use the buttons at the top left to add new slides, duplicate or delete them. You can change the order via drag & drop in the sidebar.": "Links siehst du alle Folien des Sliders. Mit den Buttons oben links fügst du neue Folien hinzu, duplizierst oder löschst sie. Per Drag & Drop in der Seitenleiste kannst du die Reihenfolge ändern.",
    "Once an element is selected, this toolbar appears. Depending on the block type, the following are available: font, size, color, bold, italic, underline, strikethrough, alignment, background (solid or gradient), border (width, style, color), corners, shadow, opacity, rotation and link.": "Sobald ein Element ausgewählt ist, erscheint diese Toolbar. Je nach Block-Typ stehen zur Verfügung: Schriftart, -größe, Farbe, Fett, Kursiv, Unterstrichen, Durchgestrichen, Ausrichtung, Hintergrund (einfarbig oder Verlauf), Rahmen (Breite, Stil, Farbe), Ecken, Schatten, Deckkraft, Rotation und Link.",
    "Opacity": "Deckkraft",
    "Opacity / Filter": "Deckkraft / Filter",
    "Outer area (container layout)": "Außenbereich (Container-Layout)",
    "Outer area color": "Außenbereichfarbe",
    "Outer area": "Außenbereich",
    "Overlay": "Überlagerung",
    "Overshoot": "Überschwingen",
    "Padding": "Innenabstand",
    "Paste": "Einfügen",
    "Play preview": "Vorschau abspielen",
    "Position & scale element": "Element positionieren & skalieren",
    "Position & Scale": "Positionieren & Skalieren",
    "Position": "Positionieren",
    "Radial": "Radial",
    "Rectangle": "Rechteck",
    "Redo (Ctrl+Y)": "Wiederholen (Strg+Y)",
    "Remove background color": "Hintergrundfarbe entfernen",
    "Remove background video": "Hintergrundvideo entfernen",
    "Remove the active slide. At least one slide must always remain.": "Die aktive Folie entfernen. Es muss immer mindestens eine Folie vorhanden sein.",
    "Remove video": "Video entfernen",
    "Reorder slides in the left sidebar via drag & drop.": "Folien in der linken Seitenleiste per Drag & Drop neu anordnen.",
    "Reset": "Zurücksetzen",
    "Right-click → Animation → Type. 8 curves from \"Ease\" to \"Elastic\" selectable.": "Rechtsklick → Animation → Art. 8 Kurven von „Ease\" bis „Elastic\" auswählbar.",
    "Right-click → Link or link icon in the button toolbar. Link types: Internal (page), External (URL), Email, Phone, Media Library file.": "Rechtsklick → Link oder Link-Symbol in der Button-Toolbar. Link-Typen: Intern (Seite), Extern (URL), E-Mail, Telefon, Mediathek-Datei.",
    "Right-click menu": "Rechtsklick-Menü",
    "Right-click on an element → \"Bring forward\" / \"Send backward\" to change the stacking order.": "Rechtsklick auf ein Element → „Nach vorne\" / „Nach hinten\", um die Stapelreihenfolge zu ändern.",
    "Right-click on an element → Animation → select effect. 18 entrance effects available (Slide, Fade, Zoom, Bounce, Fly in, etc.).": "Rechtsklick auf ein Element → Animation → Effekt auswählen. 18 Einblend-Effekte verfügbar (Slide, Fade, Zoom, Bounce, Fly-in usw.).",
    "Fly-in animations": "Fly-in Animationen",
    "4 new effects: \"Fly from top / bottom / left / right\". The element starts completely outside the visible area and flies into its position – no fade, pure movement.": "4 neue Effekte: „Fliegen von oben / unten / links / rechts\". Das Element startet komplett außerhalb des sichtbaren Bereichs und fliegt in seine Position – kein Einblenden, nur Bewegung.",
    "Right-click on an element → Animation → select effect. Set delay and duration in the same menu or adjust by dragging in the \"Animation Timeline\" (button at the bottom).": "Rechtsklick auf ein Element → Animation → Effekt auswählen. Verzögerung und Dauer im gleichen Menü einstellen oder durch Ziehen in der „Animations-Zeitleiste\" (Button unten) anpassen.",
    "Right-click on an element → Link. Available types: Internal, External, Email, Phone and Media Library file. Button blocks also have a link icon directly in the toolbar.": "Rechtsklick auf ein Element → Link. Verfügbare Typen: Intern, Extern, E-Mail, Telefon und Mediathek-Datei. Button-Blöcke haben auch ein Link-Symbol direkt in der Toolbar.",
    "Right-click on an element opens: Duplicate, Delete, Bring forward/backward, Link, Animation.": "Rechtsklick auf ein Element öffnet: Duplizieren, Löschen, Nach vorne/hinten, Link, Animation.",
    "Right-clicking on an element opens the context menu: duplicate, delete, move layer forward/backward, assign link and configure animation – all in one place.": "Rechtsklick auf ein Element öffnet das Kontextmenü: Duplizieren, Löschen, Ebene nach vorne/hinten, Link zuweisen und Animation konfigurieren – alles an einem Ort.",
    "Right": "Rechts",
    "Rise up": "Aufsteigen",
    "Fly from top": "Fliegen von oben",
    "Fly from bottom": "Fliegen von unten",
    "Fly from left": "Fliegen von links",
    "Fly from right": "Fliegen von rechts",
    "Rotate in": "Eindrehen",
    "Rotate": "Drehen",
    "Rotation": "Rotation",
    "Border / Corners": "Rahmen / Ecken",
    "Round corners": "Ecken abrunden",
    "Safety frame (8% margin), center lines (50%) and third lines (33 / 66%). Elements snap automatically to these lines when dragging.": "Sicherheitsrahmen (8 % Rand), Mittellinien (50 %) und Drittellinien (33 / 66 %). Elemente rasten beim Ziehen automatisch an diesen Linien ein.",
    "Save": "Speichern",
    "Scale": "Skalieren",
    "Select a block → the block toolbar appears automatically at the top. Depending on type: font, color, gradient, border, shadow, corners, opacity, rotation etc. Double-click on text or button blocks to edit the text directly.": "Block auswählen → die Block-Toolbar erscheint automatisch oben. Je nach Typ: Schrift, Farbe, Verlauf, Rahmen, Schatten, Ecken, Deckkraft, Rotation usw. Doppelklick auf Text- oder Button-Blöcke, um den Text direkt zu bearbeiten.",
    "Select background": "Hintergrund auswählen",
    "Select image": "Bild auswählen",
    "Select video": "Video auswählen",
    "Send to back": "Nach hinten",
    "Sepia": "Sepia",
    "Set color": "Farbe setzen",
    "Set line-height and letter-spacing in the typography dropdown (Aa icon).": "Zeilenhöhe und Zeichenabstand im Typografie-Dropdown (Aa-Symbol) einstellen.",
    "Set the height in vw in the layout panel (toolbar) (10–100). The banner is then x% as tall as the screen is wide.": "Die Höhe in vw im Layout-Panel (Toolbar) einstellen (10–100). Der Banner ist dann x % so hoch wie der Bildschirm breit ist.",
    "Set up background": "Hintergrund einrichten",
    "Duotone": "Duotone",
    "Custom": "Benutzerdefiniert",
    "Highlight": "Glanzlicht",
    "Remove": "Entfernen",
    "Kirsche": "Kirsche",
    "Fuchsia": "Fuchsia",
    "Pop": "Pop",
    "Violett": "Violett",
    "Ozeanblau": "Ozeanblau",
    "Ozeangrün": "Ozeangrün",
    "Senfgelb": "Senfgelb",
    "Bernstein": "Bernstein",
    "Pomelo": "Pomelo",
    "Blush": "Blush",
    "Pfefferminz": "Pfefferminz",
    "Mystisch": "Mystisch",
    "Pastell": "Pastell",
    "Koralle": "Koralle",
    "Shadow": "Schatten",
    "Spiegeln": "Spiegeln",
    "Horizontal": "Horizontal",
    "Vertikal": "Vertikal",
    "Shape block": "Form-Block",
    "Shape": "Form",
    "Shapes": "Formen",
    "Show button at the bottom. Displays all blocks of the active slide in a timeline – change delay and duration by dragging.": "Button unten anzeigen. Zeigt alle Blöcke der aktiven Folie in einer Zeitleiste an – Verzögerung und Dauer durch Ziehen ändern.",
    "Show/hide guides": "Hilfslinien ein-/ausblenden",
    "Crop image": "Bild zuschneiden",
    "Change crop": "Ausschnitt ändern",
    "Remove crop": "Ausschnitt entfernen",
    "Remove duotone": "Duotone entfernen",
    "Set crop": "Ausschnitt festlegen",
    "Skip": "Überspringen",
    "Slide background": "Folien-Hintergrund",
    "Slide toolbar": "Folien-Toolbar",
    "Slide": "Folie",
    "Slides": "Folien",
    "Smooth": "Sanft",
    "Solid or gradient (linear with angle or radial). Two colors freely selectable, CI colors are offered as quick selection.": "Einfarbig oder Verlauf (linear mit Winkel oder radial). Zwei Farben frei wählbar, CI-Farben werden als Schnellauswahl angeboten.",
    "Solid": "Einfarbig",
    "Standard colors": "Standardfarben",
    "Star": "Stern",
    "Stretch": "Strecken",
    "Start guided tour": "Geführte Tour starten",
    "Start tour": "Tour starten",
    "Step-by-step guide": "Schritt-für-Schritt-Anleitung",
    "Stepwise": "Stufenweise",
    "Strikethrough": "Durchgestrichen",
    "Style": "Stil",
    "Text & Button toolbar": "Text & Button Toolbar",
    "Text alignment": "Textausrichtung",
    "Text background": "Text-Hintergrund",
    "Text block": "Text-Block",
    "Text color": "Textfarbe",
    "Text": "Text",
    "This short tour shows you all the important features. You can click \"Skip\" at any time or restart the tour via \"Help & Instructions\".": "Diese kurze Tour zeigt dir alle wichtigen Funktionen. Du kannst jederzeit auf „Überspringen\" klicken oder die Tour über „Hilfe & Anleitung\" erneut starten.",
    "Top edge": "Oberkante",
    "Top": "Oben",
    "Transparent": "Transparent",
    "Triangle": "Dreieck",
    "Type": "Typ",
    "Typography": "Typografie",
    "Underline": "Unterstrichen",
    "Undo (Ctrl+Z)": "Rückgängig (Strg+Z)",
    "Undo / Redo": "Rückgängig / Wiederholen",
    "Undo & Save": "Rückgängig & Speichern",
    "Unit vw": "Einheit vw",
    "Use the \"+ Element\" button at the bottom right to add new blocks to the slide. Options: Text, Shapes (Rectangle, Circle, Triangle, Diamond, Star, Hexagon, Arrow), Button and Image. The button appears once a background is set.": "Mit dem „+ Element\"-Button unten rechts neue Blöcke auf der Folie einfügen. Zur Auswahl stehen: Text, Formen (Rechteck, Kreis, Dreieck, Raute, Stern, Hexagon, Pfeil), Button und Bild. Der Button erscheint sobald ein Hintergrund gesetzt ist.",
    "Video": "Video",
    "Enter video URL": "Video-URL eingeben",
    "YouTube, Vimeo or video URL...": "YouTube-, Vimeo- oder Video-URL...",
    "Autoplay": "Autoplay",
    "Mute": "Stumm",
    "Loop": "Wiederholen",
    "Controls": "Steuerung",
    "Portrait / Landscape": "Hochformat / Querformat",
    "Corner radius": "Eckenradius",
    "Welcome to the Slider Editor!": "Willkommen im Slider-Editor!",
    "Width": "Breite",
    "With \"Centered\" layout the side outer area can be left empty, colored or blurred (image blur).": "Beim „Zentriert\"-Layout kann der seitliche Außenbereich leer gelassen, eingefärbt oder weichgezeichnet werden (Bild-Blur).",
    "Would you like a brief introduction to the most important features?": "Möchtest du eine kurze Einführung in die wichtigsten Funktionen?",
    "Zoom in": "Hereinzoomen",
    "Zoom out": "Herauszoomen"
  }
}
</i18n>
